import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from './supabase';
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

//  FONTS & GLOBAL STYLES 
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; }
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: #F5F0E8; }
    ::-webkit-scrollbar-thumb { background: #C4956A; border-radius: 3px; }
    .fade-in { animation: fadeIn 0.3s ease; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    .slide-in { animation: slideIn 0.25s ease; }
    @keyframes slideIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
    .pulse { animation: pulse 2s infinite; }
    @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
    input, textarea, select { outline: none; }
    button { cursor: pointer; }
  `}</style>
);

//  PALETTE 
const LIGHT = {
  cream: "#F5F0E8", sand: "#E8DFD0", clay: "#C4956A",
  terra: "#B85C38", ink: "#1C2333", slate: "#3D4A5C",
  sage: "#7A9E7E", mist: "#A8BFC4", gold: "#D4A843",
  white: "#FDFCFA", red: "#C0392B", green: "#27AE60",
  ctBg: "#0F1923", ctCard: "#1A2535", ctBorder: "#2A3A52",
  ctAccent: "#3B82F6", ctGold: "#F59E0B",
};
const DARK = {
  cream: "#0F1318",   // page background
  sand: "#1E2535",    // borders, dividers
  clay: "#D4A87A",    // muted accent text
  terra: "#D4744A",   // primary brand accent
  ink: "#EDE8E0",     // primary text (was dark, now light)
  slate: "#8A97A8",   // secondary text
  sage: "#6B9970",    // success
  mist: "#A8BFC4",
  gold: "#D4A843",
  white: "#1A2130",   // card/surface backgrounds (was white surface, now dark surface)
  red: "#E74C3C",
  green: "#3DAA60",
  ctBg: "#0F1923", ctCard: "#1A2535", ctBorder: "#2A3A52",
  ctAccent: "#3B82F6", ctGold: "#F59E0B",
};
// C is set at render time via window.__darkMode
let C = LIGHT;

// ── BACKEND CONFIG ─────────────────────────────────────────────────
// Replace with your Railway/Render URL once deployed
const BACKEND_URL = "renobid-backend-production.up.railway.app";
const STRIPE_PK = "pk_test_51TGHguQ19RAhoxolyIyFQOgkziMC8dK8ShgXsEEBbhEtxH4Tppj8ufUmqzd3RwPYF5ZuhtM6r4nwAZ8OL8qyInDc00pqJBmnUc";
const stripePromise = loadStripe(STRIPE_PK);

//  CATALOG 
//  REAL PRODUCT CATALOG 
// Each product: id, label, brand, price, color (for canvas swatch),
// emoji, img (product photo URL), url (affiliate deep link), sku, w/h (real-world ft)
const CATALOG = {
  "Cabinets": [
    { id:"c1", label:"Shaker White Cabinet", brand:"Hampton Bay", price:229, color:"#F0EDE8", colorB:"#DDD8D0", emoji:"🚪", url:"https://www.homedepot.com/p/Hampton-Bay/301882725", sku:"301882725", w:2.5, h:2.5 },
    { id:"c2", label:"Navy Blue Shaker", brand:"Hampton Bay", price:249, color:"#2C3E6B", colorB:"#1A2845", emoji:"🚪", url:"https://www.homedepot.com/p/Hampton-Bay-Assembled/315753359", sku:"315753359", w:2.5, h:2.5 },
    { id:"c3", label:"Walnut Flat Front", brand:"IKEA SEKTION", price:195, color:"#8B5E3C", colorB:"#6B4424", emoji:"🪵", url:"https://www.ikea.com/us/en/p/sektion-base-cabinet-frame-walnut-effect-50449516/", sku:"50449516", w:2.5, h:2.5 },
    { id:"c4", label:"Sage Green Shaker", brand:"Hampton Bay", price:259, color:"#7A9E7E", colorB:"#5A7E5E", emoji:"🚪", url:"https://www.homedepot.com/p/Hampton-Bay/316284958", sku:"316284958", w:2.5, h:2.5 },
    { id:"c5", label:"Matte Black Shaker", brand:"Diamond NOW", price:289, color:"#2A2A2A", colorB:"#111111", emoji:"🚪", url:"https://www.homedepot.com/p/Diamond-NOW/316770152", sku:"316770152", w:2.5, h:2.5 },
    { id:"c6", label:"Natural Oak Shaker", brand:"IKEA SEKTION", price:215, color:"#C4955A", colorB:"#A07440", emoji:"🪵", url:"https://www.ikea.com/us/en/p/sektion-base-cabinet-frame-oak-effect-30449520/", sku:"30449520", w:2.5, h:2.5 },
  ],
  "Appliances": [
    { id:"a1", label:"5-Burner Gas Range", brand:"Samsung", price:998, color:"#3A3A3A", colorB:"#222222", emoji:"🍳", url:"https://www.homedepot.com/p/Samsung-30-in-5-Burner-Gas-Range/315234421", sku:"315234421", w:2.5, h:3.0 },
    { id:"a2", label:"French Door Refrigerator", brand:"Samsung", price:1649, color:"#6A8CAA", colorB:"#4A6C8A", emoji:"🧊", url:"https://www.homedepot.com/p/Samsung-22-6-cu-ft-Family-Hub/313945285", sku:"313945285", w:3.0, h:5.5 },
    { id:"a3", label:"Top Control Dishwasher", brand:"Bosch", price:849, color:"#888888", colorB:"#666666", emoji:"🫧", url:"https://www.homedepot.com/p/Bosch-800-Series/315321647", sku:"315321647", w:2.0, h:2.83 },
    { id:"a4", label:"Wall Mount Range Hood", brand:"Cosmo", price:399, color:"#5A5A5A", colorB:"#3A3A3A", emoji:"💨", url:"https://www.homedepot.com/p/Cosmo-30-in-500-CFM/308165890", sku:"308165890", w:2.5, h:1.0 },
    { id:"a5", label:"30\" Gas Double Oven", brand:"LG", price:1299, color:"#4A4A4A", colorB:"#2A2A2A", emoji:"🔥", url:"https://www.homedepot.com/p/LG-30-in-7-3-cu-ft-Smart/316468823", sku:"316468823", w:2.5, h:3.0 },
    { id:"a6", label:"Undercounter Wine Fridge", brand:"NewAir", price:429, color:"#7A4A6A", colorB:"#5A2A4A", emoji:"🍷", url:"https://www.homedepot.com/p/NewAir-29-Bottle/315456123", sku:"315456123", w:2.0, h:2.83 },
  ],
  "Flooring": [
    { id:"f1", label:"Pergo Oak Hardwood", brand:"Pergo", price:3.49, color:"#C8A96E", colorB:"#A08040", emoji:"🪵", url:"https://www.homedepot.com/p/Pergo-Outlast-Natural-Oak/203476804", sku:"203476804", w:12, h:2, priceUnit:"/sq ft" },
    { id:"f2", label:"Calacatta Marble Tile", brand:"MSI", price:8.99, color:"#E8E4DC", colorB:"#C8C0B0", emoji:"⬜", url:"https://www.homedepot.com/p/MSI-Calacatta-Gold-12x12/206658731", sku:"206658731", w:12, h:2, priceUnit:"/sq ft" },
    { id:"f3", label:"Hex Cement Porcelain", brand:"Daltile", price:4.49, color:"#8C8C8C", colorB:"#6C6C6C", emoji:"⬡", url:"https://www.homedepot.com/p/Daltile-Hex-Mosaic/206036893", sku:"206036893", w:12, h:2, priceUnit:"/sq ft" },
    { id:"f4", label:"Herringbone LVP", brand:"LifeProof", price:2.99, color:"#B89B6A", colorB:"#8B7040", emoji:"🪵", url:"https://www.homedepot.com/p/LifeProof-Sterling-Oak/310166611", sku:"310166611", w:12, h:2, priceUnit:"/sq ft" },
    { id:"f5", label:"Wide Plank Vinyl", brand:"COREtec", price:4.19, color:"#A08060", colorB:"#806040", emoji:"🟫", url:"https://www.homedepot.com/p/COREtec-Pro-Plus/301808527", sku:"301808527", w:12, h:2, priceUnit:"/sq ft" },
    { id:"f6", label:"Terrazzo Porcelain", brand:"Emser", price:6.49, color:"#C8C0B8", colorB:"#A8A098", emoji:"🪨", url:"https://www.homedepot.com/p/Emser-Tile-Terrazzo/315897264", sku:"315897264", w:12, h:2, priceUnit:"/sq ft" },
  ],
  "Tile & Walls": [
    { id:"w1", label:"Shiplap Wall Panels", brand:"Timberwall", price:3.99, color:"#D4C5B0", colorB:"#B4A590", emoji:"🏠", url:"https://www.homedepot.com/p/Timberwall-Shiplap-White/310177628", sku:"310177628", w:8, h:8, priceUnit:"/sq ft" },
    { id:"w2", label:"Subway Tile 3×6\"", brand:"Daltile", price:1.89, color:"#E8E8E8", colorB:"#C8C8C8", emoji:"⬜", url:"https://www.homedepot.com/p/Daltile-Arctic-White-3x6/100674993", sku:"100674993", w:4, h:3, priceUnit:"/sq ft" },
    { id:"w3", label:"Ledger Stone Panel", brand:"MSI", price:7.99, color:"#C4714E", colorB:"#A45030", emoji:"🪨", url:"https://www.homedepot.com/p/MSI-Ledger-Panel/202263397", sku:"202263397", w:4, h:4, priceUnit:"/sq ft" },
    { id:"w4", label:"Peel & Stick Wallpaper", brand:"NuWallpaper", price:39, color:"#E8C5D0", colorB:"#C8A0B0", emoji:"🌸", url:"https://www.homedepot.com/p/NuWallpaper-Floral-Peel-Stick/315234811", sku:"315234811", w:8, h:8, priceUnit:"/roll" },
    { id:"w5", label:"Marble Hex Backsplash", brand:"MSI", price:14.99, color:"#F5F0EB", colorB:"#D5CFC8", emoji:"💎", url:"https://www.homedepot.com/p/MSI-Carrara-White-Hex/205591852", sku:"205591852", w:4, h:3, priceUnit:"/sq ft" },
    { id:"w6", label:"Beadboard Paneling", brand:"Wainscot Solutions", price:2.49, color:"#EDE8DF", colorB:"#CDC8BF", emoji:"🏠", url:"https://www.homedepot.com/p/Wainscot-Solutions-Beadboard/205697512", sku:"205697512", w:4, h:8, priceUnit:"/sq ft" },
  ],
  "Fixtures": [
    { id:"x1", label:"Farmhouse Apron Sink", brand:"Kohler", price:895, color:"#E0DDD8", colorB:"#B0ADA8", emoji:"🚿", url:"https://www.homedepot.com/p/Kohler-Whitehaven-35-5-in/202543811", sku:"202543811", w:3.0, h:2.0 },
    { id:"x2", label:"3-Light Pendant Set", brand:"Globe Electric", price:129, color:"#D4A843", colorB:"#A47823", emoji:"💡", url:"https://www.homedepot.com/p/Globe-Electric-3-Light-Pendant/313456780", sku:"313456780", w:1.5, h:1.0 },
    { id:"x3", label:"Barn Door Kit 36\"", brand:"Barndoorz", price:349, color:"#8B6A4A", colorB:"#6B4A2A", emoji:"🚪", url:"https://www.homedepot.com/p/Barndoorz-36-in-Barn-Door/310267541", sku:"310267541", w:3.0, h:6.5 },
    { id:"x4", label:"Electric Fireplace Insert", brand:"Dimplex", price:549, color:"#C4714E", colorB:"#843420", emoji:"🔥", url:"https://www.homedepot.com/p/Dimplex-25-in-Electric-Fireplace/313567123", sku:"313567123", w:2.5, h:2.0 },
    { id:"x5", label:"Floating Wall Shelves", brand:"Wallniture", price:89, color:"#C8A96E", colorB:"#A07838", emoji:"📚", url:"https://www.homedepot.com/p/Wallniture-Floating-Shelves/315234199", sku:"315234199", w:3.0, h:1.5 },
    { id:"x6", label:"Vessel Sink Round", brand:"Kraus", price:199, color:"#F5F0EB", colorB:"#C5C0BB", emoji:"🫙", url:"https://www.homedepot.com/p/Kraus-Viva-Round-Vessel/314567234", sku:"314567234", w:1.5, h:0.5 },
  ],
  "Countertops": [
    { id:"t1", label:"White Quartz", brand:"Silestone", price:75, color:"#F5F2EE", colorB:"#E0DBD5", emoji:"⬜", url:"https://www.homedepot.com/p/Silestone-Lyra-White/301234501", sku:"301234501", w:10, h:2, priceUnit:"/sq ft" },
    { id:"t2", label:"Calacatta Quartz", brand:"MSI Q Premium", price:85, color:"#EDE8E0", colorB:"#D5CEC5", emoji:"🪨", url:"https://www.homedepot.com/p/MSI-Q-Premium-Calacatta-Laza/315234601", sku:"315234601", w:10, h:2, priceUnit:"/sq ft" },
    { id:"t3", label:"Absolute Black Granite", brand:"MSI", price:65, color:"#2A2A2A", colorB:"#111111", emoji:"🖤", url:"https://www.homedepot.com/p/MSI-Absolute-Black-Granite/205891001", sku:"205891001", w:10, h:2, priceUnit:"/sq ft" },
    { id:"t4", label:"Butcher Block Walnut", brand:"IKEA BADELUNDA", price:45, color:"#8B6340", colorB:"#6B4820", emoji:"🪵", url:"https://www.ikea.com/us/en/p/badelunda-countertop-walnut-80511756/", sku:"80511756", w:10, h:2, priceUnit:"/sq ft" },
    { id:"t5", label:"Concrete Gray", brand:"Buddy Rhodes", price:95, color:"#9A9A9A", colorB:"#7A7A7A", emoji:"🪨", url:"https://www.homedepot.com/p/Buddy-Rhodes-Concrete-Countertop/314567101", sku:"314567101", w:10, h:2, priceUnit:"/sq ft" },
    { id:"t6", label:"Carrara Marble", brand:"MSI", price:110, color:"#EAE6E0", colorB:"#C8C2BA", emoji:"💎", url:"https://www.homedepot.com/p/MSI-Carrara-White-Marble/204912001", sku:"204912001", w:10, h:2, priceUnit:"/sq ft" },
  ],
  "Lighting": [
    { id:"l1", label:"Aged Brass Pendant", brand:"Progress Lighting", price:149, color:"#C8A050", colorB:"#A07828", emoji:"💡", url:"https://www.homedepot.com/p/Progress-Lighting-Aged-Brass/313901001", sku:"313901001", w:1.0, h:1.0 },
    { id:"l2", label:"Matte Black Pendant", brand:"Hampton Bay", price:79, color:"#2A2A2A", colorB:"#111111", emoji:"🔦", url:"https://www.homedepot.com/p/Hampton-Bay-Matte-Black-Pendant/315678901", sku:"315678901", w:1.0, h:1.0 },
    { id:"l3", label:"Linear LED Island Light", brand:"Kichler", price:299, color:"#B8B8B8", colorB:"#888888", emoji:"💡", url:"https://www.homedepot.com/p/Kichler-Stelata-LED-Linear/316789012", sku:"316789012", w:3.0, h:0.5 },
    { id:"l4", label:"Recessed Lighting Kit (6pk)", brand:"Halo", price:129, color:"#FFFFFF", colorB:"#DDDDDD", emoji:"⭕", url:"https://www.homedepot.com/p/Halo-4-in-Recessed-LED-6-Pack/316890123", sku:"316890123", w:1.0, h:0.5 },
    { id:"l5", label:"Under-Cabinet LED Strip", brand:"Armacost Lighting", price:49, color:"#F5F0E0", colorB:"#D5CDB8", emoji:"✨", url:"https://www.homedepot.com/p/Armacost-Lighting-LED-Strip/315901234", sku:"315901234", w:4.0, h:0.5, priceUnit:"/kit" },
    { id:"l6", label:"Drum Chandelier", brand:"Possini Euro", price:229, color:"#D4C8B0", colorB:"#B0A888", emoji:"🕯️", url:"https://www.homedepot.com/p/Possini-Euro-Drum-Chandelier/314012345", sku:"314012345", w:2.0, h:1.5 },
  ],
  "Paint": [
    { id:"p1", label:"White Dove OC-17", brand:"Benjamin Moore", price:72, color:"#F5F2EC", colorB:"#E5E0D8", emoji:"🎨", url:"https://www.benjaminmoore.com/en-us/paint-colors/color/OC-17/white-dove", sku:"OC-17", w:8, h:8, priceUnit:"/gal" },
    { id:"p2", label:"Chantilly Lace OC-65", brand:"Benjamin Moore", price:72, color:"#F8F6F2", colorB:"#E8E4DE", emoji:"🎨", url:"https://www.benjaminmoore.com/en-us/paint-colors/color/OC-65/chantilly-lace", sku:"OC-65", w:8, h:8, priceUnit:"/gal" },
    { id:"p3", label:"Accessible Beige SW 7036", brand:"Sherwin-Williams", price:68, color:"#D4C8B0", colorB:"#B8AA94", emoji:"🎨", url:"https://www.sherwin-williams.com/homeowners/color/find-and-explore-colors/paint-colors-by-family/SW7036-accessible-beige", sku:"SW7036", w:8, h:8, priceUnit:"/gal" },
    { id:"p4", label:"Agreeable Gray SW 7029", brand:"Sherwin-Williams", price:68, color:"#C8C0B0", colorB:"#A8A090", emoji:"🎨", url:"https://www.sherwin-williams.com/homeowners/color/find-and-explore-colors/paint-colors-by-family/SW7029-agreeable-gray", sku:"SW7029", w:8, h:8, priceUnit:"/gal" },
    { id:"p5", label:"Hale Navy HC-154", brand:"Benjamin Moore", price:72, color:"#2C3E52", colorB:"#1A2838", emoji:"🎨", url:"https://www.benjaminmoore.com/en-us/paint-colors/color/HC-154/hale-navy", sku:"HC-154", w:8, h:8, priceUnit:"/gal" },
    { id:"p6", label:"Black Beauty 2128-10", brand:"Benjamin Moore", price:72, color:"#1E1E1E", colorB:"#0A0A0A", emoji:"🎨", url:"https://www.benjaminmoore.com/en-us/paint-colors/color/2128-10/black-beauty", sku:"2128-10", w:8, h:8, priceUnit:"/gal" },
  ],
};

const ROOMS = ["Kitchen","Bathroom","Living Room","Bedroom","Basement","Dining Room","Office","Laundry"];
const BUDGETS = ["Under $5k","$5k–$10k","$10k–$25k","$25k–$50k","$50k+"];

// ============================================================
// FULL_SCOPE_CATALOG — 15 categories, 134 items
// ============================================================
 
const FULL_SCOPE_CATALOG = [
  {
    category: "DEMOLITION & SITE PREP",
    items: [
      { id: "demo_001", category: "DEMOLITION & SITE PREP", label: "Selective demo — cabinets", unit: "each", unit_label: "per cabinet", trade: "demo", permit_risk: "none", contractor_only: true, typical_cost_low: 50, typical_cost_high: 150, notes: "Price varies by cabinet size and access; includes haul-away", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many cabinets and are any full-height or specialty units?", homeowner_prompt: "Are all cabinets being removed or just select ones?", labor_low: 40, labor_high: 120, material_low: 10, material_high: 30 },
      { id: "demo_002", category: "DEMOLITION & SITE PREP", label: "Selective demo — countertops", unit: "LF", unit_label: "per linear foot", trade: "demo", permit_risk: "none", contractor_only: true, typical_cost_low: 15, typical_cost_high: 40, notes: "Stone counters require two-person crew; disconnect plumbing first", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is the countertop stone or laminate, and is there an undermount sink requiring plumbing disconnect?", homeowner_prompt: "Are countertops being fully removed or just in certain areas?", labor_low: 12, labor_high: 32, material_low: 3, material_high: 8 },
      { id: "demo_003", category: "DEMOLITION & SITE PREP", label: "Selective demo — flooring", unit: "SF", unit_label: "per sq ft", trade: "demo", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 4, notes: "Tile demo is highest cost; hardwood and carpet lower; asbestos testing required pre-1980", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "What flooring material is being demoed — tile, hardwood, carpet, or LVP?", homeowner_prompt: "Is the entire floor being removed or just select areas?", labor_low: 1, labor_high: 4, material_low: 0, material_high: 0 },
      { id: "demo_004", category: "DEMOLITION & SITE PREP", label: "Non-load-bearing wall removal", unit: "each", unit_label: "per wall", trade: "demo", permit_risk: "possible", contractor_only: true, typical_cost_low: 500, typical_cost_high: 1500, notes: "Confirm non-structural before demo; may include patching floors/ceiling", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Has the wall been confirmed non-load-bearing? Will patching floor/ceiling be needed after?", homeowner_prompt: "Is this a full wall removal or just a portion? Any electrical or plumbing in the wall?", labor_low: 400, labor_high: 1200, material_low: 100, material_high: 300 },
      { id: "demo_005", category: "DEMOLITION & SITE PREP", label: "Load-bearing wall removal", unit: "each", unit_label: "per wall", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 3000, typical_cost_high: 10000, notes: "Requires engineer stamp, permit, and temporary shoring; beam sizing critical", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Has a structural engineer provided a beam spec? What is the wall length and ceiling height?", homeowner_prompt: "Has a structural engineer reviewed this? Are you aware this requires a permit?", labor_low: 2000, labor_high: 7000, material_low: 1000, material_high: 3000 },
      { id: "demo_006", category: "DEMOLITION & SITE PREP", label: "Selective demo — tile / backsplash", unit: "SF", unit_label: "per sq ft", trade: "demo", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 6, notes: "Old thin-set can be stubborn; budget extra for plaster substrate damage", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "Is this tile on concrete/mortar bed or standard drywall/backer board?", homeowner_prompt: "Is the tile on walls, floor, or both? Any tile that must be preserved?", labor_low: 2, labor_high: 6, material_low: 0, material_high: 0 },
      { id: "demo_007", category: "DEMOLITION & SITE PREP", label: "Selective demo — drywall", unit: "SF", unit_label: "per sq ft", trade: "demo", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 3, notes: "Test for asbestos in homes pre-1980; leads to additional remediation cost", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "Is this a full room of drywall removal or selective sections? Is the home pre-1980 (asbestos risk)?", homeowner_prompt: "Is the entire room being demoed to studs or just damaged sections?", labor_low: 1, labor_high: 3, material_low: 0, material_high: 0 },
      { id: "demo_008", category: "DEMOLITION & SITE PREP", label: "Full gut — single room", unit: "each", unit_label: "per room", trade: "demo", permit_risk: "possible", contractor_only: true, typical_cost_low: 2000, typical_cost_high: 6000, notes: "Down to studs; includes all dumpster/haul-away; does not include hazmat", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the approximate room size and what surfaces are included — all the way to studs?", homeowner_prompt: "Is this a complete gut to studs including ceiling and all walls?", labor_low: 1500, labor_high: 4500, material_low: 500, material_high: 1500 },
      { id: "demo_009", category: "DEMOLITION & SITE PREP", label: "Dumpster / haul-away", unit: "per load", unit_label: "per dumpster load", trade: "demo", permit_risk: "none", contractor_only: false, typical_cost_low: 400, typical_cost_high: 800, notes: "10–15 yard dumpster typical for single-room gut; street permit may be needed", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many dumpster loads do you anticipate? Is there driveway access or will a street permit be needed?", homeowner_prompt: "Do you have HOA restrictions on dumpsters or a narrow driveway that limits placement?", labor_low: 100, labor_high: 200, material_low: 300, material_high: 600 },
      { id: "demo_010", category: "DEMOLITION & SITE PREP", label: "Hazardous material testing", unit: "flat", unit_label: "flat fee", trade: "demo", permit_risk: "none", contractor_only: true, typical_cost_low: 300, typical_cost_high: 800, notes: "Asbestos and lead paint testing; required before demo in pre-1978 homes in many jurisdictions", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many rooms need testing and is the home pre-1980?", homeowner_prompt: "Do you have any existing test results for asbestos or lead paint?", labor_low: 200, labor_high: 500, material_low: 100, material_high: 300 },
      { id: "demo_011", category: "DEMOLITION & SITE PREP", label: "Hazardous material remediation", unit: "flat", unit_label: "flat fee", trade: "demo", permit_risk: "likely", contractor_only: true, typical_cost_low: 1500, typical_cost_high: 8000, notes: "Licensed abatement contractor required; cost varies heavily by scope", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What materials are confirmed present and what is the square footage of the affected area?", homeowner_prompt: "Do you have a test report identifying what hazardous materials are present and where?", labor_low: 1000, labor_high: 5000, material_low: 500, material_high: 3000 },
      { id: "demo_012", category: "DEMOLITION & SITE PREP", label: "Window / door rough opening demo", unit: "each", unit_label: "per opening", trade: "demo", permit_risk: "possible", contractor_only: true, typical_cost_low: 300, typical_cost_high: 900, notes: "Include in structural permit if load-bearing header is involved", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "Is the wall at this opening load-bearing? Will a temporary header be needed during demo?", homeowner_prompt: "Do you know if the wall at this location is load-bearing?", labor_low: 300, labor_high: 900, material_low: 0, material_high: 0 },
      { id: "demo_013", category: "DEMOLITION & SITE PREP", label: "Concrete / masonry breaking", unit: "SF", unit_label: "per sq ft", trade: "demo", permit_risk: "none", contractor_only: true, typical_cost_low: 5, typical_cost_high: 15, notes: "Jackhammer rental or saw cut; reinforced concrete adds cost", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this concrete reinforced with rebar? What is the thickness and total area?", homeowner_prompt: "Is this a slab, sidewalk, or foundation wall?", labor_low: 4, labor_high: 12, material_low: 1, material_high: 3 },
      { id: "demo_014", category: "DEMOLITION & SITE PREP", label: "Site protection / dust containment", unit: "flat", unit_label: "flat fee", trade: "demo", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 600, notes: "Zip-wall systems and floor protection; essential for occupied homes", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many doorways need sealing and how many days of protection are anticipated?", homeowner_prompt: "Will the home be occupied during construction?", labor_low: 150, labor_high: 450, material_low: 50, material_high: 150 },
    ]
  },
  {
    category: "STRUCTURAL",
    items: [
      { id: "struct_001", category: "STRUCTURAL", label: "Beam installation (LVL / steel)", unit: "LF", unit_label: "per linear foot", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 200, typical_cost_high: 600, notes: "Requires engineer sizing; steel beams need crane or boom lift", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Do you have an engineer-stamped beam spec? What is the span and load condition?", homeowner_prompt: "Has a structural engineer provided drawings for this beam?", labor_low: 100, labor_high: 300, material_low: 100, material_high: 300 },
      { id: "struct_002", category: "STRUCTURAL", label: "Post / column installation", unit: "each", unit_label: "per post", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 500, typical_cost_high: 2000, notes: "Point loads require footing inspection; flush beam pockets add cost", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the post height, load condition, and footing situation?", homeowner_prompt: "Where exactly are posts being added and what are they supporting?", labor_low: 300, labor_high: 1200, material_low: 200, material_high: 800 },
      { id: "struct_003", category: "STRUCTURAL", label: "Foundation crack repair", unit: "LF", unit_label: "per linear foot", trade: "structural", permit_risk: "possible", contractor_only: true, typical_cost_low: 100, typical_cost_high: 400, notes: "Epoxy injection for hairline cracks; carbon fiber straps for bowing walls", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the total linear footage of cracks and have they been tested for moisture intrusion?", homeowner_prompt: "Are the cracks actively leaking water or dry?", labor_low: 75, labor_high: 300, material_low: 25, material_high: 100 },
      { id: "struct_004", category: "STRUCTURAL", label: "Subfloor repair / sistering", unit: "SF", unit_label: "per sq ft", trade: "structural", permit_risk: "none", contractor_only: true, typical_cost_low: 5, typical_cost_high: 20, notes: "Sistering joists for bounce; full replacement needed if rot or termite damage", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the extent of damage — bounce only, or visible rot/termite damage?", homeowner_prompt: "Have you noticed soft spots, bounce, or visible damage in the subfloor?", labor_low: 4, labor_high: 15, material_low: 1, material_high: 5 },
      { id: "struct_005", category: "STRUCTURAL", label: "Roof framing repair", unit: "flat", unit_label: "flat fee", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 2000, typical_cost_high: 8000, notes: "Rafter sister or ridge board repair; permit required; engineer may be needed", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Are rafters broken or just cracked? Is there a permit and engineer already on board?", homeowner_prompt: "What is the extent of the roof framing damage — is the roof currently leaking?", labor_low: 1500, labor_high: 6000, material_low: 500, material_high: 2000 },
      { id: "struct_006", category: "STRUCTURAL", label: "Header installation — window/door", unit: "each", unit_label: "per opening", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 800, typical_cost_high: 2500, notes: "Size per span tables; temporary shoring required during install", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the span of the opening and what header size has been spec'd?", homeowner_prompt: "Is this for a new opening or replacing an existing undersized header?", labor_low: 600, labor_high: 2000, material_low: 200, material_high: 500 },
      { id: "struct_007", category: "STRUCTURAL", label: "Deck ledger / structural repair", unit: "flat", unit_label: "flat fee", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 1000, typical_cost_high: 4000, notes: "Ledger flashing failure is top deck defect; through-bolt connections required", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the ledger length and what material is the house-side framing?", homeowner_prompt: "Are you aware of any existing deck ledger leaks or flex?", labor_low: 700, labor_high: 2500, material_low: 300, material_high: 1500 },
      { id: "struct_008", category: "STRUCTURAL", label: "Crawl space / basement structural work", unit: "flat", unit_label: "flat fee", trade: "structural", permit_risk: "likely", contractor_only: true, typical_cost_low: 3000, typical_cost_high: 15000, notes: "Beam jacking, pier replacement; highly variable; get multiple quotes", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What specific work is needed — beam jacking, pier replacement, or drainage correction?", homeowner_prompt: "Has an engineer assessed the crawl space or basement condition?", labor_low: 2000, labor_high: 10000, material_low: 1000, material_high: 5000 },
    ]
  },
  {
    category: "PLUMBING",
    items: [
      { id: "plumb_001", category: "PLUMBING", label: "Fixture rough-in — new location", unit: "each", unit_label: "per fixture", trade: "plumbing", permit_risk: "likely", contractor_only: true, typical_cost_low: 500, typical_cost_high: 1500, notes: "Moving drain stack or supply lines requires opening walls/floors; permit almost always needed", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is moving — toilet, sink, tub, or shower? How far is the new location from existing stack?", homeowner_prompt: "Which fixture is moving and approximately how far from its current location?", labor_low: 400, labor_high: 1200, material_low: 100, material_high: 300 },
      { id: "plumb_002", category: "PLUMBING", label: "Fixture swap — same location", unit: "each", unit_label: "per fixture", trade: "plumbing", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 400, notes: "Like-for-like toilet, faucet, or valve replacement; no rough-in changes", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "How many fixtures are being swapped? Any shutoff valve replacement needed?", homeowner_prompt: "Which fixtures are being replaced — toilet, faucet, valve, or all of the above?", labor_low: 150, labor_high: 400, material_low: 0, material_high: 0 },
      { id: "plumb_003", category: "PLUMBING", label: "Toilet installation", unit: "each", unit_label: "per toilet", trade: "plumbing", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 350, notes: "Includes wax ring and supply line; add $100–200 for flange repair", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "Is the flange in good condition or does it need repair? Is the rough-in 12\"?", homeowner_prompt: "Have you selected and purchased a toilet?", labor_low: 150, labor_high: 350, material_low: 0, material_high: 0 },
      { id: "plumb_004", category: "PLUMBING", label: "Sink / faucet installation", unit: "each", unit_label: "per sink", trade: "plumbing", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 400, notes: "Undermount sinks require countertop coordination; add for vessel sink drain height", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "Is this an undermount, drop-in, or vessel sink? Are shutoffs in good condition?", homeowner_prompt: "Have you selected and purchased the sink and faucet?", labor_low: 150, labor_high: 400, material_low: 0, material_high: 0 },
      { id: "plumb_005", category: "PLUMBING", label: "Shower / tub rough-in", unit: "each", unit_label: "per unit", trade: "plumbing", permit_risk: "likely", contractor_only: true, typical_cost_low: 800, typical_cost_high: 2500, notes: "Drain location and slope critical; shower pan liner or pre-slope needed before tile", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this a shower-only or tub-shower combo? Is the drain moving from its current location?", homeowner_prompt: "Is the shower staying in the same location or moving?", labor_low: 600, labor_high: 2000, material_low: 200, material_high: 500 },
      { id: "plumb_006", category: "PLUMBING", label: "Water heater replacement", unit: "each", unit_label: "per unit", trade: "plumbing", permit_risk: "possible", contractor_only: true, typical_cost_low: 800, typical_cost_high: 2000, notes: "Tankless units cost more; permit often required; expansion tank may be code-required", supply_model: "either", bid_type: "either", contractor_prompt: "What size/type is the existing unit — tank or tankless? Is the homeowner supplying the new heater?", homeowner_prompt: "Are you supplying the water heater or would you like the contractor to source and supply it?", labor_low: 400, labor_high: 800, material_low: 400, material_high: 1200 },
      { id: "plumb_007", category: "PLUMBING", label: "Supply line replacement (whole home)", unit: "flat", unit_label: "flat fee", trade: "plumbing", permit_risk: "likely", contractor_only: true, typical_cost_low: 4000, typical_cost_high: 12000, notes: "Repipe with PEX; cost varies by home size and access; permit always required", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the home size and current supply line material — galvanized, copper, or other?", homeowner_prompt: "How large is your home and how many bathrooms does it have?", labor_low: 3000, labor_high: 8000, material_low: 1000, material_high: 4000 },
      { id: "plumb_008", category: "PLUMBING", label: "Drain / waste / vent (DWV) work", unit: "flat", unit_label: "flat fee", trade: "plumbing", permit_risk: "likely", contractor_only: true, typical_cost_low: 1000, typical_cost_high: 5000, notes: "Cast iron replacement or new ABS/PVC runs; scope-dependent pricing", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What work is needed — new drain runs, cast iron replacement, or both? What is the total linear footage?", homeowner_prompt: "Is this a new drain layout or replacement of existing drain lines?", labor_low: 800, labor_high: 4000, material_low: 200, material_high: 1000 },
      { id: "plumb_009", category: "PLUMBING", label: "Dishwasher installation", unit: "each", unit_label: "per unit", trade: "plumbing", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 300, notes: "Includes supply, drain, and air gap; electrical hook-up billed separately", supply_model: "either", bid_type: "either", contractor_prompt: "Is there an existing water line and drain? Is an air gap required by local code? Are you supplying the dishwasher or is the homeowner?", homeowner_prompt: "Are you supplying the dishwasher or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 300, material_low: 0, material_high: 0 },
      { id: "plumb_010", category: "PLUMBING", label: "Gas line — new run or extension", unit: "LF", unit_label: "per linear foot", trade: "plumbing", permit_risk: "likely", contractor_only: true, typical_cost_low: 20, typical_cost_high: 60, notes: "Licensed plumber or gas fitter required; pressure test and inspection mandatory", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the total linear footage and what size pipe is required?", homeowner_prompt: "What appliance needs the gas connection — stove, dryer, fireplace, or other?", labor_low: 15, labor_high: 45, material_low: 5, material_high: 15 },
      { id: "plumb_011", category: "PLUMBING", label: "Outdoor hose bib / irrigation tie-in", unit: "each", unit_label: "per bib", trade: "plumbing", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 500, notes: "Frost-free sill cock recommended in cold climates; backflow preventer for irrigation", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this a new hose bib or replacing an existing one? Is it on an exterior wall with easy access?", homeowner_prompt: "Is this for irrigation, outdoor use, or both?", labor_low: 150, labor_high: 400, material_low: 50, material_high: 100 },
    ]
  },
  {
    category: "ELECTRICAL",
    items: [
      { id: "elec_001", category: "ELECTRICAL", label: "Panel upgrade / replacement", unit: "each", unit_label: "per panel", trade: "electrical", permit_risk: "likely", contractor_only: true, typical_cost_low: 2000, typical_cost_high: 5000, notes: "100A to 200A is most common upgrade; utility coordination may add days; permit always required", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the current panel size and what size upgrade is needed? Is utility side work included?", homeowner_prompt: "Do you know the current amperage of your electrical panel?", labor_low: 1200, labor_high: 3000, material_low: 800, material_high: 2000 },
      { id: "elec_002", category: "ELECTRICAL", label: "New circuit — dedicated", unit: "each", unit_label: "per circuit", trade: "electrical", permit_risk: "likely", contractor_only: true, typical_cost_low: 300, typical_cost_high: 800, notes: "Kitchen appliances, EV charger, A/C; requires permit in most jurisdictions", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many circuits, what amperage, and how far is the run from the panel?", homeowner_prompt: "What appliances or equipment need dedicated circuits?", labor_low: 200, labor_high: 600, material_low: 100, material_high: 200 },
      { id: "elec_003", category: "ELECTRICAL", label: "Outlet / switch addition or relocation", unit: "each", unit_label: "per device", trade: "electrical", permit_risk: "possible", contractor_only: true, typical_cost_low: 150, typical_cost_high: 350, notes: "GFCI required in wet areas; AFCI required in bedrooms per NEC 2020", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many outlets/switches and are any in wet areas requiring GFCI? Are walls open or finished?", homeowner_prompt: "Are walls open or finished? Which rooms need outlets added?", labor_low: 120, labor_high: 280, material_low: 30, material_high: 70 },
      { id: "elec_004", category: "ELECTRICAL", label: "Recessed lighting installation", unit: "each", unit_label: "per can", trade: "electrical", permit_risk: "possible", contractor_only: true, typical_cost_low: 100, typical_cost_high: 250, notes: "IC-rated required at insulated ceilings; add for dimmer switches", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many cans, are ceilings open or finished, and are dimmers included?", homeowner_prompt: "How many recessed lights and in which rooms?", labor_low: 75, labor_high: 200, material_low: 25, material_high: 50 },
      { id: "elec_005", category: "ELECTRICAL", label: "Under-cabinet lighting", unit: "LF", unit_label: "per linear foot", trade: "electrical", permit_risk: "none", contractor_only: false, typical_cost_low: 30, typical_cost_high: 80, notes: "LED strip or puck lights; hardwired is cleaner than plug-in", supply_model: "either", bid_type: "either", contractor_prompt: "Is this hardwired or plug-in? What total linear footage of cabinet run? Are you supplying the lighting or is the homeowner?", homeowner_prompt: "Are you supplying the lighting fixtures or would you like the contractor to source and supply them?", labor_low: 25, labor_high: 70, material_low: 0, material_high: 0 },
      { id: "elec_006", category: "ELECTRICAL", label: "Ceiling fan / light fixture installation", unit: "each", unit_label: "per fixture", trade: "electrical", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 300, notes: "Fan-rated box required for ceiling fans; heavy fixtures need blocking", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "Is there an existing box rated for a fan? Is a new circuit or switch needed?", homeowner_prompt: "Have you selected and purchased the ceiling fan or light fixture?", labor_low: 100, labor_high: 300, material_low: 0, material_high: 0 },
      { id: "elec_007", category: "ELECTRICAL", label: "Smoke / CO detector installation", unit: "each", unit_label: "per unit", trade: "electrical", permit_risk: "none", contractor_only: false, typical_cost_low: 75, typical_cost_high: 200, notes: "Interconnected hardwired units required by code in new work; battery backup required", supply_model: "either", bid_type: "either", contractor_prompt: "How many units and are they hardwired interconnected or battery-only? Are you supplying the detectors?", homeowner_prompt: "Are you supplying the smoke/CO detectors or would you like the contractor to source and supply them?", labor_low: 75, labor_high: 200, material_low: 0, material_high: 0 },
      { id: "elec_008", category: "ELECTRICAL", label: "EV charger installation (Level 2)", unit: "each", unit_label: "per charger", trade: "electrical", permit_risk: "likely", contractor_only: true, typical_cost_low: 500, typical_cost_high: 1500, notes: "240V/50A dedicated circuit; panel capacity check required; rebates often available", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What amperage charger and is there panel capacity for the new circuit?", homeowner_prompt: "What type of EV do you have and what charging speed are you looking for?", labor_low: 400, labor_high: 1200, material_low: 100, material_high: 300 },
      { id: "elec_009", category: "ELECTRICAL", label: "Whole-home rewire", unit: "flat", unit_label: "flat fee", trade: "electrical", permit_risk: "likely", contractor_only: true, typical_cost_low: 8000, typical_cost_high: 20000, notes: "Knob-and-tube or aluminum wiring replacement; highly invasive; open walls typically required", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What type of existing wiring — knob-and-tube or aluminum? What is the home square footage?", homeowner_prompt: "Do you have documentation of the existing wiring type?", labor_low: 6000, labor_high: 15000, material_low: 2000, material_high: 5000 },
      { id: "elec_010", category: "ELECTRICAL", label: "Thermostat / smart home device install", unit: "each", unit_label: "per device", trade: "electrical", permit_risk: "none", contractor_only: false, typical_cost_low: 75, typical_cost_high: 200, notes: "Smart thermostats may need C-wire adapter; check HVAC compatibility", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "Is there an existing C-wire for thermostat compatibility? What HVAC brand and system type?", homeowner_prompt: "Have you selected and purchased the smart thermostat or device?", labor_low: 75, labor_high: 200, material_low: 0, material_high: 0 },
      { id: "elec_011", category: "ELECTRICAL", label: "Exhaust fan installation", unit: "each", unit_label: "per fan", trade: "electrical", permit_risk: "none", contractor_only: true, typical_cost_low: 200, typical_cost_high: 500, notes: "Must vent to exterior (not attic); combo fan/light units are popular", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is there an existing duct path to exterior? Is this in an insulated ceiling requiring an IC-rated unit?", homeowner_prompt: "Is there an existing exhaust fan or is this new? Does it need to vent a shower?", labor_low: 150, labor_high: 400, material_low: 50, material_high: 100 },
    ]
  },
  {
    category: "HVAC",
    items: [
      { id: "hvac_001", category: "HVAC", label: "Central A/C — new system", unit: "per system", unit_label: "per system", trade: "hvac", permit_risk: "likely", contractor_only: true, typical_cost_low: 4000, typical_cost_high: 10000, notes: "Sizing by Manual J required; permit and refrigerant certification mandatory", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the home square footage, climate zone, and current system type?", homeowner_prompt: "Is this a replacement for an existing system or new installation in a home without central A/C?", labor_low: 1500, labor_high: 4000, material_low: 2500, material_high: 6000 },
      { id: "hvac_002", category: "HVAC", label: "Furnace replacement", unit: "per system", unit_label: "per system", trade: "hvac", permit_risk: "likely", contractor_only: true, typical_cost_low: 2500, typical_cost_high: 6000, notes: "Gas permit required; flue sizing and CO testing at completion", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the BTU size of the existing furnace and what fuel type?", homeowner_prompt: "Is this replacing a failed furnace or an upgrade for efficiency?", labor_low: 1000, labor_high: 2500, material_low: 1500, material_high: 3500 },
      { id: "hvac_003", category: "HVAC", label: "Mini-split installation (single zone)", unit: "each", unit_label: "per zone", trade: "hvac", permit_risk: "possible", contractor_only: true, typical_cost_low: 2000, typical_cost_high: 5000, notes: "No ductwork needed; ideal for additions or garages; requires electrical circuit", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many zones, what BTU size, and does the homeowner want a heat pump or cooling-only unit?", homeowner_prompt: "How many rooms or zones need to be conditioned?", labor_low: 800, labor_high: 2000, material_low: 1200, material_high: 3000 },
      { id: "hvac_004", category: "HVAC", label: "Ductwork — new or modification", unit: "flat", unit_label: "flat fee", trade: "hvac", permit_risk: "possible", contractor_only: true, typical_cost_low: 1000, typical_cost_high: 5000, notes: "Manual D sizing required for new systems; flex duct vs sheet metal affects cost", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this new duct for an addition or modifying existing? Total linear footage estimate?", homeowner_prompt: "Is this extending existing ductwork or installing a new system?", labor_low: 600, labor_high: 3000, material_low: 400, material_high: 2000 },
      { id: "hvac_005", category: "HVAC", label: "Duct cleaning", unit: "per system", unit_label: "per system", trade: "hvac", permit_risk: "none", contractor_only: false, typical_cost_low: 300, typical_cost_high: 700, notes: "Recommended before occupying renovated homes; NADCA-certified contractors preferred", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many vents/registers and what is the rough air handler size?", homeowner_prompt: "When was the last time ducts were cleaned? Is this post-renovation or routine?", labor_low: 250, labor_high: 600, material_low: 50, material_high: 100 },
      { id: "hvac_006", category: "HVAC", label: "Radiant floor heat — new", unit: "SF", unit_label: "per sq ft", trade: "hvac", permit_risk: "possible", contractor_only: true, typical_cost_low: 8, typical_cost_high: 20, notes: "Electric mat is simplest; hydronic is more efficient for large areas but expensive", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this electric mat or hydronic? What is the square footage to be heated?", homeowner_prompt: "Is this for a bathroom, kitchen, or other room?", labor_low: 4, labor_high: 10, material_low: 4, material_high: 10 },
      { id: "hvac_007", category: "HVAC", label: "Heat pump water heater", unit: "each", unit_label: "per unit", trade: "hvac", permit_risk: "possible", contractor_only: true, typical_cost_low: 1200, typical_cost_high: 3000, notes: "Significant energy savings; needs adequate airspace around unit; federal tax credit available", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the existing water heater fuel type and size? Is there adequate airspace in the installation area?", homeowner_prompt: "Is this a replacement or new installation?", labor_low: 500, labor_high: 1000, material_low: 700, material_high: 2000 },
      { id: "hvac_008", category: "HVAC", label: "Ventilation / ERV / HRV installation", unit: "each", unit_label: "per unit", trade: "hvac", permit_risk: "possible", contractor_only: true, typical_cost_low: 1500, typical_cost_high: 4000, notes: "Required in tight homes per ASHRAE 62.2; improves IAQ and controls moisture", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What system type — ERV or HRV — and what is the home size?", homeowner_prompt: "Is this for a new build or retrofit into an existing home?", labor_low: 600, labor_high: 1500, material_low: 900, material_high: 2500 },
    ]
  },
  {
    category: "INSULATION",
    items: [
      { id: "insul_001", category: "INSULATION", label: "Batt insulation — walls", unit: "SF", unit_label: "per sq ft of wall", trade: "insulation", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 3, notes: "R-13 to R-21 for 2x4 to 2x6 walls; install before drywall", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the wall thickness (2x4 or 2x6) and what R-value is required by local code?", homeowner_prompt: "Are walls currently open or will they need to be opened?", labor_low: 0.5, labor_high: 1.5, material_low: 0.5, material_high: 1.5 },
      { id: "insul_002", category: "INSULATION", label: "Blown-in insulation — attic", unit: "SF", unit_label: "per sq ft", trade: "insulation", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 3, notes: "R-38 to R-60 recommended; cellulose or fiberglass; baffles required at eaves", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the attic square footage and current R-value? Are baffles installed at eaves?", homeowner_prompt: "Do you know the current insulation R-value in your attic?", labor_low: 0.5, labor_high: 1.5, material_low: 0.5, material_high: 1.5 },
      { id: "insul_003", category: "INSULATION", label: "Spray foam insulation", unit: "SF", unit_label: "per sq ft", trade: "insulation", permit_risk: "none", contractor_only: true, typical_cost_low: 3, typical_cost_high: 8, notes: "Closed-cell for moisture barrier and high R-value; open-cell for interior walls; off-gassing period needed", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this open-cell or closed-cell? Are surfaces open or existing walls?", homeowner_prompt: "Is this for the entire home, a specific wall system, or an addition?", labor_low: 2, labor_high: 5, material_low: 1, material_high: 3 },
      { id: "insul_004", category: "INSULATION", label: "Rigid foam — foundation / exterior", unit: "SF", unit_label: "per sq ft", trade: "insulation", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 5, notes: "XPS or polyiso; must be covered by thermal barrier (drywall) on interior", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What thickness is needed and will it be on foundation walls or below slab?", homeowner_prompt: "Is this for a basement or exterior foundation?", labor_low: 1, labor_high: 3, material_low: 1, material_high: 2 },
      { id: "insul_005", category: "INSULATION", label: "Soundproofing insulation", unit: "SF", unit_label: "per sq ft of wall/ceiling", trade: "insulation", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 6, notes: "Rockwool Safe'n'Sound most common; resilient channels add significant STC improvement", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What STC rating is the goal and will resilient channels be used?", homeowner_prompt: "Which walls or ceilings need soundproofing and what level of sound control are you looking for?", labor_low: 1, labor_high: 4, material_low: 1, material_high: 2 },
      { id: "insul_006", category: "INSULATION", label: "Air sealing", unit: "flat", unit_label: "flat fee", trade: "insulation", permit_risk: "none", contractor_only: true, typical_cost_low: 500, typical_cost_high: 2000, notes: "Blower-door test before and after recommended; biggest bang for buck in older homes", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Will a blower-door test be performed before and after? What areas are priority — attic, rim joists, penetrations?", homeowner_prompt: "Do you have an energy audit report identifying air leakage areas?", labor_low: 400, labor_high: 1500, material_low: 100, material_high: 500 },
    ]
  },
  {
    category: "DRYWALL & PLASTER",
    items: [
      { id: "dwall_001", category: "DRYWALL & PLASTER", label: "Drywall hang — new walls", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 3, notes: "1/2\" standard; 5/8\" type X for fire-rated assemblies; moisture-resistant in baths", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What thickness and type — standard, moisture-resistant, or fire-rated? Is this for a bathroom?", homeowner_prompt: "Which rooms need new drywall hung?", labor_low: 0.5, labor_high: 1.5, material_low: 0.5, material_high: 1.5 },
      { id: "dwall_002", category: "DRYWALL & PLASTER", label: "Drywall tape, mud & finish", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: true, typical_cost_low: 1, typical_cost_high: 3, notes: "Level 4 finish standard for paint; Level 5 needed under flat or skim-coat finish", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What finish level is specified — Level 4 for standard paint or Level 5 for high-gloss?", homeowner_prompt: "What finish level are you expecting — standard smooth for paint or ultra-smooth for specialty finishes?", labor_low: 0.75, labor_high: 2.5, material_low: 0.25, material_high: 0.5 },
      { id: "dwall_003", category: "DRYWALL & PLASTER", label: "Drywall patch — small repair", unit: "each", unit_label: "per patch", trade: "drywall", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 400, notes: "Feathering a patch to blend texture is skilled work; matching texture adds cost", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the approximate size of the patch and what caused the damage?", homeowner_prompt: "What caused the damage and how large is the area?", labor_low: 120, labor_high: 350, material_low: 30, material_high: 50 },
      { id: "dwall_004", category: "DRYWALL & PLASTER", label: "Skim coat — existing walls", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: true, typical_cost_low: 2, typical_cost_high: 5, notes: "Restores smooth finish on damaged plaster; two coats typical", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the square footage and how damaged are the existing walls?", homeowner_prompt: "Are entire rooms being skim coated or just damaged areas?", labor_low: 1.5, labor_high: 4, material_low: 0.5, material_high: 1 },
      { id: "dwall_005", category: "DRYWALL & PLASTER", label: "Plaster repair", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: true, typical_cost_low: 5, typical_cost_high: 15, notes: "Three-coat plaster matching is specialty work; test for horsehair and keys", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this traditional 3-coat plaster or a modern base coat? What is the extent of damage?", homeowner_prompt: "How large is the area of plaster damage?", labor_low: 4, labor_high: 12, material_low: 1, material_high: 3 },
      { id: "dwall_006", category: "DRYWALL & PLASTER", label: "Ceiling drywall — hang and finish", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: true, typical_cost_low: 3, typical_cost_high: 6, notes: "Overhead work is slower; 5/8\" often spec'd for sag resistance", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the ceiling area and height? Is access difficult or are there obstructions?", homeowner_prompt: "What is the ceiling square footage and is this new or replacement?", labor_low: 2, labor_high: 4.5, material_low: 1, material_high: 1.5 },
      { id: "dwall_007", category: "DRYWALL & PLASTER", label: "Texture application", unit: "SF", unit_label: "per sq ft", trade: "drywall", permit_risk: "none", contractor_only: true, typical_cost_low: 1, typical_cost_high: 3, notes: "Orange peel, knockdown, or smooth; matching existing texture is an art", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What texture is needed — knockdown, orange peel, skip trowel, or to match existing?", homeowner_prompt: "Do you want texture to match existing walls or a new texture style?", labor_low: 0.75, labor_high: 2.5, material_low: 0.25, material_high: 0.5 },
      { id: "dwall_008", category: "DRYWALL & PLASTER", label: "Beadboard / wainscoting install", unit: "SF", unit_label: "per sq ft", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 5, typical_cost_high: 15, notes: "MDF is most affordable; solid wood for high moisture areas; cap rail and base required", supply_model: "either", bid_type: "either", contractor_prompt: "What style beadboard — MDF panels or real tongue-and-groove? Is the homeowner supplying the material?", homeowner_prompt: "Are you supplying the beadboard panels or would you like the contractor to source them?", labor_low: 4, labor_high: 12, material_low: 1, material_high: 3 },
    ]
  },
  {
    category: "TILE WORK",
    items: [
      { id: "tile_001", category: "TILE WORK", label: "Floor tile — installation", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 8, typical_cost_high: 20, notes: "Includes substrate prep, thinset, and grout; large format tile adds cost for leveling", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying tile? What tile size — standard, large format, or mosaic?", homeowner_prompt: "Are you supplying the floor tile or would you like the contractor to source it?", labor_low: 6, labor_high: 15, material_low: 2, material_high: 5 },
      { id: "tile_002", category: "TILE WORK", label: "Wall tile — installation", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 10, typical_cost_high: 25, notes: "Subway tile is baseline; mosaic and large format add cost; waterproofing behind shower tile critical", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying tile? What tile size and is this in a wet area?", homeowner_prompt: "Are you supplying the wall tile or would you like the contractor to source it?", labor_low: 8, labor_high: 20, material_low: 2, material_high: 5 },
      { id: "tile_003", category: "TILE WORK", label: "Shower surround — full tile", unit: "each", unit_label: "per shower", trade: "tile", permit_risk: "none", contractor_only: true, typical_cost_low: 2000, typical_cost_high: 6000, notes: "Includes Schluter/waterproofing system, tile, grout, and niche if needed", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying tile? What are the shower dimensions and does it include a niche?", homeowner_prompt: "Have you selected tile for the shower surround?", labor_low: 1500, labor_high: 4500, material_low: 500, material_high: 1500 },
      { id: "tile_004", category: "TILE WORK", label: "Backsplash tile — kitchen", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 15, typical_cost_high: 35, notes: "Electrical outlet cutouts add time; outlet extenders usually needed after adding tile", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying tile? What is the square footage and will outlet cutouts be needed?", homeowner_prompt: "Have you selected the backsplash tile?", labor_low: 10, labor_high: 25, material_low: 5, material_high: 10 },
      { id: "tile_005", category: "TILE WORK", label: "Tile demo and replacement", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 10, typical_cost_high: 25, notes: "Demo is included; substrate damage common; budget for backer board repair", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying replacement tile? What is the area and what substrate is under existing tile?", homeowner_prompt: "Do you have replacement tile selected or are you looking for the contractor to match/source it?", labor_low: 8, labor_high: 20, material_low: 2, material_high: 5 },
      { id: "tile_006", category: "TILE WORK", label: "Grout recolor / re-grout", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 3, typical_cost_high: 8, notes: "Colorant can refresh old grout; full re-grout for cracked or moldy joints", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the total square footage and is this recolor or full re-grout?", homeowner_prompt: "Is the grout discolored, cracked, or moldy?", labor_low: 2, labor_high: 7, material_low: 1, material_high: 1 },
      { id: "tile_007", category: "TILE WORK", label: "Waterproofing membrane", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: true, typical_cost_low: 3, typical_cost_high: 8, notes: "Schluter Kerdi, RedGard, or Wedi; required in all wet areas; do not skip", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What system are you using — Schluter Kerdi, RedGard, or Wedi? What is the shower footprint?", homeowner_prompt: "This is a required layer in your shower — the contractor will include it.", labor_low: 2, labor_high: 6, material_low: 1, material_high: 2 },
      { id: "tile_008", category: "TILE WORK", label: "Shower pan / liner installation", unit: "each", unit_label: "per shower", trade: "tile", permit_risk: "none", contractor_only: true, typical_cost_low: 500, typical_cost_high: 1500, notes: "CPE liner for mud-set; pre-slope required; flood test before tiling", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this a CPE liner mud-set or will a prefab base be used? What are the shower dimensions?", homeowner_prompt: "Is the shower pan staying or being replaced?", labor_low: 300, labor_high: 1000, material_low: 200, material_high: 500 },
      { id: "tile_009", category: "TILE WORK", label: "Heated floor mat — electric", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 10, typical_cost_high: 20, notes: "Install before tile; thermostat required; use uncoupling membrane above", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the heating mat? What is the square footage to be heated?", homeowner_prompt: "Are you supplying the heated floor mat or would you like the contractor to source it?", labor_low: 5, labor_high: 12, material_low: 5, material_high: 8 },
      { id: "tile_010", category: "TILE WORK", label: "Tile trim / edge strips (Schluter)", unit: "LF", unit_label: "per linear foot", trade: "tile", permit_risk: "none", contractor_only: false, typical_cost_low: 5, typical_cost_high: 15, notes: "Schluter Jolly or Rondec for exposed edges; required for professional finish", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What linear footage of exposed tile edges? What Schluter profile — Jolly, Rondec, or other?", homeowner_prompt: "This is typically included as part of tile installation.", labor_low: 3, labor_high: 10, material_low: 2, material_high: 5 },
      { id: "tile_011", category: "TILE WORK", label: "Natural stone tile — installation", unit: "SF", unit_label: "per sq ft", trade: "tile", permit_risk: "none", contractor_only: true, typical_cost_low: 20, typical_cost_high: 50, notes: "Marble, travertine, slate; sealing required; back-butter every piece; heavy for floor substrates", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the stone? What type of stone and what is the square footage?", homeowner_prompt: "Have you selected and purchased the natural stone tile?", labor_low: 15, labor_high: 40, material_low: 5, material_high: 10 },
    ]
  },
  {
    category: "FLOORING",
    items: [
      { id: "floor_001", category: "FLOORING", label: "Hardwood flooring — install", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 6, typical_cost_high: 14, notes: "Nail-down for wood subfloor; glue-down over concrete; acclimate wood 3+ days before install", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the hardwood? What species, width, and finish are specified?", homeowner_prompt: "Are you supplying the hardwood flooring or would you like the contractor to source it?", labor_low: 4, labor_high: 10, material_low: 2, material_high: 4 },
      { id: "floor_002", category: "FLOORING", label: "Hardwood flooring — sand and refinish", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: true, typical_cost_low: 4, typical_cost_high: 8, notes: "3 coats oil-based or water-based poly; 24–48hr cure per coat; vacate during work", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many coats of poly and is this oil-based or water-based? Are stairs included?", homeowner_prompt: "Is this for a full refinish or spot repair? What finish sheen are you looking for?", labor_low: 3, labor_high: 6, material_low: 1, material_high: 2 },
      { id: "floor_003", category: "FLOORING", label: "Luxury vinyl plank (LVP) — install", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 3, typical_cost_high: 8, notes: "100% waterproof; floating install over most substrates; underlayment often included", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the LVP? What is the square footage and subfloor condition?", homeowner_prompt: "Have you selected LVP or would you like the contractor to supply it?", labor_low: 2, labor_high: 6, material_low: 1, material_high: 2 },
      { id: "floor_004", category: "FLOORING", label: "Carpet — install", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 3, typical_cost_high: 8, notes: "Includes pad; power stretching required; seams should avoid high-traffic paths", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying carpet and pad? What is the square footage and are there stairs?", homeowner_prompt: "Have you selected carpet and pad or would you like the contractor to supply it?", labor_low: 2, labor_high: 5, material_low: 1, material_high: 3 },
      { id: "floor_005", category: "FLOORING", label: "Carpet removal", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 1, typical_cost_high: 2, notes: "Tack strip removal adds cost; check condition of subfloor underneath", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "What is the square footage and are tack strips being removed? What is under the carpet?", homeowner_prompt: "Is this in preparation for new flooring installation?", labor_low: 1, labor_high: 2, material_low: 0, material_high: 0 },
      { id: "floor_006", category: "FLOORING", label: "Subfloor leveling / self-leveling compound", unit: "SF", unit_label: "per sq ft", trade: "flooring", permit_risk: "none", contractor_only: true, typical_cost_low: 2, typical_cost_high: 6, notes: "Required for LVP and tile if floor is out of flat; max 3/16\" per 10ft for most flooring", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How much variation is in the floor — what is the flatness measurement? Self-leveling compound or plywood?", homeowner_prompt: "Is there a hump, dip, or uneven area that needs leveling?", labor_low: 1, labor_high: 4, material_low: 1, material_high: 2 },
      { id: "floor_007", category: "FLOORING", label: "Transition strips / thresholds", unit: "each", unit_label: "per transition", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 50, typical_cost_high: 150, notes: "T-molding, reducer, or end cap; match flooring finish; snap-in for ease", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying transition strips? How many transitions and what flooring is on each side?", homeowner_prompt: "Have you selected transition strips or would you like the contractor to supply matching ones?", labor_low: 30, labor_high: 100, material_low: 20, material_high: 50 },
      { id: "floor_008", category: "FLOORING", label: "Stair treads / risers — replacement", unit: "each", unit_label: "per step", trade: "flooring", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 300, notes: "Retread over existing stringer is fastest; full replacement for squeaks or damage", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying treads and risers? How many steps and what material?", homeowner_prompt: "Have you selected stair treads and risers or would you like the contractor to source them?", labor_low: 75, labor_high: 200, material_low: 25, material_high: 100 },
    ]
  },
  {
    category: "CABINETRY & MILLWORK",
    items: [
      { id: "carp_001", category: "CABINETRY & MILLWORK", label: "Kitchen cabinets — upper", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 150, typical_cost_high: 600, notes: "Stock vs semi-custom vs custom; install only (cabinets purchased separately) runs $50–100/LF", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying cabinets? What is the total linear footage of uppers?", homeowner_prompt: "Are you supplying upper cabinets or would you like the contractor to source and supply them?", labor_low: 50, labor_high: 100, material_low: 100, material_high: 500 },
      { id: "carp_002", category: "CABINETRY & MILLWORK", label: "Kitchen cabinets — base", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 200, typical_cost_high: 800, notes: "Includes toe kick; drawers cost more than doors; dovetail boxes are quality indicator", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying cabinets? What is the total linear footage of base cabinets?", homeowner_prompt: "Are you supplying base cabinets or would you like the contractor to source and supply them?", labor_low: 50, labor_high: 100, material_low: 150, material_high: 700 },
      { id: "carp_003", category: "CABINETRY & MILLWORK", label: "Kitchen island — cabinet build-out", unit: "each", unit_label: "per island", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 1500, typical_cost_high: 5000, notes: "Includes cabinets only; countertop, plumbing, electrical billed separately", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying island cabinet components? What are the island dimensions?", homeowner_prompt: "Are you supplying island cabinets or would you like the contractor to source them?", labor_low: 500, labor_high: 1500, material_low: 1000, material_high: 3500 },
      { id: "carp_004", category: "CABINETRY & MILLWORK", label: "Bathroom vanity installation", unit: "each", unit_label: "per vanity", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 600, notes: "Plumbing connection billed separately; freestanding vs built-in affects difficulty", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the vanity? Is it freestanding or built-in?", homeowner_prompt: "Have you selected and purchased a vanity, or would you like the contractor to source one?", labor_low: 150, labor_high: 400, material_low: 50, material_high: 200 },
      { id: "carp_005", category: "CABINETRY & MILLWORK", label: "Built-in shelving / bookcase", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 200, typical_cost_high: 700, notes: "Site-built vs prefab; include crown and base for integrated look", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this site-built or from prefab components? What is the total linear footage?", homeowner_prompt: "Is this for a living room, office, or other space?", labor_low: 150, labor_high: 500, material_low: 50, material_high: 200 },
      { id: "carp_006", category: "CABINETRY & MILLWORK", label: "Crown molding installation", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 5, typical_cost_high: 20, notes: "Coped inside corners for professional look; wider profiles add cost for compound angles", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying crown molding? What is the profile size and total linear footage?", homeowner_prompt: "Are you supplying crown molding or would you like the contractor to source it?", labor_low: 4, labor_high: 15, material_low: 1, material_high: 5 },
      { id: "carp_007", category: "CABINETRY & MILLWORK", label: "Baseboard / casing installation", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 3, typical_cost_high: 10, notes: "Includes door and window casing; nail and fill before paint", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying trim? What profile and total linear footage?", homeowner_prompt: "Are you supplying baseboard and casing or would you like the contractor to source it?", labor_low: 2, labor_high: 7, material_low: 1, material_high: 3 },
      { id: "carp_008", category: "CABINETRY & MILLWORK", label: "Closet system installation", unit: "each", unit_label: "per closet", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 500, typical_cost_high: 3000, notes: "Wire shelving vs laminate vs custom wood; walk-in vs reach-in scope differs significantly", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the closet system? Is this walk-in or reach-in and what material?", homeowner_prompt: "Are you supplying a closet system (e.g., IKEA PAX, California Closets, wire shelving) or would you like the contractor to source it?", labor_low: 300, labor_high: 1500, material_low: 200, material_high: 1500 },
      { id: "carp_009", category: "CABINETRY & MILLWORK", label: "Laundry room cabinetry", unit: "flat", unit_label: "flat fee", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 500, typical_cost_high: 2000, notes: "Upper cabinets, folding shelf, and rod; often DIY-friendly with stock cabinets", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying cabinets? What is the scope — upper cabinets only or full build-out?", homeowner_prompt: "Are you supplying laundry room cabinets or would you like the contractor to source them?", labor_low: 300, labor_high: 1000, material_low: 200, material_high: 1000 },
      { id: "carp_010", category: "CABINETRY & MILLWORK", label: "Mudroom / entryway built-in", unit: "flat", unit_label: "flat fee", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 1500, typical_cost_high: 6000, notes: "Bench, cubbies, hooks, upper storage; custom width to fit space", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the width of the space and what components — bench, cubbies, upper storage?", homeowner_prompt: "What is the size of the mudroom and what features do you want — bench, hooks, cubbies, upper storage?", labor_low: 1000, labor_high: 4000, material_low: 500, material_high: 2000 },
      { id: "carp_011", category: "CABINETRY & MILLWORK", label: "Cabinet refacing", unit: "LF", unit_label: "per linear foot", trade: "carpentry", permit_risk: "none", contractor_only: true, typical_cost_low: 100, typical_cost_high: 300, notes: "New doors and drawer fronts, veneer on boxes; 50–70% savings vs full replacement if boxes are solid", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What is the total linear footage being refaced? Are new doors included in the price?", homeowner_prompt: "Are the existing cabinet boxes in solid condition — no rot, soft spots, or delamination?", labor_low: 60, labor_high: 150, material_low: 40, material_high: 150 },
      { id: "carp_012", category: "CABINETRY & MILLWORK", label: "Cabinet hardware installation", unit: "each", unit_label: "per piece", trade: "carpentry", permit_risk: "none", contractor_only: false, typical_cost_low: 5, typical_cost_high: 20, notes: "Template jig required for consistent placement; soft-close hinges are standard expectation", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "How many pieces of hardware total? Is a template jig being used for consistent placement?", homeowner_prompt: "Have you purchased and have on-hand all cabinet hardware?", labor_low: 5, labor_high: 20, material_low: 0, material_high: 0 },
    ]
  },
  {
    category: "COUNTERTOPS",
    items: [
      { id: "ctr_001", category: "COUNTERTOPS", label: "Quartz countertop — fabricate and install", unit: "SF", unit_label: "per sq ft", trade: "countertop", permit_risk: "none", contractor_only: true, typical_cost_low: 60, typical_cost_high: 120, notes: "Template after cabinets installed; sink cutout and edge profile included; seams minimized by layout", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Has the slab been selected? What is the total square footage and edge profile?", homeowner_prompt: "Have you selected a specific quartz brand and color?", labor_low: 15, labor_high: 30, material_low: 45, material_high: 90 },
      { id: "ctr_002", category: "COUNTERTOPS", label: "Granite countertop — fabricate and install", unit: "SF", unit_label: "per sq ft", trade: "countertop", permit_risk: "none", contractor_only: true, typical_cost_low: 50, typical_cost_high: 110, notes: "Stone selection at slab yard; annual sealing recommended; thickness affects price", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Has the slab been selected at the yard? What is the square footage and does it include a sink cutout?", homeowner_prompt: "Have you visited a stone yard to select a granite slab?", labor_low: 15, labor_high: 30, material_low: 35, material_high: 80 },
      { id: "ctr_003", category: "COUNTERTOPS", label: "Butcher block countertop — install", unit: "SF", unit_label: "per sq ft", trade: "countertop", permit_risk: "none", contractor_only: false, typical_cost_low: 30, typical_cost_high: 70, notes: "Oil finish required; avoid over sinks; beautiful aging with proper care", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the butcher block? What length and thickness?", homeowner_prompt: "Have you purchased the butcher block or would you like the contractor to source it?", labor_low: 20, labor_high: 50, material_low: 10, material_high: 20 },
      { id: "ctr_004", category: "COUNTERTOPS", label: "Laminate countertop — install", unit: "SF", unit_label: "per sq ft", trade: "countertop", permit_risk: "none", contractor_only: false, typical_cost_low: 15, typical_cost_high: 40, notes: "Most affordable; post-form prefab limits edge options; DIY-friendly for straight runs", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the laminate? What is the total linear footage and is this prefab or custom?", homeowner_prompt: "Are you supplying the laminate countertop or would you like the contractor to source it?", labor_low: 10, labor_high: 25, material_low: 5, material_high: 15 },
      { id: "ctr_005", category: "COUNTERTOPS", label: "Concrete countertop — custom pour", unit: "SF", unit_label: "per sq ft", trade: "countertop", permit_risk: "none", contractor_only: true, typical_cost_low: 75, typical_cost_high: 150, notes: "Precast or cast-in-place; sealing critical; hairline cracks are normal characteristic", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this precast or cast-in-place? What are the dimensions and any special features?", homeowner_prompt: "Concrete countertops take 2–4 weeks and may have natural hairline variations — are you comfortable with that?", labor_low: 40, labor_high: 80, material_low: 35, material_high: 70 },
    ]
  },
  {
    category: "FIXTURES & APPLIANCES",
    items: [
      { id: "fix_001", category: "FIXTURES & APPLIANCES", label: "Bathtub — supply and install", unit: "each", unit_label: "per tub", trade: "fixture", permit_risk: "possible", contractor_only: true, typical_cost_low: 500, typical_cost_high: 3000, notes: "Alcove vs freestanding vs drop-in; freestanding requires floor-mount faucet rough-in", supply_model: "either", bid_type: "either", contractor_prompt: "Is this an alcove, freestanding, or drop-in? Are you supplying the tub or is the homeowner?", homeowner_prompt: "Are you supplying the bathtub or would you like the contractor to source and supply it?", labor_low: 500, labor_high: 3000, material_low: 0, material_high: 0 },
      { id: "fix_002", category: "FIXTURES & APPLIANCES", label: "Walk-in shower — prefab unit", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "possible", contractor_only: true, typical_cost_low: 1000, typical_cost_high: 4000, notes: "One-piece units limit door size; check rough opening before ordering", supply_model: "either", bid_type: "either", contractor_prompt: "Does the rough opening match the unit dimensions? Are you supplying the prefab unit or is the homeowner?", homeowner_prompt: "Are you supplying the prefab shower unit or would you like the contractor to source and supply it?", labor_low: 1000, labor_high: 4000, material_low: 0, material_high: 0 },
      { id: "fix_003", category: "FIXTURES & APPLIANCES", label: "Toilet — supply and install", unit: "each", unit_label: "per toilet", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 300, typical_cost_high: 800, notes: "ADA height preferred; elongated bowl for comfort; bidet seat compatible rough-in", supply_model: "either", bid_type: "either", contractor_prompt: "Is the flange in good condition? What is the rough-in measurement — 12\" standard? Are you supplying the toilet?", homeowner_prompt: "Are you supplying the toilet or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 350, material_low: 0, material_high: 0 },
      { id: "fix_004", category: "FIXTURES & APPLIANCES", label: "Bathroom faucet — supply and install", unit: "each", unit_label: "per faucet", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 500, notes: "Match hole count to sink; widespread vs centerset vs wall-mount changes rough-in", supply_model: "either", bid_type: "either", contractor_prompt: "Is it widespread (3-hole) or single-hole? Are you supplying the faucet or is the homeowner?", homeowner_prompt: "Are you supplying the bathroom faucet or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 500, material_low: 0, material_high: 0 },
      { id: "fix_005", category: "FIXTURES & APPLIANCES", label: "Kitchen faucet — supply and install", unit: "each", unit_label: "per faucet", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 600, notes: "Pull-down spray is most popular; check deck plate if changing hole count", supply_model: "either", bid_type: "either", contractor_prompt: "How many holes in the sink deck? Are you supplying the faucet or is the homeowner?", homeowner_prompt: "Are you supplying the kitchen faucet or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 500, material_low: 0, material_high: 0 },
      { id: "fix_006", category: "FIXTURES & APPLIANCES", label: "Range / oven — install", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 400, notes: "Gas requires licensed plumber for connection; anti-tip bracket required by code", supply_model: "either", bid_type: "either", contractor_prompt: "Is this gas or electric? Are you supplying the range or is the homeowner?", homeowner_prompt: "Are you supplying the range/oven or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 400, material_low: 0, material_high: 0 },
      { id: "fix_007", category: "FIXTURES & APPLIANCES", label: "Refrigerator — install", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 250, notes: "Ice maker line requires shutoff valve; check cabinet depth vs counter-depth unit", supply_model: "either", bid_type: "either", contractor_prompt: "Is this counter-depth or standard depth? Will an ice maker line be connected? Are you supplying the refrigerator?", homeowner_prompt: "Are you supplying the refrigerator or would you like the contractor to source and supply it?", labor_low: 100, labor_high: 250, material_low: 0, material_high: 0 },
      { id: "fix_008", category: "FIXTURES & APPLIANCES", label: "Range hood / ventilation — install", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "none", contractor_only: true, typical_cost_low: 300, typical_cost_high: 1000, notes: "Duct to exterior required for vented hoods; make-up air may be required over 400 CFM", supply_model: "either", bid_type: "either", contractor_prompt: "Is this ducted to exterior — is there an existing duct path? Are you supplying the range hood or is the homeowner?", homeowner_prompt: "Are you supplying the range hood or would you like the contractor to source and supply it?", labor_low: 300, labor_high: 1000, material_low: 0, material_high: 0 },
      { id: "fix_009", category: "FIXTURES & APPLIANCES", label: "Washer / dryer hookup", unit: "each", unit_label: "per pair", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 300, notes: "Gas dryer requires licensed connection; 240V outlet for electric dryer; proper dryer vent critical", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is this gas or electric dryer? Is there an existing properly-vented dryer duct to exterior?", homeowner_prompt: "Is this a new hookup location or replacing existing connections?", labor_low: 100, labor_high: 300, material_low: 0, material_high: 0 },
      { id: "fix_010", category: "FIXTURES & APPLIANCES", label: "Garbage disposal — install", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 350, notes: "Requires dedicated switch and outlet under sink; check for quiet-rated models", supply_model: "either", bid_type: "either", contractor_prompt: "Is there a switch already or does one need to be added? Are you supplying the disposal or is the homeowner?", homeowner_prompt: "Are you supplying the garbage disposal or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 350, material_low: 0, material_high: 0 },
      { id: "fix_011", category: "FIXTURES & APPLIANCES", label: "Bathroom accessories — install", unit: "flat", unit_label: "per bathroom", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 400, notes: "Towel bars, TP holder, hooks, mirror; blocking in walls during framing makes anchoring easier", supply_model: "owner_supplied", bid_type: "labor_only", contractor_prompt: "How many pieces — towel bars, TP holder, hooks, mirror? Are walls tiled (requires drilling into tile)?", homeowner_prompt: "Have you purchased all bathroom accessories — towel bars, toilet paper holder, hooks, mirror?", labor_low: 150, labor_high: 400, material_low: 0, material_high: 0 },
      { id: "fix_012", category: "FIXTURES & APPLIANCES", label: "Medicine cabinet — install", unit: "each", unit_label: "per unit", trade: "fixture", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 600, notes: "Recessed requires stud bay clearance; check for wiring before cutting", supply_model: "either", bid_type: "either", contractor_prompt: "Is this recessed or surface-mount? What are the unit dimensions? Are you supplying the cabinet?", homeowner_prompt: "Are you supplying the medicine cabinet or would you like the contractor to source and supply it?", labor_low: 150, labor_high: 500, material_low: 0, material_high: 0 },
      { id: "fix_013", category: "FIXTURES & APPLIANCES", label: "Whole-home water filtration — install", unit: "each", unit_label: "per system", trade: "fixture", permit_risk: "possible", contractor_only: true, typical_cost_low: 500, typical_cost_high: 3000, notes: "POE carbon or salt-based softener; bypass valve required; annual filter changes", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the filtration system? What type — carbon, reverse osmosis, or water softener?", homeowner_prompt: "Have you selected a specific water filtration system or would you like the contractor to recommend one?", labor_low: 300, labor_high: 1000, material_low: 200, material_high: 2000 },
    ]
  },
  {
    category: "DOORS & WINDOWS",
    items: [
      { id: "door_001", category: "DOORS & WINDOWS", label: "Interior door — supply and install", unit: "each", unit_label: "per door", trade: "doors_windows", permit_risk: "none", contractor_only: false, typical_cost_low: 200, typical_cost_high: 600, notes: "Prehung vs slab; 80\" standard height; solid core for sound and quality feel", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the door? Is this prehung or slab-only?", homeowner_prompt: "Have you selected and purchased the interior door or would you like the contractor to source it?", labor_low: 150, labor_high: 400, material_low: 50, material_high: 200 },
      { id: "door_002", category: "DOORS & WINDOWS", label: "Exterior door — supply and install", unit: "each", unit_label: "per door", trade: "doors_windows", permit_risk: "possible", contractor_only: true, typical_cost_low: 800, typical_cost_high: 3000, notes: "Insulated fiberglass or steel; proper flashing critical for water intrusion prevention", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the door? Has proper flashing been included in the scope?", homeowner_prompt: "Have you selected the exterior door or would you like the contractor to source it?", labor_low: 400, labor_high: 1500, material_low: 400, material_high: 1500 },
      { id: "door_003", category: "DOORS & WINDOWS", label: "Window replacement — standard", unit: "each", unit_label: "per window", trade: "doors_windows", permit_risk: "possible", contractor_only: true, typical_cost_low: 400, typical_cost_high: 1200, notes: "Insert vs full-frame replacement; full-frame required if rot or rough opening change", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying windows? Is this an insert replacement or full-frame?", homeowner_prompt: "Have you selected windows or would you like the contractor to source them?", labor_low: 200, labor_high: 600, material_low: 200, material_high: 600 },
      { id: "door_004", category: "DOORS & WINDOWS", label: "Sliding glass / patio door — install", unit: "each", unit_label: "per door", trade: "doors_windows", permit_risk: "possible", contractor_only: true, typical_cost_low: 1500, typical_cost_high: 5000, notes: "Multi-point locks standard; low-E glass required for energy code; proper sill flashing critical", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the door? What are the rough opening dimensions?", homeowner_prompt: "Have you selected the sliding/patio door or would you like the contractor to source it?", labor_low: 600, labor_high: 2000, material_low: 900, material_high: 3000 },
      { id: "door_005", category: "DOORS & WINDOWS", label: "Pocket door installation", unit: "each", unit_label: "per door", trade: "doors_windows", permit_risk: "none", contractor_only: true, typical_cost_low: 500, typical_cost_high: 1500, notes: "Requires wall cavity access; avoid if wall has plumbing or electrical; soft-close hardware upgrade worthwhile", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "Is there existing wall cavity access? What is the door opening size?", homeowner_prompt: "Is the pocket door style and size already selected?", labor_low: 350, labor_high: 1000, material_low: 150, material_high: 500 },
      { id: "door_006", category: "DOORS & WINDOWS", label: "Barn door — supply and install", unit: "each", unit_label: "per door", trade: "doors_windows", permit_risk: "none", contractor_only: false, typical_cost_low: 400, typical_cost_high: 1200, notes: "Wall blocking required for track; no full privacy seal; not ideal for bathrooms", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying the barn door and hardware? Is there sufficient wall space for the door to slide?", homeowner_prompt: "Have you purchased the barn door and track hardware?", labor_low: 200, labor_high: 600, material_low: 200, material_high: 600 },
      { id: "door_007", category: "DOORS & WINDOWS", label: "Window / door trim — replace or install", unit: "each", unit_label: "per opening", trade: "doors_windows", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 300, notes: "Match existing profile for cohesive look; extension jambs needed if wall is thicker than door unit", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying trim? What profile and how many openings?", homeowner_prompt: "Are you supplying door/window trim or would you like the contractor to source matching trim?", labor_low: 75, labor_high: 200, material_low: 25, material_high: 100 },
    ]
  },
  {
    category: "PAINTING",
    items: [
      { id: "paint_001", category: "PAINTING", label: "Interior painting — walls", unit: "SF", unit_label: "per sq ft of wall", trade: "painting", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 5, notes: "Two coats standard; includes prep, patch, and primer; eggshell finish for living areas, semi-gloss for baths", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying paint? What sheen level and how many coats?", homeowner_prompt: "Have you selected paint colors? Are you supplying the paint or would you like the contractor to source it?", labor_low: 1.5, labor_high: 4, material_low: 0.5, material_high: 1 },
      { id: "paint_002", category: "PAINTING", label: "Interior painting — ceilings", unit: "SF", unit_label: "per sq ft of ceiling", trade: "painting", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 5, notes: "Flat white is standard; roller spray for large areas; 2 coats over new drywall", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying paint? What finish level is specified — flat or low-sheen?", homeowner_prompt: "Have you selected ceiling paint or would you like the contractor to source it?", labor_low: 1.5, labor_high: 4, material_low: 0.5, material_high: 1 },
      { id: "paint_003", category: "PAINTING", label: "Cabinet painting / refinishing", unit: "each", unit_label: "per door/drawer front", trade: "painting", permit_risk: "none", contractor_only: true, typical_cost_low: 50, typical_cost_high: 150, notes: "Spray finish required for smooth result; proper degloss and primer critical for adhesion", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many doors and drawer fronts total? Are these being sprayed off-site or on-site?", homeowner_prompt: "How many cabinet doors and drawer fronts need to be painted?", labor_low: 40, labor_high: 120, material_low: 10, material_high: 30 },
      { id: "paint_004", category: "PAINTING", label: "Exterior painting — siding", unit: "SF", unit_label: "per sq ft", trade: "painting", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 6, notes: "Pressure wash, prime bare wood, two finish coats; paint during moderate temps for adhesion", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying paint? What siding material and condition — bare wood or existing paint peeling?", homeowner_prompt: "Have you selected exterior paint or would you like the contractor to source it?", labor_low: 1.5, labor_high: 4.5, material_low: 0.5, material_high: 1.5 },
      { id: "paint_005", category: "PAINTING", label: "Trim and door painting", unit: "LF", unit_label: "per linear foot of trim", trade: "painting", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 6, notes: "Semi-gloss standard for trim; brush application for best finish; tape protection adds time", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying trim paint? What total linear footage and how many door faces?", homeowner_prompt: "Have you selected trim paint color or would you like the contractor to source it?", labor_low: 1.5, labor_high: 4.5, material_low: 0.5, material_high: 1.5 },
      { id: "paint_006", category: "PAINTING", label: "Deck / fence staining or painting", unit: "SF", unit_label: "per sq ft", trade: "painting", permit_risk: "none", contractor_only: false, typical_cost_low: 2, typical_cost_high: 5, notes: "Strip or sand before recoat if peeling; penetrating stain lasts longer than film-forming on horizontal surfaces", supply_model: "either", bid_type: "either", contractor_prompt: "Is the homeowner supplying stain or paint? What is the square footage and is the surface in good condition?", homeowner_prompt: "Have you selected deck/fence stain or paint?", labor_low: 1.5, labor_high: 4, material_low: 0.5, material_high: 1 },
    ]
  },
  {
    category: "CLEANUP & GENERAL",
    items: [
      { id: "clean_001", category: "CLEANUP & GENERAL", label: "Post-construction cleaning", unit: "flat", unit_label: "flat fee", trade: "general", permit_risk: "none", contractor_only: false, typical_cost_low: 300, typical_cost_high: 1000, notes: "Includes window cleaning, dust removal, floor protection removal; typically done before punch list walk", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many rooms are included and will there be multiple phases of cleaning?", homeowner_prompt: "Is this a one-time final clean or multiple cleanings during the project?", labor_low: 250, labor_high: 800, material_low: 50, material_high: 200 },
      { id: "clean_002", category: "CLEANUP & GENERAL", label: "Permit fees — estimate", unit: "per permit", unit_label: "per permit", trade: "general", permit_risk: "likely", contractor_only: false, typical_cost_low: 200, typical_cost_high: 2000, notes: "Highly variable by municipality and project value; budget 1–2% of project cost", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "What permits are anticipated — structural, electrical, plumbing?", homeowner_prompt: "Are you aware permit fees are typically billed as a pass-through and not included in trade quotes?", labor_low: 0, labor_high: 0, material_low: 200, material_high: 2000 },
      { id: "clean_003", category: "CLEANUP & GENERAL", label: "Temporary toilet / porta-potty", unit: "per week", unit_label: "per week", trade: "general", permit_risk: "none", contractor_only: false, typical_cost_low: 150, typical_cost_high: 300, notes: "Required for jobs over 2 weeks per OSHA; placement on private property vs street may need permit", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How long is the project estimated to run and where on the property will the porta-potty be placed?", homeowner_prompt: "Will workers have access to a bathroom inside or is a portable toilet needed?", labor_low: 50, labor_high: 100, material_low: 100, material_high: 200 },
      { id: "clean_004", category: "CLEANUP & GENERAL", label: "Project management / general contractor fee", unit: "flat", unit_label: "flat fee", trade: "general", permit_risk: "none", contractor_only: true, typical_cost_low: 0, typical_cost_high: 0, notes: "Typically 10–20% of total project cost; covers scheduling, coordination, and warranty period", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "What percentage are you including for GC markup — typically 10–20% of total project cost?", homeowner_prompt: "Are you aware the GC fee covers project management, scheduling, and warranty coordination?", labor_low: 0, labor_high: 0, material_low: 0, material_high: 0 },
      { id: "clean_005", category: "CLEANUP & GENERAL", label: "Temporary fencing / site security", unit: "per week", unit_label: "per week", trade: "general", permit_risk: "none", contractor_only: false, typical_cost_low: 100, typical_cost_high: 300, notes: "Chain link panel fencing; required around open excavations or for security on vacant properties", supply_model: "contractor_supplied", bid_type: "labor_and_materials", contractor_prompt: "How many panels and how long is fencing needed?", homeowner_prompt: "Is the project site in an area requiring security fencing — vacant property or street-facing excavation?", labor_low: 75, labor_high: 200, material_low: 25, material_high: 100 },
      { id: "clean_006", category: "CLEANUP & GENERAL", label: "Punch list / final inspection coordination", unit: "flat", unit_label: "flat fee", trade: "general", permit_risk: "none", contractor_only: true, typical_cost_low: 0, typical_cost_high: 0, notes: "Walk-through with homeowner to identify remaining items; final payment typically tied to punch list completion", supply_model: "contractor_supplied", bid_type: "labor_only", contractor_prompt: "How many walk-throughs are planned and is there a formal punch list protocol?", homeowner_prompt: "Do you want to schedule a walk-through to review all completed work before final payment?", labor_low: 0, labor_high: 0, material_low: 0, material_high: 0 },
    ]
  }
];
 
// ── Helpers ──────────────────────────────────────────────────
const ALL_SCOPE_FLAT = FULL_SCOPE_CATALOG.flatMap(g => g.items);
const lookupScopeItem = (id) => ALL_SCOPE_FLAT.find(i => i.id === id);
const hasPermitRisk = (item) => item?.permit_risk && item.permit_risk !== "none";
 

//  SEED DATA 
const SEED_PROJECTS = [
  { id: "p1", title: "Modern Kitchen Overhaul", room: "Kitchen", budget: "$25k–$50k", description: "Full gut renovation. Want navy shaker cabinets, marble countertops, hardwood floors, and a farmhouse sink. Open concept into dining room.", homeowner: "Sarah M.", location: "Brooklyn, NY", postedAt: "2h ago", items: [{label:"Navy Shaker",emoji:"",color:"#2C3E6B"},{label:"Marble Tile",emoji:"",color:"#E8E4DC"},{label:"Farm Sink",emoji:"",color:"#E0DDD8"},{label:"Pendant Light",emoji:"",color:"#D4A843"}], bids: [], messages: [], status: "open" },
  { id: "p2", title: "Master Bath Spa Retreat", room: "Bathroom", budget: "$10k–$25k", description: "Convert standard bathroom into spa-like retreat. Hex cement floors, subway tile walls, vessel sink, rainfall shower.", homeowner: "James K.", location: "Austin, TX", postedAt: "5h ago", items: [{label:"Hex Cement",emoji:"",color:"#8C8C8C"},{label:"Subway Tile",emoji:"",color:"#E8E8E8"}], bids: [], messages: [], status: "open" },
  { id: "p3", title: "Open Living Room Refresh", room: "Living Room", budget: "$5k–$10k", description: "Remove carpet, install herringbone hardwood. Add shiplap accent wall and electric fireplace. Want warm, cozy aesthetic.", homeowner: "Dana R.", location: "Denver, CO", postedAt: "1d ago", items: [{label:"Herringbone",emoji:"",color:"#B89B6A"},{label:"Shiplap",emoji:"",color:"#D4C5B0"},{label:"Fireplace",emoji:"",color:"#C4714E"}], bids: [], messages: [], status: "open" },
];

const SEED_CONTRACTOR = {
  id: "c1", name: "Marcus Rivera", company: "Apex Renovations LLC",
  rating: 4.9, reviews: 128, completedJobs: 89, responseRate: "98%",
  specialties: ["Kitchen","Bathroom","Flooring"], location: "NYC Metro",
  verified: true, licenseNo: "NY-GC-48821", joined: "2019",
  earnings: 14200, pendingBids: 3, activeJobs: 2,
  lat: null, lng: null, serviceRadius: 50,
};

//  STORAGE HELPERS 
async function loadProjects(userId, role) {
  try {
    if (role === "homeowner") {
      const { data, error } = await supabase
        .from('projects')
        .select('*, bids(*), messages(*)')
        .eq('homeowner_id', userId)
        .order('created_at', { ascending: false });
      if (error) console.error('Load projects error:', error.message, error);
      return data?.length ? data.map(normalizeProject) : SEED_PROJECTS;
    } else {
      const { data, error } = await supabase
        .from('projects')
        .select('*, bids(*), messages(*)')
        .order('created_at', { ascending: false });
      if (error) console.error('Load projects error:', error.message, error);
      return data?.length ? data.map(normalizeProject) : SEED_PROJECTS;
    }
  } catch (err) { console.error('Load projects exception:', err); return SEED_PROJECTS; }
}
function normalizeProject(p) {
  const messages = (p.messages || [])
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map(m => ({
      from: m.sender_id === p.homeowner_id ? "homeowner" : "contractor",
      text: m.content,
      time: m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "",
      sender: m.sender_name || null,
    }));
  return {
    ...p,
    postedAt: p.created_at ? new Date(p.created_at).toLocaleDateString() : "Recently",
    homeowner: p.homeowner_name || "Homeowner",
    bids: (p.bids || []).map(b => ({
      ...b,
      contractorId: b.contractorId || b.contractor_id,
      amount: b.amount,
      status: b.status || "pending",
      duration: b.duration || b.timeline,
      needsConsultation: b.needsConsultation || b.needs_consultation || false,
      consultationBooked: b.consultationBooked || b.consultation_booked || null,
      consultationComplete: b.consultationComplete || b.consultation_complete || false,
      contractorAvailability: b.contractorAvailability || b.contractor_availability || null,
    })),
    messages,
    scope: p.scope || [],
    items: p.items || [],
    milestones: p.milestones || [],
    escrow: p.escrow || null,
    renderedPhoto: p.renderedPhoto || p.rendered_photo || null,
  };
}
async function loadContractor(userId) {
  if (userId) {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (data) return {
        ...SEED_CONTRACTOR,
        // clear seed placeholders so new accounts start blank
        licenseNo: null, specialties: [], location: null,
        rating: null, reviews: 0, completedJobs: 0, responseRate: null,
        ...data,
        // map DB snake_case back to camelCase
        licenseNo: data.license_no || null,
        specialties: data.specialties || [],
        location: data.location || null,
        lat: data.lat || null,
        lng: data.lng || null,
        serviceRadius: data.service_radius || 50,
        reviewSummary: data.review_summary || null,
        availability: data.availability || {},
        jobManagementMode: data.job_management_mode || null,
      };
    } catch {}
  }
  try {
    const r = await window.storage.get("renobid_contractor");
    return r ? JSON.parse(r.value) : SEED_CONTRACTOR;
  } catch { return SEED_CONTRACTOR; }
}
async function saveContractor(c, userId) {
  if (userId) {
    try {
      const { error } = await supabase.from('profiles').update({
        name: c.name,
        company: c.company,
        specialties: c.specialties,
        location: c.location,
        lat: c.lat || null,
        lng: c.lng || null,
        service_radius: c.serviceRadius || 50,
        license_no: c.licenseNo,
        availability: c.availability || {},
        job_management_mode: c.jobManagementMode || null,
      }).eq('id', userId);
      if (error) console.error('Save contractor profile error:', error.message, error);
    } catch (err) { console.error('Save contractor error:', err); }
  }
  try { await window.storage.set("renobid_contractor", JSON.stringify(c)); } catch {}
}

function ReviewModal({ project, contractorId, user, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    const data = await submitReview(project.id, contractorId, user.id, rating, comment);
    setSubmitting(false);
    if (data) { setDone(true); setTimeout(() => { onSubmitted(data); onClose(); }, 1200); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:C.white, borderRadius:16, padding:28, maxWidth:420, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        {done ? (
          <div style={{ textAlign:"center", padding:"8px 0" }}>
            <div style={{ fontSize:40, marginBottom:8 }}>⭐</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:C.green, fontWeight:700 }}>Review submitted!</div>
          </div>
        ) : (
          <>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:C.ink, fontWeight:700, marginBottom:4 }}>Rate your contractor</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginBottom:20 }}>{project.title}</div>

            {/* Star picker */}
            <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:20 }}>
              {[1,2,3,4,5].map(s => (
                <span key={s} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)}
                  style={{ fontSize:36, cursor:"pointer", color: s <= (hover||rating) ? "#F4C430" : C.sand, transition:"color 0.1s" }}>★</span>
              ))}
            </div>
            {rating > 0 && (
              <div style={{ textAlign:"center", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginBottom:16 }}>
                {["","Poor","Fair","Good","Great","Excellent!"][rating]}
              </div>
            )}

            <textarea value={comment} onChange={e => setComment(e.target.value)}
              placeholder="Describe the quality of work, communication, and whether you'd hire them again..."
              rows={4} style={{ width:"100%", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"10px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.white, resize:"none", outline:"none", marginBottom:16 }} />

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={onClose} style={{ flex:1, background:C.cream, border:`1px solid ${C.sand}`, borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, cursor:"pointer" }}>Cancel</button>
              <button onClick={handleSubmit} disabled={!rating || submitting}
                style={{ flex:2, background: rating ? C.terra : C.sand, color: rating ? C.white : C.slate, border:"none", borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor: rating ? "pointer" : "not-allowed" }}>
                {submitting ? "Submitting…" : "Submit Review"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

async function loadReviews(contractorId) {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('contractor_id', contractorId)
      .order('created_at', { ascending: false });
    if (error) console.error('Load reviews error:', error.message);
    return data || [];
  } catch { return []; }
}

async function submitReview(projectId, contractorId, homeownerId, rating, comment) {
  try {
    // 1. Save the review
    const { data, error } = await supabase.from('reviews').insert({
      project_id: projectId,
      contractor_id: contractorId,
      homeowner_id: homeownerId,
      rating,
      comment,
    }).select().single();
    if (error) { console.error('Submit review error:', error.message, error); return null; }

    // 2. Load all reviews for this contractor to build aggregate summary
    const { data: allReviews } = await supabase
      .from('reviews')
      .select('rating, comment')
      .eq('contractor_id', contractorId);

    if (allReviews?.length) {
      try {
        const backendBase = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
        const res = await fetch(`${backendBase}/ai/review-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviews: allReviews }),
        });
        if (res.ok) {
          const { summary } = await res.json();
          if (summary) {
            await supabase.from('profiles')
              .update({ review_summary: summary })
              .eq('id', contractorId);
          }
        }
      } catch (err) { console.error('AI summary error:', err); }
    }

    return data;
  } catch (err) { console.error('Submit review exception:', err); return null; }
}

//
// DALL-E RENDER GENERATOR
//
async function generateDALLERender(photo, items, roomType, roomW, roomL) {
  const backendBase = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
  const res = await fetch(`${backendBase}/ai/render`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ photo, items, roomType, roomW, roomL }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Render failed");
  }
  const { url } = await res.json();
  return url;
}

//
// AI SCOPE TRANSLATOR
//
async function translateScopeWithAI(description, roomType, conversationHistory = []) {
  const backendBase = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
  const res = await fetch(`${backendBase}/ai/scope`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description, roomType, conversationHistory }),
  });
  if (!res.ok) throw new Error("AI scope translation failed");
  const { responseText, userMessage } = await res.json();
  try {
    const result = JSON.parse(responseText);
    return { type: "scope", result, userMessage, assistantMessage: responseText };
  } catch {
    return { type: "question", text: responseText, userMessage, assistantMessage: responseText };
  }
}

//
// GEOCODING HELPERS
//
const STATE_CODES = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA",
  "Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA",
  "Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA",
  "Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD",
  "Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS","Missouri":"MO",
  "Montana":"MT","Nebraska":"NE","Nevada":"NV","New Hampshire":"NH","New Jersey":"NJ",
  "New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND","Ohio":"OH",
  "Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC",
  "South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT","Vermont":"VT",
  "Virginia":"VA","Washington":"WA","West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY",
  "District of Columbia":"DC",
};

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function CityAutocomplete({ value, onChange, placeholder, inputStyle }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = (q) => {
    clearTimeout(timer.current);
    if (q.length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=10&lang=en&countrycode=us&layer=city&layer=locality&layer=district&layer=state`
        );
        const { features } = await res.json();
        const seen = new Set();
        const items = (features || [])
          .filter(f => {
            const p = f.properties;
            return p.countrycode === "US" && p.name && p.state &&
              ["city","town","village","hamlet","suburb","municipality","locality","district","quarter","neighbourhood"].includes(p.osm_value || p.type);
          })
          .map(f => {
            const p = f.properties;
            const stateCode = STATE_CODES[p.state] || p.state?.slice(0,2).toUpperCase();
            const [lng, lat] = f.geometry.coordinates;
            return { label: `${p.name}, ${stateCode}`, lat, lng };
          })
          .filter(r => { if (seen.has(r.label)) return false; seen.add(r.label); return true; });
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {}
    }, 350);
  };

  return (
    <div style={{ position:"relative" }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); search(e.target.value); onChange(null, e.target.value); }}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || "City, State"}
        style={inputStyle}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:C.white, border:`1px solid ${C.sand}`, borderRadius:8, zIndex:200, maxHeight:220, overflowY:"auto", boxShadow:"0 4px 16px rgba(0,0,0,0.12)" }}>
          {suggestions.map((s, i) => (
            <div key={i}
              onMouseDown={() => { setQuery(s.label); setSuggestions([]); setOpen(false); onChange(s, s.label); }}
              style={{ padding:"9px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, cursor:"pointer", borderBottom: i < suggestions.length-1 ? `1px solid ${C.sand}` : "none" }}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

//
// AVAILABILITY HELPER — find next available date for a contractor
//
function getNextAvailableDate(availability) {
  if (!availability || Object.keys(availability).length === 0) return null;
  const DAY_KEYS = ["sun","mon","tue","wed","thu","fri","sat"];
  const today = new Date();
  for (let i = 1; i <= 90; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = DAY_KEYS[d.getDay()];
    if ((availability[key] || []).length > 0) {
      return d.toISOString().split("T")[0];
    }
  }
  return null;
}

//
// ROOT APP
//
export default function RenoBid() {
  const [mode, setMode] = useState("auth"); // auth | role-select | homeowner | contractor
  const [user, setUser] = useState(null); // { name, email, role }
  const [projects, setProjects] = useState([]);
  const [contractor, setContractor] = useState(SEED_CONTRACTOR);
  const [loaded, setLoaded] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Wire C to current palette — all child components read this
  C = darkMode ? DARK : LIGHT;

  useEffect(() => {
    // Restore existing Supabase session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        supabase.from('profiles').select('*').eq('id', session.user.id).single().then(({ data: profile }) => {
          const role = profile?.role || session.user.user_metadata?.role || "homeowner";
          const name = profile?.name || session.user.user_metadata?.name || session.user.email.split("@")[0];
          setUser({ id: session.user.id, name, email: session.user.email, role });
          setMode(role);
        });
      }
      loadContractor().then(c => {
        setContractor(c);
        setLoaded(true);
      });
    });

    // Listen for auth state changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setMode("auth");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadProjects(user.id, user.role).then(p => setProjects(p));
    if (user.role === 'contractor') {
      loadContractor(user.id).then(c => setContractor(c));
    }
  }, [user]);

  const updateProjects = useCallback((p) => {
    setProjects(p);
  }, [user]);
  const updateContractor = useCallback((c) => { setContractor(c); saveContractor(c, user?.id); }, [user]);

  const handleAuth = (userData) => {
    setUser(userData);
    setMode(userData.role || "homeowner");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProjects([]);
    setMode("auth");
  };

  if (!loaded) return (
    <div style={{ minHeight:"100vh", background:C.ink, display:"flex", alignItems:"center", justifyContent:"center" }}><GlobalStyles /><div style={{ color:C.clay, fontFamily:"'Playfair Display',serif", fontSize:24 }}>Loading RenoBid…</div></div>
  );

  if (mode === "auth") return <AuthScreen onAuth={handleAuth} projects={projects} darkMode={darkMode} toggleDark={() => setDarkMode(d => !d)} />;
  if (mode === "homeowner") return <HomeownerApp user={user} projects={projects} updateProjects={updateProjects} darkMode={darkMode} toggleDark={() => setDarkMode(d => !d)} onLogout={handleLogout} />;
  if (mode === "contractor") return <ContractorApp user={user} projects={projects} updateProjects={updateProjects} contractor={contractor} updateContractor={updateContractor} darkMode={darkMode} toggleDark={() => setDarkMode(d => !d)} onLogout={handleLogout} />;
  return null;
}

// 
// AUTH SCREEN — Login / Create Account
// 
function AuthScreen({ onAuth, projects, darkMode, toggleDark }) {
  const [tab, setTab] = useState("login"); // login | signup
  const [form, setForm] = useState({ name:"", email:"", password:"", confirm:"" });
  const [role, setRole] = useState("homeowner"); // homeowner | contractor
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const openProjects = projects.filter(p => p.status === "open").length;
  const emailRef = useRef(null);
  const passRef = useRef(null);

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setError(""); };

  const handleSubmit = async (formEl) => {
  // Read from form.elements (most reliable — captures autofill), then refs, then state
  const emailVal = formEl?.elements?.email?.value || emailRef.current?.value || form.email || "";
  const passVal  = formEl?.elements?.password?.value || passRef.current?.value || form.password || "";
  if (!emailVal || !passVal) return setError("Please fill in all fields.");
  setError("");

  if (tab === "signup") {
    if (!form.name) return setError(role === "contractor" ? "Please enter your business name." : "Please enter your name.");
    if (passVal.length < 6) return setError("Password must be at least 6 characters.");
    if (passVal !== (form.confirm || document.querySelectorAll('input[type="password"]')[1]?.value || "")) return setError("Passwords don't match.");

    // Sign up with Supabase
    const { data, error } = await supabase.auth.signUp({
      email: emailVal,
      password: passVal,
      options: {
        data: {
          name: form.name,
          role: role,
        }
      }
    });

    if (error) return setError(error.message);

    // If email confirmation is required, session will be null — tell the user
    if (data.user && !data.session) {
      return setError("Account created! Please check your email to confirm your account, then log in.");
    }

    // Create profile row in database
    if (data.user && data.session) {
      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        email: emailVal,
        name: form.name,
        role: role,
        company: role === "contractor" ? form.name : null,
      });
      if (profileError) console.error('Profile insert error:', profileError.message, profileError);
      onAuth({ id: data.user.id, name: form.name, email: emailVal, role });
    }

  } else {
    // Sign in with Supabase
    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailVal,
      password: passVal,
    });

    if (error) return setError(error.message);

    if (data.user) {
      // Fetch their profile to get role
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      const userRole = profile?.role || "homeowner";
      const userName = profile?.name || emailVal.split("@")[0];
      onAuth({ id: data.user.id, name: userName, email: emailVal, role: userRole });
    }
  }
};

  // Auth-specific palette — responds to darkMode toggle
  const AP = darkMode ? {
    bg: "#1C2333",
    cardBg: "rgba(255,255,255,0.04)",
    cardBorder: "rgba(255,255,255,0.08)",
    primaryText: "#FDFCFA",
    mutedText: "rgba(255,255,255,0.4)",
    inputBg: "rgba(255,255,255,0.06)",
    inputBorder: "rgba(255,255,255,0.12)",
    inputText: "rgba(255,255,255,0.85)",
    divider: "rgba(255,255,255,0.08)",
    dividerText: "rgba(255,255,255,0.25)",
    tabInactive: "rgba(255,255,255,0.35)",
    tabBorder: "rgba(255,255,255,0.1)",
    forgotText: "rgba(255,255,255,0.35)",
    socialBorder: "rgba(255,255,255,0.12)",
    socialBg: "rgba(255,255,255,0.05)",
    socialText: "rgba(255,255,255,0.7)",
    toggleBg: "rgba(255,255,255,0.06)",
    toggleBorder: "rgba(255,255,255,0.12)",
    toggleText: "rgba(255,255,255,0.5)",
    termsText: "rgba(255,255,255,0.25)",
  } : {
    bg: "#F5F0E8",
    cardBg: "#FDFCFA",
    cardBorder: "#E8DFD0",
    primaryText: "#1C2333",
    mutedText: "#3D4A5C",
    inputBg: "#F5F0E8",
    inputBorder: "#E8DFD0",
    inputText: "#1C2333",
    divider: "#E8DFD0",
    dividerText: "#3D4A5C",
    tabInactive: "#3D4A5C",
    tabBorder: "#E8DFD0",
    forgotText: "#3D4A5C",
    socialBorder: "#E8DFD0",
    socialBg: "#F5F0E8",
    socialText: "#1C2333",
    toggleBg: "rgba(0,0,0,0.04)",
    toggleBorder: "#E8DFD0",
    toggleText: "#3D4A5C",
    termsText: "#3D4A5C",
  };

  const fieldSt = {
    width:"100%", border:`1.5px solid ${AP.inputBorder}`,
    borderRadius:10, padding:"13px 14px", fontFamily:"'DM Sans',sans-serif",
    fontSize:15, color:AP.inputText, background:AP.inputBg,
    marginBottom:12, display:"block", transition:"border-color 0.15s",
  };

  return (
    <div style={{ minHeight:"100vh", background:AP.bg, display:"flex", flexDirection:"column", transition:"background 0.2s" }}><GlobalStyles /><style>{`
        .auth-input:focus { border-color: rgba(184,92,56,0.6) !important; outline: none; }
        .auth-tab { border:none; background:none; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600; cursor:pointer; padding:8px 0; transition:all 0.15s; }
        .social-btn { width:100%; border:1.5px solid ${AP.socialBorder}; border-radius:10px; padding:12px 0; background:${AP.socialBg}; color:${AP.socialText}; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:10px; transition:all 0.15s; margin-bottom:10px; }
        .social-btn:hover { opacity: 0.85; }
      `}</style>

      {/* Top bar */}
      <div style={{ padding:"20px 24px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:AP.primaryText }}>
          Reno<span style={{ color:"#B85C38" }}>Bid</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={toggleDark}
            style={{ background:AP.toggleBg, border:`1px solid ${AP.toggleBorder}`, borderRadius:100, padding:"4px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:AP.toggleText, cursor:"pointer", transition:"all 0.2s" }}>
            {darkMode ? "Light" : "Dark"}
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(212,168,67,0.12)", border:`1px solid rgba(212,168,67,0.25)`, borderRadius:100, padding:"4px 12px" }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:"#D4A843", display:"inline-block" }} />
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"#D4A843", letterSpacing:1 }}>{openProjects} LIVE</span>
          </div>
        </div>
      </div>

      {/* Centered content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center", padding:"16px 16px 40px" }}><div style={{ maxWidth:440, width:"100%", margin:"0 auto" }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, color:AP.primaryText, lineHeight:1.2, marginBottom:8 }}>
          Your renovation,<br /><em style={{ color:"#C4956A" }}>done right.</em>
        </h1>
        <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:AP.mutedText, lineHeight:1.6 }}>
          Design your space, post your project, get competitive bids from verified contractors — all in one place.
        </p>
      </div>

      {/* Auth card */}
        <div style={{ background:AP.cardBg, border:`1px solid ${AP.cardBorder}`, borderRadius:16, padding:"24px 20px", transition:"all 0.2s" }}>

          {/* Tab switcher */}
          <div style={{ display:"flex", borderBottom:`1px solid ${AP.tabBorder}`, marginBottom:20 }}>
            {[["login","Sign In"],["signup","Create Account"]].map(([t,l]) => (
              <button key={t} className="auth-tab" onClick={() => { setTab(t); setError(""); }}
                style={{ flex:1, color: tab===t ? "#C4956A" : AP.tabInactive, borderBottom: tab===t ? `2px solid #C4956A` : "2px solid transparent", paddingBottom:10 }}>
                {l}
              </button>
            ))}
          </div>

          {/* Social logins */}
          <button className="social-btn"><svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <button className="social-btn"><svg width="18" height="18" viewBox="0 0 24 24" fill={darkMode ? "white" : "#1C2333"}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Continue with X
          </button>

          {/* Divider */}
          <div style={{ display:"flex", alignItems:"center", gap:10, margin:"4px 0 16px" }}>
            <div style={{ flex:1, height:1, background:AP.divider }} />
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:AP.dividerText }}>or</span>
            <div style={{ flex:1, height:1, background:AP.divider }} />
          </div>

          {/* Form fields */}
          <form onSubmit={e => { e.preventDefault(); handleSubmit(e.currentTarget); }}>
          {tab === "signup" && (
            <>
              <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                {[["homeowner","Homeowner"],["contractor","Contractor"]].map(([r,l]) => (
                  <button type="button" key={r} onClick={() => setRole(r)}
                    style={{ flex:1, background: role===r ? "#B85C38" : AP.inputBg, border:`1.5px solid ${role===r ? "#B85C38" : AP.inputBorder}`, borderRadius:10, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color: role===r ? "#FDFCFA" : AP.tabInactive, cursor:"pointer", transition:"all 0.15s" }}>
                    {l}
                  </button>
                ))}
              </div>
              <input className="auth-input" style={fieldSt}
                placeholder={role === "contractor" ? "Business name" : "Full name"}
                value={form.name} onChange={set("name")} />
            </>
          )}
          <input ref={emailRef} name="email" className="auth-input" style={fieldSt} type="email" placeholder="Email address"
            autoComplete="email"
            value={form.email} onChange={set("email")} onInput={set("email")}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSubmit(e.currentTarget.closest("form")); } }} />
          <div style={{ position:"relative", marginBottom:12 }}>
            <input ref={passRef} name="password" className="auth-input" style={{ ...fieldSt, marginBottom:0, paddingRight:48 }}
              type={showPass ? "text" : "password"} placeholder="Password"
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              value={form.password} onChange={set("password")} onInput={set("password")}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSubmit(e.currentTarget.closest("form")); } }} />
            <button type="button" onClick={() => setShowPass(s=>!s)}
              style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:AP.forgotText, fontSize:13, fontFamily:"'DM Sans',sans-serif", cursor:"pointer" }}>
              {showPass ? "Hide" : "Show"}
            </button>
          </div>
          {tab === "signup" && (
            <input className="auth-input" style={fieldSt} type="password" placeholder="Confirm password"
              value={form.confirm} onChange={set("confirm")} />
          )}

          {/* Error */}
          {error && (
            <div style={{ background:"rgba(192,57,43,0.15)", border:"1px solid rgba(192,57,43,0.3)", borderRadius:8, padding:"9px 12px", marginBottom:12, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:"#FF8A80" }}>
              {error}
            </div>
          )}

          {/* Forgot password */}
          {tab === "login" && (
            <div style={{ textAlign:"right", marginBottom:12 }}>
              <button type="button" style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:AP.forgotText, cursor:"pointer" }}>Forgot password?</button>
            </div>
          )}

          {/* Submit */}
          <button type="submit"
            style={{ width:"100%", background:`linear-gradient(135deg,#B85C38,#C4956A)`, color:"#FDFCFA", border:"none", borderRadius:10, padding:"14px 0", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:700, cursor:"pointer", boxShadow:`0 4px 20px rgba(184,92,56,0.4)` }}>
            {tab === "login" ? "Sign In →" : "Create Account →"}
          </button>
          </form>

          {/* Terms */}
          {tab === "signup" && (
            <div style={{ marginTop:12, fontFamily:"'DM Sans',sans-serif", fontSize:11, color:AP.termsText, textAlign:"center", lineHeight:1.6 }}>
              By creating an account you agree to our Terms of Service and Privacy Policy
            </div>
          )}

</div>

        {/* Trust badges */}
        <div style={{ display:"flex", justifyContent:"center", gap:20, marginTop:20 }}>
          {[" Escrow protected"," Verified contractors"," 4.9 avg rating"].map(t => (
            <span key={t} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.2)" }}>{t}</span>
          ))}
        </div></div></div></div>
  );
}

//
// ROLE SELECTOR — Post-login, choose Homeowner or Contractor
// 
function RoleSelector({ user, onSelect, projects }) {
  const openProjects = projects.filter(p => p.status === "open").length;
  const firstName = user?.name?.split("")[0] || "there";
  return (
    <div style={{ height:"100vh", background:C.ink, display:"flex", flexDirection:"column", overflow:"hidden" }}><GlobalStyles /><style>{`
        .role-card { border-radius:14px; padding:22px 20px; cursor:pointer; transition:all 0.18s; border:2px solid transparent; -webkit-tap-highlight-color:transparent; }
        .role-card:active { transform:scale(0.98); }
      `}</style><nav style={{ padding:"18px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:C.white }}>
          Reno<span style={{ color:C.terra }}>Bid</span></div><div style={{ display:"flex", alignItems:"center", gap:8 }}><div style={{ width:32, height:32, borderRadius:"50%", background:`linear-gradient(135deg,${C.terra},${C.clay})`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13, color:C.white }}>
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:"rgba(255,255,255,0.5)" }}>{user?.email}</span></div></nav><div style={{ padding:"4px 20px 20px", flexShrink:0 }}><h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:C.white, lineHeight:1.2, marginBottom:6 }}>
          Welcome back, <em style={{ color:C.clay }}>{firstName}.</em></h1><p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:"rgba(255,255,255,0.4)" }}>
          How are you using RenoBid today?
        </p></div><div style={{ flex:1, padding:"0 16px 20px", display:"flex", flexDirection:"column", gap:12, overflow:"hidden" }}>

        {/* Homeowner card */}
        <div className="role-card" onClick={() => onSelect("homeowner")}
          style={{ flex:1, background:"linear-gradient(135deg,#2A1F18,#1C2333)", borderColor:"rgba(196,149,106,0.3)", display:"flex", flexDirection:"column" }}><div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12 }}><div style={{ width:48, height:48, borderRadius:12, background:"rgba(196,149,106,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0 }}></div><div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.white }}>I'm a Homeowner</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:2 }}>Design, post, and receive bids</div></div></div><div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:"auto" }}>
            {[" Upload room photo"," Design canvas"," Competitive bids"," Escrow payments"].map(t => (
              <span key={t} style={{ background:"rgba(196,149,106,0.12)", border:`1px solid rgba(196,149,106,0.25)`, borderRadius:100, padding:"3px 10px", fontSize:11, color:C.clay, fontFamily:"'DM Sans',sans-serif" }}>{t}</span>
            ))}
          </div></div>

        {/* Contractor card */}
        <div className="role-card" onClick={() => onSelect("contractor")}
          style={{ flex:1, background:"linear-gradient(135deg,#0D1B2E,#1A2535)", borderColor:"rgba(59,130,246,0.25)", display:"flex", flexDirection:"column" }}><div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12 }}><div style={{ width:48, height:48, borderRadius:12, background:"rgba(59,130,246,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0 }}></div><div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.white }}>I'm a Contractor</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:2 }}>Browse projects and submit bids</div></div></div><div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:"auto" }}>
            {[" Browse jobs"," Itemized bids"," Message clients"," Milestone payouts"].map(t => (
              <span key={t} style={{ background:"rgba(59,130,246,0.1)", border:`1px solid rgba(59,130,246,0.2)`, borderRadius:100, padding:"3px 10px", fontSize:11, color:C.terra, fontFamily:"'DM Sans',sans-serif" }}>{t}</span>
            ))}
          </div></div><div style={{ display:"flex", justifyContent:"center", gap:16, fontFamily:"'DM Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.18)" }}><span>Free to post</span><span>·</span><span>{openProjects} live projects</span><span>·</span><span>Escrow protected</span></div></div></div>
  );
}

// 
// AI DESIGN RENDERER — Claude vision → Stable Diffusion img2img
// Pipeline:
//   1. Claude analyzes photo + materials → optimized SD prompt
//   2. Replicate runs controlnet-tile img2img on the photo
//   3. Poll until image ready, display side-by-side before/after
// 
// DESIGN CANVAS — touch-friendly, tap to place swatches on photo
// 
//  ITEM REAL-WORLD SIZES (width × height in feet) 
const ITEM_SIZES = {
  // Tile & Walls
  w1:{w:8,h:8},w2:{w:4,h:3},w3:{w:4,h:4},w4:{w:8,h:8},w5:{w:4,h:3},w6:{w:4,h:8},
  // Flooring — wide strip
  f1:{w:12,h:2},f2:{w:12,h:2},f3:{w:12,h:2},f4:{w:12,h:2},f5:{w:12,h:2},f6:{w:12,h:2},
  // Cabinets
  c1:{w:2.5,h:2.5},c2:{w:2.5,h:2.5},c3:{w:2.5,h:2.5},c4:{w:2.5,h:2.5},c5:{w:2.5,h:2.5},c6:{w:2.5,h:2.5},
  // Appliances
  a1:{w:2.5,h:3.0},a2:{w:3.0,h:5.5},a3:{w:2.0,h:2.83},a4:{w:2.5,h:1.0},a5:{w:2.5,h:3.0},a6:{w:2.0,h:2.83},
  // Fixtures
  x1:{w:3.0,h:2.0},x2:{w:1.5,h:1.0},x3:{w:3.0,h:6.5},x4:{w:2.5,h:2.0},x5:{w:3.0,h:1.5},x6:{w:1.5,h:0.5},
};

function DesignRenderer({ photo, renderedPhoto, items, roomW=12, roomL=14, roomH=9 }) {
  const canvasRef = useRef(null);
  const [placedItems, setPlacedItems] = useState([]);
  const [draggingUid, setDraggingUid] = useState(null);
  const [showRender, setShowRender] = useState(false);
  // Canvas display size (px) — used to compute % sizes
  const [canvasSize, setCanvasSize] = useState({ w:375, h:300 });

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setCanvasSize({ w:e.contentRect.width, h:e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Convert real-world item size → % of canvas
  const getSizePercent = (item) => {
    const sizes = ITEM_SIZES[item.id] || { w:3, h:2.5 };
    // Width: item width / room width → % of canvas width
    const wPct = Math.min(55, Math.max(10, (sizes.w / roomW) * 100));
    // Height: item height / room height → % of canvas height
    const hPct = Math.min(45, Math.max(6, (sizes.h / roomH) * 100));
    return { wPct, hPct };
  };

  // Sync removals
  useEffect(() => {
    setPlacedItems(prev => prev.filter(p => items.some(i => i.id === p.id)));
  }, [items]);

  // Auto-place new items
  useEffect(() => {
    // Smart default positions by category
    const defaultPos = (id) => {
      if (id.startsWith("f")) return { x:50, y:88 }; // flooring at bottom
      if (id.startsWith("w")) return { x:50, y:35 }; // walls in middle
      if (id.startsWith("c")) return { x:25, y:55 }; // cabinets lower left
      if (id.startsWith("a")) return { x:75, y:55 }; // appliances lower right
      return { x:50, y:40 };
    };
    setPlacedItems(prev => {
      const newItems = items.filter(i => !prev.some(p => p.id === i.id));
      const kept = prev.filter(p => items.some(i => i.id === p.id));
      const added = newItems.map(item => ({
        ...item,
        uid: `${item.id}_${Date.now()}`,
        ...defaultPos(item.id),
      }));
      return [...kept, ...added];
    });
  }, [items]);

  // Touch drag
  const handleTouchStart = (e, uid) => {
    e.stopPropagation();
    setDraggingUid(uid);
  };
  const handleTouchMove = (e) => {
    if (!draggingUid || !canvasRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(2, Math.min(98, ((touch.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(2, Math.min(98, ((touch.clientY - rect.top) / rect.height) * 100));
    setPlacedItems(prev => prev.map(p => p.uid === draggingUid ? { ...p, x, y } : p));
  };
  const handleTouchEnd = () => setDraggingUid(null);

  return (
    <div style={{ flexShrink:0, position:"relative" }}><style>{`
        @keyframes popIn { from{opacity:0;transform:translate(-50%,-50%) scale(0.6)} to{opacity:1;transform:translate(-50%,-50%) scale(1)} }
        .swatch-block { position:absolute; transform:translate(-50%,-50%); touch-action:none; cursor:grab; }
        .swatch-block:active { cursor:grabbing; }
      `}</style>

      {/* Scale legend */}
      <div style={{ background:C.sand, padding:"6px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>
           Scaled to your {roomW}′ × {roomL}′ × {roomH}′ room
        </span>
        {placedItems.length > 0 && (
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, fontWeight:600 }}>
            {placedItems.length} placed · drag to reposition
          </span>
        )}
      </div>

      {/* Before / AI Render toggle */}
      {renderedPhoto && (
        <div style={{ display:"flex", background:"#111", flexShrink:0 }}>
          {[["before","Before"], ["render","AI Render ✨"]].map(([key, label]) => (
            <button key={key} onClick={() => setShowRender(key === "render")}
              style={{ flex:1, padding:"8px 0", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer",
                background: (key === "render") === showRender ? C.terra : "transparent",
                color: (key === "render") === showRender ? "#fff" : "rgba(255,255,255,0.5)",
                borderBottom: (key === "render") === showRender ? `2px solid ${C.terra}` : "2px solid transparent",
              }}>{label}</button>
          ))}
        </div>
      )}

      {/* Canvas */}
      <div
        ref={canvasRef}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ position:"relative", width:"100%", height:300, overflow:"hidden", background:"#1A1A1A", touchAction:"none", userSelect:"none" }}>

        {/* AI Render mode */}
        {showRender && renderedPhoto ? (
          <img src={renderedPhoto} alt="AI render" style={{ width:"100%", height:"100%", objectFit:"contain", display:"block", pointerEvents:"none" }} />
        ) : (
          photo && <img src={photo} alt="room" style={{ width:"100%", height:"100%", objectFit:"contain", display:"block", pointerEvents:"none" }} />
        )}

        {/* Floor guide line */}
        {photo && (
          <div style={{ position:"absolute", bottom:"18%", left:0, right:0, height:1, background:"rgba(255,255,255,0.15)", pointerEvents:"none" }} />
        )}

        {/* Empty hint */}
        {placedItems.length === 0 && photo && (
          <div style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.62)", color:"#fff", borderRadius:100, padding:"7px 18px", fontSize:12, fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", backdropFilter:"blur(4px)", pointerEvents:"none" }}>
             Tap materials to place them
          </div>
        )}

        {/* Scaled swatches */}
        {placedItems.map(item => {
          const { wPct, hPct } = getSizePercent(item);
          const isDragging = draggingUid === item.uid;
          const isFloor = item.id.startsWith("f");

          return (
            <div
              key={item.uid}
              className="swatch-block"
              onTouchStart={(e) => handleTouchStart(e, item.uid)}
              style={{
                left:`${item.x}%`,
                top:`${item.y}%`,
                width:`${wPct}%`,
                height:`${hPct * (canvasSize.h / canvasSize.w) * (canvasSize.w / canvasSize.h)}%`,
                zIndex: isDragging ? 30 : 10,
                animation: isDragging ? "none" : "popIn 0.2s ease",
              }}>
              {/* Solid scaled block */}
              <div style={{
                width:"100%",
                paddingTop: isFloor ? "18%" : `${(hPct / wPct) * 50}%`,
                position:"relative",
                borderRadius: isFloor ? "4px" : "8px",
                background: item.color + (isFloor ? "CC" : "BB"),
                border:`2px solid ${item.color}`,
                boxShadow: isDragging
                  ? `0 8px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)`
                  : `0 3px 14px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)`,
                transform: isDragging ? "scale(1.04)" : "scale(1)",
                transition: "box-shadow 0.15s, transform 0.15s",
                backdropFilter:"blur(2px)",
                overflow:"hidden",
              }}>
                {/* Texture overlay */}
                <div style={{
                  position:"absolute", inset:0,
                  backgroundImage: isFloor
                    ? `repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 20%)`
                    : item.id.startsWith("w")
                    ? `repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 12%)`
                    : "none",
                  borderRadius:"inherit",
                }} />
                {/* Label */}
                <div style={{
                  position:"absolute", inset:0,
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2,
                }}><span style={{ fontSize: wPct > 25 ? 16 : 12 }}>{item.emoji}</span><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize: wPct > 25 ? 10 : 8, fontWeight:700, color:"rgba(255,255,255,0.9)", textShadow:"0 1px 3px rgba(0,0,0,0.5)", textAlign:"center", lineHeight:1.2, padding:"0 2px" }}>
                    {item.label}
                  </span><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:7, color:"rgba(255,255,255,0.6)", textShadow:"0 1px 2px rgba(0,0,0,0.4)" }}>
                    {ITEM_SIZES[item.id]?.w || 3}′ × {ITEM_SIZES[item.id]?.h || 2.5}′
                  </span></div></div></div>
          );
        })}

        {/* Room count badge */}
        {placedItems.length > 0 && (
          <div style={{ position:"absolute", top:8, right:8, background:"rgba(28,35,51,0.75)", color:C.cream, borderRadius:100, padding:"3px 9px", fontSize:10, fontFamily:"'DM Sans',sans-serif", fontWeight:600, backdropFilter:"blur(4px)", pointerEvents:"none" }}>
            {placedItems.length} item{placedItems.length!==1?"s":""}
          </div>
        )}
      </div></div>
  );
}

// 
// HOMEOWNER APP
// 
// 
// MILESTONE TRACKER — Escrow payment flow (homeowner side)
// 
function MilestoneTracker({ project, bid, onUpdate, onReview }) {
  const [releasing, setReleasing] = useState(null);
  const [disputing, setDisputing] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError] = useState(null);
  const [coCountering, setCoCountering] = useState(null); // change order id being countered
  const [coCounterAmount, setCoCounterAmount] = useState("");
  const escrow = project.escrow || { total: bid.amount, funded:true, released:0, platform_fee: Math.round(bid.amount*0.04) };
  const milestones = project.milestones || [];
  const totalReleased = milestones.filter(m => m.status==="released").reduce((s,m) => s + Math.round(escrow.total * m.pct / 100), 0);
  const inEscrow = escrow.total - totalReleased;
  const nextPending = milestones.find(m => m.status==="pending");

  const releaseMilestone = async (milestoneId) => {
    const milestone = project.milestones.find(m => m.id === milestoneId);
    const milestoneAmount = Math.round(escrow.total * (milestone?.pct || 0) / 100);

    setReleaseLoading(true);
    setReleaseError(null);

    try {
      // ── REAL STRIPE CALL ──
      // Uncomment when backend is live:
      // const res = await fetch(`${BACKEND_URL}/payments/release-milestone`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({
      //     milestoneAmount,
      //     contractorStripeAccountId: bid.contractorStripeAccountId,
      //     projectId: project.id,
      //     milestoneId
      //   })
      // });
      // if (!res.ok) throw new Error((await res.json()).error);
      // const { transferId } = await res.json();

      // ── SIMULATED (remove when backend live) ──
      await new Promise(r => setTimeout(r, 800)); // simulate network
      const transferId = `tr_sim_${Date.now()}`;

      const updated = {
        ...project,
        milestones: project.milestones.map(m => m.id === milestoneId
          ? { ...m, status:"released", releasedAt: new Date().toLocaleDateString(), transferId }
          : m),
        escrow: { ...escrow, released: totalReleased + milestoneAmount }
      };
      onUpdate(updated);
      setReleasing(null);
      // Persist milestone + escrow state to Supabase
      try {
        await supabase.from('projects').update({
          milestones: updated.milestones,
          escrow: updated.escrow,
        }).eq('id', project.id);
      } catch (err) { console.error('Milestone Supabase update error:', err); }
    } catch (err) {
      setReleaseError(err.message || "Payment failed. Please try again.");
    } finally {
      setReleaseLoading(false);
    }
  };

  const disputeMilestone = async (milestoneId) => {
    try {
      // ── REAL CALL (uncomment when backend live) ──
      // await fetch(`${BACKEND_URL}/payments/dispute-milestone`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ projectId: project.id, milestoneId, reason: "Homeowner flagged issue" })
      // });

      const updated = {
        ...project,
        milestones: project.milestones.map(m => m.id === milestoneId ? { ...m, status:"disputed" } : m),
      };
      onUpdate(updated);
      setDisputing(null);
    } catch (err) {
      console.error("Dispute failed:", err);
    }
  };

  const statusColor = { pending:C.sand, released:C.sage, disputed:"#E74C3C", awaiting_approval:C.gold };
  const statusLabel = { pending:"Upcoming", released:" Released", disputed:" Disputed", awaiting_approval:"Awaiting your approval" };

  return (
    <div style={{ marginTop:16, borderRadius:12, overflow:"hidden", border:`1.5px solid ${C.sage}40` }}>
      {/* Escrow header */}
      <div style={{ background:C.white, padding:"14px 16px" }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>Escrow Balance</div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:C.gold }}>${inEscrow.toLocaleString()}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:2 }}>held securely · releases on your approval</div></div><div style={{ textAlign:"right" }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, marginBottom:3 }}>Total project</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:16, fontWeight:700, color:C.ink }}>${escrow.total.toLocaleString()}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>Incl. ${escrow.platform_fee.toLocaleString()} platform fee</div></div></div>
        {/* Progress bar */}
        <div style={{ height:6, background:C.sand, borderRadius:3, overflow:"hidden" }}><div style={{ height:"100%", background:`linear-gradient(90deg,${C.sage},${C.gold})`, width:`${(totalReleased/escrow.total)*100}%`, borderRadius:3, transition:"width 0.5s" }} /></div><div style={{ display:"flex", justifyContent:"space-between", marginTop:5 }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.sage }}>${totalReleased.toLocaleString()} released</span><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>${inEscrow.toLocaleString()} remaining</span></div></div>

      {/* Milestones */}
      <div style={{ background:C.white }}>
        {milestones.map((m, idx) => {
          const amount = Math.round(escrow.total * m.pct / 100);
          const isNext = m.id === nextPending?.id;
          return (
            <div key={m.id} style={{ padding:"14px 16px", borderBottom: idx < milestones.length-1 ? `1px solid ${C.sand}` : "none", background: m.status==="released" ? "#F0FFF4" : m.status==="disputed" ? "#FFF5F5" : "transparent" }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}><div style={{ flex:1, marginRight:12 }}><div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}><div style={{ width:22, height:22, borderRadius:"50%", background: m.status==="released" ? C.sage : m.status==="disputed" ? "#E74C3C" : isNext ? C.terra : C.sand, color:C.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>
                      {m.status==="released" ? "" : m.status==="disputed" ? "!" : idx+1}
                    </div><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>{m.label}</span><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color: m.status==="released" ? C.sage : C.slate, fontWeight:600 }}>{m.pct}%</span></div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, paddingLeft:30, lineHeight:1.5 }}>{m.description}</div>
                  {m.releasedAt && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.sage, paddingLeft:30, marginTop:3 }}>Released {m.releasedAt}</div>}
                </div><div style={{ textAlign:"right", flexShrink:0 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color: m.status==="released" ? C.sage : C.terra }}>${amount.toLocaleString()}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>{statusLabel[m.status] || m.status}</div></div></div>

              {/* Action buttons — only on next pending milestone */}
              {isNext && m.status === "pending" && (
                releasing === m.id ? (
                  <div style={{ marginTop:10, background:C.cream, borderRadius:8, padding:"12px 14px" }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink, marginBottom:8 }}>
                      Release ${amount.toLocaleString()} to contractor?
                    </div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginBottom:12, lineHeight:1.5 }}>
                      Only release if you're satisfied with the work completed for this milestone. This cannot be undone.
                    </div>
                    {releaseError && <div style={{ background:"rgba(192,57,43,0.1)", border:"1px solid rgba(192,57,43,0.3)", borderRadius:6, padding:"8px 10px", marginBottom:10, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"#E74C3C" }}>{releaseError}</div>}
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => releaseMilestone(m.id)} disabled={releaseLoading}
                        style={{ flex:1, background: releaseLoading ? C.sand : C.sage, color:C.white, border:"none", borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13, cursor: releaseLoading ? "default" : "pointer" }}>
                        {releaseLoading ? "Processing..." : "Confirm Release"}
                      </button>
                      <button onClick={() => setReleasing(null)} style={{ flex:1, background:"none", border:`1.5px solid ${C.sand}`, borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>Cancel</button>
                    </div>
                  </div>
                ) : disputing === m.id ? (
                  <div style={{ marginTop:10, background:"#FFF5F5", borderRadius:8, padding:"12px 14px", border:`1px solid #FFCCCC` }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink, marginBottom:8 }}>
                      Flag a problem with this milestone?
                    </div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginBottom:12, lineHeight:1.5 }}>
                      Funds will be frozen and RenoBid will review. The contractor will be notified.
                    </div><div style={{ display:"flex", gap:8 }}><button onClick={() => disputeMilestone(m.id)} style={{ flex:1, background:"#E74C3C", color:C.white, border:"none", borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13 }}> Open Dispute</button><button onClick={() => setDisputing(null)} style={{ flex:1, background:"none", border:`1.5px solid ${C.sand}`, borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>Cancel</button></div></div>
                ) : (
                  <div style={{ display:"flex", gap:8, marginTop:10 }}><button onClick={() => setReleasing(m.id)} style={{ flex:2, background:C.terra, color:C.white, border:"none", borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13 }}>
                       Approve & Release ${amount.toLocaleString()}
                    </button><button onClick={() => setDisputing(m.id)} style={{ flex:1, background:"none", border:`1.5px solid #FFCCCC`, borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"#E74C3C" }}>
                       Issue
                    </button></div>
                )
              )}

              {m.status === "disputed" && (
                <div style={{ marginTop:8, padding:"8px 12px", background:"#FFF5F5", borderRadius:7, border:"1px solid #FFCCCC", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"#E74C3C" }}>
                   Dispute open — RenoBid is reviewing. Funds frozen until resolved.
                </div>
              )}
            </div>
          );
        })}

        {/* All released */}
        {milestones.length > 0 && milestones.every(m => m.status === "released") && (
          <div style={{ padding:"16px", background:"#F0FFF4", textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:6 }}>🎉</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.green, fontWeight:700 }}>Project Complete!</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:4, marginBottom:12 }}>All funds released.</div>
            {onReview && !project.reviewed && (
              <button onClick={onReview} style={{ background:C.terra, color:C.white, border:"none", borderRadius:8, padding:"10px 22px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                Leave a Review ★
              </button>
            )}
            {project.reviewed && (
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.green, fontWeight:600 }}>✓ Review submitted</div>
            )}
          </div>
        )}
      </div>

      {/* Change Orders Section */}
      {(() => {
        const cos = project.change_orders || [];
        if (cos.length === 0) return null;
        const pendingCos = cos.filter(co => co.status === "pending" || co.status === "countered");
        return (
          <div style={{ borderTop:`2px solid ${C.sand}` }}>
            <div style={{ padding:"12px 16px", background:C.cream }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>Change Orders</span>
                {pendingCos.length > 0 && (
                  <span style={{ background:C.terra, color:"#fff", borderRadius:100, padding:"1px 8px", fontSize:10, fontWeight:700 }}>{pendingCos.length} pending</span>
                )}
              </div>
            </div>
            {cos.map(co => {
              const isPending = co.status === "pending";
              const isCountered = co.status === "countered";
              const isApproved = co.status === "approved";
              const isRejected = co.status === "rejected";
              const isCountering = coCountering === co.id;
              const handleApproveCO = async () => {
                const newTotal = (project.escrow?.total || bid.amount) + co.amount;
                const newMilestone = {
                  id: `m_co_${co.id}`,
                  label: `Change Order: ${co.description.slice(0,40)}`,
                  description: co.description,
                  pct: Math.round((co.amount / newTotal) * 100),
                  status: "pending",
                };
                const updatedCOs = (project.change_orders || []).map(c =>
                  c.id === co.id ? { ...c, status:"approved", resolvedAt: new Date().toISOString().split("T")[0] } : c
                );
                const updated = {
                  ...project,
                  change_orders: updatedCOs,
                  escrow: { ...project.escrow, total: newTotal },
                  milestones: [...(project.milestones || []), newMilestone],
                };
                onUpdate(updated);
                await supabase.from('projects').update({
                  change_orders: updatedCOs,
                  escrow: updated.escrow,
                  milestones: updated.milestones,
                }).eq('id', project.id);
              };
              const handleRejectCO = async () => {
                const updatedCOs = (project.change_orders || []).map(c =>
                  c.id === co.id ? { ...c, status:"rejected", resolvedAt: new Date().toISOString().split("T")[0] } : c
                );
                onUpdate({ ...project, change_orders: updatedCOs });
                await supabase.from('projects').update({ change_orders: updatedCOs }).eq('id', project.id);
              };
              const handleCounterCO = async () => {
                const amt = parseFloat(coCounterAmount);
                if (!amt || amt <= 0) return;
                const updatedCOs = (project.change_orders || []).map(c =>
                  c.id === co.id ? { ...c, status:"countered", counterAmount: amt } : c
                );
                onUpdate({ ...project, change_orders: updatedCOs });
                await supabase.from('projects').update({ change_orders: updatedCOs }).eq('id', project.id);
                setCoCountering(null);
                setCoCounterAmount("");
              };
              return (
                <div key={co.id} style={{ padding:"14px 16px", borderBottom:`1px solid ${C.sand}`, background: isApproved ? "#F0FFF4" : isRejected ? "#FFF5F5" : C.white }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div style={{ flex:1, marginRight:12 }}>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:2 }}>
                        {co.description}
                      </div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>
                        Requested by contractor · {co.createdAt}
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, color: isApproved ? C.sage : isRejected ? C.red : C.terra }}>+${co.amount.toLocaleString()}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color: isApproved ? C.green : isRejected ? C.red : isCountered ? C.gold : C.slate, fontWeight:600 }}>
                        {isApproved ? "✓ Approved" : isRejected ? "✗ Rejected" : isCountered ? `Counter: $${co.counterAmount?.toLocaleString()}` : "Pending"}
                      </div>
                    </div>
                  </div>
                  {(isPending || isCountered) && (
                    isCountering ? (
                      <div style={{ marginTop:8, background:C.cream, borderRadius:8, padding:"12px 14px" }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink, marginBottom:8 }}>Your counter-offer:</div>
                        <input type="number" value={coCounterAmount} onChange={e => setCoCounterAmount(e.target.value)}
                          placeholder="Enter amount" style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.white, boxSizing:"border-box", marginBottom:8 }} />
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={handleCounterCO} style={{ flex:2, background:C.gold, color:"#fff", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>Send Counter</button>
                          <button onClick={() => { setCoCountering(null); setCoCounterAmount(""); }} style={{ flex:1, background:"none", border:`1px solid ${C.sand}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:8, marginTop:8 }}>
                        <button onClick={handleApproveCO} style={{ flex:2, background:C.sage, color:"#fff", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                          ✓ Approve +${co.amount.toLocaleString()}
                        </button>
                        <button onClick={() => { setCoCountering(co.id); setCoCounterAmount(String(co.amount)); }} style={{ flex:1, background:"none", border:`1.5px solid ${C.gold}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.gold, fontWeight:600, cursor:"pointer" }}>Counter</button>
                        <button onClick={handleRejectCO} style={{ flex:1, background:"none", border:`1.5px solid #FFCCCC`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.red, cursor:"pointer" }}>Reject</button>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Stripe note */}
      <div style={{ background:C.cream, padding:"10px 16px", borderTop:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, display:"flex", alignItems:"center", gap:6 }}><span></span><span>Payments secured by Stripe Connect · Funds held in trust until you approve each milestone · 4% platform fee deducted on release</span></div></div>
  );
}

// 
// HOMEOWNER APP
// 
function HomeownerApp({ projects, updateProjects, darkMode, toggleDark, user, onLogout }) {
  const [view, setView] = useState("dashboard");
  const [activeProject, setActiveProject] = useState(null);
  const [photo, setPhoto] = useState(null); // kept separate so async FileReader never loses it
  const [draft, setDraft] = useState({ room:"Kitchen", budget:"$10k–$25k", title:"", description:"", items:[], roomW:12, roomL:14, roomH:9, scope:[], desiredStart:"", floorPlan:null, location:"", lat:null, lng:null });

  // Guided intake state
  const [intakeStep, setIntakeStep] = useState(0);
  const [intake, setIntake] = useState({
    projectType: null,   // "refresh" | "reno" | "layout" | "addspace" | "gut"
    spaces: [],          // multi-select: ["Kitchen","Bathroom","Living areas",...]
    changes: [],         // multi-select: walls, plumbing, electrical, hvac, cosmetic-only
    removal: [],         // multi-select: gut, cabinets, flooring, tile, fixtures, nothing
    diy: null,           // "none" | "some" | "lot"
    timing: null,        // "flexible" | "few-months" | "hard-deadline"
    budget: null,
  });
  const [dragging, setDragging] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Cabinets");
  const [selectedItem, setSelectedItem] = useState(null);
  const [msgInput, setMsgInput] = useState("");
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set(["DEMOLITION & SITE PREP"]));
  const [checkoutBid, setCheckoutBid] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardDone, setCardDone] = useState(false);
  const [homeAddress, setHomeAddress] = useState("");
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("idle"); // idle|card_processing|ach_pending|succeeded|failed
  const [paymentMethod, setPaymentMethod] = useState("card"); // "card" | "bank"
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  // Plaid bank verification state
  const [plaidVerified, setPlaidVerified] = useState(false);
  const [plaidBankName, setPlaidBankName] = useState("");
  const [plaidBankLast4, setPlaidBankLast4] = useState("");
  const [plaidBankAccountId, setPlaidBankAccountId] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [savedBank, setSavedBank] = useState(null); // { bankName, last4 } loaded from profile
  const [usingSavedBank, setUsingSavedBank] = useState(false);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [plaidError, setPlaidError] = useState(null);
  const [reviewModal, setReviewModal] = useState(null); // { project, contractorId }
  const [schedulingBid, setSchedulingBid] = useState(null); // { bid, project, contractor }
  const [contractorAvailCache, setContractorAvailCache] = useState({}); // contractorId -> availability
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [aiScopeInput, setAiScopeInput] = useState("");
  const [aiScopeLoading, setAiScopeLoading] = useState(false);
  const [aiScopeResult, setAiScopeResult] = useState(null); // { costEstimates, permitsNeeded, notes, room }
  const [aiIntakeDone, setAiIntakeDone] = useState(false);
  const [aiIntakeBudget, setAiIntakeBudget] = useState(null);
  const [aiIntakeTiming, setAiIntakeTiming] = useState(null);
  const [aiIntakeDiy, setAiIntakeDiy] = useState(null);
  const [aiConversationHistory, setAiConversationHistory] = useState([]);
  const [aiFollowUpMessage, setAiFollowUpMessage] = useState(null);
  const [availFilter, setAvailFilter] = useState("all"); // "all" | "30" | "60" | "90"
  const [changeOrders, setChangeOrders] = useState({}); // projectId -> []
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatTyping, setChatTyping] = useState(false);
  const [lastAIQuestions, setLastAIQuestions] = useState([]);
  const [numberedAnswers, setNumberedAnswers] = useState([]);
  const fileRef = useRef(null);
  const floorPlanRef = useRef(null);
  const canvasRef = useRef(null);
  const msgEndRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [activeProject?.messages?.length]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [chatMessages, chatTyping]);

  // Load saved bank account from homeowner profile on mount
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('profiles')
      .select('stripe_customer_id, saved_bank_last4, saved_bank_name')
      .eq('id', user.id).single()
      .then(({ data }) => {
        if (data?.stripe_customer_id) {
          setStripeCustomerId(data.stripe_customer_id);
          if (data.saved_bank_last4) {
            setSavedBank({ bankName: data.saved_bank_name || 'Bank Account', last4: data.saved_bank_last4 });
          }
        }
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Plaid Link script from CDN when entering checkout
  useEffect(() => {
    if (view !== "checkout" || document.getElementById("plaid-link-script")) return;
    const s = document.createElement("script");
    s.id = "plaid-link-script";
    s.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
    document.head.appendChild(s);
  }, [view]);

  // Handle return from ACH bank verification redirect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const intentId = params.get("payment_intent");
    const redirectStatus = params.get("redirect_status");
    if (!intentId) return;
    window.history.replaceState({}, "", window.location.pathname);
    const pendingProjectId = sessionStorage.getItem("reno_pending_project_id");
    const pendingAddress   = sessionStorage.getItem("reno_pending_address");
    sessionStorage.removeItem("reno_pending_project_id");
    sessionStorage.removeItem("reno_pending_address");
    if (!pendingProjectId) return;
    const isPending = redirectStatus === "processing";
    const newStatus = isPending ? "ach_pending" : "awarded";
    supabase.from("projects").update({
      status: newStatus,
      escrow: { funded: !isPending, paymentIntentId: intentId },
      address: pendingAddress || null,
    }).eq("id", pendingProjectId).then(() => {
      setPaymentStatus(isPending ? "ach_pending" : "succeeded");
      setPaymentIntentId(intentId);
      setView(isPending ? "dashboard" : "bids");
    });
  }, []);

  // Fetch Stripe clientSecret when checkout view mounts
  useEffect(() => {
    if (view !== "checkout" || !checkoutBid || paymentClientSecret) return;
    const base = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
    fetch(`${base}/payments/create-escrow`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bidAmount:                 checkoutBid.total,
        projectId:                 checkoutBid.project.id,
        contractorStripeAccountId: checkoutBid.bid.contractorStripeAccountId,
        homeownerId:               user?.id,
      }),
    })
      .then(r => r.json())
      .then(({ clientSecret, error: err }) => {
        if (err) { setCardError(err); return; }
        setPaymentClientSecret(clientSecret);
      })
      .catch(err => setCardError(err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, checkoutBid]);

  const myProjects = projects.filter(p => p.homeowner_id === user.id);

  const postProject = async () => {
    const newProject = {
      id: `p${Date.now()}`, ...draft, photo,
      renderedPhoto: draft.renderedPhoto || null,
      scope: draft.scope || [],
      desiredStart: draft.desiredStart || null,
      homeowner: "You", location: draft.location || "Your Area",
      lat: draft.lat || null, lng: draft.lng || null,
      postedAt: "Just now", bids: [], messages: [], status: "open",
    };
  // Save to Supabase
  if (user?.id) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('postProject: No active Supabase session — insert blocked by RLS');
      } else {
        const { data, error } = await supabase.from('projects').insert({
          homeowner_id: user.id,
          homeowner_name: user?.name || "Homeowner",
          title: newProject.title,
          room: newProject.room,
          budget: newProject.budget,
          description: newProject.description,
          location: newProject.location || "Your Area",
          lat: newProject.lat,
          lng: newProject.lng,
          status: 'open',
          scope: newProject.scope || [],
          items: newProject.items || [],
          photo: newProject.photo || null,
          rendered_photo: newProject.renderedPhoto || null,
        }).select().single();
        if (error) { console.error('Post project DB error:', error.message, error); }
        if (data) newProject.id = data.id;
      }
    } catch (err) { console.error('Post project error:', err); }
  } else {
    console.warn('postProject: no user.id, skipping insert');
  }

  updateProjects([...projects, newProject]);
  setActiveProject(newProject);
  setView("bids");
    setPhoto(null);
    setDraft({ room:"Kitchen", budget:"$10k–$25k", title:"", description:"", items:[], roomW:12, roomL:14, roomH:9, scope:[], desiredStart:"", floorPlan:null, location:"", lat:null, lng:null });
    setAiConversationHistory([]);
    setAiScopeInput("");
    setAiScopeResult(null);
    setAiFollowUpMessage(null);
    setAiIntakeDone(false);
  };

  const sendMessage = async (projectId) => {
    if (!msgInput.trim()) return;
    const project = projects.find(p => p.id === projectId);
    const acceptedBid = project?.bids?.find(b => b.status === 'accepted');
    const updated = projects.map(p => p.id === projectId ? {
      ...p, messages: [...(p.messages||[]), { from:"homeowner", text:msgInput.trim(), time: new Date().toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) }]
    } : p);
    // Persist to Supabase
    if (user?.id) {
      try {
        const { error } = await supabase.from('messages').insert({
          project_id: projectId,
          sender_id: user.id,
          recipient_id: acceptedBid?.contractorId || null,
          content: msgInput.trim(),
          read: false,
        });
        if (error) console.error('Send message DB error:', error.message, error);
      } catch (err) { console.error('Send message error:', err); }
    }
    updateProjects(updated);
    setActiveProject(updated.find(p => p.id === projectId));
    setMsgInput("");
  };

  // Translate intake answers → pre-populated scope [{id, by}]
  const intakeToScope = (ans) => {
    const items = [];
    const add = (id, by="contractor") => { if (!items.find(i=>i.id===id)) items.push({id,by,rooms:[]}); };
    const demoBy = (ans.diy === "some" || ans.diy === "lot") ? "homeowner" : "contractor";
    const paintBy = ans.diy === "lot" ? "homeowner" : "contractor";
    const hasWalls    = ans.changes.includes("walls");
    const hasPlumbing = ans.changes.includes("plumbing");
    const hasElec     = ans.changes.includes("electrical");
    const hasHVAC     = ans.changes.includes("hvac");
    const isGut       = ans.projectType === "gut" || ans.removal.includes("gut");
    const isAddSpace  = ans.projectType === "addspace";
    const hasKitchen  = ans.spaces.includes("Kitchen");
    const hasBath     = ans.spaces.includes("Bathrooms");
    const hasBasement = ans.spaces.includes("Basement");

    //  Demo & Removal
    if (isGut)                            { add("demo_008");               }  // full gut — always contractor
    if (ans.removal.includes("cabinets")) { add("demo_001", demoBy);       }  // selective demo — cabinets
    if (ans.removal.includes("flooring")) { add("demo_003", demoBy);       }  // selective demo — flooring
    if (ans.removal.includes("tile"))     { add("demo_006", demoBy);       }  // selective demo — tile/backsplash
    if (ans.removal.includes("fixtures")) { add("plumb_002", demoBy);      }  // fixture swap/removal

    //  Structural
    if (hasWalls) {
      add("demo_004");   // non-load-bearing wall removal
      add("demo_005");   // load-bearing wall removal
      add("struct_001"); // beam installation / structural framing
    }
    if (ans.projectType === "layout") {
      add("demo_012");   // window/door rough opening demo — add/widen doorways
    }
    if (isGut || isAddSpace) {
      add("struct_004"); // subfloor repair/sistering — common in gut/addition
    }
    if (hasBasement) {
      add("struct_008"); // crawl space/basement structural work
    }

    //  Plumbing
    if (hasPlumbing) {
      add("plumb_001"); // fixture rough-in — new location
      add("plumb_008"); // drain/waste/vent work
    }
    if (isAddSpace && hasBath)  { add("plumb_001"); add("plumb_008"); add("plumb_005"); } // new bathroom
    if (hasKitchen || hasBath)  { add("plumb_006"); } // water heater — relevant for wet rooms

    //  Electrical
    if (hasElec) {
      add("elec_001"); // panel upgrade / replacement
      add("elec_009"); // whole-home rewire
      add("elec_004"); // recessed lighting
      add("elec_008"); // EV charger (240V)
    } else if (ans.projectType !== "refresh") {
      add("elec_004"); // at minimum, recessed lighting for any real reno
    }

    //  HVAC
    if (hasHVAC || hasWalls || isGut) { add("hvac_004"); } // ductwork modification
    if (isAddSpace)                   { add("hvac_004"); add("hvac_003"); } // mini-split for addition

    //  Building & Finishing
    if (hasWalls || isAddSpace)       { add("struct_001"); add("dwall_001"); add("insul_001"); }
    else if (isGut)                   { add("dwall_001"); add("insul_001"); }
    if (isAddSpace)                   { add("door_003"); } // window replacement / exterior doors
    if (ans.projectType !== null)     { add("paint_001", paintBy); } // interior painting — almost always

    //  Installation
    // Kitchen installs
    if (hasKitchen && ans.projectType !== "refresh") {
      add("carp_001"); // kitchen cabinets — upper
      add("plumb_004"); // sink & faucet installation
      add("fix_006"); // range/oven install
    }
    if (hasKitchen) {
      add("tile_004"); // backsplash tile — kitchen
      add("elec_006"); // ceiling/light fixture
    }
    // Flooring — any non-refresh reno
    if (ans.projectType !== "refresh" && ans.removal.includes("flooring")) {
      add("floor_003"); // LVP install
    } else if (ans.projectType === "reno" || isGut) {
      add("floor_003");
    }
    // Bathroom installs
    if (hasBath && ans.projectType !== "refresh") {
      add("plumb_004"); // sink & faucet
      add("fix_003");   // toilet supply and install
      add("fix_001");   // bathtub supply and install
      add("carp_004");  // bathroom vanity installation
      add("tile_002");  // wall tile installation
    }
    if (isAddSpace && hasBath) {
      add("plumb_004"); add("fix_003"); add("fix_001"); add("carp_004"); add("tile_002");
    }
    // Doors & trim — any gut or layout change
    if (isGut || hasWalls || isAddSpace) { add("door_001"); } // interior door supply and install
    // Lighting — always relevant for non-refresh
    if (ans.projectType !== "refresh") { add("elec_006"); }

    return items;
  };

  const acceptBid = async (projectId, bidId) => {
    const project = projects.find(p => p.id === projectId);
    const bid = project?.bids?.find(b => b.id === bidId);
    const total = bid?.amount || 0;
    const STRUCTURAL_ITEM_IDS = new Set([
      "struct_001","struct_002","struct_003","struct_004",
      "struct_005","struct_006","struct_007","struct_008",
      "demo_004","demo_008"
    ]);
    const isStructural = project.scope.some(s => STRUCTURAL_ITEM_IDS.has(s.id));

    const milestones = isStructural ? [
      { id:"m1", label:"Demo & Rough-in Complete", description:"Demolition done, structural/plumbing/electrical rough-ins inspected", pct:30, status:"pending" },
      { id:"m2", label:"Finish Work Complete", description:"Drywall, tile, cabinets, fixtures installed", pct:50, status:"pending" },
      { id:"m3", label:"Final Walkthrough Approved", description:"Punch list complete, final clean, all items signed off", pct:20, status:"pending" },
    ] : [
      { id:"m1", label:"Work Started", description:"Materials delivered, work underway", pct:50, status:"pending" },
      { id:"m2", label:"Job Complete", description:"All work finished, final walkthrough approved", pct:50, status:"pending" },
    ];

    // Optimistically update UI state
    const updatedEscrow = { total, funded: false, released: 0, platform_fee: Math.round(total * 0.04) };
    const updated = projects.map(p => p.id === projectId ? {
      ...p, status:"awaiting_payment", awardedBid: bidId,
      escrow: updatedEscrow,
      milestones,
      bids: p.bids.map(b => ({ ...b, status: b.id === bidId ? "accepted" : "declined" }))
    } : p);
    updateProjects(updated);
    setActiveProject(updated.find(p => p.id === projectId));
    setView("checkout");
    setCheckoutBid({ project, bid, milestones, total });
    // Persist to Supabase
    if (user?.id) {
      try {
        const { error: projErr } = await supabase.from('projects').update({
          status: 'awaiting_payment',
          awarded_bid_id: bidId,
          milestones,
          escrow: updatedEscrow,
        }).eq('id', projectId);
        if (projErr) console.error('Accept bid project error:', projErr.message, projErr);

        // Update each bid's status in the bids table
        for (const bid of (project.bids || [])) {
          if (typeof bid.id === 'string' && bid.id.includes('-')) { // only real UUIDs
            const { error: bidErr } = await supabase.from('bids')
              .update({ status: bid.id === bidId ? 'accepted' : 'declined' })
              .eq('id', bid.id);
            if (bidErr) console.error('Bid status update error:', bidErr.message);
          }
        }
      } catch (err) { console.error('Accept bid Supabase error:', err); }
    }
  };

  // Canvas drag handlers
  const handleCanvasDrop = (e) => {
    e.preventDefault(); setIsDragOver(false);
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const newItem = { ...dragging, uid: Date.now(), x, y };
    setDraft(d => ({ ...d, items: dragging.isNew ? [...d.items, newItem] : d.items.map(i => i.uid === dragging.uid ? { ...i, x, y } : i) }));
    setDragging(null);
  };

  const Nav = () => (
    <nav style={{ background:C.white, borderBottom:`1px solid ${C.sand}`, padding:"0 20px", height:52, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <button onClick={() => setView("dashboard")} style={{ background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.ink }}>
          Reno<span style={{ color:C.terra }}>Bid</span>
        </button>
        {view !== "dashboard" && (
          <button onClick={() => setView("dashboard")} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"4px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            Home
          </button>
        )}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <button onClick={toggleDark} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"4px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>
          {darkMode ? "Light" : "Dark"}
        </button>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.clay, fontWeight:600 }}>Homeowner</div>
        <button onClick={onLogout} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"4px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>Log out</button>
      </div>
    </nav>
  );

  //  DASHBOARD 
  if (view === "dashboard") return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ flex:1, maxWidth:960, margin:"0 auto", padding:"32px 24px", width:"100%" }} className="fade-in"><div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}><h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, color:C.ink }}>My Projects</h2><button onClick={() => { setView("chat-intake"); setChatMessages([{ role:"assistant", content:"Hi! Tell me about your renovation project. What are you looking to do?" }]); setChatInput(""); setChatTyping(false); }} style={{ background:C.terra, color:C.white, border:"none", borderRadius:8, padding:"10px 20px", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, cursor:"pointer" }}>+ New Project</button></div><p style={{ fontFamily:"'DM Sans',sans-serif", color:C.slate, fontSize:14, marginBottom:28 }}>Track your renovation projects and contractor bids</p>

        {/* Availability filter chips */}
        {myProjects.length > 0 && (
          <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, alignSelf:"center", marginRight:4 }}>Filter by bid start:</div>
            {[["all","All"],["30","≤30 days"],["60","≤60 days"],["90","≤90 days"]].map(([val,label]) => (
              <button key={val} onClick={() => setAvailFilter(val)}
                style={{ padding:"4px 12px", borderRadius:100, fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, cursor:"pointer", border:`1px solid ${availFilter===val ? C.terra : C.sand}`, background: availFilter===val ? C.terra : C.white, color: availFilter===val ? C.white : C.slate }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {myProjects.length === 0 ? (
          <div style={{ background:C.white, borderRadius:16, padding:56, textAlign:"center", border:`2px dashed ${C.sand}` }}><div style={{ fontSize:48, marginBottom:16 }}></div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:C.ink, marginBottom:8 }}>No projects yet</div><div style={{ fontFamily:"'DM Sans',sans-serif", color:C.slate, marginBottom:24 }}>Post your first project and start receiving bids</div><button onClick={() => { setView("chat-intake"); setChatMessages([{ role:"assistant", content:"Hi! Tell me about your renovation project. What are you looking to do?" }]); setChatInput(""); setChatTyping(false); }} style={{ background:C.terra, color:C.white, border:"none", borderRadius:8, padding:"12px 28px", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:600 }}>Create Your First Project</button></div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(380px, 1fr))", gap:16 }}>
            {myProjects.filter(p => {
              if (availFilter === "all") return true;
              const days = parseInt(availFilter);
              const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + days);
              return (p.bids || []).some(b => {
                if (!b.startDate) return false;
                return new Date(b.startDate + "T12:00:00") <= cutoff;
              });
            }).map(p => {
              const earliestBid = (p.bids||[]).filter(b=>b.startDate).sort((a,b)=>new Date(a.startDate)-new Date(b.startDate))[0];
              const pendingCOs = (changeOrders[p.id]||[]).filter(co=>co.status==="pending").length;
              const displayStatus = p.status === "awarded" ? "awarded" : p.bids?.some(b => b.status === "accepted") ? "accepted" : "open";
              return (
              <div key={p.id} style={{ background:C.white, borderRadius:12, padding:24, border:`1px solid ${C.sand}`, display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}><div style={{ flex:1, cursor:"pointer" }} onClick={() => { setActiveProject(p); setView("project-detail"); }}><div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}><span style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:C.ink }}>{p.title}</span><span style={{ background: displayStatus==="awarded" ? "#E8F5E9" : displayStatus==="accepted" ? "#E8F0FE" : "#FFF8E1", color: displayStatus==="awarded" ? C.green : displayStatus==="accepted" ? "#1A56DB" : C.gold, borderRadius:100, padding:"2px 10px", fontSize:11, fontFamily:"'DM Sans',sans-serif", fontWeight:600, textTransform:"uppercase" }}>
                      {displayStatus==="awarded" ? "Awarded" : displayStatus==="accepted" ? "Accepted" : "Open"}
                    </span>
                    {pendingCOs > 0 && <span style={{ background:"#FEE2E2", color:"#DC2626", borderRadius:100, padding:"2px 9px", fontSize:11, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>⚡ {pendingCOs} Change Order{pendingCOs>1?"s":""}</span>}
                  </div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>{p.room} · {p.budget} · {p.bids?.length || 0} bid{p.bids?.length !== 1 ? "s" : ""} · {p.postedAt}</div>
                  {earliestBid && (
                    <div style={{ marginTop:4, fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"#2D6A4F", fontWeight:600 }}>
                      📅 Earliest bid start: {new Date(earliestBid.startDate+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                    </div>
                  )}
                  {p.description && <div style={{ marginTop:4, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:1, WebkitBoxOrient:"vertical" }}>{p.description}</div>}
                </div><div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0, marginLeft:16 }}>
                  <button onClick={() => { setActiveProject(p); setView("bids"); }} style={{ background:C.terra, color:C.white, border:"none", borderRadius:6, padding:"8px 16px", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:600, cursor:"pointer" }}>
                    View Bids →
                  </button>
                  <button onClick={() => { setActiveProject(p); setView("messages"); }} style={{ display:"flex", alignItems:"center", gap:5, background:C.cream, border:`1px solid ${C.sand}`, borderRadius:6, padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", color:C.slate, cursor:"pointer" }}>
                    <span>💬</span>
                    {p.messages?.length > 0 && <span style={{ background:C.slate, color:C.white, borderRadius:100, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{p.messages.length}</span>}
                  </button>
                </div></div>
              );
            })}
          </div>
        )}

        {/* Seed projects as browsable */}
        <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:C.ink, marginTop:40, marginBottom:16 }}>Community Projects Near You</h3><div style={{ display:"grid", gap:12 }}>
          {projects.filter(p => p.homeowner_id !== user.id).map(p => (
            <div key={p.id} style={{ background:C.white, borderRadius:12, padding:20, border:`1px solid ${C.sand}`, opacity:0.7 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, marginBottom:4 }}>{p.title}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>{p.room} · {p.budget} · {p.location} · {p.bids?.length||0} bids</div></div>
          ))}
        </div></div></div>
  );

  //  AI-FIRST INTAKE
  if (view === "ai-intake") {
    const BUDGETS_LIST = ["Under $5k","$5k–$10k","$10k–$25k","$25k–$50k","$50k+"];
    const canProceed = aiIntakeDone && aiIntakeBudget && aiIntakeTiming && aiIntakeDiy;
    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav />
        <div style={{ flex:1, maxWidth:580, margin:"0 auto", padding:"32px 24px", width:"100%" }} className="fade-in">
          {/* Header */}
          <div style={{ marginBottom:28 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:C.ink, marginBottom:6 }}>Describe your renovation</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.slate, lineHeight:1.6 }}>Tell us what you have in mind — AI will build your scope checklist, flag permits, and estimate costs.</div>
          </div>

          {/* Follow-up question from AI */}
          {aiFollowUpMessage && !aiIntakeDone && (
            <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sage}40`, marginBottom:14 }} className="fade-in">
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.sage, textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>A few quick questions</div>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{aiFollowUpMessage}</div>
            </div>
          )}

          {/* Textarea */}
          <div style={{ background:C.white, borderRadius:14, padding:20, border:`1.5px solid ${C.sand}`, marginBottom:16 }}>
            <textarea
              value={aiScopeInput}
              onChange={e => { setAiScopeInput(e.target.value); if (aiIntakeDone || aiFollowUpMessage) { setAiIntakeDone(false); setAiScopeResult(null); setAiConversationHistory([]); setAiFollowUpMessage(null); } }}
              placeholder={aiFollowUpMessage ? "Answer the questions above…" : 'e.g. "I want to gut the bathroom, add a walk-in shower, heated tile floors, and a new double vanity"'}
              rows={4}
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, background:C.cream, resize:"none", boxSizing:"border-box", outline:"none", lineHeight:1.6 }}
            />
            <button
              disabled={!aiScopeInput.trim() || aiScopeLoading}
              onClick={async () => {
                setAiScopeLoading(true);
                try {
                  const response = await translateScopeWithAI(aiScopeInput, draft.room, aiConversationHistory);
                  const newHistory = [
                    ...aiConversationHistory,
                    { role: "user", content: response.userMessage },
                    { role: "assistant", content: response.assistantMessage },
                  ];
                  setAiConversationHistory(newHistory);
                  if (response.type === "scope") {
                    const rawResult = response.result;
                    const normalizedResult = Array.isArray(rawResult) ? { scope: rawResult } : rawResult;
                    setAiScopeResult(normalizedResult);
                    setAiIntakeDone(true);
                    setAiFollowUpMessage(null);
                    const match = BUDGETS_LIST.find(b => b === normalizedResult.suggestedBudget);
                    setAiIntakeBudget(match || null);
                  } else {
                    setAiFollowUpMessage(response.text);
                    setAiScopeInput("");
                  }
                } catch (e) {
                  if (e.message === "NO_KEY") alert("Add your Anthropic API key to .env to use AI scope generation.");
                  else alert("AI analysis failed. Please try again.");
                } finally { setAiScopeLoading(false); }
              }}
              style={{ marginTop:12, display:"flex", alignItems:"center", gap:8, background: aiScopeInput.trim() && !aiScopeLoading ? C.terra : C.sand, color: aiScopeInput.trim() && !aiScopeLoading ? C.white : C.slate, border:"none", borderRadius:8, padding:"10px 20px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor: aiScopeInput.trim() && !aiScopeLoading ? "pointer" : "not-allowed" }}>
              {aiScopeLoading ? <><span style={{ display:"inline-block", width:14, height:14, border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />&nbsp;{aiFollowUpMessage ? "Sending…" : "Analyzing your project…"}</> : aiFollowUpMessage ? "Send Reply →" : "✨ Analyze Project"}
            </button>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>

          {/* Phase 2: Confirmation chips — shown after AI runs */}
          {aiIntakeDone && aiScopeResult && (
            <div className="fade-in">
              {/* AI summary */}
              <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sage}40`, marginBottom:14 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.sage, textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>✓ Scope Generated</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, marginBottom:6 }}>
                  {aiScopeResult.scope?.length || 0} scope items identified
                  {aiScopeResult.permitsNeeded?.length > 0 && ` · ${aiScopeResult.permitsNeeded.length} permit(s) flagged`}
                </div>
                {aiScopeResult.notes && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.6, marginBottom:8 }}>💡 {aiScopeResult.notes}</div>
                )}
                {aiScopeResult.permitsNeeded?.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                    {aiScopeResult.permitsNeeded.map(p => (
                      <span key={p} style={{ background:"#FFF8E1", border:`1px solid ${C.gold}55`, borderRadius:100, padding:"2px 9px", fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:600, color:C.gold }}>📋 {p}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Budget */}
              <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sand}`, marginBottom:10 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:4 }}>What's your budget?</div>
                {aiScopeResult.suggestedBudget && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.clay, marginBottom:8 }}>AI suggests: {aiScopeResult.suggestedBudget}</div>
                )}
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {BUDGETS_LIST.map(b => (
                    <button key={b} onClick={() => setAiIntakeBudget(b)} style={{ padding:"6px 14px", borderRadius:100, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer", border:`1.5px solid ${aiIntakeBudget===b ? C.slate : C.sand}`, background: aiIntakeBudget===b ? C.slate : C.white, color: aiIntakeBudget===b ? C.white : C.slate }}>{b}</button>
                  ))}
                </div>
              </div>

              {/* Timing */}
              <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sand}`, marginBottom:10 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:8 }}>When do you want to start?</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {[["flexible","Flexible"],["few-months","In a few months"],["hard-deadline","Hard deadline"]].map(([val,label]) => (
                    <button key={val} onClick={() => setAiIntakeTiming(val)} style={{ padding:"6px 14px", borderRadius:100, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer", border:`1.5px solid ${aiIntakeTiming===val ? C.terra : C.sand}`, background: aiIntakeTiming===val ? C.terra : C.white, color: aiIntakeTiming===val ? C.white : C.slate }}>{label}</button>
                  ))}
                </div>
              </div>

              {/* DIY */}
              <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sand}`, marginBottom:20 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:8 }}>Planning any DIY work?</div>
                <div style={{ display:"flex", gap:6 }}>
                  {[["none","None — all contractor"],["some","Some DIY"],["lot","Mostly DIY"]].map(([val,label]) => (
                    <button key={val} onClick={() => setAiIntakeDiy(val)} style={{ flex:1, padding:"6px 8px", borderRadius:100, fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${aiIntakeDiy===val ? C.terra : C.sand}`, background: aiIntakeDiy===val ? C.terra : C.white, color: aiIntakeDiy===val ? C.white : C.slate, textAlign:"center" }}>{label}</button>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <button
                disabled={!canProceed}
                onClick={() => {
                  const inferredRoom = aiScopeResult.room || "Kitchen";
                  setDraft(d => ({ ...d, scope: aiScopeResult.scope || [], budget: aiIntakeBudget, room: inferredRoom }));
                  setIntake(i => ({ ...i, timing: aiIntakeTiming, diy: aiIntakeDiy, budget: aiIntakeBudget }));
                  setView("create");
                }}
                style={{ width:"100%", background: canProceed ? C.terra : C.sand, color: canProceed ? C.white : C.slate, border:"none", borderRadius:10, padding:"14px 0", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:700, cursor: canProceed ? "pointer" : "not-allowed" }}>
                Let's build your project →
              </button>
            </div>
          )}

          {/* Escape hatch */}
          <div style={{ textAlign:"center", marginTop:20 }}>
            <button onClick={() => { setIntakeStep(0); setView("intake"); }} style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer", textDecoration:"underline" }}>
              Prefer to answer questions step by step →
            </button>
          </div>
        </div>
      </div>
    );
  }

  //  CONVERSATIONAL AI INTAKE
  if (view === "chat-intake") {
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL));

    // Extract numbered questions from an AI message (lines starting with "1. ", "2. " etc.)
    const extractNumberedQuestions = (text) => {
      const lines = text.split('\n');
      const questions = [];
      let current = null;
      for (const line of lines) {
        const match = line.match(/^(\d+)\.\s+(.+)/);
        if (match) {
          if (current) questions.push(current);
          current = match[2].trim();
        } else if (current && line.trim()) {
          current += ' ' + line.trim();
        } else if (current && !line.trim()) {
          questions.push(current);
          current = null;
        }
      }
      if (current) questions.push(current);
      return questions;
    };

    const sendChat = async () => {
      // Build text from numbered answers or plain textarea
      let text;
      if (numberedAnswers.length > 0) {
        const filled = numberedAnswers.map((a, i) => `${i + 1}. ${a.trim() || "n/a"}`);
        text = filled.join('\n');
      } else {
        text = chatInput.trim();
      }
      if (!text || chatTyping) return;

      const userMsg = { role:"user", content: text };
      const updated = [...chatMessages, userMsg];
      setChatMessages(updated);
      setChatInput("");
      setLastAIQuestions([]);
      setNumberedAnswers([]);
      setChatTyping(true);
      try {
        const res = await fetch(backendUrl + "/intake", {
          method:"POST",
          headers:{ "Content-Type":"application/json" },
          body: JSON.stringify({ messages: updated }),
        });
        if (!res.ok) throw new Error("Network error");
        const data = await res.json();
        const rawContent = data.reply || data.message || data.content || "";
        const reply = Array.isArray(rawContent)
          ? rawContent.map(b => b.text || "").join("")
          : rawContent;
        setChatTyping(false);

        if (reply.includes("SCOPE_READY")) {
          // Add final AI message (strip JSON block for display)
          const displayText = reply.replace(/SCOPE_READY[\s\S]*/,"").trim();
          if (displayText) setChatMessages(prev => [...prev, { role:"assistant", content: displayText }]);

          // Parse JSON block after SCOPE_READY
          const jsonMatch = reply.match(/SCOPE_READY\s*\n?([\s\S]+)/);
          let preChecked = [];
          if (jsonMatch) {
            try {
              const parsed = JSON.parse(jsonMatch[1].trim());
              if (Array.isArray(parsed)) {
                preChecked = parsed
                  .filter(item => item.id && ALL_SCOPE_FLAT.some(s => s.id === item.id))
                  .map(item => ({
                    id: item.id,
                    by: item.by === "owner" || item.by === "homeowner" ? "homeowner" : "contractor",
                    rooms: []
                  }));
              }
              // Non-array format: preChecked stays [] — user builds scope manually in create view
            } catch(e) { console.warn("SCOPE_READY parse error", e); }
          }
          setDraft(d => {
            const existingIds = new Set(d.scope.map(s => s.id));
            const merged = [...d.scope, ...preChecked.filter(s => !existingIds.has(s.id))];
            return { ...d, scope: merged };
          });
          setIntake(i => ({ ...i, projectType:"chat" }));
          setTimeout(() => setView("create"), 600);
        } else {
          setChatMessages(prev => [...prev, { role:"assistant", content: reply }]);
          // If the AI sent numbered questions, set up answer fields
          const qs = extractNumberedQuestions(reply);
          if (qs.length >= 2) {
            setLastAIQuestions(qs);
            setNumberedAnswers(new Array(qs.length).fill(""));
          }
        }
      } catch(err) {
        setChatTyping(false);
        setChatMessages(prev => [...prev, { role:"assistant", content:"Sorry, I couldn't connect right now. You can skip ahead and build your scope manually." }]);
      }
    };

    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}>
        <GlobalStyles />
        <style>{`
          @keyframes chatBounce {
            0%,80%,100% { transform: translateY(0); }
            40% { transform: translateY(-6px); }
          }
        `}</style>
        <Nav />

        <div style={{ flex:1, display:"flex", flexDirection:"column", maxWidth:600, margin:"0 auto", width:"100%", padding:"0 0 16px" }}>
          {/* Header */}
          <div style={{ padding:"20px 20px 12px", borderBottom:`1px solid ${C.sand}` }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:C.ink, fontWeight:700 }}>New Project</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginTop:2 }}>Chat with our AI to build your scope checklist</div>
          </div>

          {/* Message list */}
          <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 8px", display:"flex", flexDirection:"column", gap:12, WebkitOverflowScrolling:"touch" }}>
            {chatMessages.map((msg, i) => {
              const isAI = msg.role === "assistant";
              return (
                <div key={i} style={{ display:"flex", justifyContent: isAI ? "flex-start" : "flex-end" }}>
                  <div style={{
                    maxWidth:"78%",
                    padding:"11px 15px",
                    borderRadius: isAI ? "18px 18px 18px 4px" : "18px 18px 4px 18px",
                    background: isAI ? C.white : C.terra,
                    color: isAI ? C.ink : C.white,
                    fontFamily:"'DM Sans',sans-serif",
                    fontSize:14,
                    lineHeight:1.65,
                    whiteSpace:"pre-wrap",
                    border: isAI ? `1px solid ${C.sand}` : "none",
                    boxShadow: isAI ? "0 1px 4px rgba(0,0,0,0.06)" : "0 2px 8px rgba(184,92,56,0.25)",
                  }}>
                    {msg.content}
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {chatTyping && (
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{ padding:"12px 16px", background:C.white, borderRadius:"18px 18px 18px 4px", border:`1px solid ${C.sand}`, display:"flex", alignItems:"center", gap:5 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:C.clay, animation:`chatBounce 1.2s infinite`, animationDelay:`${i*0.2}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input area */}
          <div style={{ padding:"8px 16px 0", borderTop:`1px solid ${C.sand}` }}>
            {numberedAnswers.length > 0 ? (
              /* Numbered answer fields — one per AI question */
              <div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:10 }}>
                  {lastAIQuestions.map((q, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{
                        minWidth:22, height:22, borderRadius:"50%",
                        background: C.terra, color: C.white,
                        fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        flexShrink:0
                      }}>{i + 1}</span>
                      <input
                        type="text"
                        placeholder={q}
                        value={numberedAnswers[i]}
                        onChange={e => {
                          const next = [...numberedAnswers];
                          next[i] = e.target.value;
                          setNumberedAnswers(next);
                        }}
                        onKeyDown={e => { if (e.key === "Enter") sendChat(); }}
                        style={{
                          flex:1, padding:"8px 12px", borderRadius:8,
                          border:`1.5px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif",
                          fontSize:13, color:C.ink, background:C.white, outline:"none",
                        }}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={sendChat}
                  disabled={chatTyping || numberedAnswers.every(a => !a.trim())}
                  style={{
                    width:"100%", background: !chatTyping && numberedAnswers.some(a => a.trim()) ? C.terra : C.sand,
                    color: !chatTyping && numberedAnswers.some(a => a.trim()) ? C.white : C.slate,
                    border:"none", borderRadius:10, padding:"11px 0",
                    fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700,
                    cursor: !chatTyping && numberedAnswers.some(a => a.trim()) ? "pointer" : "not-allowed",
                    transition:"all 0.15s", marginBottom:2
                  }}>
                  Send answers →
                </button>
              </div>
            ) : (
              /* Default free-form textarea */
              <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
                <textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Describe your project…"
                  rows={2}
                  style={{ flex:1, border:`1.5px solid ${C.sand}`, borderRadius:12, padding:"10px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, background:C.white, resize:"none", outline:"none", lineHeight:1.5 }}
                />
                <button
                  onClick={sendChat}
                  disabled={!chatInput.trim() || chatTyping}
                  style={{ background: chatInput.trim() && !chatTyping ? C.terra : C.sand, color: chatInput.trim() && !chatTyping ? C.white : C.slate, border:"none", borderRadius:12, padding:"10px 18px", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor: chatInput.trim() && !chatTyping ? "pointer" : "not-allowed", transition:"all 0.15s", alignSelf:"stretch" }}>
                  Send
                </button>
              </div>
            )}
          </div>

          {/* Continue to scope / Skip */}
          <div style={{ textAlign:"center", padding:"14px 16px 0", display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
            {intake.projectType === "chat" && (
              <button
                onClick={() => setView("create")}
                style={{ background:C.terra, color:"#fff", border:"none", borderRadius:100, padding:"10px 24px", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                → Continue to scope
              </button>
            )}
            <button
              onClick={() => { setDraft(d => ({ ...d, scope:[] })); setView("create"); }}
              style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer", textDecoration:"underline" }}>
              Skip — build my own scope
            </button>
          </div>
        </div>
      </div>
    );
  }

  //  GUIDED INTAKE
  if (view === "intake") {

    // Questions — mix of single-select and multi-select
    const QUESTIONS = [
      {
        key: "projectType",
        type: "single",
        q: "What best describes your project?",
        sub: "Don't worry about getting this perfect — we'll refine it in the next steps.",
        options: [
          { val:"refresh",   emoji:"", label:"Refresh & update",     desc:"New finishes, fixtures, or paint — nothing structural" },
          { val:"reno",      emoji:"", label:"Room renovation",       desc:"Updating cabinets, flooring, or fixtures in one or more rooms" },
          { val:"layout",    emoji:"", label:"Layout change",         desc:"Opening up spaces, moving or removing walls between rooms" },
          { val:"addspace",  emoji:"", label:"Adding space",           desc:"New bathroom, finishing a basement, addition, or ADU" },
          { val:"gut",       emoji:"", label:"Full gut renovation",    desc:"Starting from scratch — everything comes out" },
        ]
      },
      {
        key: "spaces",
        type: "multi",
        q: "Which spaces are involved?",
        sub: "Select all that apply — a project can span multiple rooms.",
        options: [
          { val:"Kitchen",       emoji:"", label:"Kitchen" },
          { val:"Bathrooms",     emoji:"", label:"Bathrooms" },
          { val:"Living areas",  emoji:"", label:"Living / dining areas" },
          { val:"Bedrooms",      emoji:"", label:"Bedrooms" },
          { val:"Basement",      emoji:"", label:"Basement" },
          { val:"Outdoor",       emoji:"", label:"Outdoor / exterior" },
          { val:"Whole home",    emoji:"", label:"Whole home" },
        ]
      },
      {
        key: "changes",
        type: "multi",
        q: "What's physically changing in the space?",
        sub: "This tells us what trades are involved and what permits you'll likely need.",
        options: [
          { val:"walls",         emoji:"", label:"Walls moving or being removed",  desc:"Opening rooms up, removing or adding partitions" },
          { val:"plumbing",      emoji:"", label:"Plumbing moving locations",       desc:"Sink, toilet, or shower going somewhere new" },
          { val:"electrical",    emoji:"", label:"New electrical circuits or rewire", desc:"Panel upgrade, new circuits, or full rewire" },
          { val:"hvac",          emoji:"", label:"HVAC or ductwork changing",       desc:"Vents being relocated or new system going in" },
          { val:"cosmetic-only", emoji:"", label:"Cosmetic changes only",           desc:"Nothing structural — finishes, fixtures, paint" },
        ]
      },
      {
        key: "removal",
        type: "multi",
        q: "What's coming out?",
        sub: "Demolition scope affects timeline, cost, and what permits contractors need to pull.",
        options: [
          { val:"gut",       emoji:"", label:"Everything — full gut to studs",  desc:"Walls, subfloor, insulation — completely bare" },
          { val:"cabinets",  emoji:"", label:"Cabinets & countertops" },
          { val:"flooring",  emoji:"", label:"Flooring" },
          { val:"tile",      emoji:"", label:"Tile (walls or floor)" },
          { val:"fixtures",  emoji:"", label:"Fixtures (sinks, toilets, tubs, appliances)" },
          { val:"nothing",   emoji:"", label:"Nothing — adding to what's there" },
        ]
      },
      {
        key: "diy",
        type: "single",
        q: "Are you planning to do any of this work yourself?",
        sub: "Being upfront here saves money — contractors only price what they actually need to do.",
        options: [
          { val:"none", emoji:"", label:"Contractor does everything",           desc:"Just want it done" },
          { val:"some", emoji:"", label:"I'll handle a few things myself",      desc:"Maybe demo, painting, or installing some fixtures" },
          { val:"lot",  emoji:"", label:"I'm doing a lot of the work",          desc:"Need help with technical work only — plumbing, electrical, structural" },
        ]
      },
      {
        key: "timing",
        type: "single",
        q: "How's your timing?",
        sub: "This helps contractors know whether they're a realistic match for your project.",
        options: [
          { val:"flexible",       emoji:"", label:"Flexible",              desc:"Whenever the right contractor is available" },
          { val:"few-months",     emoji:"", label:"Within a few months",   desc:"General timeframe but no hard date" },
          { val:"hard-deadline",  emoji:"⏰", label:"I have a hard deadline", desc:"Move-in date, new baby, party — a fixed date" },
        ]
      },
      {
        key: "budget",
        type: "single",
        q: "What's your budget range?",
        sub: "This filters contractors to realistic matches for your project size.",
        options: [
          { val:"Under $5k",    emoji:"", label:"Under $5k" },
          { val:"$5k–$10k",    emoji:"", label:"$5k – $10k" },
          { val:"$10k–$25k",   emoji:"", label:"$10k – $25k" },
          { val:"$25k–$50k",   emoji:"", label:"$25k – $50k" },
          { val:"$50k–$100k",  emoji:"", label:"$50k – $100k" },
          { val:"$100k+",      emoji:"", label:"$100k+" },
        ]
      },
    ];

    const q = QUESTIONS[intakeStep];
    const isMulti = q.type === "multi";
    const currentVal = intake[q.key];
    const isLast = intakeStep === QUESTIONS.length - 1;
    const progress = (intakeStep / QUESTIONS.length) * 100;

    // Multi-select toggle
    const toggleMulti = (val) => {
      const current = intake[q.key] || [];
      // "cosmetic-only" and "nothing" are exclusive — deselect others
      if (val === "cosmetic-only" || val === "nothing") {
        setIntake(a => ({ ...a, [q.key]: current.includes(val) ? [] : [val] }));
        return;
      }
      // Deselect exclusive options if selecting something real
      const filtered = current.filter(v => v !== "cosmetic-only" && v !== "nothing");
      setIntake(a => ({
        ...a,
        [q.key]: filtered.includes(val) ? filtered.filter(v=>v!==val) : [...filtered, val]
      }));
    };

    const canAdvance = isMulti
      ? (intake[q.key]?.length > 0)
      : (intake[q.key] !== null && intake[q.key] !== undefined);

    const advance = () => {
      if (!canAdvance) return;
      if (isLast) {
        // Derive room from spaces for draft compatibility
        const spaces = intake.spaces || [];
        const room = spaces.includes("Kitchen") ? "Kitchen"
          : spaces.includes("Bathrooms") ? "Bathroom"
          : spaces.includes("Basement") ? "Basement"
          : spaces.includes("Living areas") ? "Living Room"
          : spaces.includes("Bedrooms") ? "Bedroom"
          : "Other";
        const scopeItems = intakeToScope(intake);
        setDraft(d => ({
          ...d,
          room,
          budget: intake.budget || d.budget,
          scope: scopeItems,
        }));
        setView("create");
      } else {
        setIntakeStep(s => s + 1);
      }
    };

    // Single-select auto-advances
    const handleSingle = (val) => {
      const newIntake = { ...intake, [q.key]: val };
      setIntake(newIntake);
      if (isLast) {
        const spaces = newIntake.spaces || [];
        const room = spaces.includes("Kitchen") ? "Kitchen"
          : spaces.includes("Bathrooms") ? "Bathroom"
          : spaces.includes("Basement") ? "Basement"
          : spaces.includes("Living areas") ? "Living Room"
          : spaces.includes("Bedrooms") ? "Bedroom"
          : "Other";
        const scopeItems = intakeToScope(newIntake);
        setDraft(d => ({ ...d, room, budget: newIntake.budget || d.budget, scope: scopeItems }));
        setView("create");
      } else {
        setTimeout(() => setIntakeStep(s => s + 1), 200);
      }
    };

    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ flex:1, overflowY:"auto", padding:"20px 16px 48px", WebkitOverflowScrolling:"touch" }} className="fade-in">

          {/* Progress */}
          <div style={{ marginBottom:24 }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontWeight:600 }}>
                Step {intakeStep + 1} of {QUESTIONS.length}
              </span>
              {intakeStep > 0 && (
                <button onClick={() => setIntakeStep(s => s - 1)}
                  style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>
                  ← Back
                </button>
              )}
            </div><div style={{ height:4, background:C.sand, borderRadius:2, overflow:"hidden" }}><div style={{ height:"100%", background:C.terra, borderRadius:2, width:`${progress}%`, transition:"width 0.3s ease" }} /></div></div>

          {/* Question */}
          <div style={{ marginBottom:24 }}><h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:C.ink, lineHeight:1.3, marginBottom:8 }}>
              {q.q}
            </h2><p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:1.6, margin:0 }}>
              {q.sub}
            </p>
            {isMulti && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:5, marginTop:8, background:"rgba(184,92,56,0.08)", borderRadius:100, padding:"3px 10px" }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.terra, fontWeight:600 }}>Select all that apply</span></div>
            )}
          </div>

          {/* Options */}
          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            {q.options.map(opt => {
              const isSelected = isMulti
                ? (currentVal || []).includes(opt.val)
                : currentVal === opt.val;
              return (
                <button key={opt.val}
                  onClick={() => isMulti ? toggleMulti(opt.val) : handleSingle(opt.val)}
                  style={{
                    background: isSelected ? C.terra : C.white,
                    border: `2px solid ${isSelected ? C.terra : C.sand}`,
                    borderRadius:12, padding:"13px 16px",
                    display:"flex", alignItems:"center", gap:12,
                    cursor:"pointer", textAlign:"left", transition:"all 0.15s",
                    boxShadow: isSelected ? `0 4px 16px rgba(184,92,56,0.18)` : "none",
                  }}><div style={{ flex:1, minWidth:0 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, color: isSelected ? C.white : C.ink }}>
                      {opt.label}
                    </div>
                    {opt.desc && (
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color: isSelected ? "rgba(255,255,255,0.75)" : C.slate, marginTop:2, lineHeight:1.4 }}>
                        {opt.desc}
                      </div>
                    )}
                  </div>
                  {isMulti && (
                    <div style={{ width:20, height:20, borderRadius:5, border:`2px solid ${isSelected ? "rgba(255,255,255,0.6)" : C.sand}`, background: isSelected ? "rgba(255,255,255,0.25)" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {isSelected && <span style={{ color:"white", fontSize:11, fontWeight:700 }}>✓</span>}
                    </div>
                  )}
                  {!isMulti && isSelected && <span style={{ color:"white", fontSize:14, fontWeight:700, flexShrink:0 }}>✓</span>}
                </button>
              );
            })}
          </div>

          {/* Multi-select continue button */}
          {isMulti && (
            <button onClick={advance} disabled={!canAdvance}
              style={{ marginTop:20, width:"100%", background: canAdvance ? C.terra : C.sand, color:C.white, border:"none", borderRadius:10, padding:"14px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:15, cursor: canAdvance ? "pointer" : "default", transition:"background 0.2s" }}>
              Continue →
            </button>
          )}

          {/* Skip */}
          <button onClick={() => setView("create")}
            style={{ marginTop:16, width:"100%", background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer", textDecoration:"underline", textAlign:"center" }}>
            Skip — I'll fill in the scope myself
          </button></div></div>
    );
  }

  //  CREATE — Step 1: Room + Budget + Scope 
  if (view === "create") {
    // scope is now [{id, by: "contractor"|"homeowner"}]
    const getScopeEntry = (id) => draft.scope.find(s => s.id === id);
    const scopeWasPrePopulated = draft.scope.length > 0 && intake.projectType !== null;
    const isStructural = draft.scope.some(s =>
      ["s1","s6","s7","s8","s9","s10"].includes(s.id)
    );
    const hasPermitItems = draft.scope.some(s => hasPermitRisk(ALL_SCOPE_FLAT.find(i => i.id === s.id)));

    // Cycle: unchecked → contractor → homeowner → unchecked
    const cycleScope = (id) => setDraft(d => {
      const existing = d.scope.find(s => s.id === id);
      if (!existing) return { ...d, scope: [...d.scope, { id, by:"contractor", rooms: [] }] };
      if (existing.by === "contractor") return { ...d, scope: d.scope.map(s => s.id===id ? { ...s, by:"homeowner" } : s) };
      return { ...d, scope: d.scope.filter(s => s.id !== id) };
    });

    const toggleScopeRoom = (id, room) => {
      setDraft(d => ({
        ...d,
        scope: d.scope.map(s =>
          s.id === id
            ? { ...s, rooms: s.rooms?.includes(room)
                ? s.rooms.filter(r => r !== room)
                : [...(s.rooms || []), room] }
            : s
        )
      }));
    };

    const setSupplyChoice = (id, choice) => {
      setDraft(d => ({
        ...d,
        scope: d.scope.map(s => s.id === id ? { ...s, supplyChoice: choice } : s)
      }));
    };

    const contractorItems = draft.scope.filter(s => s.by === "contractor");
    const homeownerItems = draft.scope.filter(s => s.by === "homeowner");

    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ flex:1, overflowY:"auto", padding:"20px 16px 40px", maxWidth:860, margin:"0 auto", width:"100%", WebkitOverflowScrolling:"touch" }} className="fade-in">

          {/* Step indicator */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
            {[["1","Project Type"],["2","Brief & Media"],["3","Review & Post"]].map(([n,l],i) => (
              <div key={n} style={{ display:"flex", alignItems:"center", gap:6 }}><div style={{ width:24,height:24,borderRadius:"50%",background:i===0?C.terra:C.sand,color:i===0?C.white:C.slate,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:11,flexShrink:0 }}>{n}</div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:i===0?C.ink:C.slate,fontWeight:i===0?600:400 }}>{l}</span>
                {i<2 && <span style={{ color:C.sand,fontSize:12 }}>›</span>}
              </div>
            ))}
          </div>

          {/* Room type */}
          <div style={{ marginBottom:20 }}><label style={labelSt}>Which room?</label><div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {ROOMS.map(r => <button key={r} onClick={() => setDraft(d=>({...d,room:r}))} style={{ ...chipSt, background:draft.room===r?C.terra:C.white, color:draft.room===r?C.white:C.slate, borderColor:draft.room===r?C.terra:C.sand }}>{r}</button>)}
            </div></div>

          {/* Budget */}
          <div style={{ marginBottom:20 }}><label style={labelSt}>Budget range</label><div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {BUDGETS.map(b => <button key={b} onClick={() => setDraft(d=>({...d,budget:b}))} style={{ ...chipSt, background:draft.budget===b?C.slate:C.white, color:draft.budget===b?C.white:C.slate, borderColor:draft.budget===b?C.slate:C.sand }}>{b}</button>)}
            </div></div>

          {/* Scope checklist — FIRST so it determines what brief is needed */}
          <div style={{ marginBottom:20 }}><label style={labelSt}>
              Scope of Work
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, fontWeight:400, textTransform:"none", letterSpacing:0, marginLeft:6 }}>
                — tap once for contractor, twice for DIY
              </span></label>

            {/* Pre-populated banner */}
            {scopeWasPrePopulated ? (
              <div style={{ background:"rgba(39,174,96,0.08)", border:`1px solid rgba(39,174,96,0.25)`, borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:10 }}>
                <div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.sage, marginBottom:2 }}>
                    Pre-filled from your answers — review each item and uncheck anything that doesn't apply.
                  </div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.5 }}>
                    <strong>1st click</strong> = Contractor handles it &nbsp;·&nbsp; <strong>2nd click</strong> = You DIY it (saves on labor) &nbsp;·&nbsp; <strong>3rd click</strong> = remove it
                  </div>
                  {intake.projectType !== "chat" && (
                    <button onClick={() => { setIntakeStep(0); setView("intake"); }}
                      style={{ marginTop:6, background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, cursor:"pointer", padding:0, textDecoration:"underline" }}>
                      ← Redo guided questions
                    </button>
                  )}
                  {intake.projectType === "chat" && (
                    <button onClick={() => { setChatInput(""); setChatTyping(false); setView("chat-intake"); }}
                      style={{ marginTop:6, background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, cursor:"pointer", padding:0, textDecoration:"underline" }}>
                      ← Back to chat
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ background:"rgba(196,149,106,0.06)", border:`1px solid rgba(196,149,106,0.2)`, borderRadius:10, padding:"10px 14px", marginBottom:12, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.6 }}>
                Check each item that applies. <strong>First tap = contractor handles it.</strong> Second tap = you'll handle it yourself (saves money).
              </div>
            )}

            {FULL_SCOPE_CATALOG.map(group => {
              const isExpanded = expandedCategories.has(group.category);
              const selectedInGroup = group.items.filter(item => draft.scope.find(s => s.id === item.id)).length;
              return (
                <div key={group.category} style={{ marginBottom:8 }}>
                  {/* Category header */}
                  <div onClick={() => setExpandedCategories(prev => {
                    const next = new Set(prev);
                    isExpanded ? next.delete(group.category) : next.add(group.category);
                    return next;
                  })} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:C.white, borderRadius: isExpanded ? "10px 10px 0 0" : 10, border:`1px solid ${selectedInGroup > 0 ? C.terra : C.sand}`, cursor:"pointer", userSelect:"none" }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>{group.category}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      {selectedInGroup > 0 && (
                        <span style={{ background:C.terra, color:C.white, borderRadius:100, padding:"2px 8px", fontSize:10, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>{selectedInGroup} selected</span>
                      )}
                      <span style={{ color:C.slate, fontSize:14, transition:"transform 0.15s", display:"inline-block", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
                    </div>
                  </div>

                  {/* Items */}
                  {isExpanded && (
                    <div style={{ background:C.white, borderRadius:"0 0 10px 10px", border:`1px solid ${C.sand}`, borderTop:"none", overflow:"hidden" }}>
                      {group.items.map((item, idx) => {
                        const entry = getScopeEntry(item.id);
                        const isContractor = entry?.by === "contractor";
                        const isHomeowner = entry?.by === "homeowner";
                        const isChecked = !!entry;
                        const bgColor = isContractor ? "rgba(184,92,56,0.05)" : isHomeowner ? "rgba(255,243,205,0.4)" : "transparent";
                        const borderColor = isContractor ? C.terra : isHomeowner ? "#856404" : C.sand;
                        const permitRisk = hasPermitRisk(item);
                        return (
                          <div key={item.id} style={{ borderBottom: idx < group.items.length-1 ? `1px solid ${C.sand}` : "none" }}>
                            <div onClick={() => cycleScope(item.id)}
                              style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"12px 14px", background:bgColor, cursor:"pointer", transition:"background 0.15s", borderLeft: isChecked ? `3px solid ${borderColor}` : "3px solid transparent" }}>

                              {/* Checkbox */}
                              <div style={{ width:22, height:22, borderRadius:5, flexShrink:0, marginTop:1,
                                border: isChecked ? `2px solid ${borderColor}` : `2px solid ${C.sand}`,
                                background: isContractor ? C.terra : isHomeowner ? "#856404" : "transparent",
                                display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                                {isChecked && <span style={{ color:"white", fontSize:11, fontWeight:700, lineHeight:1 }}>✓</span>}
                              </div>

                              <div style={{ flex:1, minWidth:0 }}>
                                {/* Label row */}
                                <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:2 }}>
                                  <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:isChecked?600:400, color:isChecked?C.ink:C.slate }}>{item.label}</span>
                                  {isContractor && <span style={{ background:"rgba(184,92,56,0.08)", color:C.terra, borderRadius:100, padding:"1px 8px", fontSize:10, fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>Contractor</span>}
                                  {isHomeowner && <span style={{ background:"rgba(133,100,4,0.1)", color:"#856404", borderRadius:100, padding:"1px 8px", fontSize:10, fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>DIY</span>}
                                  {permitRisk && (
                                    <span style={{ background:"#FFF3CD", color:"#856404", border:"1px solid #FFEAA7", borderRadius:100, padding:"1px 6px", fontSize:9, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>
                                      {item.permit_risk === "likely" ? "Permit likely" : "Permit possible"}
                                    </span>
                                  )}
                                  {item.contractor_only && (
                                    <span style={{ background:"rgba(184,92,56,0.06)", color:C.clay, border:`1px solid rgba(184,92,56,0.2)`, borderRadius:100, padding:"1px 6px", fontSize:9, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Contractor only</span>
                                  )}
                                </div>
                                {/* Unit label */}
                                {item.unit_label && (
                                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.clay, marginBottom:1 }}>{item.unit_label}</div>
                                )}
                                {/* Notes */}
                                {item.notes && (
                                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:"#aaa", lineHeight:1.4 }}>{item.notes}</div>
                                )}
                              </div>
                            </div>

                            {/* Room tag pills — only when selected */}
                            {isChecked && (
                              <div onClick={e => e.stopPropagation()} style={{ display:"flex", flexWrap:"wrap", gap:5, padding:"0 14px 6px 46px" }}>
                                {ROOMS.map(room => {
                                  const active = entry.rooms?.includes(room);
                                  return (
                                    <button key={room} onClick={() => toggleScopeRoom(item.id, room)}
                                      style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:active?700:400, color:active?C.white:C.slate, background:active?C.terra:C.cream, border:`1px solid ${active?C.terra:C.sand}`, borderRadius:100, padding:"2px 9px", cursor:"pointer", transition:"all 0.15s" }}>
                                      {room}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Supply method — toggle for "either", badge for fixed */}
                            {isChecked && item.supply_model === "either" && (
                              <div onClick={e => e.stopPropagation()} style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:6, padding:"0 14px 10px 46px" }}>
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, fontWeight:600 }}>Who supplies?</span>
                                {[["contractor_supplied","Contractor supplies"],["owner_supplied","I'll supply"]].map(([val, label]) => {
                                  const active = entry.supplyChoice === val;
                                  const activeColor = val === "owner_supplied" ? "#856404" : C.terra;
                                  return (
                                    <button key={val} onClick={() => setSupplyChoice(item.id, val)}
                                      style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:active?700:400, color:active?C.white:C.slate, background:active?activeColor:C.cream, border:`1px solid ${active?activeColor:C.sand}`, borderRadius:100, padding:"2px 9px", cursor:"pointer", transition:"all 0.15s" }}>
                                      {label}
                                    </button>
                                  );
                                })}
                                {!entry.supplyChoice && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.clay, fontStyle:"italic" }}>choose one</span>}
                              </div>
                            )}
                            {isChecked && item.supply_model === "owner_supplied" && (
                              <div style={{ padding:"0 14px 10px 46px" }}>
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:"#856404", background:"rgba(133,100,4,0.07)", borderRadius:100, padding:"2px 9px", border:"1px solid rgba(133,100,4,0.2)" }}>
                                  You supply this item — contractor installs only
                                </span>
                              </div>
                            )}
                            {isChecked && item.supply_model === "contractor_supplied" && (
                              <div style={{ padding:"0 14px 10px 46px" }}>
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.clay, background:"rgba(196,149,106,0.07)", borderRadius:100, padding:"2px 9px", border:"1px solid rgba(196,149,106,0.2)" }}>
                                  Contractor supplies &amp; installs
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Work split summary */}
            {draft.scope.length > 0 && (
              <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.sand}`, overflow:"hidden", marginTop:4 }}>
                {contractorItems.length > 0 && (
                  <div style={{ padding:"12px 14px", borderBottom: homeownerItems.length > 0 ? `1px solid ${C.sand}` : "none" }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.terra, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>
                       Contractor scope ({contractorItems.length} items)
                    </div><div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                      {contractorItems.map(s => {
                        const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
                        return item ? <span key={s.id} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, background:C.cream, borderRadius:100, padding:"3px 9px", border:`1px solid ${C.sand}` }}>{item.label}</span> : null;
                      })}
                    </div></div>
                )}
                {homeownerItems.length > 0 && (
                  <div style={{ padding:"12px 14px" }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.terra, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>
                       I'll do myself ({homeownerItems.length} items — not in contractor bid)
                    </div><div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                      {homeownerItems.map(s => {
                        const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
                        return item ? <span key={s.id} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, background:"#FFF4EE", borderRadius:100, padding:"3px 9px", border:`1px solid rgba(184,92,56,0.25)` }}>{item.label}</span> : null;
                      })}
                    </div></div>
                )}
              </div>
            )}
          </div>

          {/* Dynamic brief type indicator */}
          <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${isStructural ? C.terra : C.sand}`, marginBottom:16 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:8 }}>
               Brief type based on your scope:
            </div><div style={{ display:"flex", flexDirection:"column", gap:8 }}><div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:C.sage,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0 }}></div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.ink }}><strong>Room photo</strong> — visual of current state</span></div><div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:C.sage,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0 }}></div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.ink }}><strong>Material selections</strong> — finishes, fixtures, appliances</span></div>
              {isStructural && (
                <div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:C.terra,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0 }}>!</div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.terra,fontWeight:600 }}><strong>Floor plan required</strong> — structural/layout changes need a sketch or scan</span></div>
              )}
              {hasPermitItems && (
                <div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:"#856404",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0,color:"white" }}></div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#856404" }}>Permits likely required — contractors will factor in timeline</span></div>
              )}
              {homeownerItems.length > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:C.terra,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0 }}></div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.terra }}><strong>{homeownerItems.length} DIY item{homeownerItems.length!==1?"s":""}</strong> — excluded from contractor bids</span></div>
              )}
            </div></div>

          {/* Room dimensions */}
          <div style={{ marginBottom:20 }}><label style={labelSt}>Room dimensions</label><div style={{ background:C.white, borderRadius:12, border:`1.5px solid ${C.sand}`, padding:"14px 16px" }}><div style={{ display:"flex", gap:10, marginBottom:10 }}>
                {[["Width","roomW"],["Length","roomL"],["Ceiling","roomH"]].map(([label,key]) => (
                  <div key={key} style={{ flex:1 }}><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:C.slate,marginBottom:4,fontWeight:600 }}>{label}</div><div style={{ display:"flex",alignItems:"center",background:C.cream,borderRadius:8,border:`1.5px solid ${C.sand}`,overflow:"hidden" }}><input type="number" min="4" max="60" value={draft[key]}
                        onChange={e=>setDraft(d=>({...d,[key]:Math.max(4,Math.min(60,parseInt(e.target.value)||0))}))}
                        style={{ flex:1,border:"none",background:"transparent",padding:"8px 6px",fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:700,color:C.ink,textAlign:"center",outline:"none" }} /><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:C.slate,paddingRight:7 }}>ft</span></div></div>
                ))}
              </div><div style={{ background:"rgba(196,149,106,0.08)",borderRadius:8,border:`1px dashed ${C.clay}`,height:44,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden" }}><div style={{ width:`${Math.min(88,(draft.roomW/30)*88+10)}%`,height:`${Math.min(80,(draft.roomL/30)*80+10)}%`,background:"rgba(196,149,106,0.15)",border:`2px solid ${C.clay}`,borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:9,color:C.clay,fontWeight:600 }}>{draft.roomW}′×{draft.roomL}′×{draft.roomH}′ · {(draft.roomW*draft.roomL).toFixed(0)} sqft</span></div></div></div></div><button onClick={() => setView("media")} style={{ width:"100%",background:C.terra,color:C.white,border:"none",borderRadius:10,padding:"15px 0",fontFamily:"'DM Sans',sans-serif",fontSize:15,fontWeight:700,cursor:"pointer" }}>
            {isStructural ? "Next: Upload Photo & Floor Plan →" : "Next: Upload Photo & Add Materials →"}
          </button></div></div>
    );
  }

  //  MEDIA — Step 2: Photo (+ floor plan if structural) 
  if (view === "media") {
    const isStructural = draft.scope.some(s => ["s1","s6","s7","s8","s9","s10"].includes(s.id));
    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ maxWidth:680, margin:"0 auto", padding:"20px 16px 40px", width:"100%" }} className="fade-in"><div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
            {[["1","Project Type"],["2","Brief & Media"],["3","Review & Post"]].map(([n,l],i) => (
              <div key={n} style={{ display:"flex", alignItems:"center", gap:6 }}><div style={{ width:24,height:24,borderRadius:"50%",background:i===1?C.terra:i<1?C.sage:C.sand,color:i<=1?C.white:C.slate,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:11,flexShrink:0 }}>{i<1?"":n}</div><span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:i===1?C.ink:C.slate,fontWeight:i===1?600:400 }}>{l}</span>
                {i<2 && <span style={{ color:C.sand,fontSize:12 }}>›</span>}
              </div>
            ))}
          </div><button onClick={() => setView("create")} style={{ background:"none",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,cursor:"pointer",marginBottom:16,padding:0 }}>← Back</button>

          {/* Photo upload */}
          <label style={labelSt}>Current state photo</label><div onClick={() => fileRef.current.click()} style={{ border:`2px dashed ${photo?C.sage:C.clay}`,borderRadius:12,minHeight:160,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative",overflow:"hidden",background:photo?C.ink:C.white,marginBottom:16 }}>
            {photo && <img src={photo} alt="preview" style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} />}
            {photo ? (
              <><div style={{ position:"absolute",inset:0,background:"rgba(28,35,51,0.45)",zIndex:1 }}/><div style={{ position:"relative",zIndex:2,color:C.white,textAlign:"center",fontFamily:"'DM Sans',sans-serif" }}><div style={{ fontSize:28 }}></div><div style={{ fontWeight:500,fontSize:14 }}>Photo ready · Tap to change</div></div></>
            ) : (
              <><div style={{ fontSize:32,marginBottom:6 }}></div><div style={{ fontFamily:"'DM Sans',sans-serif",color:C.clay,fontWeight:500,fontSize:14 }}>Tap to upload your room photo</div></>
            )}
          </div><input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e => { const f=e.target.files[0]; if(!f) return; const r=new FileReader(); r.onload=ev=>setPhoto(ev.target.result); r.readAsDataURL(f); }} />

          {/* Floor plan section — only shown if structural scope selected */}
          {isStructural && (
            <div style={{ marginBottom:16 }}><label style={labelSt}>
                Floor plan / layout sketch
                <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:C.terra,fontWeight:600,textTransform:"none",marginLeft:6 }}>Required for structural changes</span></label><div style={{ background:"rgba(184,92,56,0.06)",border:`1.5px dashed ${C.terra}`,borderRadius:12,padding:"18px 16px",marginBottom:10 }}><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.ink,fontWeight:600,marginBottom:8 }}> How to provide your floor plan:</div>
                {[
                  ["Scan with Magicplan","Point your iPhone camera at each corner of the room — the app auto-generates a dimensioned floor plan you can export as an image. Free for up to 2 projects.","→ magicplan.app"],
                  ["Sketch & photograph","Draw your current layout on paper, mark proposed changes in red, then photograph it",""],
                  ["Upload existing plans","If you have builder drawings or previous architectural plans",""],
                ].map(([title,desc,link]) => (
                  <div key={title} style={{ display:"flex",gap:10,marginBottom:10,paddingBottom:10,borderBottom:`1px solid rgba(196,149,106,0.2)` }}><div style={{ width:6,height:6,borderRadius:"50%",background:C.clay,flexShrink:0,marginTop:6 }} /><div><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,color:C.ink }}>
                        {title} {link && <a href="https://magicplan.app" target="_blank" rel="noreferrer" style={{ color:C.terra,fontSize:11,textDecoration:"none" }} onClick={e=>e.stopPropagation()}>{link}</a>}
                      </div><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:C.slate,marginTop:2 }}>{desc}</div></div></div>
                ))}

                {/* Working upload zone */}
                <div onClick={() => floorPlanRef.current.click()}
                  style={{ background:draft.floorPlan ? C.ink : C.white, borderRadius:8, border:`2px dashed ${draft.floorPlan ? C.sage : C.sand}`, minHeight:90, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", marginTop:4, position:"relative", overflow:"hidden" }}>
                  {draft.floorPlan ? (
                    <><img src={draft.floorPlan} alt="floor plan" style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",background:C.ink }} /><div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1 }}><div style={{ textAlign:"center",color:C.white,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600 }}>
                           Floor plan uploaded · Tap to replace
                        </div></div></>
                  ) : (
                    <div style={{ textAlign:"center",fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.clay }}><div style={{ fontSize:24,marginBottom:4 }}></div>
                      Tap to upload floor plan image
                      <div style={{ fontSize:11,color:C.slate,marginTop:2 }}>JPG, PNG, or PDF screenshot</div></div>
                  )}
                </div><input ref={floorPlanRef} type="file" accept="image/*,.pdf" style={{ display:"none" }}
                  onChange={e => { const f=e.target.files[0]; if(!f) return; const r=new FileReader(); r.onload=ev=>setDraft(d=>({...d,floorPlan:ev.target.result})); r.readAsDataURL(f); }} />

                {!draft.floorPlan && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:C.slate,marginTop:8,textAlign:"center",lineHeight:1.5 }}>
                    Optional but strongly recommended — contractors bid more accurately with a layout sketch
                  </div>
                )}
              </div></div>
          )}

          <button onClick={() => setView("design")} disabled={!photo} style={{ width:"100%",background:photo?C.terra:C.sand,color:photo?C.white:C.slate,border:"none",borderRadius:10,padding:"15px 0",fontFamily:"'DM Sans',sans-serif",fontSize:15,fontWeight:700,transition:"all 0.2s",cursor:photo?"pointer":"not-allowed" }}>
            {isStructural ? "Continue to Materials →" : "Continue to Design Canvas →"}
          </button></div></div>
    );
  }

  //  DESIGN — AI-powered rendering
  if (view === "design") {
    // Per-item catalog product → matching scope item IDs
    const CATALOG_ITEM_TO_SCOPE = {
      // Appliances — specific product to its own scope item only
      a1: ["fix_006"],   // 5-Burner Gas Range → Range/oven install
      a2: ["fix_007"],   // French Door Refrigerator → Refrigerator install
      a3: ["plumb_009"], // Top Control Dishwasher → Dishwasher installation
      a4: ["fix_008"],   // Wall Mount Range Hood → Range hood install
      a5: ["fix_006"],   // 30" Gas Double Oven → Range/oven install
      a6: [],            // Wine Fridge → no matching scope item
      // Cabinets
      c1: ["carp_001","carp_002","carp_003","carp_004"],
      c2: ["carp_001","carp_002","carp_003","carp_004"],
      c3: ["carp_001","carp_002","carp_003","carp_004"],
      c4: ["carp_001","carp_002","carp_003","carp_004"],
      c5: ["carp_001","carp_002","carp_003","carp_004"],
      c6: ["carp_001","carp_002","carp_003","carp_004"],
      // Flooring
      f1: ["floor_001","floor_003","floor_004"],
      f2: ["floor_001","floor_003","floor_004"],
      f3: ["floor_001","floor_003","floor_004"],
      f4: ["floor_001","floor_003","floor_004"],
      f5: ["floor_001","floor_003","floor_004"],
      f6: ["floor_001","floor_003","floor_004"],
      // Tile & Walls
      w1: ["tile_002","tile_004","tile_001"],
      w2: ["tile_002","tile_004","tile_001"],
      w3: ["tile_002","tile_004","tile_001"],
      w4: ["tile_002","tile_004","tile_001"],
      w5: ["tile_002","tile_004","tile_001"],
      w6: ["tile_002","tile_004","tile_001"],
      // Fixtures
      x1: ["fix_001","fix_002"],
      x2: ["elec_006"],
      x3: [],
      x4: [],
      x5: [],
      x6: ["fix_003","fix_004"],
      // Countertops
      t1: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      t2: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      t3: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      t4: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      t5: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      t6: ["ctr_001","ctr_002","ctr_003","ctr_004","ctr_005"],
      // Lighting
      l1: ["elec_006","elec_005"],
      l2: ["elec_006","elec_005"],
      l3: ["elec_006","elec_005"],
      l4: ["elec_004"],
      l5: ["elec_005"],
      l6: ["elec_006"],
      // Paint
      p1: ["paint_001","paint_002","paint_003"],
      p2: ["paint_001","paint_002","paint_003"],
      p3: ["paint_001","paint_002","paint_003"],
      p4: ["paint_001","paint_002","paint_003"],
      p5: ["paint_001","paint_002","paint_003"],
      p6: ["paint_001","paint_002","paint_003"],
    };
    const tapToPlace = (item) => {
      const linkedScopeIds = CATALOG_ITEM_TO_SCOPE[item.id] || [];
      if (draft.items.some(i => i.id === item.id)) {
        // Remove from canvas AND clear selectedProduct on linked scope entries
        setDraft(d => ({
          ...d,
          items: d.items.filter(i => i.id !== item.id),
          scope: d.scope.map(s =>
            linkedScopeIds.includes(s.id) && s.selectedProduct?.id === item.id
              ? { ...s, selectedProduct: null }
              : s
          ),
        }));
      } else {
        // Add to canvas AND auto-link to matching scope entries that don't yet have a product
        setDraft(d => ({
          ...d,
          items: [...d.items, { ...item, uid: Date.now() }],
          scope: d.scope.map(s =>
            linkedScopeIds.includes(s.id) && !s.selectedProduct
              ? { ...s, selectedProduct: item }
              : s
          ),
        }));
      }
    };

    const handleGenerateRender = async () => {
      setRendering(true);
      setRenderError(null);
      try {
        const url = await generateDALLERender(photo, draft.items, draft.room || "kitchen", draft.roomW, draft.roomL);
        if (url) setDraft(d => ({ ...d, renderedPhoto: url }));
        else setRenderError("No image returned. Try again.");
      } catch (err) {
        if (err.message === "NO_KEY") {
          setRenderError("Add your OpenAI API key to .env as REACT_APP_OPENAI_API_KEY to enable AI renders.");
        } else {
          setRenderError(err.message || "Render failed. Please try again.");
        }
      } finally {
        setRendering(false);
      }
    };

    return (
      <div style={{ height:"100vh", background:C.cream, display:"flex", flexDirection:"column", overflow:"hidden" }}><GlobalStyles /><style>{`
          .cat-pill { border:none; border-radius:20px; padding:7px 14px; font-family:'DM Sans',sans-serif; font-size:13px; cursor:pointer; transition:all 0.15s; white-space:nowrap; font-weight:500; }
          .mat-chip { display:flex; align-items:center; gap:8px; border-radius:10px; padding:10px 12px; cursor:pointer; background:white; border:1.5px solid #E8DFD0; transition:all 0.15s; user-select:none; -webkit-user-select:none; }
          .mat-chip:active { transform:scale(0.96); }
          .render-btn { width:100%; border:none; border-radius:10px; padding:15px 0; font-family:'DM Sans',sans-serif; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.2s; }
          .render-btn:disabled { opacity:0.5; cursor:not-allowed; }
          @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
          .shimmer { background: linear-gradient(90deg, #E8DFD0 25%, #F5F0E8 50%, #E8DFD0 75%); background-size:200% 100%; animation: shimmer 1.5s infinite; }
        `}</style>

        {/* Nav */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.sand}`, padding:"0 16px", height:54, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, gap:8 }}>
          <button onClick={() => setView("create")} style={{ background:"none", border:"none", fontSize:13, color:C.slate, fontFamily:"'DM Sans',sans-serif", padding:"8px 0", flexShrink:0 }}>← Back</button>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:C.ink, flexShrink:0 }}>Reno<span style={{ color:C.terra }}>Bid</span></div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button onClick={handleGenerateRender} disabled={draft.items.length === 0 || rendering}
              style={{ background: draft.items.length > 0 && !rendering ? C.slate : C.sand, color: draft.items.length > 0 && !rendering ? C.white : C.slate, border:"none", borderRadius:8, padding:"7px 12px", fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:12, cursor: draft.items.length > 0 && !rendering ? "pointer" : "not-allowed", whiteSpace:"nowrap" }}>
              {rendering ? "Rendering…" : "✨ AI Render"}
            </button>
            <button
              onClick={() => {
                const unresolved = draft.scope.filter(s => ALL_SCOPE_FLAT.find(i => i.id === s.id)?.supply_model === "either" && !s.supplyChoice);
                if (draft.items.length > 0 && unresolved.length === 0) setView("post");
              }}
              disabled={draft.items.length === 0 || draft.scope.some(s => ALL_SCOPE_FLAT.find(i => i.id === s.id)?.supply_model === "either" && !s.supplyChoice)}
              style={{ background: (draft.items.length > 0 && !draft.scope.some(s => ALL_SCOPE_FLAT.find(i => i.id === s.id)?.supply_model === "either" && !s.supplyChoice)) ? C.terra : C.sand, color: (draft.items.length > 0 && !draft.scope.some(s => ALL_SCOPE_FLAT.find(i => i.id === s.id)?.supply_model === "either" && !s.supplyChoice)) ? C.white : C.slate, border:"none", borderRadius:8, padding:"7px 12px", fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:12 }}>
              Post →
            </button>
          </div>
        </div>

        {renderError && (
          <div style={{ background:"#FFF3CD", borderBottom:`1px solid ${C.gold}44`, padding:"8px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"#856404", flexShrink:0 }}>
            {renderError}
          </div>
        )}

        {/* Photo / Render area */}
        <DesignRenderer photo={photo} renderedPhoto={draft.renderedPhoto} items={draft.items} roomW={draft.roomW} roomL={draft.roomL} roomH={draft.roomH} />

        {/* Category tabs */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.sand}`, padding:"10px 16px", display:"flex", gap:8, overflowX:"auto", flexShrink:0, WebkitOverflowScrolling:"touch" }}>
          {Object.keys(CATALOG).map(cat => (
            <button key={cat} className="cat-pill" onClick={() => setActiveCategory(cat)}
              style={{ background: activeCategory===cat ? C.terra : C.cream, color: activeCategory===cat ? C.white : C.slate }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Material grid */}
        <div style={{ flex:1, overflowY:"auto", padding:"12px 16px 24px", WebkitOverflowScrolling:"touch" }}><div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {CATALOG[activeCategory].map(item => {
              const isAdded = draft.items.some(i => i.id === item.id);
              const priceDisplay = item.priceUnit
                ? `$${item.price}${item.priceUnit}`
                : `$${item.price.toLocaleString()}`;
              return (
                <div key={item.id} style={{
                  borderRadius:12, overflow:"hidden", background:C.white,
                  border: isAdded ? `2px solid ${C.terra}` : `1.5px solid ${C.sand}`,
                  boxShadow: isAdded ? `0 2px 12px rgba(184,92,56,0.15)` : "0 1px 4px rgba(0,0,0,0.06)",
                  transition:"all 0.15s",
                }}>
                  {/* Product illustration */}
                  <div style={{ position:"relative", height:110, overflow:"hidden", cursor:"pointer",
                    background:`linear-gradient(135deg, ${item.color} 0%, ${item.colorB||item.color} 100%)` }}
                    onClick={() => tapToPlace(item)}>
                    {/* Texture overlay for flooring/tiles */}
                    {["f1","f2","f3","f4","f5","f6"].includes(item.id) && (
                      <div style={{ position:"absolute", inset:0, opacity:0.18,
                        backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 18px,rgba(255,255,255,0.6) 18px,rgba(255,255,255,0.6) 20px),repeating-linear-gradient(90deg,transparent,transparent 28px,rgba(255,255,255,0.6) 28px,rgba(255,255,255,0.6) 30px)" }} />
                    )}
                    {["w1","w2","w3","w4","w5","w6"].includes(item.id) && (
                      <div style={{ position:"absolute", inset:0, opacity:0.2,
                        backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 22px,rgba(255,255,255,0.8) 22px,rgba(255,255,255,0.8) 24px)" }} />
                    )}
                    {["c1","c2","c3","c4","c5","c6"].includes(item.id) && (
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", gap:4, padding:"10px 8px" }}>
                        {[0,1,2].map(i=>(
                          <div key={i} style={{ flex:1, height:"80%", borderRadius:4, background:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.35)", display:"flex", flexDirection:"column", justifyContent:"flex-end", alignItems:"center", paddingBottom:4 }}>
                            <div style={{ width:"40%", height:2, background:"rgba(255,255,255,0.5)", borderRadius:1, marginBottom:3 }} />
                            <div style={{ width:6, height:6, borderRadius:"50%", background:"rgba(255,255,255,0.6)" }} />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Emoji icon */}
                    <div style={{ position:"absolute", bottom:8, right:10, fontSize:28, opacity:0.75, lineHeight:1 }}>
                      {item.emoji}
                    </div>
                    {/* Label pill */}
                    <div style={{ position:"absolute", top:8, left:8, background:"rgba(0,0,0,0.28)", borderRadius:6, padding:"2px 7px", color:"#fff", fontSize:9, fontWeight:600, letterSpacing:0.4, textTransform:"uppercase", backdropFilter:"blur(4px)" }}>
                      {Object.keys(CATALOG).find(cat=>CATALOG[cat].some(p=>p.id===item.id))}
                    </div>
                    {isAdded && (
                      <div style={{ position:"absolute", inset:0, background:"rgba(184,92,56,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <div style={{ background:C.terra, borderRadius:"50%", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:18, fontWeight:700 }}>✓</div>
                      </div>
                    )}
                  </div>
                  {/* Product info */}
                  <div style={{ padding:"8px 10px 10px" }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5, marginBottom:2 }}>
                      {item.brand}
                    </div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink, lineHeight:1.3, marginBottom:4 }}>
                      {item.label}
                    </div><div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:700, color:C.terra }}>
                        {priceDisplay}
                      </div><a
                        href={item.url} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ background:C.slate, color:C.white, borderRadius:6, padding:"3px 9px", fontSize:10, fontFamily:"'DM Sans',sans-serif", fontWeight:700, textDecoration:"none", flexShrink:0 }}>
                        Buy →
                      </a></div><button onClick={() => tapToPlace(item)} style={{
                      width:"100%", marginTop:7, border:`1.5px solid ${isAdded ? C.terra : C.sand}`,
                      borderRadius:7, padding:"6px 0", fontFamily:"'DM Sans',sans-serif", fontSize:11,
                      fontWeight:700, cursor:"pointer", background: isAdded ? "#FFF4EE" : C.cream,
                      color: isAdded ? C.terra : C.slate, transition:"all 0.15s",
                    }}>
                      {isAdded ? " Added to Design" : "+ Add to Design"}
                    </button></div></div>
              );
            })}
          </div>

          {draft.items.length > 0 && (
            <div style={{ marginTop:14, background:C.white, borderRadius:12, padding:14, border:`1px solid ${C.sand}` }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.5 }}>Your selections</div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:700, color:C.terra }}>
                  ~${draft.items.reduce((sum, item) => sum + (item.price || 0), 0).toLocaleString()} est.
                </div></div><div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {draft.items.map(item => (
                  <div key={item.uid} onClick={() => tapToPlace(item)}
                    style={{ display:"flex", alignItems:"center", gap:5, background:item.color+"22", border:`1px solid ${item.color}66`, borderRadius:100, padding:"4px 10px", cursor:"pointer" }}><span style={{ fontSize:12 }}>{item.emoji}</span><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, color:C.ink }}>{item.label}</span><span style={{ fontSize:10, color:C.terra, fontWeight:600, fontFamily:"'DM Sans',sans-serif" }}>
                      {item.priceUnit ? `$${item.price}${item.priceUnit}` : `$${item.price?.toLocaleString()}`}
                    </span></div>
                ))}
              </div></div>
          )}

          {(() => {
            const unresolvedSupply = draft.scope.filter(s => {
              const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
              return item?.supply_model === "either" && !s.supplyChoice;
            });
            const canProceed = draft.items.length > 0 && unresolvedSupply.length === 0;
            return (
              <>
                <button onClick={() => canProceed && setView("post")} disabled={!canProceed}
                  className="render-btn"
                  style={{ marginTop:14, background: canProceed ? C.terra : C.sand, color: canProceed ? C.white : C.slate }}>
                  Review & Post →
                </button>
                {unresolvedSupply.length > 0 && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.clay, marginTop:6, textAlign:"center" }}>
                    {unresolvedSupply.length} item{unresolvedSupply.length > 1 ? "s" : ""} need a supply decision — choose "Contractor supplies" or "I'll supply" above
                  </div>
                )}
              </>
            );
          })()}</div></div>
    );
  }

  //  POST 
  if (view === "post") return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ maxWidth:720, margin:"0 auto", padding:"36px 24px", width:"100%" }} className="fade-in"><div style={{ marginBottom:24 }}><button onClick={() => setView("create")} style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, cursor:"pointer", padding:0, display:"inline-flex", alignItems:"center", gap:4, marginBottom:10 }}>← Back to revise</button><h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:C.ink, margin:0 }}>Review & Post Project</h2></div><div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}><div><label style={labelSt}>Project Location</label><CityAutocomplete value={draft.location} onChange={(result, text) => setDraft(d => ({ ...d, location: text, lat: result?.lat || null, lng: result?.lng || null }))} placeholder="City, State" inputStyle={inputSt} /><label style={labelSt}>Project Title</label><input value={draft.title} onChange={e=>setDraft(d=>({...d,title:e.target.value}))} placeholder={`My ${draft.room} Renovation`} style={inputSt} /><label style={labelSt}>Describe what you want</label><textarea value={draft.description} onChange={e=>setDraft(d=>({...d,description:e.target.value}))} placeholder="Describe the scope of work, style goals, special requirements..." rows={4} style={{...inputSt,resize:"vertical"}} /><label style={labelSt}>Desired Start Date</label><div style={{ background:"rgba(196,149,106,0.06)", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"10px 14px", marginBottom:18 }}><input type="date" value={draft.desiredStart} onChange={e=>setDraft(d=>({...d,desiredStart:e.target.value}))}
                min={new Date().toISOString().split("T")[0]}
                style={{ width:"100%", border:"none", background:"transparent", fontFamily:"'DM Sans',sans-serif", fontSize:15, color:C.ink, outline:"none" }} /><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:5, lineHeight:1.5 }}>
                Contractors will only bid if their earliest available start aligns with your window. This helps filter out contractors who are already overbooked.
              </div></div><div style={{ background:C.white, borderRadius:10, padding:16, border:`1px solid ${C.sand}` }}>
              {[
                ["Room", draft.room],
                ["Budget", draft.budget],
                ["Desired start", draft.desiredStart ? new Date(draft.desiredStart + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "Flexible"],
                ["Materials", `${draft.items.length} selected`],
                ["Scope", draft.scope.length > 0 ? `${draft.scope.filter(s=>s.by==="contractor").length} contractor, ${draft.scope.filter(s=>s.by==="homeowner").length} DIY` : "Cosmetic only"],
              ].map(([k,v])=>(
                <div key={k} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.sand}`,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}><span style={{ color:C.slate }}>{k}</span><span style={{ color: k==="Scope" && draft.scope.length > 0 ? C.terra : k==="Desired start" ? C.terra : C.ink, fontWeight:600 }}>{v}</span></div>
              ))}
              {draft.scope.some(s => hasPermitRisk(ALL_SCOPE_FLAT.find(i => i.id === s.id))) && (
                <div style={{ marginTop:8, background:"#FFF3CD", borderRadius:6, padding:"6px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"#856404", fontWeight:600 }}>
                   Permits likely required — contractors will advise
                </div>
              )}

              {/* Supply breakdown — contractor-supplied vs owner-supplied */}
              {draft.scope.length > 0 && (() => {
                // Strip installation language — show just the material/appliance name
                const supplyLabel = (label) => label
                  ? label
                      .replace(/\s*[—–-]\s*(supply and install|supply & install|fabricate and install|install|installation)\s*$/i, "")
                      .replace(/\s+installation\s*$/i, "")
                      .trim()
                  : label;
                const contractorSupplied = draft.scope.filter(s => {
                  const item = lookupScopeItem(s.id);
                  const eff = s.supplyChoice || item?.supply_model;
                  return eff !== "owner_supplied";
                });
                const ownerSupplied = draft.scope.filter(s => {
                  const item = lookupScopeItem(s.id);
                  const eff = s.supplyChoice || item?.supply_model;
                  return eff === "owner_supplied";
                });
                const ownerMaterialsCost = ownerSupplied.reduce((sum, s) => s.selectedProduct?.price ? sum + s.selectedProduct.price : sum, 0);
                if (ownerSupplied.length === 0) return null;
                return (
                  <div style={{ marginTop:10, borderTop:`1px solid ${C.sand}`, paddingTop:10 }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:C.terra, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Supply Breakdown</div>
                    {contractorSupplied.length > 0 && (
                      <div style={{ marginBottom:6 }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:600, color:C.clay, marginBottom:3 }}>Contractor supplies ({contractorSupplied.length})</div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, lineHeight:1.6 }}>
                          {contractorSupplied.map(s => supplyLabel(lookupScopeItem(s.id)?.label)).filter(Boolean).join(", ")}
                        </div>
                      </div>
                    )}
                    {ownerSupplied.length > 0 && (
                      <div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:600, color:"#856404", marginBottom:3 }}>You supply ({ownerSupplied.length})</div>
                        {ownerSupplied.map(s => {
                          const item = lookupScopeItem(s.id);
                          return (
                            <div key={s.id} style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, padding:"2px 0" }}>
                              <span>{supplyLabel(item?.label)}</span>
                              {s.selectedProduct && <span style={{ color:"#856404", fontWeight:600 }}>${s.selectedProduct.price?.toLocaleString()}</span>}
                            </div>
                          );
                        })}
                        {ownerMaterialsCost > 0 && (
                          <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:"#856404", borderTop:`1px solid ${C.sand}`, paddingTop:4, marginTop:2 }}>
                            <span>Your materials est.</span>
                            <span>${ownerMaterialsCost.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div></div><div><div style={{ borderRadius:10,overflow:"hidden",aspectRatio:"4/3",background:C.sand,position:"relative",marginBottom:12,boxShadow:"0 4px 20px rgba(0,0,0,0.1)" }}>
              {photo && <img src={photo} alt="preview" style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} />}
              {draft.items.map(item=>(
                <div key={item.uid} style={{ position:"absolute",left:`${item.x}%`,top:`${item.y}%`,transform:"translate(-50%,-50%)",background:"rgba(255,255,255,0.9)",borderRadius:5,padding:"3px 7px",fontSize:10,fontFamily:"'DM Sans',sans-serif",fontWeight:600,color:C.ink,border:`1.5px solid ${item.color}`,whiteSpace:"nowrap" }}>{item.label}</div>
              ))}
            </div><div style={{ display:"flex",flexWrap:"wrap",gap:6,marginBottom:20 }}>
              {draft.items.map(item=>(
                <span key={item.uid} style={{ background:item.color,borderRadius:100,padding:"3px 10px",fontSize:11,fontFamily:"'DM Sans',sans-serif",color:C.ink,opacity:0.8 }}>{item.label}</span>
              ))}
            </div><button onClick={postProject} style={{ width:"100%",background:C.terra,color:C.white,border:"none",borderRadius:8,padding:"14px 0",fontFamily:"'DM Sans',sans-serif",fontSize:15,fontWeight:600,boxShadow:`0 4px 20px ${C.terra}55` }}>
               Publish & Receive Bids
            </button></div></div></div></div>
  );

  //  BIDS VIEW
  // ── CHECKOUT — Stripe payment to fund escrow ──

  // Helper: persist escrow funding to state + Supabase
  const finalizeEscrow = async (intentId, isPending = false) => {
    const projectId = checkoutBid?.project?.id;
    if (!projectId) return;
    const newStatus = isPending ? "ach_pending" : "awarded";
    const updated = projects.map(p =>
      p.id === projectId
        ? { ...p, status: newStatus,
            escrow: { ...p.escrow, funded: !isPending, paymentIntentId: intentId },
            address: homeAddress.trim() || p.address || null }
        : p
    );
    updateProjects(updated);
    setActiveProject(updated.find(p => p.id === projectId));
    await supabase.from("projects").update({
      status:  newStatus,
      escrow:  { ...checkoutBid.project.escrow, funded: !isPending, paymentIntentId: intentId },
      address: homeAddress.trim() || null,
    }).eq("id", projectId);
  };

  // Open Plaid Link — fetches a link token, then opens the Plaid UI
  const openPlaidLink = async () => {
    setPlaidLoading(true);
    setPlaidError(null);
    const base = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
    try {
      const { link_token, error: ltErr } = await fetch(`${base}/payments/create-plaid-link-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ homeownerId: user?.id }),
      }).then(r => r.json());
      if (ltErr) throw new Error(ltErr);

      if (!window.Plaid) throw new Error("Plaid failed to load. Please allow popups and try again.");

      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (publicToken, metadata) => {
          const accountId = metadata.accounts?.[0]?.id;
          try {
            const { stripeCustomerId: cid, bankAccountId, bankName, last4, error: exErr }
              = await fetch(`${base}/payments/exchange-plaid-token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  publicToken, accountId,
                  homeownerId: user?.id,
                  existingStripeCustomerId: stripeCustomerId,
                }),
              }).then(r => r.json());
            if (exErr) throw new Error(exErr);
            setStripeCustomerId(cid);
            setPlaidBankAccountId(bankAccountId);
            setPlaidBankName(bankName);
            setPlaidBankLast4(last4);
            setPlaidVerified(true);
            setUsingSavedBank(false);
            setSavedBank({ bankName, last4 });
          } catch (err) {
            setPlaidError(err.message);
          }
          setPlaidLoading(false);
        },
        onExit: (err) => {
          if (err) setPlaidError(err.display_message || "Bank connection was cancelled.");
          setPlaidLoading(false);
        },
      });
      handler.open();
    } catch (err) {
      setPlaidError(err.message);
      setPlaidLoading(false);
    }
  };

  // Fund escrow via Plaid-verified bank account (ACH Charge path)
  const handleAchFundEscrow = async () => {
    if (!homeAddress.trim()) return;
    setCardLoading(true);
    setCardError(null);
    const base = process.env.REACT_APP_BACKEND_URL || ("https://" + BACKEND_URL);
    const bankId = plaidBankAccountId;
    const custId = stripeCustomerId;
    try {
      const { chargeId, error: escrowErr } = await fetch(`${base}/payments/create-escrow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bidAmount:                 checkoutBid.total,
          projectId:                 checkoutBid.project.id,
          contractorStripeAccountId: checkoutBid.bid.contractorStripeAccountId,
          homeownerId:               user?.id,
          stripeCustomerId:          custId,
          bankAccountId:             bankId,
        }),
      }).then(r => r.json());
      if (escrowErr) throw new Error(escrowErr);
      // ACH charge is 'pending' — charge.succeeded webhook fires when it clears (3–5 days)
      setPaymentStatus("ach_pending");
      setPaymentIntentId(chargeId);
      await finalizeEscrow(chargeId, true);
    } catch (err) {
      setCardError(err.message);
    }
    setCardLoading(false);
  };

  // Inner component: fee comparison tabs
  const CheckoutPaymentSelector = ({ total: amt, paymentMethod: pm, onSelect }) => {
    const cardFeeCents = Math.round(amt * 0.029 + 0.30);
    const achFeeDisplay = 5;
    const savingsDollars = Math.round(cardFeeCents / 100) - achFeeDisplay;
    const showSavings = amt >= 10000 && savingsDollars > 0;
    const tab = (active) => ({
      flex:1, padding:"10px 0", border:"none", borderRadius:8, cursor:"pointer",
      fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, transition:"all 0.15s",
      background: active ? C.terra : "transparent",
      color:      active ? "#fff"   : C.slate,
    });
    return (
      <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>Payment Method</div>
        {showSavings && (
          <div style={{ background:"rgba(122,158,126,0.12)", border:`1px solid ${C.sage}`, borderRadius:8, padding:"10px 14px", marginBottom:12, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.sage, lineHeight:1.5 }}>
            Pay by bank and save ~<strong>${savingsDollars.toLocaleString()}</strong> on this ${amt.toLocaleString()} escrow — ACH is capped at $5
          </div>
        )}
        <div style={{ display:"flex", gap:6, background:C.cream, borderRadius:10, padding:4, marginBottom:12 }}>
          <button style={tab(pm==="card")}   onClick={() => onSelect("card")}>Card <span style={{ fontWeight:400, fontSize:11 }}>(~2.9%)</span></button>
          <button style={tab(pm==="bank")}   onClick={() => onSelect("bank")}>Bank Transfer <span style={{ fontWeight:400, fontSize:11 }}>(ACH · $5 max)</span></button>
        </div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.5 }}>
          {pm === "card"
            ? `Instant confirmation. Stripe fee: ~$${(cardFeeCents/100).toLocaleString(undefined,{maximumFractionDigits:0})}.`
            : "US bank account. Instant verification in most cases. Settlement takes 1–2 business days."}
        </div>
      </div>
    );
  };

  // Inner component: ACH pending state banner
  const AchPendingBanner = ({ paymentIntentId: intentId }) => (
    <div style={{ background:"rgba(212,168,67,0.1)", border:`1px solid ${C.gold}`, borderRadius:12, padding:"20px", textAlign:"center", marginBottom:14 }}>
      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, color:C.gold, fontWeight:700, marginBottom:6 }}>Bank Transfer Initiated</div>
      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:1.6, marginBottom:8 }}>
        Your ACH transfer is processing. Most transfers complete in 1–2 business days. Your contractor will be notified once funds settle.
      </div>
      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, fontStyle:"italic" }}>Ref: {intentId}</div>
    </div>
  );

  // Inner component: Stripe PaymentElement form (must live inside <Elements> provider)
  const StripeCheckoutForm = ({ total: formTotal }) => {
    const stripe   = useStripe();
    const elements = useElements();

    const handleSubmit = async () => {
      if (!stripe || !elements || !homeAddress.trim()) return;
      setCardLoading(true);
      setCardError(null);
      sessionStorage.setItem("reno_pending_project_id", checkoutBid.project.id);
      sessionStorage.setItem("reno_pending_address",    homeAddress.trim());

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.origin + "/" },
        redirect: "if_required",
      });

      if (error) {
        sessionStorage.removeItem("reno_pending_project_id");
        sessionStorage.removeItem("reno_pending_address");
        setCardError(error.message);
        setPaymentStatus("failed");
        setCardLoading(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        setPaymentStatus("succeeded");
        setCardDone(true);
        await finalizeEscrow(paymentIntent.id);
        setTimeout(() => { setView("bids"); setHomeAddress(""); setPaymentClientSecret(null); setCardDone(false); setPaymentStatus("idle"); }, 1800);
      } else if (paymentIntent?.status === "processing") {
        setPaymentStatus("ach_pending");
        setPaymentIntentId(paymentIntent.id);
        await finalizeEscrow(paymentIntent.id, true);
      }
      setCardLoading(false);
    };

    const canPay = homeAddress.trim() && !cardLoading && !!stripe;
    return (
      <>
        <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:14 }}>Payment Details</div>
          <PaymentElement options={{ layout:{ type:"accordion", defaultCollapsed:false }, defaultValues:{ billingDetails:{ address:{ country:"US" } } } }} />
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:10, lineHeight:1.5 }}>Payments secured by Stripe · RenoBid never stores your payment details</div>
        </div>
        {cardError && (
          <div style={{ background:"rgba(192,57,43,0.1)", border:"1px solid rgba(192,57,43,0.3)", borderRadius:8, padding:"10px 14px", marginBottom:14, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:"#E74C3C" }}>
            {cardError}
          </div>
        )}
        {paymentStatus === "ach_pending" ? (
          <AchPendingBanner paymentIntentId={paymentIntentId} />
        ) : cardDone ? (
          <div style={{ background:"rgba(39,174,96,0.1)", border:`1px solid ${C.sage}`, borderRadius:12, padding:"18px 20px", textAlign:"center" }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:C.sage, fontWeight:700, marginBottom:4 }}>Escrow Funded</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>Your contractor has been notified. Redirecting…</div>
          </div>
        ) : (
          <button onClick={handleSubmit} disabled={!canPay}
            style={{ width:"100%", background:canPay?C.terra:C.sand, color:canPay?"#FDFCFA":C.slate, border:"none", borderRadius:12, padding:"16px 0", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:700, cursor:canPay?"pointer":"default", boxShadow:canPay?"0 4px 20px rgba(184,92,56,0.3)":"none", transition:"all 0.2s" }}>
            {cardLoading ? "Processing…" : `Fund Escrow — $${formTotal.toLocaleString()}`}
          </button>
        )}
      </>
    );
  };

  if (view === "checkout" && checkoutBid) {
    const { project, bid, total } = checkoutBid;
    const fee = Math.round(total * 0.04);

    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}>
        <GlobalStyles /><Nav />
        <div style={{ maxWidth:520, margin:"0 auto", padding:"28px 16px 48px", width:"100%" }} className="fade-in">
          <button onClick={() => { setView("bids"); setPaymentClientSecret(null); setPaymentStatus("idle"); setCardError(null); setCardDone(false); }} style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, cursor:"pointer", marginBottom:20, padding:0 }}>← Back</button>

          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:C.ink, marginBottom:4 }}>Fund Escrow</h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.slate, marginBottom:24, lineHeight:1.6 }}>
            Your payment is held securely until you approve each milestone. Nothing is paid to the contractor until you release it.
          </p>

          {/* Order summary */}
          <div style={{ background:C.white, borderRadius:14, padding:"18px 20px", border:`1px solid ${C.sand}`, marginBottom:16 }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:14 }}>Order Summary</div>
            {[
              ["Project", project.title],
              ["Contractor", bid.company],
              ["Bid amount", `$${total.toLocaleString()}`],
              ["Platform fee (4%)", `$${fee.toLocaleString()}`],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
                <span style={{ color:C.slate }}>{k}</span>
                <span style={{ color:C.ink, fontWeight:600 }}>{v}</span>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0", fontFamily:"'Playfair Display',serif", fontSize:18 }}>
              <span style={{ color:C.ink, fontWeight:700 }}>Total charged today</span>
              <span style={{ color:C.terra, fontWeight:700 }}>${total.toLocaleString()}</span>
            </div>
          </div>

          {/* Job site address — required before funding */}
          <div style={{ background:C.white, borderRadius:14, padding:"18px 20px", border:`1px solid ${homeAddress.trim() ? C.sand : C.terra}`, marginBottom:16 }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Job Site Address</div>
            <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginBottom:12, lineHeight:1.5, margin:"0 0 12px" }}>
              Your full address will be shared with your contractor once escrow is funded.
            </p>
            <input
              type="text"
              placeholder="123 Main St, City, State ZIP"
              value={homeAddress}
              onChange={e => setHomeAddress(e.target.value)}
              style={{ width:"100%", border:`1.5px solid ${homeAddress.trim() ? C.sand : "rgba(184,92,56,0.4)"}`, borderRadius:8, padding:"11px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, background:C.cream, outline:"none", boxSizing:"border-box" }}
            />
            {!homeAddress.trim() && (
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.terra, marginTop:6 }}>Required — your contractor needs this to show up on job day</div>
            )}
          </div>

          {/* Payment method tabs */}
          <CheckoutPaymentSelector total={total} paymentMethod={paymentMethod} onSelect={(m) => {
            setPaymentMethod(m);
            setPlaidError(null);
          }} />

          {/* Bank Transfer — Plaid verification flow */}
          {paymentMethod === "bank" && (
            <div style={{ background:C.white, borderRadius:14, padding:"18px 20px", border:`1px solid ${C.sand}`, marginBottom:16 }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:14 }}>Bank Account</div>

              {/* Verified bank confirmed */}
              {(plaidVerified || usingSavedBank) ? (
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(122,158,126,0.1)", border:`1px solid ${C.sage}`, borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
                    <span style={{ fontSize:18 }}>✓</span>
                    <div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.sage }}>Bank Verified</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>{plaidVerified ? plaidBankName : savedBank?.bankName} ····{plaidVerified ? plaidBankLast4 : savedBank?.last4}</div>
                    </div>
                    <button onClick={() => { setPlaidVerified(false); setUsingSavedBank(false); setPlaidBankAccountId(null); }} style={{ marginLeft:"auto", background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer", textDecoration:"underline" }}>Change</button>
                  </div>
                  {cardError && <div style={{ color:"#E74C3C", fontFamily:"'DM Sans',sans-serif", fontSize:13, marginBottom:10 }}>{cardError}</div>}
                  <button
                    onClick={handleAchFundEscrow}
                    disabled={cardLoading || !homeAddress.trim()}
                    style={{ width:"100%", background: cardLoading || !homeAddress.trim() ? C.sand : C.terra, color:"#FDFCFA", border:"none", borderRadius:10, padding:"14px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:15, cursor: cardLoading || !homeAddress.trim() ? "not-allowed" : "pointer", transition:"background 0.15s" }}>
                    {cardLoading ? "Initiating Transfer…" : `Fund Escrow — $${total.toLocaleString()}`}
                  </button>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:8, textAlign:"center" }}>Bank transfer · settles in 1–3 business days · no processing fee</div>
                </div>
              ) : savedBank && !plaidVerified ? (
                /* Saved bank from a previous project */
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:10, background:C.cream, border:`1px solid ${C.sand}`, borderRadius:10, padding:"12px 14px", marginBottom:12 }}>
                    <span style={{ fontSize:16 }}>🏦</span>
                    <div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>{savedBank.bankName} ····{savedBank.last4}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>Saved from a previous project</div>
                    </div>
                  </div>
                  <button onClick={() => { setUsingSavedBank(true); setPlaidBankAccountId(null); }} style={{ width:"100%", background:C.sage, color:"#FDFCFA", border:"none", borderRadius:10, padding:"12px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:8 }}>
                    Use {savedBank.bankName} ····{savedBank.last4}
                  </button>
                  <button onClick={openPlaidLink} style={{ width:"100%", background:"none", border:`1.5px solid ${C.sand}`, borderRadius:10, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, cursor:"pointer" }}>
                    Connect a Different Account
                  </button>
                  {plaidError && <div style={{ color:"#E74C3C", fontFamily:"'DM Sans',sans-serif", fontSize:12, marginTop:8 }}>{plaidError}</div>}
                </div>
              ) : (
                /* First time — run Plaid Link */
                <div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:1.6, marginBottom:14 }}>
                    Securely connect your bank account in about 60 seconds. No processing fee — just a $5 flat ACH fee.
                  </div>
                  <button
                    onClick={openPlaidLink}
                    disabled={plaidLoading}
                    style={{ width:"100%", background: plaidLoading ? C.sand : C.terra, color:"#FDFCFA", border:"none", borderRadius:10, padding:"13px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, cursor: plaidLoading ? "not-allowed" : "pointer" }}>
                    {plaidLoading ? "Connecting…" : "Connect Bank Account"}
                  </button>
                  {plaidError && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ color:"#E74C3C", fontFamily:"'DM Sans',sans-serif", fontSize:12, marginBottom:6 }}>{plaidError}</div>
                      <button onClick={openPlaidLink} style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.terra, cursor:"pointer", textDecoration:"underline" }}>Try again</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Card — existing Stripe Elements flow */}
          {paymentMethod === "card" && (
            paymentClientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: paymentClientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary:    C.terra,
                      colorBackground: C.white,
                      colorText:       C.ink,
                      colorDanger:     "#E74C3C",
                      fontFamily:      "'DM Sans', sans-serif",
                      borderRadius:    "8px",
                    },
                  },
                }}
              >
                <StripeCheckoutForm total={total} />
              </Elements>
            ) : (
              <div style={{ background:C.white, borderRadius:14, padding:"18px 20px", border:`1px solid ${C.sand}`, marginBottom:16 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:14 }}>Payment Details</div>
                {cardError
                  ? <div style={{ color:"#E74C3C", fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>{cardError}</div>
                  : <div style={{ background:C.cream, borderRadius:8, height:44, display:"flex", alignItems:"center", paddingLeft:14, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>
                      Loading payment form…
                    </div>
                }
              </div>
            )
          )}

          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textAlign:"center", marginTop:12, lineHeight:1.6 }}>
            Protected by RenoBid escrow · Milestone-based release · Full dispute resolution
          </div>
        </div>
      </div>
    );
  }

  //  PROJECT DETAIL (homeowner)
  if (view === "project-detail" && activeProject) {
    const p = activeProject;
    const contractorScopeItems = (p.scope || []).filter(s => s.by === "contractor");
    const diyItems = (p.scope || []).filter(s => s.by === "owner");
    const acceptedBid = p.bids?.find(b => b.status === "accepted");
    const detailDisplayStatus = p.status === "awarded" ? "awarded" : acceptedBid ? "accepted" : "open";
    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav />
        <div style={{ maxWidth:760, margin:"0 auto", padding:"32px 24px", width:"100%" }} className="fade-in">
          {/* Back */}
          <button onClick={() => setView("dashboard")} style={{ background:"none", border:"none", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginBottom:16, cursor:"pointer", padding:0 }}>← My Projects</button>

          {/* Title row */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:C.ink, marginBottom:6 }}>{p.title || `${p.room} Renovation`}</h2>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ background: detailDisplayStatus==="awarded" ? "#E8F5E9" : detailDisplayStatus==="accepted" ? "#E8F0FE" : "#FFF8E1", color: detailDisplayStatus==="awarded" ? C.green : detailDisplayStatus==="accepted" ? "#1A56DB" : C.gold, borderRadius:100, padding:"3px 12px", fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:600, textTransform:"uppercase" }}>
                  {detailDisplayStatus==="awarded" ? "Awarded" : detailDisplayStatus==="accepted" ? "Accepted" : "Open"}
                </span>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>{p.room} · {p.budget} · {p.location || "Your Area"}</span>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>Posted {p.postedAt}</span>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {acceptedBid ? (
                <button onClick={() => setView("bids")} style={{ background:C.cream, color:C.slate, border:`1px solid ${C.sand}`, borderRadius:8, padding:"10px 18px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  Bid Details →
                </button>
              ) : (
                <button onClick={() => setView("bids")} style={{ background:C.terra, color:"#FDFCFA", border:"none", borderRadius:8, padding:"10px 18px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  View Bids ({p.bids?.length || 0}) →
                </button>
              )}
              <button onClick={() => setView("messages")} style={{ background:C.cream, border:`1px solid ${C.sand}`, borderRadius:8, padding:"10px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, cursor:"pointer" }}>
                💬 {p.messages?.length > 0 && p.messages.length}
              </button>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            {/* Left column */}
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {/* Details card */}
              <div style={{ background:C.white, borderRadius:12, padding:20, border:`1px solid ${C.sand}` }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>Project Details</div>
                {[
                  ["Room", p.room],
                  ["Budget", p.budget],
                  ["Location", p.location || "Not specified"],
                  ["Desired Start", p.desiredStart ? new Date(p.desiredStart + "T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "Flexible"],
                  ["Status", p.status === "awarded" ? "Awarded" : acceptedBid ? "Contractor selected" : "Open — accepting bids"],
                  ["Bids received", `${p.bids?.length || 0} bid${p.bids?.length !== 1 ? "s" : ""}`],
                ].map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
                    <span style={{ color:C.slate }}>{k}</span>
                    <span style={{ color:C.ink, fontWeight:600, textAlign:"right", maxWidth:"55%" }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              {p.description && (
                <div style={{ background:C.white, borderRadius:12, padding:20, border:`1px solid ${C.sand}` }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Description</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, lineHeight:1.7 }}>{p.description}</div>
                </div>
              )}

              {/* Scope */}
              {(p.scope || []).length > 0 && (
                <div style={{ background:C.white, borderRadius:12, padding:20, border:`1px solid ${C.sand}` }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>
                    Scope of Work
                  </div>
                  {contractorScopeItems.length > 0 && (
                    <>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontWeight:600, marginBottom:6 }}>Contractor work ({contractorScopeItems.length} items)</div>
                      {contractorScopeItems.map(s => {
                        const item = lookupScopeItem(s.id);
                        return item ? (
                          <div key={s.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink }}>
                            <span style={{ color:C.terra }}>✓</span>{item.label}
                          </div>
                        ) : null;
                      })}
                    </>
                  )}
                  {diyItems.length > 0 && (
                    <>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontWeight:600, marginTop:10, marginBottom:6 }}>Homeowner DIY ({diyItems.length} items)</div>
                      {diyItems.map(s => {
                        const item = lookupScopeItem(s.id);
                        return item ? (
                          <div key={s.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>
                            <span>🔨</span>{item.label}
                          </div>
                        ) : null;
                      })}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right column */}
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {/* Photo / render */}
              {(p.photo || p.renderedPhoto) && (
                <div style={{ background:C.white, borderRadius:12, overflow:"hidden", border:`1px solid ${C.sand}` }}>
                  <img src={p.renderedPhoto || p.photo} alt="project" style={{ width:"100%", aspectRatio:"4/3", objectFit:"cover", display:"block" }} />
                  <div style={{ padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>
                    {p.renderedPhoto ? "AI Vision Render" : "Project Photo"}
                  </div>
                </div>
              )}

              {/* Materials */}
              {(p.items || []).length > 0 && (
                <div style={{ background:C.white, borderRadius:12, padding:20, border:`1px solid ${C.sand}` }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>Selected Materials</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {p.items.map((item, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:6, background:C.cream, borderRadius:8, padding:"6px 10px", border:`1px solid ${C.sand}` }}>
                        <div style={{ width:12, height:12, borderRadius:3, background:item.color, flexShrink:0 }} />
                        <div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, color:C.ink }}>{item.label}</div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>{item.brand}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Escrow & Milestone Tracker — shown when a bid is accepted */}
          {acceptedBid && (
            <div style={{ marginTop:24 }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>Your Contractor</div>
              <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sand}`, display:"flex", alignItems:"center", gap:14, marginBottom:4 }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:C.slate, color:"#FDFCFA", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, flexShrink:0 }}>{acceptedBid.avatar}</div>
                <div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:C.ink }}>{acceptedBid.company}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:2 }}>
                    {acceptedBid.rating} · {acceptedBid.reviews} reviews · {acceptedBid.duration}
                  </div>
                </div>
                <div style={{ marginLeft:"auto", textAlign:"right" }}>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.terra }}>${(acceptedBid.total || acceptedBid.amount || 0).toLocaleString()}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>Total Estimate</div>
                </div>
              </div>
              <MilestoneTracker
                project={p}
                bid={acceptedBid}
                onUpdate={(updatedProject) => {
                  const updated = projects.map(pr => pr.id === updatedProject.id ? updatedProject : pr);
                  updateProjects(updated);
                  setActiveProject(updatedProject);
                }}
                onReview={() => setReviewModal({ project: p, contractorId: acceptedBid.contractorId || acceptedBid.contractor_id })}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === "bids" && activeProject) return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ maxWidth:760, margin:"0 auto", padding:"32px 24px", width:"100%" }} className="fade-in"><div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}><div><button onClick={() => setView(activeProject.bids?.some(b=>b.status==="accepted") ? "project-detail" : "dashboard")} style={{ background:"none",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,marginBottom:6,cursor:"pointer" }}>{activeProject.bids?.some(b=>b.status==="accepted") ? "← Project Details" : "← Dashboard"}</button><h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, color:C.ink }}>{activeProject.title || `${activeProject.room} Renovation`}</h2><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginTop:2 }}>
              {activeProject.bids?.length||0} bids received · <span style={{ color:C.sage,fontWeight:600 }}>Project {activeProject.status==="awarded" ? "Awarded" : activeProject.bids?.some(b=>b.status==="accepted") ? "Accepted" : "Live"}</span>
              {activeProject.desiredStart && (
                <> · <span style={{ color:C.terra, fontWeight:600 }}> Want to start {new Date(activeProject.desiredStart + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span></>
              )}
            </div></div></div>

        {(!activeProject.bids || activeProject.bids.length===0) ? (
          <div style={{ background:C.white,borderRadius:16,padding:48,textAlign:"center",border:`2px dashed ${C.sand}` }}><div style={{ fontSize:40,marginBottom:12 }}>⏳</div><div style={{ fontFamily:"'Playfair Display',serif",fontSize:20,color:C.ink,marginBottom:8 }}>Waiting for bids</div><div style={{ fontFamily:"'DM Sans',sans-serif",color:C.slate,fontSize:14 }}>Contractors can see your project. Bids typically arrive within a few hours.</div></div>
        ) : (
          <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
            {activeProject.bids.map(bid => (
              <div key={bid.id} style={{ background:C.white,borderRadius:12,padding:22,border:`2px solid ${bid.status==="accepted"?C.sage:C.sand}`,boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}><div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><div style={{ display:"flex",alignItems:"center",gap:14 }}><div style={{ width:48,height:48,borderRadius:"50%",background:C.slate,color:C.white,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:15,flexShrink:0 }}>{bid.avatar}</div><div><div style={{ display:"flex",alignItems:"center",gap:8 }}><span style={{ fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:C.ink }}>{bid.company}</span>
                        {bid.badge && <span style={{ background:bid.badge==="Top Rated"?C.gold:bid.badge==="Best Value"?C.sage:C.mist,color:bid.badge==="Top Rated"?C.ink:C.white,borderRadius:100,padding:"2px 9px",fontSize:10,fontFamily:"'DM Sans',sans-serif",fontWeight:700 }}>{bid.badge}</span>}
                        {bid.status==="accepted" && <span style={{ background:"#E8F5E9",color:C.green,borderRadius:100,padding:"2px 9px",fontSize:10,fontFamily:"'DM Sans',sans-serif",fontWeight:700 }}> Accepted</span>}
                      </div><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,marginTop:2 }}>
                         {bid.rating} · {bid.reviews} reviews
                        {bid.startDate && <> · Starts <strong style={{ color:C.ink }}>{new Date(bid.startDate + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</strong></>}
                        {bid.duration && <> · {bid.duration}</>}
                      </div>
                      {/* Schedule alignment badge */}
                      {bid.startDate && activeProject.desiredStart && (
                        <div style={{ marginTop:4 }}>
                          {new Date(bid.startDate) <= new Date(activeProject.desiredStart + "T12:00:00")
                            ? <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, fontWeight:600 }}> Available by your start date</span>
                            : <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.terra, fontWeight:600 }}> Available {Math.ceil((new Date(bid.startDate)-new Date(activeProject.desiredStart + "T12:00:00"))/(1000*60*60*24))}d after your preferred start</span>
                          }
                        </div>
                      )}
                    </div></div><div style={{ textAlign:"right" }}>
                      {(() => {
                        const ownerMaterialsCost = (activeProject.scope || []).reduce((sum, s) => {
                          const effectiveSupply = s.supplyChoice || (lookupScopeItem(s.id)?.supply_model);
                          return (effectiveSupply === "owner_supplied" && s.selectedProduct?.price) ? sum + s.selectedProduct.price : sum;
                        }, 0);
                        if (ownerMaterialsCost > 0) {
                          return (
                            <>
                              <div style={{ fontFamily:"'Playfair Display',serif",fontSize:22,color:C.terra,fontWeight:700 }}>${bid.amount.toLocaleString()}</div>
                              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:C.slate,marginBottom:4 }}>Contractor bid</div>
                              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#856404",fontWeight:700 }}>+${ownerMaterialsCost.toLocaleString()}</div>
                              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:C.slate,marginBottom:4 }}>Your materials</div>
                              <div style={{ borderTop:`1px solid ${C.sand}`,paddingTop:4 }}>
                                <div style={{ fontFamily:"'Playfair Display',serif",fontSize:18,color:C.ink,fontWeight:700 }}>${(bid.amount+ownerMaterialsCost).toLocaleString()}</div>
                                <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:C.slate }}>Combined total</div>
                              </div>
                            </>
                          );
                        }
                        return (
                          <>
                            <div style={{ fontFamily:"'Playfair Display',serif",fontSize:26,color:C.terra,fontWeight:700 }}>${bid.amount.toLocaleString()}</div>
                            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:C.slate }}>Total Estimate</div>
                          </>
                        );
                      })()}
                    </div></div>
                {bid.note && <div style={{ marginTop:12,padding:"10px 14px",background:C.cream,borderRadius:8,fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,lineHeight:1.6 }}>"{bid.note}"</div>}
                {(() => {
                  const hasLineItems = bid.lineItems && Object.keys(bid.lineItems).length > 0;
                  if (hasLineItems) {
                    // Group by category
                    const categoryMap = {};
                    Object.entries(bid.lineItems).forEach(([id, li]) => {
                      const scopeItem = lookupScopeItem(id);
                      const cat = scopeItem?.category || "Other";
                      if (!categoryMap[cat]) categoryMap[cat] = { subtotal: 0, items: [] };
                      const lineTotal = (li.quantity || 0) * (li.unit_cost || 0);
                      categoryMap[cat].subtotal += lineTotal;
                      categoryMap[cat].items.push({ id, li, scopeItem, lineTotal });
                    });
                    const grandTotal = Object.values(categoryMap).reduce((s, c) => s + c.subtotal, 0);
                    return (
                      <div style={{ marginTop:12, background:C.cream, borderRadius:8, padding:"10px 14px" }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.5, marginBottom:8 }}>Bid Breakdown</div>
                        {Object.entries(categoryMap).map(([cat, { subtotal }]) => (
                          <div key={cat} style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${C.sand}` }}>
                            <span style={{ color:C.slate }}>{cat}</span>
                            <span style={{ color:C.ink, fontWeight:600 }}>${subtotal.toLocaleString()}</span>
                          </div>
                        ))}
                        <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, paddingTop:6, marginTop:4 }}>
                          <span style={{ color:C.ink }}>Total</span>
                          <span style={{ color:C.terra }}>${grandTotal.toLocaleString()}</span>
                        </div>
                        {bid.acknowledged && <div style={{ marginTop:8, fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage }}> Contractor confirmed full scope coverage</div>}
                      </div>
                    );
                  }
                  if (bid.breakdown && (bid.breakdown.labor || bid.breakdown.materials || bid.breakdown.permits)) {
                    return (
                      <div style={{ marginTop:12, background:C.cream, borderRadius:8, padding:"10px 14px" }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.5, marginBottom:8 }}>Bid Breakdown</div>
                        {[[" Labor", bid.breakdown.labor],[" Materials", bid.breakdown.materials],[" Permits", bid.breakdown.permits]].filter(([,v])=>v>0).map(([label,val])=>(
                          <div key={label} style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:12, padding:"3px 0" }}><span style={{ color:C.slate }}>{label}</span><span style={{ color:C.ink, fontWeight:600 }}>${val.toLocaleString()}</span></div>
                        ))}
                        {bid.acknowledged && <div style={{ marginTop:8, fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage }}> Contractor confirmed full scope coverage</div>}
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop:12, padding:"10px 14px", background:C.cream, borderRadius:8, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>
                      No itemized breakdown provided for this bid.
                    </div>
                  );
                })()}
                {activeProject.status!=="awarded" && (
                  <div style={{ marginTop:14 }}>
                    {/* Consultation needed — not yet booked */}
                    {bid.needsConsultation && !bid.consultationBooked && !bid.consultationComplete && (
                      <div style={{ marginBottom:12 }}>
                        <div style={{ background:"#FFF8E1", border:`1px solid ${C.gold}55`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.gold, marginBottom:3 }}>In-Person Consultation Required</div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, lineHeight:1.5 }}>This contractor needs to visit before finalizing their estimate. Schedule a consultation — this does not commit you to this contractor.</div>
                        </div>
                        {schedulingBid?.bidId === bid.id ? (
                          <ConsultationScheduler
                            contractor={{ availability: bid.contractorAvailability || contractorAvailCache[bid.contractorId] || {}, company: bid.company }}
                            onBook={async (date, time, address) => {
                              const booked = { date, time, address };
                              const updated = projects.map(p => p.id === activeProject.id ? {
                                ...p, bids: p.bids.map(b => b.id === bid.id ? { ...b, consultationBooked: booked } : b)
                              } : p);
                              // Persist to Supabase
                              if (user?.id && bid.id) {
                                const { error: bookErr } = await supabase.from('bids').update({ consultation_booked: booked }).eq('id', bid.id);
                                if (bookErr) console.error('Failed to save consultation booking:', bookErr.message, bookErr);
                              }
                              updateProjects(updated);
                              setActiveProject(updated.find(p => p.id === activeProject.id));
                              setSchedulingBid(null);
                            }}
                            onCancel={() => setSchedulingBid(null)}
                          />
                        ) : (
                          <button onClick={async () => {
                              // If no availability snapshot on the bid, fetch live from contractor profile
                              if (!bid.contractorAvailability && bid.contractorId && !contractorAvailCache[bid.contractorId]) {
                                try {
                                  const { data } = await supabase.from('profiles').select('availability').eq('id', bid.contractorId).single();
                                  if (data?.availability) setContractorAvailCache(c => ({ ...c, [bid.contractorId]: data.availability }));
                                } catch {}
                              }
                              setSchedulingBid({ bidId: bid.id });
                            }}
                            style={{ width:"100%", background:C.slate, color:C.white, border:"none", borderRadius:6, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                            Schedule Consultation →
                          </button>
                        )}
                      </div>
                    )}

                    {/* Consultation booked — waiting for contractor to revise */}
                    {bid.needsConsultation && bid.consultationBooked && !bid.consultationComplete && (
                      <div style={{ marginBottom:12, background:"#F0FFF4", border:`1px solid ${C.sage}55`, borderRadius:8, padding:"10px 14px" }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.green, marginBottom:3 }}>Consultation Scheduled</div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:600 }}>
                          {new Date(bid.consultationBooked.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} at {bid.consultationBooked.time}
                        </div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:4 }}>Awaiting the contractor's revised estimate after your visit.</div>
                      </div>
                    )}

                    {/* Ready to accept — either no consultation needed, or consultation is complete */}
                    {bid.status !== "accepted" && (!bid.needsConsultation || bid.consultationComplete) && (
                      <>
                        {bid.consultationComplete && (
                          <div style={{ marginBottom:10, background:"rgba(184,92,56,0.06)", border:`1px solid rgba(184,92,56,0.18)`, borderRadius:8, padding:"8px 12px" }}>
                            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.terra, fontWeight:600 }}>Revised estimate after in-person consultation</div>
                          </div>
                        )}
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={() => acceptBid(activeProject.id, bid.id)}
                            style={{ flex:2, background:C.terra, color:"#FDFCFA", border:"none", borderRadius:6, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                            Accept & Fund Escrow
                          </button>
                          <button onClick={() => { setView("messages"); }}
                            style={{ flex:1, background:C.white, color:C.slate, border:`1.5px solid ${C.sand}`, borderRadius:6, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, cursor:"pointer" }}>💬 Message</button>
                        </div>
                      </>
                    )}
                    {/* Message button always shown when consultation is pending */}
                    {bid.needsConsultation && !bid.consultationComplete && (
                      <div style={{ marginTop:8 }}>
                        <button onClick={() => { setView("messages"); }}
                          style={{ background:C.white, color:C.slate, border:`1.5px solid ${C.sand}`, borderRadius:6, padding:"8px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:13, cursor:"pointer" }}>💬 Message Contractor</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop:20 }}>
          <button onClick={() => setView("messages")} style={{ display:"flex", alignItems:"center", gap:8, background:C.slate, color:"#FDFCFA", border:"none", borderRadius:8, padding:"11px 20px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            <span style={{ fontSize:16 }}>💬</span>
            Messages
            {activeProject.messages?.length > 0 && (
              <span style={{ background:"rgba(255,255,255,0.25)", borderRadius:100, padding:"1px 8px", fontSize:11, fontWeight:700 }}>{activeProject.messages.length}</span>
            )}
          </button>
        </div>
      </div></div>
  );

  //  MESSAGES
  if (view === "messages" && activeProject) return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ maxWidth:700, margin:"0 auto", padding:"24px 24px 0", width:"100%", display:"flex", flexDirection:"column", height:"calc(100vh - 56px)" }}><div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}><button onClick={() => setView("bids")} style={{ background:"none",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,cursor:"pointer" }}>← Bids</button><div style={{ fontFamily:"'Playfair Display',serif",fontSize:20,color:C.ink,fontWeight:700 }}>Messages — {activeProject.title}</div></div><div style={{ flex:1,overflowY:"auto",background:C.white,borderRadius:"12px 12px 0 0",padding:20,border:`1px solid ${C.sand}` }}>
          {(!activeProject.messages||activeProject.messages.length===0) ? (
            <div style={{ textAlign:"center",padding:40,color:C.slate,fontFamily:"'DM Sans',sans-serif" }}><div style={{ fontSize:36,marginBottom:12 }}></div><div>No messages yet. Send a message to contractors who bid on your project.</div></div>
          ) : (
            activeProject.messages.map((m,i) => (
              <div key={i} className="fade-in" style={{ display:"flex",justifyContent:m.from==="homeowner"?"flex-end":"flex-start",marginBottom:12 }}><div style={{ maxWidth:"70%",background:m.from==="homeowner"?C.terra:C.cream,color:m.from==="homeowner"?C.white:C.ink,borderRadius:m.from==="homeowner"?"12px 12px 4px 12px":"12px 12px 12px 4px",padding:"10px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:14,lineHeight:1.5 }}>
                  {m.text}
                  <div style={{ fontSize:10,opacity:0.6,marginTop:4,textAlign:"right" }}>{m.time}</div></div></div>
            ))
          )}
          <div ref={msgEndRef} /></div><div style={{ background:C.white,borderRadius:"0 0 12px 12px",borderTop:`1px solid ${C.sand}`,padding:12,display:"flex",gap:10,border:`1px solid ${C.sand}`,borderTop:"none",marginBottom:24 }}><input value={msgInput} onChange={e=>setMsgInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendMessage(activeProject.id)} placeholder="Message your contractors..." style={{ flex:1,border:`1.5px solid ${C.sand}`,borderRadius:8,padding:"10px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:C.ink,background:C.cream }} /><button onClick={()=>sendMessage(activeProject.id)} style={{ background:C.terra,color:C.white,border:"none",borderRadius:8,padding:"10px 20px",fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14 }}>Send</button></div></div></div>
  );

  return (
    <>
      {reviewModal && (
        <ReviewModal
          project={reviewModal.project}
          contractorId={reviewModal.contractorId}
          user={user}
          onClose={() => setReviewModal(null)}
          onSubmitted={(review) => {
            const updated = projects.map(p => p.id === reviewModal.project.id ? { ...p, reviewed: true } : p);
            updateProjects(updated);
            if (activeProject?.id === reviewModal.project.id) setActiveProject({ ...activeProject, reviewed: true });
            setReviewModal(null);
          }}
        />
      )}
    </>
  );
}

//
// CONTRACTOR PHOTO COMPONENTS
// 

function RoomPlaceholder({ items, label, accent }) {
  return (
    <div style={{ width:"100%", height:"100%", background: accent ? "rgba(184,92,56,0.05)" : C.cream,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8, padding:16 }}><div style={{ fontSize:32 }}>{accent ? "" : ""}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color: accent ? "rgba(184,92,56,0.40)" : "rgba(0,0,0,0.22)",
        textAlign:"center", fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>{label}</div>
      {!accent && items?.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", gap:5, marginTop:2 }}>
          {items.slice(0,4).map((item,i) => (
            <div key={i} style={{ width:22, height:22, borderRadius:5, background:item.color, border:"1px solid rgba(255,255,255,0.1)" }} />
          ))}
        </div>
      )}
      {accent && items?.length > 0 && (
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:"rgba(184,92,56,0.30)", textAlign:"center" }}>
          {items.map(i=>i.label).join(" · ")}
        </div>
      )}
    </div>
  );
}

// Thumbnail strip shown on browse cards
function ProjectPhotoStrip({ project }) {
  const hasPhoto = !!project.photo;
  const hasRender = !!project.renderedPhoto;
  return (
    <div style={{ display:"flex", height:140, overflow:"hidden", borderBottom:`1px solid ${C.sand}` }}><div style={{ flex:1, position:"relative", overflow:"hidden" }}>
        {hasPhoto
          ? <img src={project.photo} alt="before" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
          : <RoomPlaceholder items={project.items} label="No photo yet" accent={false} />}
        <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"18px 8px 6px", background:"linear-gradient(to top,rgba(0,0,0,0.7),transparent)" }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.8)", background:"rgba(0,0,0,0.5)", borderRadius:3, padding:"2px 6px" }}>BEFORE</span></div></div><div style={{ width:2, background:C.cream, flexShrink:0 }} /><div style={{ flex:1, position:"relative", overflow:"hidden" }}>
        {hasRender
          ? <img src={project.renderedPhoto} alt="render" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
          : <RoomPlaceholder items={project.items} label="Render pending" accent={true} />}
        <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"18px 8px 6px", background:`linear-gradient(to top,${hasRender?"rgba(0,0,0,0.6)":"rgba(184,92,56,0.08)"},transparent)` }}><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color: hasRender?C.white:"rgba(184,92,56,0.70)", background: hasRender?"rgba(184,92,56,0.70)":"rgba(184,92,56,0.10)", border: hasRender?"none":"1px solid rgba(184,92,56,0.20)", borderRadius:3, padding:"2px 6px" }}>
            {hasRender ? "AI RENDER " : "VISION "}
          </span></div></div></div>
  );
}

// Full before/after viewer on project detail page
function ContractorPhotoViewer({ project }) {
  const [showing, setShowing] = useState("before");
  const hasPhoto = !!project.photo;
  const hasRender = !!project.renderedPhoto;
  const scopeLevel = (project.items?.length||0) >= 4 ? "High" : (project.items?.length||0) >= 2 ? "Medium" : "Light";
  const scopeColor = scopeLevel==="High" ? "#E74C3C" : scopeLevel==="Medium" ? C.gold : C.sage;

  return (
    <div style={{ background:C.white, overflow:"hidden", borderBottom:`1px solid ${C.sand}`, marginBottom:0 }}>
      {/* Scope header */}
      <div style={{ padding:"10px 16px", background:C.cream, display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${C.sand}` }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>
          {project.room} · {project.items?.length||0} materials requested
        </div><div style={{ display:"flex", alignItems:"center", gap:5 }}><div style={{ width:7, height:7, borderRadius:"50%", background:scopeColor }} /><span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:scopeColor }}>{scopeLevel} Scope</span></div></div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.sand}` }}>
        {[["before"," Current State"], ["after"," Homeowner's Vision"]].map(([key, label]) => {
          const active = showing===key;
          const enabled = key==="before" ? true : hasRender;
          return (
            <button key={key} onClick={() => enabled && setShowing(key)}
              style={{ flex:1, padding:"12px 8px", border:"none",
                background: active ? "rgba(184,92,56,0.08)" : "transparent",
                color: !enabled ? "rgba(0,0,0,0.20)" : active ? C.terra : "rgba(0,0,0,0.45)",
                fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight: active?700:500,
                cursor: enabled?"pointer":"not-allowed",
                borderBottom: active?`2px solid ${C.terra}`:"2px solid transparent" }}>
              {label}{key==="after"&&!hasRender?" (pending)":""}
            </button>
          );
        })}
      </div>

      {/* Image */}
      <div style={{ position:"relative", width:"100%", aspectRatio:"16/9", maxHeight:420, overflow:"hidden", background:"#0D1420" }}>
        {showing==="before" && (
          hasPhoto
            ? <img src={project.photo} alt="before" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
            : <div style={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10 }}><div style={{ fontSize:48 }}></div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, textAlign:"center", padding:"0 32px", lineHeight:1.6 }}>
                  No photo uploaded yet — use the description and materials to assess scope
                </div></div>
        )}
        {showing==="after" && (
          hasRender
            ? <img src={project.renderedPhoto} alt="vision" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
            : <div style={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, padding:24 }}><div style={{ fontSize:48 }}></div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, textAlign:"center" }}>No AI render yet</div><div style={{ background:"rgba(184,92,56,0.05)", border:"1px solid rgba(184,92,56,0.12)", borderRadius:8, padding:"10px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, textAlign:"center", lineHeight:1.6 }}>
                  Refer to materials list below to understand the homeowner's vision
                </div></div>
        )}
      </div>

    </div>
  );
}

//
// AVAILABILITY MANAGER (contractor sets weekly availability windows)
//
const DAYS = ["mon","tue","wed","thu","fri","sat","sun"];
const DAY_LABELS = { mon:"Monday", tue:"Tuesday", wed:"Wednesday", thu:"Thursday", fri:"Friday", sat:"Saturday", sun:"Sunday" };
const HOUR_OPTIONS = Array.from({length:24},(_,i)=>{
  const h = i % 12 || 12; const ampm = i < 12 ? "am" : "pm";
  return { value: String(i).padStart(2,"0")+":00", label: `${h}:00 ${ampm}` };
});
const PRESETS = [
  { label:"Morning", start:"09:00", end:"12:00" },
  { label:"Afternoon", start:"13:00", end:"17:00" },
  { label:"Evening", start:"17:00", end:"20:00" },
];
function AvailabilityManager({ availability, onChange }) {
  const avail = availability || {};
  function addWindow(day, preset) {
    const windows = [...(avail[day] || []), preset ? { start: preset.start, end: preset.end } : { start:"09:00", end:"17:00" }];
    onChange({ ...avail, [day]: windows });
  }
  function removeWindow(day, idx) {
    const windows = (avail[day] || []).filter((_,i) => i !== idx);
    onChange({ ...avail, [day]: windows });
  }
  function updateWindow(day, idx, field, value) {
    const windows = (avail[day] || []).map((w, i) => i === idx ? { ...w, [field]: value } : w);
    onChange({ ...avail, [day]: windows });
  }
  return (
    <div style={{ marginTop:14 }}>
      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:0.6, marginBottom:10 }}>Weekly Availability</div>
      {DAYS.map(day => (
        <div key={day} style={{ marginBottom:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink }}>{DAY_LABELS[day]}</span>
            <div style={{ display:"flex", gap:4 }}>
              {PRESETS.map(p => (
                <button key={p.label} onClick={() => addWindow(day, p)} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:6, padding:"2px 7px", fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, cursor:"pointer" }}>{p.label}</button>
              ))}
              <button onClick={() => addWindow(day, null)} style={{ background:C.cream, border:`1px solid ${C.sand}`, borderRadius:6, padding:"2px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.terra, fontWeight:600, cursor:"pointer" }}>+ Custom</button>
            </div>
          </div>
          {(avail[day] || []).length === 0 ? (
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontStyle:"italic", paddingLeft:4 }}>Unavailable</div>
          ) : (avail[day] || []).map((w, idx) => (
            <div key={idx} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <select value={w.start} onChange={e => updateWindow(day, idx, "start", e.target.value)} style={{ flex:1, padding:"5px 8px", borderRadius:6, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, background:C.cream }}>
                {HOUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>to</span>
              <select value={w.end} onChange={e => updateWindow(day, idx, "end", e.target.value)} style={{ flex:1, padding:"5px 8px", borderRadius:6, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, background:C.cream }}>
                {HOUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => removeWindow(day, idx)} style={{ background:"none", border:"none", color:C.slate, fontSize:16, cursor:"pointer", lineHeight:1, padding:"0 2px" }}>×</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

//
// CONSULTATION SCHEDULER (homeowner picks a slot from contractor's availability)
//
function ConsultationScheduler({ contractor, onBook, onCancel }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [address, setAddress] = useState("");
  const avail = contractor.availability || {};
  const DAY_KEYS = ["sun","mon","tue","wed","thu","fri","sat"];

  function getAvailableSlots(dateStr) {
    const d = new Date(dateStr + "T12:00:00");
    const dayKey = DAY_KEYS[d.getDay()];
    const windows = avail[dayKey] || [];
    const slots = [];
    windows.forEach(w => {
      let h = parseInt(w.start.split(":")[0]);
      const endH = parseInt(w.end.split(":")[0]);
      while (h < endH) {
        const ampm = h < 12 ? "am" : "pm"; const disp = (h % 12 || 12) + ":00 " + ampm;
        slots.push({ value: disp, hour: h });
        h++;
      }
    });
    return slots;
  }

  function isDayAvailable(dateStr) {
    const d = new Date(dateStr + "T12:00:00");
    const dayKey = DAY_KEYS[d.getDay()];
    return (avail[dayKey] || []).length > 0;
  }

  // Build calendar grid
  const { year, month: mo } = month;
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0];
  const monthLabel = new Date(year, mo, 1).toLocaleDateString("en-US", { month:"long", year:"numeric" });
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const slots = selectedDate ? getAvailableSlots(selectedDate) : [];

  return (
    <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.sand}`, padding:"20px 22px", marginTop:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, fontWeight:700 }}>Schedule Consultation</div>
        <button onClick={onCancel} style={{ background:"none", border:"none", fontSize:20, color:C.slate, cursor:"pointer", lineHeight:1 }}>×</button>
      </div>
      {Object.keys(avail).length === 0 || DAYS.every(d => (avail[d]||[]).length === 0) ? (
        <div style={{ textAlign:"center", padding:"24px 0", color:C.slate, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
          This contractor hasn't set their availability yet. Send them a message to coordinate a time.
        </div>
      ) : (<>
        {/* Month nav */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <button onClick={() => setMonth(m => { const d = new Date(m.year, m.month-1,1); return {year:d.getFullYear(),month:d.getMonth()}; })} style={{ background:"none", border:"none", fontSize:18, color:C.slate, cursor:"pointer" }}>‹</button>
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>{monthLabel}</span>
          <button onClick={() => setMonth(m => { const d = new Date(m.year, m.month+1,1); return {year:d.getFullYear(),month:d.getMonth()}; })} style={{ background:"none", border:"none", fontSize:18, color:C.slate, cursor:"pointer" }}>›</button>
        </div>
        {/* Day headers */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
            <div key={d} style={{ textAlign:"center", fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, fontWeight:600, padding:"2px 0" }}>{d}</div>
          ))}
        </div>
        {/* Calendar cells */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:3 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const dateStr = `${year}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const isPast = dateStr < today;
            const isAvail = !isPast && isDayAvailable(dateStr);
            const isSel = selectedDate === dateStr;
            return (
              <div key={dateStr} onClick={() => { if (isAvail) { setSelectedDate(dateStr); setSelectedSlot(null); } }}
                style={{ textAlign:"center", padding:"6px 0", borderRadius:6, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight: isAvail ? 600 : 400,
                  background: isSel ? "#2D6A4F" : isAvail ? "#D4EDDA" : "transparent",
                  color: isSel ? "#fff" : isAvail ? "#2D6A4F" : isPast ? "rgba(0,0,0,0.2)" : C.slate,
                  cursor: isAvail ? "pointer" : "default", border: isSel ? "none" : isAvail ? "1px solid #7A9E7E" : "1px solid transparent",
                }}>
                {d}
              </div>
            );
          })}
        </div>
        {/* Time slots */}
        {selectedDate && (
          <div style={{ marginTop:14 }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink, marginBottom:8 }}>
              Available times on {new Date(selectedDate+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"})}:
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {slots.map(s => (
                <button key={s.value} onClick={() => setSelectedSlot(s.value)}
                  style={{ padding:"6px 14px", borderRadius:20, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer", border:"none",
                    background: selectedSlot === s.value ? "#2D6A4F" : C.cream,
                    color: selectedSlot === s.value ? "#fff" : C.ink,
                    outline: selectedSlot === s.value ? "2px solid #2D6A4F" : `1px solid ${C.sand}`,
                  }}>{s.value}</button>
              ))}
            </div>
            {selectedSlot && (
              <div style={{ marginTop:14 }}>
                <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink, display:"block", marginBottom:5 }}>
                  Your property address <span style={{ color:C.terra }}>*</span>
                </label>
                <input
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="123 Main St, City, State ZIP"
                  style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:`1.5px solid ${address ? "#7A9E7E" : C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.cream, boxSizing:"border-box", outline:"none" }}
                />
                <button onClick={() => { if (address.trim()) onBook(selectedDate, selectedSlot, address.trim()); }}
                  disabled={!address.trim()}
                  style={{ width:"100%", marginTop:10, background: address.trim() ? "#2D6A4F" : C.sand, color: address.trim() ? "#fff" : C.slate, border:"none", borderRadius:8, padding:"12px 0", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor: address.trim() ? "pointer" : "not-allowed" }}>
                  Confirm — {new Date(selectedDate+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} at {selectedSlot}
                </button>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}

//
// CONTRACTOR CALENDAR — Google Calendar-style with events inside cells
//
function ContractorCalendar({ contractor, myBids, availDraft, setAvailDraft, availSaving, handleSaveAvailability, onViewProject }) {
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingAvail, setEditingAvail] = useState(false);
  const DAY_KEYS = ["sun","mon","tue","wed","thu","fri","sat"];
  const avail = availDraft || {};

  function fmt12(timeStr) {
    if (!timeStr) return "";
    const h = parseInt(timeStr.split(":")[0]);
    const ampm = h < 12 ? "am" : "pm";
    return (h % 12 || 12) + ampm;
  }

  // Collect booked consultations keyed by date
  const bookedByDate = {};
  (myBids || []).forEach(p => {
    (p.bids || []).forEach(b => {
      if (b.consultationBooked) {
        const cb = b.consultationBooked;
        const ds = cb.date;
        if (!bookedByDate[ds]) bookedByDate[ds] = [];
        bookedByDate[ds].push({
          time: cb.time,
          address: cb.address || null,
          projectTitle: p.title || p.room,
          projectRoom: p.room,
          projectBudget: p.budget,
          projectDescription: p.description,
          homeowner: p.homeownerName || p.homeowner || "Homeowner",
          bidAmount: b.amount,
          bidNote: b.note,
          bidDuration: b.duration,
          breakdown: b.breakdown,
          project: p,
        });
      }
    });
  });

  const { year, month: mo } = calMonth;
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0];
  const monthLabel = new Date(year, mo, 1).toLocaleDateString("en-US", { month:"long", year:"numeric" });

  function dateStr(d) { return `${year}-${String(mo+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
  function getWindows(d) {
    const key = DAY_KEYS[new Date(dateStr(d)+"T12:00:00").getDay()];
    return avail[key] || [];
  }

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selStr = selectedDay ? dateStr(selectedDay) : null;
  const selBooked = selStr ? (bookedByDate[selStr] || []) : [];
  const selWindows = selectedDay ? getWindows(selectedDay) : [];

  return (
    <div style={{ borderTop:`1px solid ${C.sand}`, marginTop:14, paddingTop:14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, color:C.ink, fontWeight:700 }}>Consultation Calendar</div>
        <button onClick={() => setEditingAvail(e => !e)} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:8, padding:"4px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>
          {editingAvail ? "Hide" : "Edit Availability"}
        </button>
      </div>

      {/* Month nav */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <button onClick={() => setCalMonth(m => { const d = new Date(m.year, m.month-1,1); return {year:d.getFullYear(),month:d.getMonth()}; })} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:C.slate, padding:"0 8px" }}>‹</button>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>{monthLabel}</div>
        <button onClick={() => setCalMonth(m => { const d = new Date(m.year, m.month+1,1); return {year:d.getFullYear(),month:d.getMonth()}; })} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:C.slate, padding:"0 8px" }}>›</button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", borderLeft:`1px solid ${C.sand}`, borderTop:`1px solid ${C.sand}` }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} style={{ textAlign:"center", fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:C.slate, padding:"5px 0", borderRight:`1px solid ${C.sand}`, borderBottom:`1px solid ${C.sand}`, background:C.cream }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid — Google Calendar style */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", borderLeft:`1px solid ${C.sand}` }}>
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} style={{ borderRight:`1px solid ${C.sand}`, borderBottom:`1px solid ${C.sand}`, minHeight:90, background:"#fafafa" }} />;
          const ds = dateStr(d);
          const isPast = ds < today;
          const booked = bookedByDate[ds] || [];
          const windows = getWindows(d);
          const isSelected = selectedDay === d;
          const isToday = ds === today;
          return (
            <div key={d} onClick={() => setSelectedDay(d === selectedDay ? null : d)}
              style={{ borderRight:`1px solid ${C.sand}`, borderBottom:`1px solid ${C.sand}`, minHeight:90, padding:"4px 3px 3px", cursor:"pointer", background: isSelected ? "#FFF8F5" : isPast ? "#fafafa" : C.white, position:"relative", transition:"background 0.1s" }}>
              {/* Day number */}
              <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:22, height:22, borderRadius:"50%", fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight: isToday ? 700 : 400, color: isToday ? "#fff" : isPast ? "#ccc" : C.ink, background: isToday ? C.terra : "transparent", marginBottom:2 }}>{d}</div>

              {/* Booked consultations — always show, red pills */}
              {booked.map((b,bi) => (
                <div key={bi} style={{ background:"#DBEAFE", borderLeft:"3px solid #3B82F6", borderRadius:"0 3px 3px 0", padding:"2px 4px", marginBottom:2, fontSize:9, fontFamily:"'DM Sans',sans-serif", color:"#1E3A8A", fontWeight:600, lineHeight:1.4, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>
                  🔒 {b.time} · {b.projectTitle}
                </div>
              ))}

              {/* Available windows — show on all days (past ones dimmed) */}
              {windows.map((w,wi) => (
                <div key={wi} style={{ background: isPast ? "#eee" : "#D4EDDA", borderLeft:`3px solid ${isPast ? "#ccc" : "#7A9E7E"}`, borderRadius:"0 3px 3px 0", padding:"2px 4px", marginBottom:2, fontSize:9, fontFamily:"'DM Sans',sans-serif", color: isPast ? "#aaa" : "#2D6A4F", fontWeight:500, lineHeight:1.4, overflow:"hidden" }}>
                  {fmt12(w.start)}–{fmt12(w.end)}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:14, marginTop:8 }}>
        {[["#D4EDDA","#7A9E7E","Available"],["#DBEAFE","#3B82F6","Booked"]].map(([bg,border,label]) => (
          <div key={label} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:12, height:10, borderRadius:"0 2px 2px 0", background:bg, borderLeft:`3px solid ${border}` }} />
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Selected day detail panel */}
      {selectedDay && (
        <div style={{ marginTop:10, background:C.white, borderRadius:10, border:`1px solid ${C.sand}`, padding:"12px 14px" }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink, marginBottom:8 }}>
            {new Date(dateStr(selectedDay)+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
          </div>
          {selBooked.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:"#1E3A8A", textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Booked Consultations</div>
              {selBooked.map((b,i) => (
                <div key={i} style={{ background:"#EFF6FF", borderRadius:10, border:"1px solid #BFDBFE", padding:"12px 14px", marginBottom:8 }}>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>🔒 {b.time} — {b.projectTitle}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:1 }}>{b.projectRoom}{b.projectBudget ? ` · ${b.projectBudget}` : ""}</div>
                    </div>
                    {b.bidAmount && <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:C.terra, flexShrink:0, marginLeft:8 }}>${b.bidAmount.toLocaleString()}</div>}
                  </div>
                  {/* Address */}
                  {b.address ? (
                    <div style={{ display:"flex", alignItems:"center", gap:6, background:"#DBEAFE", borderRadius:6, padding:"6px 10px", marginBottom:8 }}>
                      <span style={{ fontSize:14 }}>📍</span>
                      <div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:"#1E3A8A", textTransform:"uppercase", letterSpacing:0.4 }}>Property Address</div>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:"#1E40AF" }}>{b.address}</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontStyle:"italic", marginBottom:8 }}>No address provided</div>
                  )}
                  {/* Homeowner */}
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>
                    <strong style={{ color:C.ink }}>Homeowner:</strong> {b.homeowner}
                  </div>
                  {/* Duration */}
                  {b.bidDuration && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:2 }}><strong style={{ color:C.ink }}>Duration:</strong> {b.bidDuration}</div>}
                  {/* Note */}
                  {b.bidNote && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:6, lineHeight:1.5, background:C.cream, borderRadius:5, padding:"5px 8px" }}>"{b.bidNote}"</div>}
                  {/* Breakdown */}
                  {b.breakdown && (b.breakdown.labor || b.breakdown.materials || b.breakdown.permits) && (
                    <div style={{ marginTop:6, display:"flex", gap:8, flexWrap:"wrap" }}>
                      {[["Labor", b.breakdown.labor],["Materials", b.breakdown.materials],["Permits", b.breakdown.permits]].filter(([,v])=>v>0).map(([label,val]) => (
                        <div key={label} style={{ background:C.white, borderRadius:5, padding:"3px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>
                          {label}: <strong style={{ color:C.ink }}>${val.toLocaleString()}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* View full bid link */}
                  {b.project && onViewProject && (
                    <button onClick={() => onViewProject(b.project)}
                      style={{ marginTop:10, width:"100%", background:"#1E3A8A", color:"#fff", border:"none", borderRadius:7, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      View Full Project & Bid →
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {selWindows.length > 0 ? (
            <div>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, color:"#2D6A4F", textTransform:"uppercase", letterSpacing:0.5, marginBottom:5 }}>Available Windows</div>
              {selWindows.map((w,i) => (
                <div key={i} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, padding:"5px 8px", background:"#EBF7ED", borderRadius:6, marginBottom:4 }}>
                  {fmt12(w.start)} – {fmt12(w.end)}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontStyle:"italic" }}>No availability set for this day of the week</div>
          )}
        </div>
      )}

      {/* Edit weekly availability */}
      {editingAvail && (
        <div style={{ marginTop:12 }}>
          <AvailabilityManager availability={availDraft} onChange={setAvailDraft} />
          <button onClick={handleSaveAvailability} disabled={availSaving} style={{ marginTop:12, background:C.terra, color:"#fff", border:"none", borderRadius:8, padding:"9px 20px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {availSaving ? "Saving…" : "Save Availability"}
          </button>
        </div>
      )}
    </div>
  );
}

//
// PROFILE DETAILS (contractor editable profile panel)
//
function ProfileDetails({ contractor, projects, myBids, contractorReviews, updateContractor, onViewProject, jobMode, setJobMode, bidTemplates, setBidTemplates }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    licenseNo: contractor.licenseNo || "",
    specialties: (contractor.specialties || []).join(", "),
    location: contractor.location || "",
    lat: contractor.lat || null,
    lng: contractor.lng || null,
    serviceRadius: contractor.serviceRadius || 50,
  });
  const [saving, setSaving] = useState(false);
  const [showAvail, setShowAvail] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  // Derived stats from real data
  const completedJobs = projects.filter(p =>
    p.bids?.some(b => b.contractorId === contractor.id && b.status === "accepted") &&
    p.milestones?.length > 0 &&
    p.milestones.every(m => m.released)
  ).length;

  const totalBids = myBids.length;
  const acceptedBids = myBids.filter(p => p.bids?.some(b => b.contractorId === contractor.id && b.status === "accepted")).length;
  const responseRate = totalBids > 0 ? Math.round((acceptedBids / totalBids) * 100) + "%" : null;

  async function handleSave() {
    setSaving(true);
    const specialtiesArr = form.specialties.split(",").map(s => s.trim()).filter(Boolean);
    await updateContractor({
      ...contractor,
      licenseNo: form.licenseNo || null,
      specialties: specialtiesArr,
      location: form.location || null,
      lat: form.lat || null,
      lng: form.lng || null,
      serviceRadius: form.serviceRadius || 50,
    });
    setEditing(false);
    setSaving(false);
  }

  const rowStyle = { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13 };
  const labelStyle = { color:C.slate, fontWeight:500 };
  const valueStyle = { color:C.ink, fontWeight:600, textAlign:"right" };

  return (
    <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, fontWeight:700 }}>Profile Details</div>
        {!editing
          ? <button onClick={() => { setForm({ licenseNo: contractor.licenseNo || "", specialties: (contractor.specialties || []).join(", "), location: contractor.location || "", lat: contractor.lat || null, lng: contractor.lng || null, serviceRadius: contractor.serviceRadius || 50 }); setEditing(true); }} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:8, padding:"4px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>Edit</button>
          : <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => setEditing(false)} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:8, padding:"4px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background:C.clay, border:"none", borderRadius:8, padding:"4px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"#fff", fontWeight:600, cursor:"pointer" }}>{saving ? "Saving…" : "Save"}</button>
            </div>
        }
      </div>

      {editing ? (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontWeight:500 }}>License No.</label>
            <input value={form.licenseNo} onChange={e => setForm(f => ({ ...f, licenseNo: e.target.value }))} placeholder="e.g. CA-LIC-123456" style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.cream, boxSizing:"border-box" }} />
          </div>
          <div>
            <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontWeight:500 }}>Specialties <span style={{ color:C.slate, fontWeight:400 }}>(comma-separated)</span></label>
            <input value={form.specialties} onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))} placeholder="e.g. Kitchen Remodel, Bathroom, Flooring" style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.cream, boxSizing:"border-box" }} />
          </div>
          <div>
            <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontWeight:500 }}>Location</label>
            <CityAutocomplete
              value={form.location}
              onChange={(result, text) => setForm(f => ({ ...f, location: text, lat: result?.lat || null, lng: result?.lng || null }))}
              placeholder="e.g. Brooklyn, NY"
              inputStyle={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.cream, boxSizing:"border-box" }}
            />
          </div>
          <div>
            <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontWeight:500 }}>Default Search Radius</label>
            <select value={form.serviceRadius || 50} onChange={e => setForm(f => ({ ...f, serviceRadius: parseInt(e.target.value) }))} style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.cream, boxSizing:"border-box" }}>
              {[10, 25, 50, 100, 150, 200].map(r => <option key={r} value={r}>{r} miles</option>)}
            </select>
          </div>
        </div>
      ) : (
        <div>
          <div style={rowStyle}><span style={labelStyle}>License No.</span><span style={valueStyle}>{contractor.licenseNo || <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>Not set</span>}</span></div>
          <div style={rowStyle}><span style={labelStyle}>Specialties</span><span style={{ ...valueStyle, maxWidth:"60%", textAlign:"right" }}>{(contractor.specialties || []).length > 0 ? (contractor.specialties || []).join(", ") : <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>Not set</span>}</span></div>
          <div style={rowStyle}><span style={labelStyle}>Location</span><span style={valueStyle}>{contractor.location || <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>Not set</span>}</span></div>
          <div style={rowStyle}><span style={labelStyle}>Search Radius</span><span style={valueStyle}>{contractor.serviceRadius ? `${contractor.serviceRadius} mi` : <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>50 mi</span>}</span></div>
          <div style={rowStyle}><span style={labelStyle}>Response Rate</span><span style={valueStyle}>{responseRate || <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>—</span>}</span></div>
          <div style={{ ...rowStyle, borderBottom:"none" }}><span style={labelStyle}>Jobs Completed</span><span style={valueStyle}>{completedJobs > 0 ? completedJobs : <span style={{ color:C.slate, fontWeight:400, fontStyle:"italic" }}>—</span>}</span></div>
          {contractorReviews.length > 0 && (
            <div style={{ marginTop:10, padding:"10px 12px", background:C.cream, borderRadius:8, border:`1px solid ${C.sand}` }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, fontWeight:600, marginBottom:4 }}>AI Review Summary</div>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, lineHeight:1.6 }}>{contractor.reviewSummary || "No summary yet."}</div>
            </div>
          )}

          {/* Job Management Preference */}
          <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.sand}` }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, fontWeight:700, marginBottom:10 }}>Job Management Preference</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:switchingMode?10:0 }}>
              <div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>
                  {(jobMode||'payments_only') === 'full_tracking' ? 'Full Job Tracking' : 'Payments Only'}
                </div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:1 }}>
                  {(jobMode||'payments_only') === 'full_tracking' ? 'Milestone photos, notes & timeline' : 'Just payment notifications'}
                </div>
              </div>
              <button onClick={() => setSwitchingMode(m => !m)}
                style={{ background:"transparent", border:`1.5px solid ${C.sand}`, borderRadius:7, padding:"6px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.slate, cursor:"pointer" }}>
                {switchingMode ? "Cancel" : "Change"}
              </button>
            </div>
            {switchingMode && (
              <div style={{ background:C.cream, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.sand}` }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginBottom:10, lineHeight:1.5 }}>
                  {(jobMode||'payments_only') === 'full_tracking'
                    ? 'Switching to Payments Only — milestone photo history is retained but you will no longer be prompted to add photos or notes going forward.'
                    : 'Switching to Full Job Tracking — you will be prompted to upload milestone photos and progress notes for active jobs.'}
                </div>
                <button onClick={async () => {
                  const newMode = (jobMode||'payments_only') === 'full_tracking' ? 'payments_only' : 'full_tracking';
                  await updateContractor({ ...contractor, jobManagementMode: newMode });
                  if (setJobMode) setJobMode(newMode);
                  setSwitchingMode(false);
                }} style={{ background:C.terra, color:"white", border:"none", borderRadius:7, padding:"9px 18px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                  Switch to {(jobMode||'payments_only') === 'full_tracking' ? 'Payments Only' : 'Full Tracking'}
                </button>
              </div>
            )}
          </div>

          {/* Bid Templates */}
          <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.sand}` }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:showTemplateManager?10:0 }}>
              <div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, fontWeight:700 }}>Bid Templates</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:2 }}>
                  {bidTemplates?.length > 0 ? `${bidTemplates.length} saved template${bidTemplates.length>1?"s":""}` : "No templates yet"}
                </div>
              </div>
              {bidTemplates?.length > 0 && (
                <button onClick={() => setShowTemplateManager(m => !m)}
                  style={{ background:"transparent", border:`1.5px solid ${C.sand}`, borderRadius:7, padding:"6px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.slate, cursor:"pointer" }}>
                  {showTemplateManager ? "Hide" : "Manage"}
                </button>
              )}
            </div>
            {showTemplateManager && bidTemplates?.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {bidTemplates.map(t => (
                  <div key={t.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:C.cream, borderRadius:7, border:`1px solid ${C.sand}` }}>
                    <div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.ink }}>{t.name}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>{t.project_type}{t.amount?` · $${t.amount.toLocaleString()}`:""}</div>
                    </div>
                    <button onClick={async () => {
                      await supabase.from('bid_templates').delete().eq('id', t.id);
                      if (setBidTemplates) setBidTemplates(prev => prev.filter(x => x.id !== t.id));
                    }} style={{ background:"none", border:"none", color:C.red, fontSize:14, cursor:"pointer", padding:"2px 6px" }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ── LINE ITEM TOTALS — exclusive per-item logic ───────────────────────────────
// For each item: if qty×unit_cost > 0, it goes into the unit bucket only.
// Otherwise labor + material are counted. Never both, so there's no double-count.
function computeLineItemTotals(lineItems) {
  let unit = 0, labor = 0, material = 0;
  Object.values(lineItems).forEach(li => {
    const u = (parseFloat(li.quantity)||0) * (parseFloat(li.unit_cost)||0);
    if (u > 0) {
      unit += u;
    } else {
      labor    += parseFloat(li.labor_cost)||0;
      material += parseFloat(li.material_cost)||0;
    }
  });
  return { unit, labor, material };
}

// ── MARKET RANGE HELPERS ─────────────────────────────────────────────────────
function computeMarketRange(contractorScopeItems) {
  let low = 0, high = 0, productAdjustment = 0;
  contractorScopeItems.forEach(s => {
    const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
    if (!item) return;
    const qty = parseFloat(s.quantity) || 1;
    low  += (item.typical_cost_low  || 0) * qty;
    high += (item.typical_cost_high || 0) * qty;
    if (s.selectedProduct?.price) {
      productAdjustment += s.selectedProduct.price;
    }
  });
  return { low: low + productAdjustment, high: high + productAdjustment, productAdjustment };
}
function getBidPositioning(total, low, high) {
  if (!total || !low) return null;
  if (total < low * 0.9) return { label: "Below Market", color: "#E74C3C", hint: "Your bid is below the typical range — double-check your numbers." };
  if (total > high * 1.1) return { label: "Above Market", color: "#E67E22", hint: "Your bid is above the typical range — make sure your scope justifies it." };
  return { label: "Competitive", color: "#27AE60", hint: "Your bid falls within the typical range for this scope." };
}

async function saveBidTemplate(contractorId, { name, projectType, lineItems, amount, duration, note }) {
  const { data, error } = await supabase.from('bid_templates').insert({
    contractor_id: contractorId,
    name,
    project_type: projectType,
    line_items: lineItems,
    amount: amount || 0,
    duration: duration || '',
    note: note || '',
  }).select().single();
  if (error) console.error('Save template error:', error.message);
  return data;
}

// ── MARKET RANGE INDICATOR ────────────────────────────────────────────────────
function MarketRangeIndicator({ total, low, high, productAdjustment, darkMode }) {
  const pos = getBidPositioning(total, low, high);
  if (!pos || !high) return null;
  const clampedPct = Math.min(Math.max((total - low * 0.5) / (high * 1.5 - low * 0.5), 0), 1) * 100;
  return (
    <div style={{ background: darkMode ? "#1A2130" : "#F5F0E8", borderRadius:10, padding:"14px 16px", marginBottom:16, border:`1px solid ${pos.color}33` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color: darkMode?"#8A97A8":"#3D4A5C", textTransform:"uppercase", letterSpacing:0.8, fontWeight:700 }}>Market Range Check</span>
        <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color: pos.color, background:`${pos.color}18`, borderRadius:100, padding:"2px 10px" }}>{pos.label}</span>
      </div>
      <div style={{ position:"relative", height:8, borderRadius:4, background: darkMode?"#2A3A52":"#E8DFD0", overflow:"hidden", marginBottom:6 }}>
        <div style={{ position:"absolute", left:0, top:0, bottom:0, width:`${(low/high)*100}%`, background:"#E74C3C33" }} />
        <div style={{ position:"absolute", left:`${(low/high)*100}%`, top:0, bottom:0, width:`${(1-low/high)*100}%`, background:"#27AE6033" }} />
        <div style={{ position:"absolute", left:`calc(${clampedPct}% - 5px)`, top:-2, width:12, height:12, borderRadius:6, background: pos.color, border:"2px solid white", boxShadow:"0 1px 4px rgba(0,0,0,0.3)" }} />
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif", fontSize:10, color: darkMode?"#8A97A8":"#3D4A5C", marginBottom:4 }}>
        <span>Typical low: ${low.toLocaleString()}</span>
        <span>Typical high: ${high.toLocaleString()}</span>
      </div>
      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color: pos.color, fontStyle:"italic" }}>{pos.hint}</div>
      {productAdjustment > 0 && (
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color: darkMode?"#8A97A8":"#3D4A5C", marginTop:4, fontStyle:"italic" }}>
          Includes ${productAdjustment.toLocaleString()} in homeowner-selected materials
        </div>
      )}
    </div>
  );
}

// ── SAVE TEMPLATE MODAL ───────────────────────────────────────────────────────
function SaveTemplateModal({ draft, onSave, onClose, darkMode }) {
  const bg = darkMode ? "#0F1318" : "#F5F0E8";
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const PROJECT_TYPES = ["Kitchen","Bathroom","Flooring","Full Gut","Addition","Other"];
  const [name, setName] = useState(draft.name || "");
  const [projectType, setProjectType] = useState(draft.projectType || "Kitchen");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    const saved = await saveBidTemplate(draft.contractorId, { name: name.trim(), projectType, lineItems: draft.lineItems, amount: draft.amount, duration: draft.duration, note: draft.note });
    setSaving(false);
    onSave(saved);
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:card, borderRadius:14, padding:24, width:"100%", maxWidth:400, border:`1px solid ${sand}` }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:ink, fontWeight:700, marginBottom:4 }}>Save as Template?</div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginBottom:16, lineHeight:1.5 }}>Save this bid as a starting point for similar projects — load it next time to skip the repetitive work.</div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, fontWeight:600, marginBottom:4 }}>Template Name</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kitchen Remodel Standard"
            style={{ width:"100%", border:`1.5px solid ${sand}`, borderRadius:8, padding:"10px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:ink, background:bg, outline:"none", boxSizing:"border-box" }} />
        </div>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, fontWeight:600, marginBottom:4 }}>Project Type</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {PROJECT_TYPES.map(t => (
              <button key={t} onClick={() => setProjectType(t)} style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:projectType===t?700:400, color:projectType===t?"white":slate, background:projectType===t?terra:"transparent", border:`1.5px solid ${projectType===t?terra:sand}`, borderRadius:100, padding:"5px 13px", cursor:"pointer" }}>{t}</button>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={handleSave} disabled={!name.trim()||saving} style={{ flex:1, background:terra, color:"white", border:"none", borderRadius:8, padding:"12px 0", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor:name.trim()&&!saving?"pointer":"not-allowed", opacity:name.trim()&&!saving?1:0.5 }}>
            {saving ? "Saving…" : "Save Template"}
          </button>
          <button onClick={onClose} style={{ flex:1, background:"transparent", color:slate, border:`1.5px solid ${sand}`, borderRadius:8, padding:"12px 0", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, cursor:"pointer" }}>No Thanks</button>
        </div>
      </div>
    </div>
  );
}

// ── LOAD TEMPLATE DROPDOWN ────────────────────────────────────────────────────
function LoadTemplateDropdown({ templates, projectRoom, onLoad, onClose, darkMode }) {
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const clay = darkMode ? "#D4A87A" : "#C4956A";

  const matched = templates.filter(t => t.project_type === projectRoom);
  const others  = templates.filter(t => t.project_type !== projectRoom);
  const ordered = [...matched, ...others];

  return (
    <div style={{ background:card, border:`1.5px solid ${sand}`, borderRadius:10, overflow:"hidden", marginBottom:12, boxShadow:"0 4px 20px rgba(0,0,0,0.12)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:`1px solid ${sand}` }}>
        <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8 }}>Your Saved Templates</span>
        <button onClick={onClose} style={{ background:"none", border:"none", color:slate, fontSize:18, cursor:"pointer", lineHeight:1 }}>×</button>
      </div>
      {ordered.length === 0 ? (
        <div style={{ padding:"16px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, fontStyle:"italic" }}>
          No templates saved yet — fill out a bid and use "Save as Template" to create one.
        </div>
      ) : ordered.map(t => (
        <div key={t.id} onClick={() => onLoad(t)}
          style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderBottom:`1px solid ${sand}`, cursor:"pointer", transition:"background 0.15s" }}
          onMouseEnter={e => e.currentTarget.style.background = darkMode?"rgba(255,255,255,0.04)":"rgba(184,92,56,0.04)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:ink, fontWeight:600 }}>{t.name}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, marginTop:1 }}>
              {t.project_type}{t.amount ? ` · $${t.amount.toLocaleString()}` : ""}{t.duration ? ` · ${t.duration}` : ""}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {matched.includes(t) && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:terra, background:`${terra}15`, borderRadius:100, padding:"1px 7px", fontWeight:700, border:`1px solid ${terra}33` }}>Match</span>}
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:clay }}>Load →</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── JOB MODE ONBOARDING SCREEN ────────────────────────────────────────────────
function JobModeOnboardingScreen({ onSelect, darkMode }) {
  const bg = darkMode ? "rgba(0,0,0,0.85)" : "rgba(28,35,51,0.75)";
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const [selected, setSelected] = useState("payments_only");

  const options = [
    {
      key: "payments_only",
      title: "Payments Only",
      desc: "Manage my job myself — just notify me when a homeowner wants to release a payment.",
      selectable: true,
    },
    {
      key: "full_tracking",
      title: "Full Job Tracking",
      desc: "Manage milestones, upload completion photos, and track job progress inside RenoBid.",
      selectable: true,
    },
    {
      key: "coming_soon",
      title: "Coming Soon",
      desc: "Connect RenoBid to Buildertrend, Jobber, or CompanyCam.",
      selectable: false,
    },
  ];

  return (
    <div style={{ position:"fixed", inset:0, background:bg, zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:card, borderRadius:16, padding:"28px 24px", width:"100%", maxWidth:460, border:`1px solid ${sand}` }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:ink, fontWeight:700, marginBottom:6 }}>How do you want to use RenoBid?</div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:slate, marginBottom:22, lineHeight:1.5 }}>After winning a project, choose how you'd like to manage the work. You can change this any time from your profile.</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:22 }}>
          {options.map(opt => (
            <div key={opt.key}
              onClick={() => opt.selectable && setSelected(opt.key)}
              style={{ display:"flex", alignItems:"flex-start", gap:14, padding:"14px 16px", borderRadius:10, border:`2px solid ${selected===opt.key?terra:sand}`, background: selected===opt.key?`${terra}08`:"transparent", cursor:opt.selectable?"pointer":"not-allowed", opacity:opt.selectable?1:0.45, transition:"all 0.15s" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, color:selected===opt.key?terra:ink, marginBottom:3 }}>
                  {opt.title}
                  {!opt.selectable && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:slate, fontWeight:400, marginLeft:8, background:`${sand}`, borderRadius:100, padding:"1px 8px" }}>Not yet available</span>}
                </div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, lineHeight:1.5 }}>{opt.desc}</div>
              </div>
              {opt.selectable && (
                <div style={{ width:18, height:18, borderRadius:9, border:`2px solid ${selected===opt.key?terra:sand}`, background:selected===opt.key?terra:"transparent", flexShrink:0, marginTop:2, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {selected===opt.key && <div style={{ width:8, height:8, borderRadius:4, background:"white" }} />}
                </div>
              )}
            </div>
          ))}
        </div>
        <button onClick={() => onSelect(selected)}
          style={{ width:"100%", background:terra, color:"white", border:"none", borderRadius:8, padding:"13px 0", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:700, cursor:"pointer" }}>
          Set Up My Account →
        </button>
      </div>
    </div>
  );
}

// ── PAYMENTS ONLY ACTIVE JOBS ─────────────────────────────────────────────────
function PaymentsOnlyActiveJobs({ activeJobs, contractor, user, darkMode, onUpdate, onViewProject }) {
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const sage = darkMode ? "#6B9970" : "#7A9E7E";
  const gold = "#D4A843";
  const cream = darkMode ? "#12192A" : "#FAF7F2";
  const [photoPrompt, setPhotoPrompt] = useState(null); // { projectId, milestoneId }
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [coForm, setCoForm] = useState(null); // { projectId }
  const [coDescription, setCoDescription] = useState("");
  const [coAmount, setCoAmount] = useState("");
  const [coSaving, setCoSaving] = useState(false);

  async function handleMarkReady(project, milestone, files) {
    setUploading(true);
    let urls = [];
    if (files && files.length > 0) {
      for (const file of Array.from(files)) {
        const path = `${project.id}/${milestone.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
        const { error } = await supabase.storage.from('milestone-photos').upload(path, file);
        if (!error) {
          const { data: { publicUrl } } = supabase.storage.from('milestone-photos').getPublicUrl(path);
          urls.push(publicUrl);
        }
      }
    }
    const updatedMilestones = (project.milestones || []).map(m =>
      m.id === milestone.id ? { ...m, status: 'ready_for_review', photos: [...(m.photos||[]), ...urls] } : m
    );
    await supabase.from('projects').update({ milestones: updatedMilestones }).eq('id', project.id);
    onUpdate({ ...project, milestones: updatedMilestones });
    setUploading(false);
    setPhotoPrompt(null);
    setSelectedFiles([]);
  }

  if (activeJobs.length === 0) return null;

  return (
    <div>
      {activeJobs.map(p => {
        const myBid = p.bids?.find(b => b.contractorId === contractor.id);
        const activeMilestone = (p.milestones||[]).find(m => m.status === 'in_progress' || m.status === 'pending');
        const totalAmt = p.escrow?.total || myBid?.amount || 0;
        const releasedAmt = p.escrow?.released || (p.milestones||[]).filter(m=>m.status==='released').reduce((s,m)=>s+Math.round(totalAmt*m.pct/100),0);
        const remainingAmt = totalAmt - releasedAmt;
        const releasedPct = totalAmt > 0 ? Math.round((releasedAmt/totalAmt)*100) : 0;
        const homeownerFirst = (p.homeowner||p.homeowner_name||"Homeowner").split(" ")[0];
        const isPrompting = photoPrompt?.projectId === p.id && photoPrompt?.milestoneId === activeMilestone?.id;

        return (
          <div key={p.id} style={{ borderBottom:`1px solid ${sand}` }}>
            {/* Clickable header */}
            <div onClick={() => onViewProject?.(p)} style={{ padding:"14px 16px 10px", cursor: onViewProject ? "pointer" : "default" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ flex:1, minWidth:0, marginRight:10 }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, color:ink, marginBottom:2 }}>{p.title || `Project for ${homeownerFirst}`}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate }}>For {homeownerFirst}</div>
                  {activeMilestone && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:terra, marginTop:3, fontWeight:600 }}>Current: {activeMilestone.label}</div>}
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:700, color:sage }}>${totalAmt.toLocaleString()}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:slate }}>${releasedAmt.toLocaleString()} released · ${remainingAmt.toLocaleString()} remaining</div>
                </div>
              </div>
              {/* Escrow progress bar */}
              <div style={{ height:5, background:sand, borderRadius:3, overflow:"hidden", marginBottom:4 }}>
                <div style={{ height:"100%", background:`linear-gradient(90deg,${sage},${gold})`, width:`${releasedPct}%`, borderRadius:3, transition:"width 0.5s" }} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:sage }}>${releasedAmt.toLocaleString()} released</span>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:slate }}>${remainingAmt.toLocaleString()} in escrow</span>
              </div>
            </div>
            <div style={{ padding:"0 16px 14px" }}>

            {activeMilestone && activeMilestone.status !== 'ready_for_review' && !isPrompting && (
              <button onClick={() => { setPhotoPrompt({ projectId:p.id, milestoneId:activeMilestone.id }); setSelectedFiles([]); }}
                style={{ width:"100%", background:terra, color:"white", border:"none", borderRadius:8, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                Mark Ready for Review
              </button>
            )}
            {activeMilestone && activeMilestone.status === 'ready_for_review' && (
              <div style={{ textAlign:"center", padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:sage, fontWeight:600 }}>Awaiting homeowner review</div>
            )}

            {isPrompting && (
              <div style={{ background:darkMode?"rgba(255,255,255,0.04)":"rgba(184,92,56,0.04)", borderRadius:8, padding:"12px 14px", border:`1px solid ${sand}` }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:ink, marginBottom:4 }}>Add photos? (optional)</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, marginBottom:10, lineHeight:1.5 }}>Photos protect you if any questions arise. You can skip this and proceed immediately.</div>
                <input type="file" accept="image/*" multiple onChange={e => setSelectedFiles(Array.from(e.target.files).slice(0,3))}
                  style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginBottom:10, display:"block" }} />
                {selectedFiles.length > 0 && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:sage, marginBottom:8 }}>{selectedFiles.length} photo{selectedFiles.length>1?"s":""} selected</div>}
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => handleMarkReady(p, activeMilestone, selectedFiles)} disabled={uploading}
                    style={{ flex:1, background:terra, color:"white", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:uploading?"not-allowed":"pointer" }}>
                    {uploading ? "Submitting…" : (selectedFiles.length > 0 ? "Submit with Photos" : "Submit without Photos")}
                  </button>
                  <button onClick={() => setPhotoPrompt(null)}
                    style={{ flex:1, background:"transparent", color:slate, border:`1.5px solid ${sand}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Change Order */}
            {!isPrompting && (
              coForm?.projectId === p.id ? (
                <div style={{ marginTop:10, background:cream, borderRadius:10, padding:"14px", border:`1.5px solid ${gold}40` }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:ink, marginBottom:10 }}>Request Change Order</div>
                  <div style={{ marginBottom:8 }}>
                    <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate }}>Description of change</label>
                    <textarea value={coDescription} onChange={e => setCoDescription(e.target.value)} rows={2} placeholder="e.g. Additional waterproofing discovered behind tile"
                      style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:7, border:`1px solid ${sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, background:card, resize:"vertical", boxSizing:"border-box" }} />
                  </div>
                  <div style={{ marginBottom:12 }}>
                    <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate }}>Additional amount ($)</label>
                    <input type="number" value={coAmount} onChange={e => setCoAmount(e.target.value)} placeholder="0"
                      style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:7, border:`1px solid ${sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:ink, background:card, boxSizing:"border-box" }} />
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button disabled={!coDescription.trim() || !parseFloat(coAmount) || coSaving} onClick={async () => {
                      setCoSaving(true);
                      const newCO = { id:`co_${Date.now()}`, requestedBy:"contractor", description:coDescription.trim(), amount:parseFloat(coAmount), status:"pending", counterAmount:null, createdAt:new Date().toISOString().split("T")[0], resolvedAt:null };
                      const updatedCOs = [...(p.change_orders || []), newCO];
                      onUpdate({ ...p, change_orders: updatedCOs });
                      await supabase.from('projects').update({ change_orders: updatedCOs }).eq('id', p.id);
                      setCoForm(null); setCoDescription(""); setCoAmount(""); setCoSaving(false);
                    }} style={{ flex:2, background:(!coDescription.trim()||!parseFloat(coAmount)||coSaving) ? sand : terra, color:"#fff", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      {coSaving ? "Submitting…" : "Submit Change Order"}
                    </button>
                    <button onClick={() => { setCoForm(null); setCoDescription(""); setCoAmount(""); }} style={{ flex:1, background:"none", border:`1px solid ${sand}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, cursor:"pointer" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop:10 }}>
                  {/* Counter-offers from homeowner */}
                  {(p.change_orders || []).filter(co => co.status === "countered").map(co => (
                    <div key={co.id} style={{ background:`rgba(212,168,67,0.1)`, border:`1px solid ${gold}40`, borderRadius:8, padding:"10px 12px", marginBottom:8 }}>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:gold, marginBottom:4 }}>Counter-offer received</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, marginBottom:6 }}>{co.description}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginBottom:8 }}>
                        Your ask: <strong>${co.amount.toLocaleString()}</strong> → Counter: <strong>${co.counterAmount?.toLocaleString()}</strong>
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={async () => {
                          const updatedCOs = (p.change_orders || []).map(c => c.id === co.id ? { ...c, status:"approved", amount:co.counterAmount, resolvedAt:new Date().toISOString().split("T")[0] } : c);
                          const newTotal = (p.escrow?.total || myBid?.amount || 0) + co.counterAmount;
                          const newMilestone = { id:`m_co_${co.id}`, label:`Change Order: ${co.description.slice(0,40)}`, description:co.description, pct:Math.round((co.counterAmount/newTotal)*100), status:"pending" };
                          const updated = { ...p, change_orders:updatedCOs, escrow:{ ...p.escrow, total:newTotal }, milestones:[...(p.milestones||[]),newMilestone] };
                          onUpdate(updated);
                          await supabase.from('projects').update({ change_orders:updatedCOs, escrow:updated.escrow, milestones:updated.milestones }).eq('id', p.id);
                        }} style={{ flex:1, background:sage, color:"#fff", border:"none", borderRadius:7, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                          Accept ${co.counterAmount?.toLocaleString()}
                        </button>
                        <button onClick={async () => {
                          const updatedCOs = (p.change_orders || []).map(c => c.id === co.id ? { ...c, status:"withdrawn" } : c);
                          onUpdate({ ...p, change_orders: updatedCOs });
                          await supabase.from('projects').update({ change_orders:updatedCOs }).eq('id', p.id);
                        }} style={{ flex:1, background:"none", border:`1px solid #FFCCCC`, borderRadius:7, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"#C0392B", cursor:"pointer" }}>
                          Withdraw
                        </button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => { setCoForm({ projectId: p.id }); setCoDescription(""); setCoAmount(""); }}
                    style={{ background:"none", border:`1.5px dashed ${gold}`, borderRadius:7, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:gold, fontWeight:600, cursor:"pointer" }}>
                    + Request Change Order
                  </button>
                </div>
              )
            )}
          </div>
          </div>
        );
      })}
    </div>
  );
}

// ── FULL TRACKING ACTIVE JOB ──────────────────────────────────────────────────
function FullTrackingActiveJob({ project, contractor, darkMode, onUpdate, onViewProject }) {
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const sage = darkMode ? "#6B9970" : "#7A9E7E";
  const gold = darkMode ? "#D4A843" : "#D4A843";
  const red = darkMode ? "#E74C3C" : "#C0392B";

  const myBid = project.bids?.find(b => b.contractorId === contractor.id);
  const milestones = project.milestones || [];
  // Auto-expand the current active milestone so action buttons are immediately visible
  const firstActiveMs = milestones.find(m => m.status === 'in_progress') || milestones.find(m => m.status === 'pending');
  const [activeMsId, setActiveMsId] = useState(firstActiveMs?.id || null);
  const [photoFiles, setPhotoFiles] = useState({});
  const [progressNotes, setProgressNotes] = useState({});
  const [prepNotes, setPrepNotes] = useState({});
  const [uploading, setUploading] = useState(false);
  const [coForm, setCoForm] = useState(false);
  const [coDescription, setCoDescription] = useState("");
  const [coAmount, setCoAmount] = useState("");
  const [coSaving, setCoSaving] = useState(false);
  const cream = darkMode ? "#12192A" : "#FAF7F2";

  const releasedAmt = milestones.filter(m=>m.status==='released').reduce((s,m)=>s+Math.round((project.escrow?.total||myBid?.amount||0)*m.pct/100),0);
  const totalAmt = project.escrow?.total || myBid?.amount || 0;
  const remainingAmt = totalAmt - (project.escrow?.released || releasedAmt);

  // Projected completion from start + duration
  let projectedEnd = null;
  if (myBid?.startDate && myBid?.duration) {
    const start = new Date(myBid.startDate + "T12:00:00");
    const weeks = parseFloat(myBid.duration) || 3;
    start.setDate(start.getDate() + weeks * 7);
    projectedEnd = start.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
  }

  async function handleStartWork(ms) {
    const updated = milestones.map(m => m.id===ms.id ? {...m,status:'in_progress'} : m);
    await supabase.from('projects').update({milestones:updated}).eq('id',project.id);
    onUpdate({...project,milestones:updated});
  }

  async function handleSubmitForReview(ms) {
    const files = photoFiles[ms.id] || [];
    if (!files.length) return;
    setUploading(true);
    let urls = [];
    for (const file of files) {
      const path = `${project.id}/${ms.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const { error } = await supabase.storage.from('milestone-photos').upload(path, file);
      if (!error) {
        const { data:{publicUrl} } = supabase.storage.from('milestone-photos').getPublicUrl(path);
        urls.push(publicUrl);
      }
    }
    const note = progressNotes[ms.id] || '';
    const updated = milestones.map(m => m.id===ms.id ? {...m, status:'ready_for_review', photos:[...(m.photos||[]),...urls], progress_note:note} : m);
    await supabase.from('projects').update({milestones:updated}).eq('id',project.id);
    onUpdate({...project,milestones:updated});
    setUploading(false);
    setPhotoFiles(f => ({...f,[ms.id]:[]}));
  }

  const statusColor = { pending:slate, in_progress:gold, ready_for_review:terra, released:sage, disputed:red };
  const statusLabel = { pending:"Upcoming", in_progress:"In Progress", ready_for_review:"Awaiting Review", released:"Approved", disputed:"Disputed" };

  const homeownerFirst = (project.homeowner||project.homeowner_name||"Homeowner").split(" ")[0];

  return (
    <div style={{ borderBottom:`1px solid ${sand}` }}>
      {/* Project header — clickable to view project detail */}
      <div onClick={() => onViewProject?.(project)} style={{ padding:"14px 16px 10px", cursor: onViewProject ? "pointer" : "default" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
          <div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, color:ink }}>{project.title || `Project for ${homeownerFirst}`}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginTop:1 }}>
              For {homeownerFirst}{projectedEnd ? ` · Est. complete ${projectedEnd}` : ""}
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:13, fontWeight:700, color:sage }}>${totalAmt.toLocaleString()}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:slate }}>${releasedAmt.toLocaleString()} released · ${remainingAmt.toLocaleString()} remaining</div>
          </div>
        </div>
        {/* Escrow progress bar */}
        <div style={{ height:5, background:sand, borderRadius:3, overflow:"hidden", marginBottom:4 }}>
          <div style={{ height:"100%", background:`linear-gradient(90deg,${sage},${gold})`, width:`${totalAmt>0?Math.round((releasedAmt/totalAmt)*100):0}%`, borderRadius:3, transition:"width 0.5s" }} />
        </div>
        <div style={{ display:"flex", justifyContent:"space-between" }}>
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:sage }}>${releasedAmt.toLocaleString()} released</span>
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:slate }}>${remainingAmt.toLocaleString()} in escrow</span>
        </div>
      </div>

      {/* Milestones */}
      {milestones.map((ms, idx) => {
        const msAmt = Math.round(totalAmt * ms.pct / 100);
        const isActive = activeMsId === ms.id;
        const files = photoFiles[ms.id] || [];
        const canSubmit = files.length > 0;

        return (
          <div key={ms.id} style={{ borderBottom:idx<milestones.length-1?`1px solid ${sand}`:"none" }}>
            {/* Milestone header row */}
            <div onClick={() => setActiveMsId(isActive?null:ms.id)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", cursor:ms.status!=="released"?"pointer":"default", background: ms.status==='in_progress'?`${terra}06`:"transparent" }}>
              <div style={{ width:28, height:28, borderRadius:14, background:ms.status==='released'?sage:ms.status==='in_progress'?terra:ms.status==='ready_for_review'?`${terra}66`:sand, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ color:"white", fontSize:11, fontWeight:700 }}>
                  {ms.status==='released'?"✓":ms.status==='disputed'?"!":String(idx+1)}
                </span>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:ink }}>{ms.label}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:statusColor[ms.status]||slate, marginTop:1, fontWeight:600 }}>{statusLabel[ms.status]||ms.status} · ${msAmt.toLocaleString()}</div>
              </div>
              {ms.status !== 'released' && ms.status !== 'disputed' && <span style={{ color:slate, fontSize:12 }}>{isActive?"▲":"▼"}</span>}
            </div>

            {/* Expanded action area */}
            {isActive && (
              <div style={{ padding:"12px 16px 14px 16px", background:darkMode?"rgba(255,255,255,0.02)":"rgba(184,92,56,0.02)", borderTop:`1px solid ${sand}` }}>
                {ms.status === 'released' && (
                  <div>
                    {ms.photos?.length > 0 && (
                      <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
                        {ms.photos.map((url,i) => <img key={i} src={url} alt="" style={{ width:64, height:64, objectFit:"cover", borderRadius:6 }} />)}
                      </div>
                    )}
                    {ms.progress_note && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, lineHeight:1.5, marginBottom:4 }}>"{ms.progress_note}"</div>}
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:sage }}>Payment released by homeowner</div>
                  </div>
                )}
                {ms.status === 'pending' && (
                  <div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, marginBottom:8 }}>Preparation notes (visible only to you)</div>
                    <textarea value={prepNotes[ms.id]||""} onChange={e=>setPrepNotes(n=>({...n,[ms.id]:e.target.value}))} placeholder="What do you need to prepare or order for this milestone?" rows={2}
                      style={{ width:"100%", border:`1px solid ${sand}`, borderRadius:6, padding:"7px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, background:card, resize:"vertical", outline:"none", boxSizing:"border-box", marginBottom:8 }} />
                    <button onClick={() => handleStartWork(ms)}
                      style={{ background:terra, color:"white", border:"none", borderRadius:7, padding:"9px 18px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      Start Work on This Milestone
                    </button>
                  </div>
                )}
                {ms.status === 'in_progress' && (
                  <div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate, marginBottom:6 }}>Progress note</div>
                    <textarea value={progressNotes[ms.id]||""} onChange={e=>setProgressNotes(n=>({...n,[ms.id]:e.target.value}))} placeholder="Describe what was completed..." rows={2}
                      style={{ width:"100%", border:`1px solid ${sand}`, borderRadius:6, padding:"7px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, background:card, resize:"vertical", outline:"none", boxSizing:"border-box", marginBottom:8 }} />
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:terra, marginBottom:6, fontWeight:600 }}>At least 1 photo required to submit</div>
                    <input type="file" accept="image/*" multiple onChange={e=>setPhotoFiles(f=>({...f,[ms.id]:Array.from(e.target.files)}))} style={{ marginBottom:8, display:"block", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate }} />
                    {files.length > 0 && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:sage, marginBottom:8 }}>{files.length} photo{files.length>1?"s":""} ready</div>}
                    <button onClick={() => handleSubmitForReview(ms)} disabled={!canSubmit||uploading}
                      style={{ width:"100%", background:canSubmit&&!uploading?terra:sand, color:canSubmit&&!uploading?"white":slate, border:"none", borderRadius:7, padding:"10px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:canSubmit&&!uploading?"pointer":"not-allowed" }}>
                      {uploading ? "Uploading…" : "Submit for Homeowner Review"}
                    </button>
                  </div>
                )}
                {ms.status === 'ready_for_review' && (
                  <div>
                    {ms.photos?.length > 0 && <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>{ms.photos.map((url,i)=><img key={i} src={url} alt="" style={{ width:56,height:56,objectFit:"cover",borderRadius:6 }} />)}</div>}
                    {ms.progress_note && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginBottom:4 }}>"{ms.progress_note}"</div>}
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:terra, fontWeight:600 }}>Waiting for homeowner to approve payment</div>
                  </div>
                )}
                {ms.status === 'disputed' && (
                  <div style={{ background:`${red}10`, borderRadius:8, padding:"10px 12px", border:`1px solid ${red}33` }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:red, fontWeight:700 }}>Milestone disputed — contact support or message the homeowner to resolve.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Change Order section */}
      <div style={{ padding:"12px 16px", borderTop:`1px solid ${sand}` }}>
        {coForm ? (
          <div style={{ background:cream, borderRadius:10, padding:"14px", border:`1.5px solid ${gold}40` }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:ink, marginBottom:10 }}>Request Change Order</div>
            <div style={{ marginBottom:8 }}>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate }}>Description of change</label>
              <textarea value={coDescription} onChange={e => setCoDescription(e.target.value)} rows={2} placeholder="e.g. Additional waterproofing discovered behind tile"
                style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:7, border:`1px solid ${sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, background:card, resize:"vertical", boxSizing:"border-box" }} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:slate }}>Additional amount ($)</label>
              <input type="number" value={coAmount} onChange={e => setCoAmount(e.target.value)} placeholder="0"
                style={{ display:"block", width:"100%", marginTop:4, padding:"8px 10px", borderRadius:7, border:`1px solid ${sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:ink, background:card, boxSizing:"border-box" }} />
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button disabled={!coDescription.trim() || !parseFloat(coAmount) || coSaving} onClick={async () => {
                setCoSaving(true);
                const newCO = { id:`co_${Date.now()}`, requestedBy:"contractor", description:coDescription.trim(), amount:parseFloat(coAmount), status:"pending", counterAmount:null, createdAt:new Date().toISOString().split("T")[0], resolvedAt:null };
                const updatedCOs = [...(project.change_orders || []), newCO];
                onUpdate({ ...project, change_orders: updatedCOs });
                await supabase.from('projects').update({ change_orders: updatedCOs }).eq('id', project.id);
                setCoForm(false); setCoDescription(""); setCoAmount(""); setCoSaving(false);
              }} style={{ flex:2, background:(!coDescription.trim()||!parseFloat(coAmount)||coSaving)?sand:terra, color:"#fff", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                {coSaving ? "Submitting…" : "Submit Change Order"}
              </button>
              <button onClick={() => { setCoForm(false); setCoDescription(""); setCoAmount(""); }} style={{ flex:1, background:"none", border:`1px solid ${sand}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            {/* Counter-offers from homeowner */}
            {(project.change_orders || []).filter(co => co.status === "countered").map(co => (
              <div key={co.id} style={{ background:`rgba(212,168,67,0.1)`, border:`1px solid ${gold}40`, borderRadius:8, padding:"10px 12px", marginBottom:8 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:gold, marginBottom:4 }}>Counter-offer received</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:ink, marginBottom:6 }}>{co.description}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:slate, marginBottom:8 }}>
                  Your ask: <strong>${co.amount.toLocaleString()}</strong> → Counter: <strong>${co.counterAmount?.toLocaleString()}</strong>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={async () => {
                    const updatedCOs = (project.change_orders || []).map(c => c.id === co.id ? { ...c, status:"approved", amount:co.counterAmount, resolvedAt:new Date().toISOString().split("T")[0] } : c);
                    const newTotal = totalAmt + co.counterAmount;
                    const newMilestone = { id:`m_co_${co.id}`, label:`Change Order: ${co.description.slice(0,40)}`, description:co.description, pct:Math.round((co.counterAmount/newTotal)*100), status:"pending" };
                    const updated = { ...project, change_orders:updatedCOs, escrow:{ ...project.escrow, total:newTotal }, milestones:[...milestones, newMilestone] };
                    onUpdate(updated);
                    await supabase.from('projects').update({ change_orders:updatedCOs, escrow:updated.escrow, milestones:updated.milestones }).eq('id', project.id);
                  }} style={{ flex:1, background:sage, color:"#fff", border:"none", borderRadius:7, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    Accept ${co.counterAmount?.toLocaleString()}
                  </button>
                  <button onClick={async () => {
                    const updatedCOs = (project.change_orders || []).map(c => c.id === co.id ? { ...c, status:"withdrawn" } : c);
                    onUpdate({ ...project, change_orders: updatedCOs });
                    await supabase.from('projects').update({ change_orders:updatedCOs }).eq('id', project.id);
                  }} style={{ flex:1, background:"none", border:`1px solid #FFCCCC`, borderRadius:7, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"#C0392B", cursor:"pointer" }}>
                    Withdraw
                  </button>
                </div>
              </div>
            ))}
            <button onClick={() => { setCoForm(true); setCoDescription(""); setCoAmount(""); }}
              style={{ background:"none", border:`1.5px dashed ${gold}`, borderRadius:7, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:gold, fontWeight:600, cursor:"pointer" }}>
              + Request Change Order
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── UPGRADE PROMPT MODAL ──────────────────────────────────────────────────────
function UpgradePromptModal({ project, onEnableProject, onEnableAll, onDismiss, darkMode }) {
  const card = darkMode ? "#1A2130" : "#FDFCFA";
  const ink = darkMode ? "#EDE8E0" : "#1C2333";
  const slate = darkMode ? "#8A97A8" : "#3D4A5C";
  const sand = darkMode ? "#1E2535" : "#E8DFD0";
  const terra = darkMode ? "#D4744A" : "#B85C38";
  const myBid = project.bids?.find(b => b.status === "accepted");

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:490, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:card, borderRadius:14, padding:"22px 22px 20px", width:"100%", maxWidth:400, border:`1px solid ${sand}` }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, color:ink, fontWeight:700, marginBottom:6 }}>
          Larger project — consider full tracking
        </div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:slate, marginBottom:4, lineHeight:1.5 }}>
          <strong style={{ color:ink }}>{project.title}</strong> — {myBid ? `$${myBid.amount.toLocaleString()}` : "large budget"}
        </div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:slate, marginBottom:18, lineHeight:1.5 }}>
          Full job tracking with milestone photos builds your verified profile and protects you if any questions arise. Would you like to enable it?
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <button onClick={onEnableProject}
            style={{ background:terra, color:"white", border:"none", borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Enable for this project only
          </button>
          <button onClick={onEnableAll}
            style={{ background:"transparent", color:terra, border:`1.5px solid ${terra}`, borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            Switch to full tracking for all projects
          </button>
          <button onClick={onDismiss}
            style={{ background:"transparent", color:slate, border:"none", borderRadius:8, padding:"8px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, cursor:"pointer" }}>
            No thanks, keep Payments Only
          </button>
        </div>
      </div>
    </div>
  );
}

//
// CONTRACTOR APP
//
function ContractorApp({ projects, updateProjects, contractor, updateContractor, darkMode, toggleDark, user, onLogout }) {
  const [view, setView] = useState("dashboard"); // dashboard | browse | project | bid | messages
  const [activeProject, setActiveProject] = useState(null);
  const [bidForm, setBidForm] = useState({ amount:"", startDate:"", duration:"3 weeks", note:"", laborCost:"", materialCost:"", permitCost:"", acknowledged:false, needsConsultation:false, lineItems:{} });
  const [msgInput, setMsgInput] = useState("");
  const [filter, setFilter] = useState("All");
  const [dashSection, setDashSection] = useState(null); // "bids" | "active" | "reviews" | null
  const [contractorReviews, setContractorReviews] = useState([]);
  const [editingBid, setEditingBid] = useState(null); // { projectId, bidId, labor, materials, permits, note }
  const [reviseSaving, setReviseSaving] = useState(false);
  const msgEndRef = useRef(null);
  const [availDraft, setAvailDraft] = useState(contractor.availability || {});
  const [availSaving, setAvailSaving] = useState(false);
  useEffect(() => { setAvailDraft(contractor.availability || {}); }, [contractor.availability]);

  // Job management mode state
  const [bidTemplates, setBidTemplates] = useState([]);
  const [templateSaveModal, setTemplateSaveModal] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [jobMode, setJobMode] = useState(contractor.jobManagementMode || null);
  const [showModeOnboarding, setShowModeOnboarding] = useState(false);
  const [upgradePromptProject, setUpgradePromptProject] = useState(null);
  const [upgradePromptDismissed, setUpgradePromptDismissed] = useState(new Set());
  async function handleSaveAvailability() {
    setAvailSaving(true);
    await updateContractor({ ...contractor, availability: availDraft });
    setAvailSaving(false);
  }

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [activeProject?.messages?.length]);

  useEffect(() => {
    if (contractor.id && contractor.id !== "c1") {
      loadReviews(contractor.id).then(setContractorReviews);
    }
  }, [contractor.id]);

  // Sync jobMode when contractor profile loads/changes
  useEffect(() => {
    setJobMode(contractor.jobManagementMode || null);
    setShowModeOnboarding(!contractor.jobManagementMode);
    if (contractor.jobManagementMode === 'full_tracking') setDashSection('active');
  }, [contractor.jobManagementMode]);

  // Load bid templates
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('bid_templates').select('*').eq('contractor_id', user.id).order('created_at', { ascending: false })
      .then(({ data }) => setBidTemplates(data || []));
  }, [user?.id]);

  // Upgrade prompt for payments_only contractors with >$25k active job
  useEffect(() => {
    const effMode = jobMode || 'payments_only';
    if (effMode !== 'payments_only') return;
    const activeJobs = projects.filter(p => p.bids?.some(b => b.contractorId === contractor.id && b.status === 'accepted'));
    for (const p of activeJobs) {
      const myBid = p.bids?.find(b => b.contractorId === contractor.id && b.status === 'accepted');
      if (myBid && myBid.amount > 25000 && !p.upgrade_prompt_dismissed && !upgradePromptDismissed.has(p.id)) {
        setUpgradePromptProject(p);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, jobMode]);

  const myBids = projects.filter(p => p.bids?.some(b => b.contractorId === contractor.id));
  const activeJobs = projects.filter(p => p.bids?.some(b => b.contractorId === contractor.id && b.status === "accepted"));
  const openProjects = projects.filter(p => p.status === "open" && !p.bids?.some(b => b.contractorId === contractor.id));

  // Date alignment filter — only show projects where homeowner start >= contractor's available date
  // "flexible" projects (no desiredStart) always show
  const [myAvailableDate, setMyAvailableDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7); // default: available in 1 week
    return d.toISOString().split("T")[0];
  });
  const [showOnlyAligned, setShowOnlyAligned] = useState(false);
  const [browseRadius, setBrowseRadius] = useState(contractor.serviceRadius || 50);

  // Keep browseRadius in sync when contractor profile loads
  useEffect(() => { setBrowseRadius(contractor.serviceRadius || 50); }, [contractor.serviceRadius]);

  const dateFilteredProjects = showOnlyAligned
    ? openProjects.filter(p => {
        if (!p.desiredStart) return true; // flexible — always show
        return new Date(myAvailableDate) <= new Date(p.desiredStart + "T12:00:00");
      })
    : openProjects;

  const radiusFilteredProjects = dateFilteredProjects.filter(p => {
    if (!contractor.lat || !contractor.lng) return true; // contractor has no location → show all
    if (browseRadius >= 9999) return true;               // "Any distance" → show all
    if (!p.lat || !p.lng) return true;                   // project has no location → show it
    return haversineDistance(contractor.lat, contractor.lng, p.lat, p.lng) <= browseRadius;
  });

  const filteredOpen = filter === "All"
    ? radiusFilteredProjects
    : radiusFilteredProjects.filter(p => p.room === filter);

  // Count projects filtered out by date
  const dateFilteredOut = openProjects.filter(p => {
    if (!p.desiredStart) return false;
    return new Date(myAvailableDate) > new Date(p.desiredStart + "T12:00:00");
  }).length;

  const submitBid = async () => {
    const permits = parseInt(bidForm.permitCost||0);
    const hasLineItems = activeProject?.scope?.some(s=>s.by==="contractor");
    const lineLabor = Object.values(bidForm.lineItems).reduce((s,li)=>s+(parseFloat(li.labor_cost)||0),0);
    const lineMat   = Object.values(bidForm.lineItems).reduce((s,li)=>s+(parseFloat(li.material_cost)||0),0);
    const lineUnit  = Object.values(bidForm.lineItems).reduce((s,li)=>s+(parseFloat(li.quantity)||0)*(parseFloat(li.unit_cost)||0),0);
    const labor    = hasLineItems && lineLabor > 0 ? lineLabor : parseInt(bidForm.laborCost||0);
    const materials = hasLineItems && lineMat > 0  ? lineMat   : parseInt(bidForm.materialCost||0);
    const total = labor + materials + lineUnit + permits;
    if (!total || !bidForm.acknowledged) return;
    const newBid = {
      id: `b${Date.now()}`, contractorId: contractor.id,
      company: contractor.company, avatar: "AR",
      rating: contractor.rating, reviews: contractor.reviews,
      amount: total, startDate: bidForm.startDate, duration: bidForm.duration,
      note: bidForm.note, badge: "New Bid", status: "pending",
      breakdown: { labor, materials, permits, other: Math.max(0, total - labor - materials - permits) },
      acknowledged: true,
      needsConsultation: bidForm.needsConsultation,
      lineItems: bidForm.lineItems,
      consultationBooked: null,
      consultationComplete: false,
      contractorAvailability: bidForm.needsConsultation ? (contractor.availability || {}) : null,
    };
    // Save to Supabase
    if (user?.id) {
      try {
        const { data, error } = await supabase.from('bids').insert({
          project_id: activeProject.id,
          contractor_id: user.id,
          amount: total,
          timeline: bidForm.duration,
          note: bidForm.note,
          status: 'pending',
          needs_consultation: bidForm.needsConsultation,
          contractor_availability: bidForm.needsConsultation ? (contractor.availability || null) : null,
          line_items: bidForm.lineItems, // line_items JSONB column required on bids table
        }).select().single();
        if (error) console.error('Submit bid DB error:', error.message, error);
        if (data) newBid.id = data.id;
      } catch (err) { console.error('Submit bid error:', err); }
    }
    const updated = projects.map(p => p.id === activeProject.id ? { ...p, bids: [...(p.bids||[]), newBid] } : p);
    updateProjects(updated);
    updateContractor({ ...contractor, pendingBids: contractor.pendingBids + 1 });
    setActiveProject(updated.find(p => p.id === activeProject.id));
    const savedLineItems = bidForm.lineItems;
    const savedDuration = bidForm.duration;
    const savedNote = bidForm.note;
    const savedRoom = activeProject?.room;
    setBidForm({ amount:"", startDate:"", duration:"3 weeks", note:"", laborCost:"", materialCost:"", permitCost:"", acknowledged:false, needsConsultation:false, lineItems:{} });
    setTemplateSaveModal({ contractorId: user?.id, name: `${savedRoom || "Project"} Remodel`, projectType: savedRoom || "Kitchen", lineItems: savedLineItems, amount: total, duration: savedDuration, note: savedNote });
    setView("browse");
  };

  const sendMessage = async (projectId) => {
    if (!msgInput.trim()) return;
    const project = projects.find(p => p.id === projectId);
    const updated = projects.map(p => p.id === projectId ? {
      ...p, messages: [...(p.messages||[]), { from:"contractor", text:msgInput.trim(), time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}), sender:contractor.company }]
    } : p);
    // Persist to Supabase
    if (user?.id) {
      try {
        const { error } = await supabase.from('messages').insert({
          project_id: projectId,
          sender_id: user.id,
          recipient_id: project?.homeowner_id || null,
          content: msgInput.trim(),
          read: false,
        });
        if (error) console.error('Send message DB error:', error.message, error);
      } catch (err) { console.error('Send message error:', err); }
    }
    updateProjects(updated);
    setActiveProject(updated.find(p => p.id === projectId));
    setMsgInput("");
  };

  const reviseConsultationBid = async () => {
    if (!editingBid) return;
    setReviseSaving(true);
    const labor = parseInt(editingBid.labor||0);
    const materials = parseInt(editingBid.materials||0);
    const permits = parseInt(editingBid.permits||0);
    const newTotal = labor + materials + permits;
    if (!newTotal) { setReviseSaving(false); return; }
    const updated = projects.map(p => p.id === editingBid.projectId ? {
      ...p, bids: p.bids.map(b => b.id === editingBid.bidId ? {
        ...b, amount: newTotal, note: editingBid.note,
        breakdown: { labor, materials, permits, other: 0 },
        needsConsultation: false, consultationComplete: true,
      } : b)
    } : p);
    // Persist to Supabase
    if (user?.id) {
      try {
        await supabase.from('bids').update({
          amount: newTotal, note: editingBid.note,
          needs_consultation: false, consultation_complete: true,
        }).eq('id', editingBid.bidId);
      } catch {}
    }
    updateProjects(updated);
    setEditingBid(null);
    setReviseSaving(false);
  };

  const rooms = ["All",...new Set(projects.map(p=>p.room))];

  const Nav = () => (
    <nav style={{ background:C.white, borderBottom:`1px solid ${C.sand}`, padding:"0 16px", height:50, display:"flex", alignItems:"center", flexShrink:0 }}>
      {/* Left — Logo + back button */}
      <div style={{ flex:1, display:"flex", alignItems:"center", gap:10 }}>
        <button onClick={() => setView("dashboard")} style={{ background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:C.ink }}>
          Reno<span style={{ color:C.terra }}>Bid</span>
          <span style={{ fontSize:11, color:C.slate, fontWeight:400, marginLeft:4 }}>Pro</span>
        </button>
        {(view === "project" || view === "bid") && (
          <button onClick={() => setView("browse")} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"3px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            ← Back
          </button>
        )}
      </div>

      {/* Center — Tabs */}
      <div style={{ display:"flex", alignItems:"center", gap:0 }}>
        {[["dashboard","Dashboard"],["browse","Browse"],["messages","Messages"]].map(([v,label]) => (
          <button key={v} onClick={() => setView(v)} style={{
            background:"none", color: view===v ? C.terra : C.slate,
            border:"none", borderBottom: view===v ? `2px solid ${C.terra}` : "2px solid transparent",
            borderRadius:0, padding:"0 12px", height:50,
            fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight: view===v ? 700 : 400,
            cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap",
          }}>{label}</button>
        ))}
      </div>

      {/* Right — Dark toggle + Logout */}
      <div style={{ flex:1, display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button onClick={toggleDark} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"3px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, cursor:"pointer" }}>
          {darkMode ? "Light" : "Dark"}
        </button>
        <button onClick={onLogout} style={{ background:"none", border:`1px solid ${C.sand}`, borderRadius:100, padding:"3px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, cursor:"pointer" }}>
          Log out
        </button>
      </div>
    </nav>
  );

  //  CONTRACTOR DASHBOARD
  if (view === "dashboard") return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav />

      {/* Job Mode Onboarding Overlay */}
      {showModeOnboarding && (
        <JobModeOnboardingScreen darkMode={darkMode} onSelect={async (mode) => {
          await updateContractor({ ...contractor, jobManagementMode: mode });
          setJobMode(mode);
          setShowModeOnboarding(false);
          if (mode === 'full_tracking') setDashSection('active');
        }} />
      )}

      {/* Upgrade Prompt Overlay */}
      {upgradePromptProject && !showModeOnboarding && (
        <UpgradePromptModal darkMode={darkMode}
          project={upgradePromptProject}
          onEnableProject={async () => {
            setUpgradePromptDismissed(s => new Set([...s, upgradePromptProject.id]));
            await supabase.from('projects').update({ upgrade_prompt_dismissed: true }).eq('id', upgradePromptProject.id);
            setUpgradePromptProject(null);
          }}
          onEnableAll={async () => {
            await updateContractor({ ...contractor, jobManagementMode: 'full_tracking' });
            setJobMode('full_tracking');
            setDashSection('active');
            setUpgradePromptProject(null);
          }}
          onDismiss={() => {
            setUpgradePromptDismissed(s => new Set([...s, upgradePromptProject.id]));
            setUpgradePromptProject(null);
          }} />
      )}

      {/* Save Template Modal */}
      {templateSaveModal && (
        <SaveTemplateModal darkMode={darkMode}
          draft={templateSaveModal}
          onSave={(saved) => { if (saved) setBidTemplates(prev => [saved, ...prev]); setTemplateSaveModal(null); }}
          onClose={() => setTemplateSaveModal(null)} />
      )}

    <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 32px", maxWidth:1200, margin:"0 auto", width:"100%" }} className="fade-in">

        {/* Profile header */}
        <div style={{ display:"flex", alignItems:"center", gap:14, background:C.white, borderRadius:14, padding:"16px 18px", marginBottom:14, border:`1px solid ${C.sand}` }}><div style={{ width:52, height:52, borderRadius:"50%", background:`linear-gradient(135deg,${C.terra},#1E40AF)`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:17, color:C.ink, flexShrink:0 }}>
            {contractor.company.split("").map(w=>w[0]).join("").slice(0,2)}
          </div><div style={{ flex:1, minWidth:0 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, fontWeight:700, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{contractor.company}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:2 }}>{contractor.location} · Since {contractor.joined}</div></div>
          {contractor.verified && (
            <span style={{ background:"rgba(39,174,96,0.15)", color:C.green, border:`1px solid rgba(39,174,96,0.3)`, borderRadius:100, padding:"4px 10px", fontSize:11, fontFamily:"'DM Sans',sans-serif", fontWeight:600, flexShrink:0 }}> Verified</span>
          )}
        </div>

        {/* 2-column layout: left = stats+calendar+panels, right = payments+profile */}
        <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
        <div style={{ flex:1, minWidth:0 }}>

        {/* Stats — 4-col row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10, marginBottom:14 }}>
          {[
            { label:"Rating", val: contractorReviews.length ? (contractorReviews.reduce((s,r)=>s+r.rating,0)/contractorReviews.length).toFixed(1) : "—", icon:"", color:C.gold, section:"reviews" },
            { label:"Open Bids", val:myBids.filter(p => p.bids?.find(b => b.contractorId === contractor.id)?.status !== "accepted").length, icon:"", color:C.gold, section:"bids" },
            { label:"Active Jobs", val:activeJobs.length, icon:"", color:C.sage, section:"active" },
            { label:"Reviews", val:contractorReviews.length, icon:"", color:C.terra, section:"reviews" },
          ].map(s => (
            <div key={s.label} onClick={() => s.section && setDashSection(d => d === s.section ? null : s.section)}
              style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${dashSection===s.section ? C.terra : C.sand}`, cursor: s.section ? "pointer" : "default", transition:"border-color 0.15s" }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:s.color, lineHeight:1 }}>{s.val}</div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>{s.label}</span>
                {s.section && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.terra }}>{dashSection===s.section ? "▲" : "▼"}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Expanded: Open Bids */}
        {dashSection === "bids" && (
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.sand}`, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>Open Bids</div>
            {myBids.filter(p => p.bids?.find(b => b.contractorId === contractor.id)?.status !== "accepted").length === 0 ? (
              <div style={{ padding:24, textAlign:"center", color:C.slate, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>No open bids</div>
            ) : myBids.filter(p => p.bids?.find(b => b.contractorId === contractor.id)?.status !== "accepted").map(p => {
              const myBid = p.bids.find(b => b.contractorId === contractor.id);
              const hasBookedConsult = myBid?.needsConsultation && myBid?.consultationBooked && !myBid?.consultationComplete;
              const isEditing = editingBid?.bidId === myBid?.id;
              return (
                <div key={p.id} style={{ borderBottom:`1px solid ${C.sand}` }}>
                  <div onClick={() => !isEditing && (() => { setActiveProject(p); setView("project"); })()} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", cursor: isEditing ? "default" : "pointer" }}>
                    <div style={{ flex:1, minWidth:0, marginRight:12 }}>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.title}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:2 }}>{p.room} · {p.homeowner}</div>
                      {hasBookedConsult && (
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, color:C.gold, marginTop:3 }}>
                          Consultation: {new Date(myBid.consultationBooked.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} at {myBid.consultationBooked.time}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, color: myBid?.status==="accepted" ? C.sage : myBid?.consultationComplete ? C.terra : C.gold }}>${(myBid?.amount||0).toLocaleString()}</div>
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, marginTop:2, color: myBid?.status==="accepted" ? C.green : C.slate, fontWeight:600 }}>
                        {myBid?.status==="accepted" ? "✓ Accepted" : myBid?.consultationComplete ? "Revised" : "Pending"}
                      </div>
                    </div>
                  </div>
                  {/* Consult action row */}
                  {hasBookedConsult && !isEditing && (
                    <div style={{ padding:"0 16px 12px" }}>
                      <button onClick={() => setEditingBid({ projectId: p.id, bidId: myBid.id, labor: String(myBid.breakdown?.labor||myBid.amount||""), materials: String(myBid.breakdown?.materials||""), permits: String(myBid.breakdown?.permits||""), note: myBid.note||"" })}
                        style={{ background:C.terra, color:"#fff", border:"none", borderRadius:7, padding:"8px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        Mark Complete & Revise Bid →
                      </button>
                    </div>
                  )}
                  {/* Inline revise form */}
                  {isEditing && (
                    <div style={{ padding:"0 16px 16px" }}>
                      <div style={{ background:C.cream, borderRadius:10, padding:"14px", border:`1px solid ${C.sand}` }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, marginBottom:10 }}>Revise Estimate</div>
                        {[["labor","Labor"],["materials","Materials"],["permits","Permits"]].map(([key,label]) => (
                          <div key={key} style={{ marginBottom:8 }}>
                            <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>{label}</label>
                            <input type="number" value={editingBid[key]} onChange={e => setEditingBid(b => ({...b,[key]:e.target.value}))}
                              placeholder="0" style={{ display:"block", width:"100%", marginTop:2, padding:"7px 10px", borderRadius:7, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.white, boxSizing:"border-box" }} />
                          </div>
                        ))}
                        <div style={{ marginBottom:10 }}>
                          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>Updated note to homeowner</label>
                          <textarea value={editingBid.note} onChange={e => setEditingBid(b => ({...b,note:e.target.value}))}
                            rows={2} style={{ display:"block", width:"100%", marginTop:2, padding:"7px 10px", borderRadius:7, border:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, background:C.white, resize:"vertical", boxSizing:"border-box" }} />
                        </div>
                        {(parseInt(editingBid.labor||0)+parseInt(editingBid.materials||0)+parseInt(editingBid.permits||0)) > 0 && (
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.terra, fontWeight:700, marginBottom:10 }}>
                            New total: ${(parseInt(editingBid.labor||0)+parseInt(editingBid.materials||0)+parseInt(editingBid.permits||0)).toLocaleString()}
                          </div>
                        )}
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={reviseConsultationBid} disabled={reviseSaving} style={{ flex:2, background:C.terra, color:"#fff", border:"none", borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                            {reviseSaving ? "Saving…" : "Submit Revised Bid"}
                          </button>
                          <button onClick={() => setEditingBid(null)} style={{ flex:1, background:"none", border:`1px solid ${C.sand}`, borderRadius:7, padding:"9px 0", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, cursor:"pointer" }}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Active Jobs — mode-aware */}
        {dashSection === 'active' && (
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.sand}`, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>Active Jobs</div>
            {activeJobs.length === 0 && (
              <div style={{ padding:24, textAlign:"center", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>No active jobs yet</div>
            )}
            {(jobMode || 'payments_only') === 'full_tracking'
              ? activeJobs.map(p => (
                  <FullTrackingActiveJob key={p.id} project={p} contractor={contractor} darkMode={darkMode}
                    onUpdate={(updated) => updateProjects(projects.map(proj => proj.id === updated.id ? updated : proj))}
                    onViewProject={(proj) => { setActiveProject(proj); setView("project"); }} />
                ))
              : <PaymentsOnlyActiveJobs activeJobs={activeJobs} contractor={contractor} user={user} darkMode={darkMode}
                  onUpdate={(updated) => updateProjects(projects.map(p => p.id === updated.id ? updated : p))}
                  onViewProject={(proj) => { setActiveProject(proj); setView("project"); }} />
            }
          </div>
        )}

        {/* Expanded: Reviews */}
        {dashSection === "reviews" && (
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.sand}`, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.sand}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>My Reviews</span>
              {contractorReviews.length > 0 && (
                <span style={{ fontFamily:"'Playfair Display',serif", fontSize:14, color:C.gold, fontWeight:700 }}>
                  {"★".repeat(Math.round(contractorReviews.reduce((s,r)=>s+r.rating,0)/contractorReviews.length))} {(contractorReviews.reduce((s,r)=>s+r.rating,0)/contractorReviews.length).toFixed(1)} avg
                </span>
              )}
            </div>
            {contractorReviews.length === 0 ? (
              <div style={{ padding:24, textAlign:"center", color:C.slate, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>No reviews yet — reviews appear after homeowners complete a project</div>
            ) : (
              <>
                {/* AI aggregate summary */}
                {contractor.review_summary && (
                  <div style={{ padding:"14px 16px", background:"rgba(184,92,56,0.04)", borderBottom:`1px solid ${C.sand}` }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.terra, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, marginBottom:6 }}>AI Summary</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, lineHeight:1.6, fontStyle:"italic" }}>"{contractor.review_summary}"</div>
                  </div>
                )}
                {contractorReviews.map(r => (
                  <div key={r.id} style={{ padding:"14px 16px", borderBottom:`1px solid ${C.sand}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <span style={{ color:"#F4C430", fontSize:14, letterSpacing:1 }}>{"★".repeat(r.rating)}{"☆".repeat(5-r.rating)}</span>
                      <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    {r.comment && (
                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:1.5 }}>{r.comment}</div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Consultation Calendar — always visible in left column */}
        <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.sand}`, padding:"16px 18px", marginTop:14 }}>
          <ContractorCalendar
            contractor={contractor}
            myBids={myBids}
            availDraft={availDraft}
            setAvailDraft={setAvailDraft}
            availSaving={availSaving}
            handleSaveAvailability={handleSaveAvailability}
            onViewProject={(project) => { setActiveProject(project); setView("project"); }}
          />
        </div>

        </div>{/* end left column */}
        <div style={{ flex:"0 0 360px", minWidth:0 }}>

        {/* Escrow & Payout Status */}
        {(() => {
          const awardedProjects = projects.filter(p => p.status === "awarded" && p.bids?.some(b => b.contractorId === contractor.id && b.status === "accepted"));
          const totalInEscrow = awardedProjects.reduce((sum, p) => {
            const released = (p.milestones||[]).filter(m=>m.status==="released").reduce((s,m)=>s+Math.round((p.escrow?.total||0)*m.pct/100),0);
            return sum + ((p.escrow?.total||0) - released);
          }, 0);
          const nextRelease = awardedProjects.flatMap(p => (p.milestones||[]).filter(m=>m.status==="pending").slice(0,1).map(m=>({ ...m, project:p, amount: Math.round((p.escrow?.total||0)*m.pct/100) }))).slice(0,1)[0];
          const totalEarned = awardedProjects.reduce((sum,p) => {
            return sum + (p.milestones||[]).filter(m=>m.status==="released").reduce((s,m)=>s+Math.round((p.escrow?.total||0)*m.pct/100),0);
          }, 0);
          if (awardedProjects.length === 0) return null;
          return (
            <div style={{ background:`linear-gradient(135deg,#0D2A1A,#1A3A2A)`, borderRadius:14, padding:"16px 18px", border:`1px solid rgba(39,174,96,0.25)`, marginBottom:14 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}> Payments & Escrow</div><div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, marginBottom:3 }}>In Escrow</div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:C.gold }}>${totalInEscrow.toLocaleString()}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>awaiting approval</div></div><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, marginBottom:3 }}>Total Earned</div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:C.sage }}>${totalEarned.toLocaleString()}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>released to bank</div></div></div>
              {nextRelease && (
                <div style={{ background:C.cream, borderRadius:10, padding:"10px 12px", border:`1px solid rgba(39,174,96,0.2)` }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginBottom:4 }}>Next milestone payment</div><div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:600 }}>{nextRelease.label}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>{nextRelease.project.title}</div></div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:C.gold, fontWeight:700 }}>${nextRelease.amount.toLocaleString()}</div></div><div style={{ marginTop:8, fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, lineHeight:1.5 }}>
                    Released when homeowner approves this milestone · Paid to your Stripe bank account within 2 business days
                  </div></div>
              )}
              <div style={{ marginTop:10, fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, display:"flex", alignItems:"center", gap:5 }}><span></span><span>Secured by Stripe Connect · 4% platform fee deducted on each release</span></div></div>
          );
        })()}

        {/* Profile details */}
        {/* Stripe Connect — bank account onboarding */}
        <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:1, marginBottom:12 }}>Payouts</div>
          {contractor.stripeConnected ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.green }}>Bank account connected</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:2 }}>Stripe verified · payouts enabled</div>
              </div>
              <div style={{ background:"rgba(39,174,96,0.1)", border:`1px solid rgba(39,174,96,0.3)`, borderRadius:100, padding:"4px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.green, fontWeight:600 }}>Active</div>
            </div>
          ) : (
            <div>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, marginBottom:8, lineHeight:1.5 }}>
                Connect your bank account to receive milestone payments directly via Stripe.
              </div>
              <button onClick={async () => {
                try {
                  // ── REAL CALL (uncomment when backend live) ──
                  // const res = await fetch(`${BACKEND_URL}/connect/create-account`, {
                  //   method: "POST",
                  //   headers: { "Content-Type": "application/json" },
                  //   body: JSON.stringify({
                  //     email: contractor.email,
                  //     businessName: contractor.company,
                  //     contractorId: contractor.id
                  //   })
                  // });
                  // const { stripeAccountId, onboardingUrl } = await res.json();
                  // Save stripeAccountId to contractor profile then redirect:
                  // updateContractor({ ...contractor, stripeAccountId, stripeConnected: false });
                  // window.location.href = onboardingUrl;

                  // ── SIMULATED ──
                  alert("Stripe onboarding: in production this redirects to Stripe's secure onboarding flow where you enter your bank details.");
                } catch (err) {
                  alert("Failed to start onboarding: " + err.message);
                }
              }} style={{ width:"100%", background:C.terra, color:C.white, border:"none", borderRadius:10, padding:"13px 0", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor:"pointer", boxShadow:`0 4px 16px rgba(184,92,56,0.25)` }}>
                Connect Bank Account via Stripe
              </button>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textAlign:"center", marginTop:8 }}>
                Secured by Stripe · Takes ~3 minutes · Required to receive payments
              </div>
            </div>
          )}
        </div>

        <ProfileDetails
          contractor={contractor}
          projects={projects}
          myBids={myBids}
          contractorReviews={contractorReviews}
          updateContractor={updateContractor}
          onViewProject={(project) => { setActiveProject(project); setView("project"); }}
          jobMode={jobMode}
          setJobMode={setJobMode}
          bidTemplates={bidTemplates}
          setBidTemplates={setBidTemplates}
        />

        {/* Recent bids */}
        <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, marginBottom:14, fontWeight:700 }}>My Recent Bids</div>
          {myBids.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 0", color:C.slate, fontFamily:"'DM Sans',sans-serif", fontSize:13 }}><div style={{ fontSize:28, marginBottom:8 }}></div>
              No bids yet
            </div>
          ) : myBids.slice(0,5).map(p => {
            const myBid = p.bids.find(b => b.contractorId === contractor.id);
            const needsRevise = myBid?.needsConsultation && myBid?.consultationBooked && !myBid?.consultationComplete;
            return (
              <div key={p.id} style={{ padding:"10px 0", borderBottom:`1px solid ${C.sand}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ flex:1, minWidth:0, marginRight:12 }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.title}</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:2 }}>{p.room} · {p.homeowner}</div>
                    {needsRevise && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.gold, fontWeight:600, marginTop:2 }}>⚑ Consultation {new Date(myBid.consultationBooked.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} — revision needed</div>}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color: myBid?.status==="accepted" ? C.sage : C.gold, fontWeight:700 }}>${myBid?.amount?.toLocaleString()}</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color: myBid?.status==="accepted" ? C.green : "rgba(0,0,0,0.32)" }}>{myBid?.status==="accepted" ? "✓ Accepted" : "Pending"}</div>
                  </div>
                </div>
              </div>
            );
          })}
          <button onClick={() => setView("browse")} style={{ width:"100%", marginTop:14, background:"rgba(184,92,56,0.08)", color:C.terra, border:`1px solid rgba(184,92,56,0.18)`, borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600 }}>
            Browse Open Projects →
          </button></div></div>{/* end right column */}</div>{/* end 2-col */}</div></div>
  );

  //  BROWSE PROJECTS
  if (view === "browse" || (view === "project" && !activeProject)) return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ flex:1, overflowY:"auto", padding:"16px 16px 32px", maxWidth:1200, margin:"0 auto", width:"100%" }} className="fade-in"><div style={{ marginBottom:14 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:C.ink, fontWeight:700 }}>Open Projects</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginTop:2 }}>
            {filteredOpen.length} project{filteredOpen.length!==1?"s":""} matching your availability
            {dateFilteredOut > 0 && !showOnlyAligned && (
              <span style={{ color:C.slate }}> · {dateFilteredOut} start before you're available</span>
            )}
          </div></div>

        {/* Availability card — the core filter */}
        <div style={{ background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.sand}`, marginBottom:14 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, fontWeight:700, marginBottom:10 }}>
             My Earliest Available Start
          </div><div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}><input type="date" value={myAvailableDate}
              onChange={e => setMyAvailableDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              style={{ flex:1, background:"rgba(255,255,255,0.07)", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"10px 12px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, outline:"none" }} /><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, flexShrink:0 }}>
              {myAvailableDate && new Date(myAvailableDate + "T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
            </div></div>

          {/* Alignment toggle */}
          <div onClick={() => setShowOnlyAligned(s => !s)}
            style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 10px", borderRadius:8, background: showOnlyAligned ? "rgba(184,92,56,0.08)" : C.sand, border:`1px solid ${showOnlyAligned ? "rgba(184,92,56,0.20)" : C.sand}`, transition:"all 0.15s" }}><div style={{ width:36, height:20, borderRadius:10, background: showOnlyAligned ? C.terra : C.mist, position:"relative", transition:"background 0.2s", flexShrink:0 }}><div style={{ width:16, height:16, borderRadius:"50%", background:C.white, position:"absolute", top:2, left: showOnlyAligned ? 18 : 2, transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }} /></div><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color: showOnlyAligned ? C.terra : C.slate }}>
                Show only projects I can start on time
              </div>
              {dateFilteredOut > 0 && (
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, marginTop:2 }}>
                  {showOnlyAligned
                    ? `Hiding ${dateFilteredOut} project${dateFilteredOut!==1?"s":""} that start before ${new Date(myAvailableDate + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                    : `${dateFilteredOut} project${dateFilteredOut!==1?"s":""} start before you're available`}
                </div>
              )}
            </div></div>

          {/* Radius filter */}
          {contractor.lat && (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:10, padding:"8px 10px", borderRadius:8, background:C.sand }}>
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, fontWeight:600, whiteSpace:"nowrap" }}>Search within</span>
              <select value={browseRadius} onChange={e => setBrowseRadius(parseInt(e.target.value))} style={{ flex:1, background:C.white, border:`1px solid ${C.sand}`, borderRadius:8, padding:"6px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, outline:"none" }}>
                {[10, 25, 50, 100, 150, 200, 9999].map(r => <option key={r} value={r}>{r === 9999 ? "Any distance" : `${r} miles`}</option>)}
              </select>
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, whiteSpace:"nowrap" }}>of {contractor.location}</span>
            </div>
          )}
        </div>

        {/* Room type filters */}
        <div style={{ display:"flex", gap:8, marginBottom:14, overflowX:"auto", WebkitOverflowScrolling:"touch", paddingBottom:4 }}>
          {rooms.map(r => (
            <button key={r} onClick={() => setFilter(r)} style={{ background:filter===r?"rgba(184,92,56,0.12)":"rgba(255,255,255,0.05)", color:filter===r?C.terra:"rgba(0,0,0,0.45)", border:filter===r?`1px solid rgba(184,92,56,0.30)`:`1px solid rgba(255,255,255,0.08)`, borderRadius:100, padding:"6px 14px", fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:filter===r?600:400, whiteSpace:"nowrap", cursor:"pointer" }}>{r}</button>
          ))}
        </div><div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(440px, 1fr))", gap:14 }}>
          {filteredOpen.length === 0 ? (
            <div style={{ background:C.white, borderRadius:14, padding:"32px 24px", textAlign:"center", border:`1px solid ${C.sand}` }}>
              {showOnlyAligned && dateFilteredOut > 0 ? (
                <><div style={{ fontSize:36, marginBottom:12 }}></div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, marginBottom:8 }}>No projects match your start date</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, marginBottom:16, lineHeight:1.6 }}>
                    {dateFilteredOut} project{dateFilteredOut!==1?"s":""} are available but start before {new Date(myAvailableDate + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}. Update your available date or turn off the filter to see them.
                  </div><button onClick={() => setShowOnlyAligned(false)}
                    style={{ background:C.terra, color:C.ink, border:"none", borderRadius:8, padding:"10px 20px", fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                    Show all {openProjects.length} projects
                  </button></>
              ) : (
                <><div style={{ fontSize:36, marginBottom:12 }}></div><div style={{ fontFamily:"'DM Sans',sans-serif", color:"rgba(255,255,255,0.5)", fontSize:14 }}>You've bid on all available projects!</div></>
              )}
            </div>
          ) : filteredOpen.map(p => {
            // Compute alignment for this card
            const aligned = !p.desiredStart || new Date(myAvailableDate) <= new Date(p.desiredStart + "T12:00:00");
            const daysLate = p.desiredStart
              ? Math.ceil((new Date(myAvailableDate) - new Date(p.desiredStart + "T12:00:00")) / (1000*60*60*24))
              : 0;
            return (
            <div key={p.id} onClick={() => { setActiveProject(p); setView("project"); }}
              style={{ background:C.white, borderRadius:14, overflow:"hidden", border:`1px solid ${C.sand}`, cursor:"pointer" }}>

              {/* Photo thumbnail strip — before on left, render on right */}
              <ProjectPhotoStrip project={p} />

              {/* Project info */}
              <div style={{ padding:"14px 16px" }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}><div style={{ flex:1, minWidth:0, marginRight:12 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:C.ink, marginBottom:2 }}>{p.title}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>{p.room} · {contractor.lat && p.lat ? `${Math.round(haversineDistance(contractor.lat, contractor.lng, p.lat, p.lng))} mi · ` : ""}{p.location} · {p.bids?.length||0} bids · {p.postedAt}</div></div><div style={{ textAlign:"right", flexShrink:0 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:C.gold }}>{p.budget}</div></div></div>

                {p.description && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:"rgba(255,255,255,0.5)", lineHeight:1.5, marginBottom:10,
                    overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                    {p.description}
                  </div>
                )}

                {/* Material tags */}
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:12 }}>
                  {p.items?.map((item,i) => (
                    <span key={i} style={{ background:item.color+"33", border:`1px solid ${item.color}55`, borderRadius:100, padding:"3px 9px", fontSize:11, fontFamily:"'DM Sans',sans-serif", color:C.ink }}>{item.label}</span>
                  ))}
                </div>

                {/* Site visit requested badge */}
                {p.bids?.some(b => b.contractorId === contractor.id && (b.siteVisitRequested || b.siteVisitScheduled)) && (
                  <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"rgba(212,168,67,0.12)", border:`1px solid rgba(212,168,67,0.3)`, borderRadius:100, padding:"4px 10px", marginBottom:10, fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.gold, fontWeight:600 }}>
                     Site visit {p.bids.find(b=>b.contractorId===contractor.id)?.siteVisitScheduled ? "complete" : "requested"}
                  </div>
                )}

                {/* Start date alignment badge */}
                <div style={{ marginBottom:10 }}>
                  {!p.desiredStart ? (
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}> Flexible start date</span>
                  ) : aligned ? (
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, fontWeight:600 }}>
                       Starts {new Date(p.desiredStart + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} — you're available
                    </span>
                  ) : (
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"rgba(255,100,80,0.8)", fontWeight:600 }}>
                       Starts {new Date(p.desiredStart + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} — {daysLate}d before you're free
                    </span>
                  )}
                </div><button style={{ width:"100%", background: aligned ? C.terra : "rgba(255,255,255,0.08)", color: aligned ? C.white : "rgba(0,0,0,0.45)", border:"none", borderRadius:8, padding:"11px 0", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13 }}>
                  {p.bids?.some(b => b.contractorId === contractor.id) ? "View Your Bid →" : aligned ? "View Details & Bid →" : "View Details (date mismatch)"}
                </button></div></div>
            );
          })}
        </div></div></div>
  );

  //  ACCEPTED JOB DETAIL — contractor won this project; show read-only summary, not bid form
  if (view === "project" && activeProject) {
    const myAcceptedBid = activeProject.bids?.find(b => b.contractorId === contractor.id && b.status === "accepted");
    if (myAcceptedBid) {
      const p = activeProject;
      const totalAmt = p.escrow?.total || myAcceptedBid.amount || 0;
      const releasedAmt = p.escrow?.released || (p.milestones||[]).filter(m=>m.status==='released').reduce((s,m)=>s+Math.round(totalAmt*m.pct/100),0);
      const remainingAmt = totalAmt - releasedAmt;
      const releasedPct = totalAmt > 0 ? Math.round((releasedAmt/totalAmt)*100) : 0;
      const bd = myAcceptedBid.breakdown || {};
      const labor = bd.labor || 0;
      const materials = bd.materials || 0;
      const permits = bd.permits || 0;
      const milestones = p.milestones || [];
      const statusLabel = { pending:"Upcoming", in_progress:"In Progress", ready_for_review:"Awaiting Review", released:"Released", disputed:"Disputed" };
      const statusColor = { pending:C.slate, in_progress:C.gold, ready_for_review:C.terra, released:C.sage, disputed:"#E74C3C" };
      return (
        <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav />
          <div style={{ flex:1, overflowY:"auto" }} className="fade-in">
            <div style={{ maxWidth:800, margin:"0 auto", padding:"20px 16px 48px", width:"100%" }}>

              {/* Header */}
              <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0, marginRight:12 }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:C.ink, fontWeight:700, lineHeight:1.2, marginBottom:4 }}>{p.title}</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>{p.homeowner} · {p.location}</div>
                  </div>
                  <div style={{ background:`${C.sage}18`, border:`1px solid ${C.sage}40`, borderRadius:8, padding:"4px 10px", flexShrink:0 }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.sage }}>✓ Won</span>
                  </div>
                </div>
                {p.address && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 12px", background:"rgba(39,174,96,0.08)", border:"1px solid rgba(39,174,96,0.2)", borderRadius:8 }}>
                    <span style={{ fontSize:14 }}>📍</span>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>{p.address}</span>
                  </div>
                )}
              </div>

              {/* Escrow progress */}
              <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sage}40`, marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:2 }}>Escrow Balance</div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:C.gold }}>${remainingAmt.toLocaleString()}</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>held securely in escrow</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, marginBottom:2 }}>Total contract</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:18, fontWeight:700, color:C.ink }}>${totalAmt.toLocaleString()}</div>
                  </div>
                </div>
                <div style={{ height:6, background:C.sand, borderRadius:3, overflow:"hidden", marginBottom:5 }}>
                  <div style={{ height:"100%", background:`linear-gradient(90deg,${C.sage},${C.gold})`, width:`${releasedPct}%`, borderRadius:3, transition:"width 0.5s" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.sage }}>${releasedAmt.toLocaleString()} released</span>
                  <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate }}>${remainingAmt.toLocaleString()} remaining</span>
                </div>
              </div>

              {/* Accepted bid breakdown */}
              <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid ${C.sand}`, marginBottom:14 }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:C.ink, fontWeight:700, marginBottom:14 }}>Agreed Bid Details</div>
                {[["Labor", labor], ["Materials", materials], ["Permits & Fees", permits]].filter(([,v]) => v > 0).map(([label, val]) => (
                  <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:`1px solid ${C.sand}` }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>{label}</span>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>${Number(val).toLocaleString()}</span>
                  </div>
                ))}
                {myAcceptedBid.lineItems && Object.entries(myAcceptedBid.lineItems).filter(([,v])=>v>0).map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:`1px solid ${C.sand}` }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate }}>{k}</span>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>${Number(v).toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0" }}>
                  <span style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, color:C.ink }}>Total</span>
                  <span style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, color:C.terra }}>${totalAmt.toLocaleString()}</span>
                </div>
                {myAcceptedBid.note && (
                  <div style={{ marginTop:12, padding:"10px 12px", background:C.cream, borderRadius:8, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.6 }}>
                    "{myAcceptedBid.note}"
                  </div>
                )}
                {myAcceptedBid.startDate && (
                  <div style={{ marginTop:10, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>
                    Start: <strong style={{ color:C.ink }}>{myAcceptedBid.startDate}</strong>
                    {myAcceptedBid.duration && <> · Duration: <strong style={{ color:C.ink }}>{myAcceptedBid.duration}</strong></>}
                  </div>
                )}
              </div>

              {/* Milestones */}
              {milestones.length > 0 && (
                <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.sand}`, marginBottom:14, overflow:"hidden" }}>
                  <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.sand}`, fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8 }}>Milestones</div>
                  {milestones.map((ms, idx) => {
                    const msAmt = Math.round(totalAmt * ms.pct / 100);
                    return (
                      <div key={ms.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderBottom:idx<milestones.length-1?`1px solid ${C.sand}`:"none" }}>
                        <div style={{ width:26, height:26, borderRadius:13, background:ms.status==='released'?C.sage:ms.status==='in_progress'?C.terra:C.sand, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span style={{ color:"white", fontSize:11, fontWeight:700 }}>{ms.status==='released'?"✓":idx+1}</span>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>{ms.label}</div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:statusColor[ms.status]||C.slate, fontWeight:600, marginTop:1 }}>{statusLabel[ms.status]||ms.status}</div>
                        </div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:700, color:ms.status==='released'?C.sage:C.terra }}>${msAmt.toLocaleString()}</div>
                      </div>
                    );
                  })}
                </div>
              )}

            </div>
          </div>
        </div>
      );
    }
  }

  //  PROJECT DETAIL + BID FORM
  if (view === "project" && activeProject) return (
    <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ flex:1, overflowY:"auto" }} className="fade-in">

        {/* PHOTO VIEWER — constrained to content width */}
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"14px 16px 0", width:"100%" }}><div style={{ borderRadius:12, overflow:"hidden" }}><ContractorPhotoViewer project={activeProject} /></div></div><div style={{ maxWidth:1100, margin:"0 auto", padding:"14px 16px 40px", width:"100%" }}>
          {/* Project header */}
          <div style={{ background:C.white, borderRadius:14, padding:"14px 16px", border:`1px solid ${C.sand}`, marginBottom:12 }}><div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}><div style={{ flex:1, minWidth:0, marginRight:10 }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:19, color:C.ink, fontWeight:700, lineHeight:1.2, marginBottom:4 }}>{activeProject.title}</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate }}>{activeProject.homeowner} · {activeProject.location} · {activeProject.postedAt}</div></div><div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:C.gold, fontWeight:700, flexShrink:0 }}>{activeProject.budget}</div></div>
            {activeProject.description && (
              <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:1.7, marginBottom:0 }}>{activeProject.description}</p>
            )}
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:10 }}>
              {activeProject.bids?.length||0} contractor{activeProject.bids?.length!==1?"s have":"has"} already bid
            </div></div>

          {/* Job site address — only visible to the winning contractor after escrow is funded */}
          {(() => {
            const myWinningBid = activeProject.bids?.find(b => b.contractorId === contractor.id && b.status === "accepted");
            if (!myWinningBid || !activeProject.address) return null;
            return (
              <div style={{ background:"rgba(39,174,96,0.08)", border:"1px solid rgba(39,174,96,0.25)", borderRadius:12, padding:"12px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:20 }}>📍</span>
                <div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.sage, textTransform:"uppercase", letterSpacing:0.7, marginBottom:2 }}>Job Site Address</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:600, color:C.ink }}>{activeProject.address}</div>
                </div>
              </div>
            );
          })()}

          {/* Consultation status banner */}
          {(() => {
            const myBid = activeProject.bids?.find(b => b.contractorId === contractor.id);
            if (!myBid?.needsConsultation) return null;
            if (myBid.consultationComplete) return null;
            return myBid.consultationBooked ? (
              <div style={{ background:"rgba(39,174,96,0.1)", border:`1px solid rgba(39,174,96,0.3)`, borderRadius:12, padding:"14px 16px", marginBottom:12 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.sage, marginBottom:4 }}>Consultation Booked</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:600 }}>
                  {new Date(myBid.consultationBooked.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} at {myBid.consultationBooked.time}
                </div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginTop:4 }}>After your visit, go to My Bids on the dashboard to revise your estimate.</div>
              </div>
            ) : (
              <div style={{ background:"rgba(212,168,67,0.1)", border:`1px solid rgba(212,168,67,0.3)`, borderRadius:12, padding:"14px 16px", marginBottom:12 }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.gold, marginBottom:4 }}>Awaiting Homeowner to Schedule</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, lineHeight:1.5 }}>You flagged this bid as needing an in-person consultation. The homeowner will pick a time from your availability.</div>
              </div>
            );
          })()}

          {/* Bid Form */}
          <div style={{ background:C.white, borderRadius:14, padding:"16px 18px", border:`1px solid rgba(184,92,56,0.20)` }}><div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:C.ink, marginBottom:4, fontWeight:700 }}>Submit Your Bid</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, marginBottom:12, lineHeight:1.5 }}>
              Itemized bids win more work — homeowners trust transparent pricing
            </div>

            {/* Load / Save Template toolbar — always visible */}
            <div style={{ display:"flex", gap:8, marginBottom:14 }}>
              <div style={{ flex:1 }}>
                <button onClick={() => setShowTemplateDropdown(t => !t)}
                  style={{ width:"100%", background:"transparent", border:`1.5px solid ${C.clay}`, borderRadius:8, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.clay, cursor:"pointer" }}>
                  Load Template {showTemplateDropdown ? "▲" : "▾"}
                </button>
                {showTemplateDropdown && (
                  <div style={{ marginTop:8 }}>
                    <LoadTemplateDropdown
                      templates={bidTemplates}
                      projectRoom={activeProject?.room}
                      darkMode={darkMode}
                      onLoad={tpl => {
                        setBidForm(f => ({ ...f, lineItems: tpl.line_items || {}, duration: tpl.duration || f.duration, note: tpl.note || f.note }));
                        setShowTemplateDropdown(false);
                      }}
                      onClose={() => setShowTemplateDropdown(false)} />
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  const hasLI = activeProject.scope?.some(s => s.by === "contractor");
                  const { unit: _lu, labor: _ll, material: _lm } = computeLineItemTotals(bidForm.lineItems);
                  const _labor = hasLI && _ll > 0 ? _ll : parseInt(bidForm.laborCost||0);
                  const _mat   = hasLI && _lm > 0 ? _lm : parseInt(bidForm.materialCost||0);
                  const _total = _labor + _mat + _lu + parseInt(bidForm.permitCost||0);
                  setTemplateSaveModal({
                    contractorId: user?.id,
                    name: `${activeProject.room || "Project"} Remodel`,
                    projectType: activeProject.room || "Kitchen",
                    lineItems: bidForm.lineItems,
                    amount: _total,
                    duration: bidForm.duration,
                    note: bidForm.note,
                  });
                }}
                style={{ flex:1, background:"transparent", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color:C.slate, cursor:"pointer" }}>
                Save as Template
              </button>
            </div>

            {/* Scope of Work — contractor items + DIY items */}
            {activeProject.scope?.length > 0 && (
              <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px", marginBottom:16, border:`1px solid ${C.sand}` }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, fontWeight:700, marginBottom:10 }}>
                  Scope of Work
                </div>

                {/* Contractor items with cost guides + selected product info */}
                {activeProject.scope.filter(s => s.by === "contractor").length > 0 && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.terra, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>
                      Include in your bid ({activeProject.scope.filter(s=>s.by==="contractor").length} items)
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {activeProject.scope.filter(s => s.by === "contractor").map(s => {
                        const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
                        if (!item) return null;
                        return (
                          <div key={s.id} style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"6px 8px", background:"rgba(184,92,56,0.05)", borderRadius:6, border:`1px solid rgba(184,92,56,0.10)` }}>
                            <div style={{ flex:1 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, fontWeight:500 }}>{item.label}</span>
                                {hasPermitRisk(item) && <span style={{ background:"#856404", color:"#fff", borderRadius:100, padding:"1px 6px", fontSize:9, fontFamily:"'DM Sans',sans-serif", fontWeight:700, flexShrink:0 }}>Permit</span>}
                              </div>
                              {s.selectedProduct && (
                                <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:3 }}>
                                  <span style={{ fontSize:12 }}>{s.selectedProduct.emoji}</span>
                                  <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay }}>
                                    {s.selectedProduct.brand} {s.selectedProduct.label} · ${s.selectedProduct.price?.toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                            {item.typical_cost_low > 0 && (
                              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, flexShrink:0, marginTop:2 }}>
                                ${item.typical_cost_low.toLocaleString()}–${item.typical_cost_high.toLocaleString()} est.{item.unit_label ? ` · ${item.unit_label}` : ""}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, fontStyle:"italic", marginTop:6 }}>
                      Cost guides are rough estimates only.
                    </div>
                  </div>
                )}

                {/* DIY items — visual separator showing what NOT to price */}
                {activeProject.scope.filter(s => s.by === "homeowner").length > 0 && (
                  <div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>
                      Homeowner doing themselves — don't price these
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {activeProject.scope.filter(s => s.by === "homeowner").map(s => {
                        const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
                        if (!item) return null;
                        return (
                          <div key={s.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 8px", background:"rgba(184,92,56,0.08)", borderRadius:6, border:`1px solid rgba(184,92,56,0.15)`, opacity:0.7 }}>
                            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.slate, flex:1, textDecoration:"line-through" }}>{item.label}</span>
                            <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, flexShrink:0, fontWeight:600 }}>DIY</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Per-scope-item line entry */}
            {activeProject.scope?.filter(s => s.by === "contractor").length > 0 && (
              <div style={{ background:C.white, borderRadius:10, padding:"14px 16px", marginBottom:16, border:`1px solid ${C.sand}` }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:12, fontWeight:700 }}>Line Items — Price Each Scope Item</div>
                <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                  {(() => {
                    const contractorScope = activeProject.scope.filter(s => s.by === "contractor");
                    const grouped = contractorScope.reduce((acc, s) => {
                      const item = ALL_SCOPE_FLAT.find(i => i.id === s.id);
                      const cat = item?.category || "Other";
                      (acc[cat] = acc[cat] || []).push(s);
                      return acc;
                    }, {});
                    return Object.entries(grouped).map(([cat, entries]) => (
                      <div key={cat} style={{ marginBottom:16 }}>
                        <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, textTransform:"uppercase", letterSpacing:1, fontWeight:700, marginBottom:8, paddingBottom:4, borderBottom:`1px solid ${C.sand}` }}>{cat}</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                          {entries.map(scopeEntry => {
                    const item = ALL_SCOPE_FLAT.find(i => i.id === scopeEntry.id);
                    if (!item) return null;
                    const li = bidForm.lineItems[scopeEntry.id] || {};
                    const qty = li.quantity || 0;
                    const unitCost = li.unit_cost || 0;
                    const lineTotal = qty * unitCost;
                    const laborMaterialTotal = (parseFloat(li.labor_cost)||0) + (parseFloat(li.material_cost)||0);
                    const displayTotal = lineTotal > 0 ? lineTotal : laborMaterialTotal;
                    const update = (field, val) => setBidForm(f => ({
                      ...f,
                      lineItems: { ...f.lineItems, [scopeEntry.id]: { ...( f.lineItems[scopeEntry.id] || {} ), [field]: val } }
                    }));
                    // Determine effective supply model for this line item
                    const effectiveSupply = scopeEntry.supplyChoice || item.supply_model || "contractor_supplied";
                    const isOwnerSupplied = effectiveSupply === "owner_supplied";
                    return (
                      <div key={scopeEntry.id} style={{ background:C.cream, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.sand}` }}>
                        {/* Header */}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>{item.label}</span>
                              {isOwnerSupplied && (
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:"#856404", background:"rgba(133,100,4,0.07)", borderRadius:100, padding:"1px 7px", border:"1px solid rgba(133,100,4,0.2)" }}>Owner supplies</span>
                              )}
                              {!isOwnerSupplied && (
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.terra, background:"rgba(184,92,56,0.07)", borderRadius:100, padding:"1px 7px", border:"1px solid rgba(184,92,56,0.2)" }}>Contractor supplies</span>
                              )}
                            </div>
                            {item.unit_label && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay, marginTop:1 }}>{item.unit_label}</div>}
                          </div>
                          {displayTotal > 0 && (
                            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.terra }}>
                              ${displayTotal.toLocaleString()}
                            </div>
                          )}
                        </div>

                        {/* Contractor prompt — strip supply questions since homeowner already decided */}
                        {item.contractor_prompt && (() => {
                          const supplyRe = /\b(supply|supplying|supplies|purchased|purchase|delivered|on-site)\b/i;
                          const filtered = item.contractor_prompt
                            .split(/\?\s+/)
                            .map(q => q.trim()).filter(Boolean)
                            .map(q => q.endsWith('?') ? q : q + '?')
                            .filter(q => !supplyRe.test(q))
                            .join(' ');
                          return filtered ? (
                            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.clay, fontStyle:"italic", marginBottom:8, lineHeight:1.4 }}>
                              {filtered}
                            </div>
                          ) : null;
                        })()}

                        {/* Linked canvas product — show if homeowner selected one */}
                        {scopeEntry.selectedProduct && (
                          <div style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(196,149,106,0.06)", borderRadius:6, padding:"6px 10px", marginBottom:8, border:`1px solid rgba(196,149,106,0.2)` }}>
                            <span style={{ fontSize:18 }}>{scopeEntry.selectedProduct.emoji}</span>
                            <div>
                              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.ink }}>{scopeEntry.selectedProduct.brand} {scopeEntry.selectedProduct.label}</div>
                              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.clay }}>Homeowner selected · ${scopeEntry.selectedProduct.price?.toLocaleString()}{scopeEntry.selectedProduct.priceUnit ? `/${scopeEntry.selectedProduct.priceUnit}` : ""}</div>
                            </div>
                          </div>
                        )}

                        {/* Pricing — either unit pricing OR labor+materials, not both */}
                        {(() => {
                          const dimA = laborMaterialTotal > 0 && lineTotal === 0;
                          const dimB = lineTotal > 0;
                          return (
                            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:8 }}>
                              {/* Section A: Unit Pricing */}
                              <div style={{ opacity: dimA ? 0.38 : 1, transition:"opacity 0.2s", pointerEvents: dimA ? "none" : "auto" }}>
                                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.slate, fontWeight:700, textTransform:"uppercase", letterSpacing:0.6, marginBottom:4 }}>Unit Pricing</div>
                                <div style={{ display:"flex", gap:8 }}>
                                  {[["QTY", "quantity", qty||""], ["UNIT COST $", "unit_cost", unitCost||""]].map(([label, field, val]) => (
                                    <div key={field} style={{ flex:"1 1 80px", minWidth:70 }}>
                                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.slate, fontWeight:600, marginBottom:3, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
                                      <input type="number" min="0" value={val}
                                        onChange={e => update(field, parseFloat(e.target.value)||0)}
                                        style={{ width:"100%", border:`1px solid ${C.sand}`, borderRadius:6, padding:"6px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink, background:C.white, outline:"none", boxSizing:"border-box" }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {/* OR divider */}
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ flex:1, height:1, background:C.sand }} />
                                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:C.slate, fontStyle:"italic", whiteSpace:"nowrap" }}>— or break out separately —</span>
                                <div style={{ flex:1, height:1, background:C.sand }} />
                              </div>
                              {/* Section B: Labor + Materials */}
                              <div style={{ opacity: dimB ? 0.38 : 1, transition:"opacity 0.2s", pointerEvents: dimB ? "none" : "auto" }}>
                                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.slate, fontWeight:700, textTransform:"uppercase", letterSpacing:0.6, marginBottom:4 }}>Labor & Materials</div>
                                <div style={{ display:"flex", gap:8 }}>
                                  {[
                                    ["LABOR $", "labor_cost", li.labor_cost||""],
                                    ...(!isOwnerSupplied ? [["MATERIAL $", "material_cost", li.material_cost||""]] : []),
                                  ].map(([label, field, val]) => (
                                    <div key={field} style={{ flex:"1 1 80px", minWidth:70 }}>
                                      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.slate, fontWeight:600, marginBottom:3, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
                                      <input type="number" min="0" value={val}
                                        onChange={e => update(field, parseFloat(e.target.value)||0)}
                                        style={{ width:"100%", border:`1px solid ${C.sand}`, borderRadius:6, padding:"6px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink, background:C.white, outline:"none", boxSizing:"border-box" }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Room checkboxes */}
                        <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
                          {ROOMS.map(room => {
                            const checked = (li.rooms || scopeEntry.rooms || []).includes(room);
                            return (
                              <button key={room} type="button"
                                onClick={() => {
                                  const current = li.rooms || scopeEntry.rooms || [];
                                  update("rooms", checked ? current.filter(r => r !== room) : [...current, room]);
                                }}
                                style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:checked?700:400, color:checked?C.white:C.slate, background:checked?C.terra:C.white, border:`1px solid ${checked?C.terra:C.sand}`, borderRadius:100, padding:"2px 9px", cursor:"pointer", transition:"all 0.15s" }}>
                                {room}
                              </button>
                            );
                          })}
                        </div>

                        {/* Notes */}
                        <textarea
                          placeholder="Notes for this item (optional)"
                          value={li.contractor_notes || ""}
                          onChange={e => update("contractor_notes", e.target.value)}
                          rows={2}
                          style={{ width:"100%", border:`1px solid ${C.sand}`, borderRadius:6, padding:"6px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:C.ink, background:C.white, resize:"vertical", outline:"none", boxSizing:"border-box" }}
                        />
                      </div>
                    );
                  })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Itemized cost breakdown */}
            {(() => {
              const hasLineItems = activeProject.scope?.some(s => s.by === "contractor");
              const { unit: lineItemUnit, labor: lineItemLabor, material: lineItemMaterial } = computeLineItemTotals(bidForm.lineItems);
              const laborVal    = hasLineItems && lineItemLabor > 0    ? lineItemLabor    : parseInt(bidForm.laborCost||0);
              const materialVal = hasLineItems && lineItemMaterial > 0 ? lineItemMaterial : parseInt(bidForm.materialCost||0);
              const permitVal   = parseInt(bidForm.permitCost||0);
              const total = laborVal + materialVal + lineItemUnit + permitVal;
              return (
                <div style={{ background:C.cream, borderRadius:10, padding:"14px 16px", marginBottom:16, border:`1px solid ${C.sand}` }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, marginBottom:14, fontWeight:700 }}>Cost Breakdown</div>
                  {[
                    ["laborCost",   "Labor",          "Your crew time, installation, framing",     laborVal,    hasLineItems && lineItemLabor > 0],
                    ["materialCost","Materials",       "Everything listed in their spec",            materialVal, hasLineItems && lineItemMaterial > 0],
                    ...(hasLineItems && lineItemUnit > 0 ? [["_unit","Unit Costs","Qty × unit cost from line items", lineItemUnit, true]] : []),
                    ["permitCost",  "Permits & Fees",  "Filing, inspection, engineering if needed", permitVal,   false],
                  ].map(([key, label, hint, displayVal, fromLineItems]) => (
                    <div key={key} style={{ marginBottom:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:5 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, color:C.ink }}>{label}</span>
                          {fromLineItems && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:C.clay, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>from line items</span>}
                        </div>
                        <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate }}>{hint}</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", background: fromLineItems ? "#f7f3ee" : C.white, borderRadius:8, border:`1.5px solid ${fromLineItems ? C.clay+"44" : C.sand}`, overflow:"hidden" }}>
                        <span style={{ padding:"0 10px", color:C.slate, fontFamily:"'DM Sans',sans-serif", fontSize:14 }}>$</span>
                        {fromLineItems ? (
                          <span style={{ flex:1, padding:"10px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, color:C.ink }}>
                            {displayVal.toLocaleString()}
                          </span>
                        ) : (
                          <input type="text" inputMode="numeric"
                            value={bidForm[key] ? parseInt(bidForm[key]||0).toLocaleString() : ""}
                            onChange={e => setBidForm(f => ({...f, [key]: e.target.value.replace(/[^0-9]/g,'')}))}
                            placeholder="0"
                            style={{ flex:1, border:"none", background:"transparent", padding:"10px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, outline:"none" }} />
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Total */}
                  <div style={{ borderTop:`2px solid ${C.sand}`, paddingTop:12, marginTop:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color:C.ink }}>Total Estimate</span>
                    <span style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:C.terra, fontWeight:700 }}>
                      ${total.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Market Range Indicator */}
            {(() => {
              const ctScope = activeProject.scope?.filter(s => s.by === "contractor") || [];
              const { low, high, productAdjustment } = computeMarketRange(ctScope);
              if (!low) return null;
              const hasLI = activeProject.scope?.some(s => s.by === "contractor");
              const { unit: lu, labor: ll, material: lm } = computeLineItemTotals(bidForm.lineItems);
              const l = hasLI && ll > 0 ? ll : parseInt(bidForm.laborCost||0);
              const m = hasLI && lm > 0 ? lm : parseInt(bidForm.materialCost||0);
              const bidTotal = l + m + lu + parseInt(bidForm.permitCost||0);
              if (!bidTotal) return null;
              return <MarketRangeIndicator total={bidTotal} low={low} high={high} productAdjustment={productAdjustment} darkMode={darkMode} />;
            })()}

            {/* Scheduling — the core availability signal */}
            <div style={{ background:C.cream, borderRadius:10, padding:"14px", border:`1px solid ${C.sand}`, marginBottom:16 }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, textTransform:"uppercase", letterSpacing:0.8, fontWeight:700, marginBottom:12 }}>
                 Your Availability
              </div>

              {/* Homeowner's desired start — shown as reference */}
              {activeProject.desiredStart && (
                <div style={{ background:"rgba(212,168,67,0.1)", border:`1px solid rgba(212,168,67,0.2)`, borderRadius:8, padding:"8px 12px", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.gold, fontWeight:700 }}>Homeowner wants to start</div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, fontWeight:600 }}>
                      {new Date(activeProject.desiredStart + "T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"})}
                    </div></div></div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginBottom:6, fontWeight:600 }}>Earliest I can start</div><input type="date" value={bidForm.startDate} onChange={e=>setBidForm(f=>({...f,startDate:e.target.value}))}
                    min={new Date().toISOString().split("T")[0]}
                    style={{ width:"100%", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"10px 10px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:"rgba(255,255,255,0.07)", outline:"none" }} /></div><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, marginBottom:6, fontWeight:600 }}>Estimated duration</div><select value={bidForm.duration} onChange={e=>setBidForm(f=>({...f,duration:e.target.value}))}
                    style={{ width:"100%", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"10px 8px", fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.ink, background:C.white, outline:"none" }}>
                    {["3–5 days","1 week","2 weeks","3 weeks","4 weeks","5–6 weeks","2 months","3+ months"].map(o =><option key={o} value={o} style={{ background:C.white }}>{o}</option>
                    )}
                  </select></div></div>

              {/* Alignment indicator */}
              {bidForm.startDate && activeProject.desiredStart && (
                <div style={{ marginTop:10, padding:"8px 12px", borderRadius:8, background: new Date(bidForm.startDate) <= new Date(activeProject.desiredStart + "T12:00:00") ? "rgba(39,174,96,0.1)" : "rgba(231,76,60,0.1)", border: `1px solid ${new Date(bidForm.startDate) <= new Date(activeProject.desiredStart + "T12:00:00") ? "rgba(39,174,96,0.3)" : "rgba(231,76,60,0.3)"}` }}><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color: new Date(bidForm.startDate) <= new Date(activeProject.desiredStart + "T12:00:00") ? C.sage : "#E74C3C", fontWeight:600 }}>
                    {new Date(bidForm.startDate) <= new Date(activeProject.desiredStart + "T12:00:00")
                      ? " You can meet their start date"
                      : ` You're available ${Math.ceil((new Date(bidForm.startDate)-new Date(activeProject.desiredStart + "T12:00:00"))/(1000*60*60*24))} days after their preferred start`}
                  </div></div>
              )}
            </div><label style={{ ...labelSt, color:C.slate }}>Message to Homeowner</label><textarea value={bidForm.note} onChange={e=>setBidForm(f=>({...f,note:e.target.value}))}
              placeholder="Describe your approach, experience with similar jobs, and how you'd handle this specific project..."
              rows={3} style={{ ...inputSt, background:C.white, border:`1.5px solid ${C.sand}`, color:C.ink, resize:"vertical" }} />

            {/* Reworded acknowledgment — estimate not guarantee */}
            <div onClick={() => setBidForm(f=>({...f,acknowledged:!f.acknowledged}))}
              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px", background: bidForm.acknowledged ? "rgba(184,92,56,0.05)" : C.sand, border:`1.5px solid ${bidForm.acknowledged ? "rgba(184,92,56,0.30)" : C.sand}`, borderRadius:10, cursor:"pointer", marginBottom:16, transition:"all 0.2s" }}><div style={{ width:22, height:22, borderRadius:6, flexShrink:0, marginTop:1, border: `2px solid ${bidForm.acknowledged ? C.terra : C.slate}`, background: bidForm.acknowledged ? C.terra : "transparent", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                {bidForm.acknowledged && <span style={{ color:"white", fontSize:12, fontWeight:700 }}>✓</span>}
              </div><div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color: bidForm.acknowledged ? C.terra : C.ink, marginBottom:4 }}>
                  This is my best estimate based on the project brief
                </div><div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, lineHeight:1.5 }}>
                  I understand a site visit may be needed before a final contract. Any change orders above 10% of this estimate require homeowner approval through RenoBid.
                </div></div></div>

            {/* In-person consultation toggle */}
            <div onClick={() => setBidForm(f=>({...f,needsConsultation:!f.needsConsultation}))}
              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px", background: bidForm.needsConsultation ? "rgba(62,85,109,0.06)" : C.cream, border:`1.5px solid ${bidForm.needsConsultation ? C.slate : C.sand}`, borderRadius:10, cursor:"pointer", marginBottom:16, transition:"all 0.2s" }}>
              <div style={{ width:22, height:22, borderRadius:6, flexShrink:0, marginTop:1, border:`2px solid ${bidForm.needsConsultation ? C.slate : "rgba(0,0,0,0.25)"}`, background: bidForm.needsConsultation ? C.slate : "transparent", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                {bidForm.needsConsultation && <span style={{ color:"white", fontSize:12, fontWeight:700 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, color: bidForm.needsConsultation ? C.ink : C.ink, marginBottom:4 }}>
                  I need an in-person consultation to finalize this estimate
                </div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.slate, lineHeight:1.5 }}>
                  The homeowner will schedule a visit based on your availability set in Profile Details. You can revise your bid after the consultation.
                </div>
              </div>
            </div>

            {/* Summary */}
            {(() => {
              const _hasLI = activeProject.scope?.some(s=>s.by==="contractor");
              const { unit: _lu, labor: _ll, material: _lm } = computeLineItemTotals(bidForm.lineItems);
              const _labor = _hasLI && _ll > 0 ? _ll : parseInt(bidForm.laborCost||0);
              const _mat   = _hasLI && _lm > 0 ? _lm : parseInt(bidForm.materialCost||0);
              const _total = _labor + _mat + _lu + parseInt(bidForm.permitCost||0);
              if (!_total) return null;
              return (
                <div style={{ background:"rgba(184,92,56,0.06)", border:`1px solid rgba(184,92,56,0.18)`, borderRadius:10, padding:"12px 16px", marginBottom:16, fontFamily:"'DM Sans',sans-serif", fontSize:13, color:C.slate, lineHeight:2 }}>
                  <strong style={{ color:C.terra, fontSize:15 }}>${_total.toLocaleString()}</strong> estimate
                  {bidForm.startDate && <>{" · "}Start <strong style={{ color:C.ink }}>{new Date(bidForm.startDate + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</strong></>}
                  {bidForm.duration && <>{" · "}<strong style={{ color:C.ink }}>{bidForm.duration}</strong></>}
                  {" · "}Platform fee: <strong style={{ color:C.ink }}>${Math.round(_total*0.04).toLocaleString()}</strong>
                  {activeProject.scope?.length > 0 && activeProject.scope.some(s => hasPermitRisk(ALL_SCOPE_FLAT.find(i=>i.id===s.id)) && s.by==="contractor") && (
                    <div style={{ marginTop:4, fontSize:11, color:C.gold, fontWeight:600 }}> Have you included permit costs in your breakdown?</div>
                  )}
                </div>
              );
            })()}

            {(() => {
              const _hasLI2 = activeProject.scope?.some(s=>s.by==="contractor");
              const { unit: _lu2, labor: _ll2, material: _lm2 } = computeLineItemTotals(bidForm.lineItems);
              const _l = _hasLI2 && _ll2 > 0 ? _ll2 : parseInt(bidForm.laborCost||0);
              const _m = _hasLI2 && _lm2 > 0 ? _lm2 : parseInt(bidForm.materialCost||0);
              const _submitTotal = _l + _m + _lu2 + parseInt(bidForm.permitCost||0);
              const _canSubmit = !!_submitTotal && bidForm.acknowledged;
              return (
                <button onClick={submitBid} disabled={!_canSubmit}
                  style={{ width:"100%", background: _canSubmit ? C.terra : C.sand, color: _canSubmit ? C.white : C.slate, border:"none", borderRadius:8, padding:"14px 0", fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:700, transition:"all 0.2s", cursor: _canSubmit ? "pointer" : "not-allowed" }}>
                  {!bidForm.acknowledged ? "Confirm above to submit" : "Submit Estimate →"}
                </button>
              );
            })()}</div></div></div></div>
  );

  //  CONTRACTOR MESSAGES 
  if (view === "messages") {
    const projectsWithMessages = projects.filter(p => p.messages?.length > 0 || p.bids?.some(b=>b.contractorId===contractor.id));
    return (
      <div style={{ minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column" }}><GlobalStyles /><Nav /><div style={{ display:"flex", flex:1, maxHeight:"calc(100vh - 56px)", overflow:"hidden" }}>
          {/* Project list */}
          <div style={{ width:280, borderRight:`1px solid ${C.sand}`, overflowY:"auto", padding:16, flexShrink:0 }}><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:C.slate,textTransform:"uppercase",letterSpacing:1,marginBottom:12 }}>Conversations</div>
            {projectsWithMessages.length===0 ? (
              <div style={{ color:C.slate,fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"center",paddingTop:32 }}>No conversations yet</div>
            ) : projectsWithMessages.map(p => (
              <div key={p.id} onClick={()=>setActiveProject(p)} style={{ padding:"12px 14px",borderRadius:8,cursor:"pointer",marginBottom:6,background:activeProject?.id===p.id?"rgba(184,92,56,0.10)":"rgba(255,255,255,0.03)",border:`1px solid ${activeProject?.id===p.id?"rgba(184,92,56,0.20)":"transparent"}`,transition:"all 0.15s" }}><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.ink,fontWeight:500,marginBottom:2 }}>{p.title}</div><div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:C.slate }}>{p.homeowner} · {p.messages?.length||0} msgs</div></div>
            ))}
          </div>

          {/* Message thread */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            {!activeProject ? (
              <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:C.slate,fontFamily:"'DM Sans',sans-serif",fontSize:15 }}>
                Select a conversation
              </div>
            ) : (
              <><div style={{ padding:"14px 20px",borderBottom:`1px solid ${C.sand}`,fontFamily:"'Playfair Display',serif",fontSize:17,color:C.ink,fontWeight:700 }}>
                  {activeProject.title} <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:C.slate,fontWeight:400 }}>· {activeProject.homeowner}</span></div><div style={{ flex:1,overflowY:"auto",padding:20 }}>
                  {(!activeProject.messages||activeProject.messages.length===0) ? (
                    <div style={{ textAlign:"center",paddingTop:40,color:C.slate,fontFamily:"'DM Sans',sans-serif" }}>No messages yet. Introduce yourself!</div>
                  ) : activeProject.messages.map((m,i)=>(
                    <div key={i} className="fade-in" style={{ display:"flex",justifyContent:m.from==="contractor"?"flex-end":"flex-start",marginBottom:12 }}><div style={{ maxWidth:"70%",background:m.from==="contractor"?C.terra:"rgba(255,255,255,0.08)",color:C.ink,borderRadius:m.from==="contractor"?"12px 12px 4px 12px":"12px 12px 12px 4px",padding:"10px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:14,lineHeight:1.5 }}>
                        {m.text}
                        <div style={{ fontSize:10,opacity:0.5,marginTop:4,textAlign:"right" }}>{m.time}</div></div></div>
                  ))}
                  <div ref={msgEndRef} /></div><div style={{ padding:16,borderTop:`1px solid ${C.sand}`,display:"flex",gap:10 }}><input value={msgInput} onChange={e=>setMsgInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendMessage(activeProject.id)} placeholder="Message the homeowner..." style={{ flex:1,background:"rgba(255,255,255,0.07)",border:`1.5px solid ${C.sand}`,borderRadius:8,padding:"10px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:C.ink }} /><button onClick={()=>sendMessage(activeProject.id)} style={{ background:C.terra,color:C.ink,border:"none",borderRadius:8,padding:"10px 20px",fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14 }}>Send</button></div></>
            )}
          </div></div></div>
    );
  }
  return null;
}

//  SHARED STYLES 
const labelSt = { display:"block", fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, color:C.ink, textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 };
const chipSt = { border:"1.5px solid", borderRadius:100, padding:"7px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:13, cursor:"pointer", transition:"all 0.15s", fontWeight:500 };
const inputSt = { width:"100%", border:`1.5px solid ${C.sand}`, borderRadius:8, padding:"11px 13px", fontFamily:"'DM Sans',sans-serif", fontSize:14, color:C.ink, background:C.white, marginBottom:18, display:"block" };
